import { DatePipe, DecimalPipe, Location, NgClass, NgStyle } from "@angular/common";
import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  NgZone,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
} from "@angular/core";
import { taskDueLabel } from 'src/app/features/tasks/services/tasks.service';
import { FormsModule } from "@angular/forms";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from "@angular/router";
import { PickerModule } from '@ctrl/ngx-emoji-mart';
import { Store } from "@ngrx/store";
import axios from "axios";
import { Channel, Converse, Project } from "converse.svc-client";
import { InfiniteScrollModule } from "ngx-infinite-scroll";
import { Subscription, distinctUntilChanged, filter, firstValueFrom, map } from "rxjs";
import { TaskComposerService } from 'src/app/features/tasks/services/task-composer.service';
import { HighlightTextPipe } from "src/app/pipe/highlight-text.pipe";
import { getFcmRegistration } from "src/app/service/fcm-service-worker";
import { NotificationSoundService } from "src/app/service/notification-sound.service";
import { OnlineUsersService } from "src/app/service/online-users.service";
import { PwaService } from "src/app/service/pwa.service";
import { ThemeService } from "src/app/service/theme.service";
import { setUnreadCount } from "src/app/store/counter.actions";
import { messaging, onMessage } from "../../../../firebase-config";
import { environment } from "../../../environments/environment";
import { ChatSortPipe } from "../../pipe/chat-sort.pipe";
import { ChatCacheService } from '../../service/chat-cache.service';
import { ChatService } from "../../service/chat.service";
import { KlipyService } from "../../service/klipy.service";
import { UserService } from "../../service/user.service";
import { HeaderComponent } from "../header/header.component";
import { alertModalClearChat, alertModalDelete, alertModalWithoutConfirm } from "../helpers/alert";
import { AdvancedSearchComponent } from './components/advanced-search/advanced-search.component';
import { CameraCaptureComponent } from './components/camera-capture/camera-capture.component';
import { ChatHeaderComponent } from './components/chat-header/chat-header.component';
import { ChatMessageItemComponent } from './components/chat-message-item/chat-message-item.component';
import { ChatSidebarComponent } from './components/chat-sidebar/chat-sidebar.component';
import { CreateGroupModalComponent } from './components/create-group-modal/create-group-modal.component';
import { ForwardModalComponent } from './components/forward-modal/forward-modal.component';
import { GifPanelComponent } from './components/gif-panel/gif-panel.component';
import { GroupInfoPanelComponent } from './components/group-info-panel/group-info-panel.component';
import { MessageInputComponent } from './components/message-input/message-input.component';
import { MessageMenuComponent } from './components/message-menu/message-menu.component';
import { MessageSeenInfoModalComponent, SeenMember } from './components/message-seen-info-modal/message-seen-info-modal.component';
import { SearchResultsComponent } from './components/search-results/search-results.component';
import { FilePreviewModalComponent } from "./file-preview-modal.component";
import { ImageViewerDialogComponent } from "./image-viewer-dialog.component";
import { QuickReactionMenuComponent } from './quick-reaction-menu/quick-reaction-menu.component';
import { ChatDraftService } from '../../service/chat-draft.service';
import {
  DOCUMENT_EXTENSIONS,
  formatCodeOrHtmlPreview,
  getCleanFileName,
  getFileType,
  getFilesFromMessage,
  getMessageMeta,
  getReplyMediaType,
  getReplyMediaTypeIcon,
  getReplyMessageText,
  getReplyThumbnail,
  getTimeAgo,
  isAudio,
  isCodeOrHtml,
  isDocument,
  isGifMessage,
  isImage,
  isMessageRedundantWithFile,
  isSystemMessage,
  isURL,
  isValidFileType,
  isValidImageUrl,
  isVideo,
  isVoiceMessage,
  stripTimestampPrefix
} from './utils/helpers.utils';
import { getIcon } from './utils/icons.utils';
import { closeChatNotifications } from 'src/app/service/fcm-service-worker';
import { compressMultipleFiles } from './utils/image-compressor.utils';

/** Module lines a person may hide in a chat (item 28). */
type ActivityKind = 'tasks' | 'finance' | 'leads' | 'system';

import { NotificationSettingsPanelComponent } from './components/notification-settings-panel/notification-settings-panel.component';
import { StarredMessagesPanelComponent } from './components/starred-messages-panel/starred-messages-panel.component';
import { QuickSearchComponent } from './components/quick-search/quick-search.component';



const CHUNK_SIZE = 500 * 1024 * 1024;
const MIN_CHUNK_SIZE = 10 * 1024 * 1024;

// A message waiting in the offline outbox, with the push notifications to emit once it is delivered
interface OutboxItem {
  chatType: "single" | "group";
  message: any;
  notifications: any[];
  preview: string; // chat list text, e.g. "📷 Photo"
}

@Component({
  selector: "app-main-unified-chat",
  standalone: true,
  imports: [
    HeaderComponent,
    CameraCaptureComponent,
    FormsModule,
    DatePipe,
    ChatSortPipe,
    NgClass,
    NgStyle,
    InfiniteScrollModule,
    HighlightTextPipe,
    MatDialogModule,
    PickerModule,
    QuickReactionMenuComponent,
    ForwardModalComponent,
    GifPanelComponent,
    MessageMenuComponent,
    GroupInfoPanelComponent,
    CreateGroupModalComponent,
    ChatSidebarComponent,
    ChatMessageItemComponent,
    ChatHeaderComponent,
    MessageInputComponent,
    MessageSeenInfoModalComponent,
    AdvancedSearchComponent,
    SearchResultsComponent,
    DecimalPipe,
    StarredMessagesPanelComponent,
    NotificationSettingsPanelComponent,
    QuickSearchComponent,
  ],
  templateUrl: "./main-unified-chat.component.html",
  styleUrls: ["./main-unified-chat.component.css"]
})
export class MainUnifiedChatComponent implements OnInit, AfterViewChecked {
  onlineSub: Subscription;
  statusSubscription: Subscription;

  // Message notification sound
  messageNotificationSound: HTMLAudioElement;
  audioUnlocked = false;

  @ViewChild("messageInput") messageInputRef!: ElementRef;
  @ViewChild("singleMessageInput") singleMessageInput!: MessageInputComponent;
  @ViewChild("groupMessageInput") groupMessageInput!: MessageInputComponent;
  @ViewChild("groupInfoModal") groupInfoModal!: TemplateRef<any>;
  @ViewChild("advancedSearch") advancedSearchComp!: AdvancedSearchComponent;
  @ViewChild("chatSidebar") chatSidebarComp?: ChatSidebarComponent;
  @ViewChild("quickSearchComp") quickSearchComp?: QuickSearchComponent;

  converse: Converse | null = null;
  converseInitPromise: Promise<void> | null = null;
  chatLoadingError: string | null = null;
  private activeChatLoadingKey: string | null = null;
  private activeChatLoadingPromise: Promise<void> | null = null;
  chan: Channel | null = null;
  groupChannel: any;
  singleChannel: any;
  showcount: boolean = true;
  userId: any;
  isChatUserOnline: boolean = false;
  chatUserStatus: string = "";
  onlineUserList: any[] = [];
  messageMeta: {
    mentions?: Array<{
      userId: string;
      name: string;
      position: number;
      length: number;
    }>;
  } | null = null;
  isMobileView: boolean = false;
  showChatWindow: boolean = false;
  senderUsername: string = "";
  activeChannel: string = "";
  selectedChatUserId: string = "";
  selectedChatGroupId: string = "";
  showClickForInfo: boolean = false;
  private infoTimer: any;
  groupChatDescription: string = "";
  isGroupAdmin: boolean = false;
  // Emoji Picker
  showEmojiPicker: boolean = false;
  isSending: boolean = false;
  pendingGroupedUploads: Map<string, any[]> | null = null;
  // Track groupIds that have already been counted for unread messages
  processedGroupIdsForUnread: Set<string> = new Set();
  // Track message IDs deleted for me (user-specific deletion)
  deletedForMeMessageIds: Set<string> = new Set();


  // Typing status tracking
  typingUsers: Map<string, { userId: string; userName: string; timestamp: number }> = new Map();
  private typingTimer: any;
  // Background channel connections for typing indicator in sidebar (even when chat not open)
  private backgroundTypingChannels: Map<string, any> = new Map();

  showAdvancedSearchFilters: boolean = false;
  showAdvancedSearchResults: boolean = false;
  showMobileSearchResults: boolean = false;
  advancedSearchResults: any[] = [];
  advancedSearchMatches: number = 0;
  advancedSearchFilters: any = {};
  isNavigatedFromSearch: boolean = false;
  isNavigatedFromStarred: boolean = false;
  isLastNavAdvancedSearch: boolean = false;
  isSearchLoading: boolean = false;
  targetSearchMessageId: string | null = null;
  showCamera: boolean = false;

  starredMessages: any[] = [];
  showStarredMessages: boolean = false;
  isStarredMessagesPanelOpen: boolean = false;
  isQuickSearchOpen: boolean = false;
  isStarredLoading: boolean = false;
  starredMessagesPage: number = 1;
  hasMoreStarredMessages: boolean = true;
  // Background upload tracking: channelId -> Array of temp message objects
  private pendingUploadsByChannel: Map<string, any[]> = new Map();
  // Offline outbox: channelId -> messages waiting to be sent (shown with a clock until delivered)
  private outboxByChannel: Map<string, OutboxItem[]> = new Map();
  private outboxFlushing = false;
  private outboxRetryTimer: any = null;

  // Template Helpers
  getReplyThumbnail = getReplyThumbnail;
  getReplyMediaType = getReplyMediaType;
  getReplyMessageText = getReplyMessageText;
  getTimeAgo = getTimeAgo;
  isSystemMessage = isSystemMessage;

  // ── activity lines filter (per founder, 20 Oct 2026): hide a module's system lines in this chat view, per person, per device ──
  readonly activityKinds: { id: ActivityKind; label: string }[] = [
    { id: 'tasks', label: 'Tasks' },
    { id: 'finance', label: 'Finance' },
    { id: 'leads', label: 'Leads' },
    { id: 'system', label: 'Other updates' },
  ];
  hiddenActivity: Record<ActivityKind, boolean> = this.readHiddenActivity();

  private activityStorageKey(): string {
    const uid = localStorage.getItem('guestID') || '';
    return uid ? `chatActivityHidden_${uid}` : 'chatActivityHidden';
  }
  private readHiddenActivity(): Record<ActivityKind, boolean> {
    const base: Record<ActivityKind, boolean> = { tasks: false, finance: false, leads: false, system: false };
    try {
      const saved = JSON.parse(localStorage.getItem(this.activityStorageKey()) || '{}');
      for (const k of Object.keys(base) as ActivityKind[]) if (saved[k] === true) base[k] = true;
    } catch { /* keep defaults */ }
    return base;
  }
  /** Which module wrote this line, or null for a person's message. */
  activityKindOf(message: any): ActivityKind | null {
    const m = message?.message && typeof message.message === 'object' ? message.message : message;
    const meta = m?.meta || message?.meta || {};
    const mod = String(meta.module || '').toLowerCase();
    if (mod === 'tasks') return 'tasks';
    if (mod === 'finance' || meta.type === 'finance' || m?.type === 'finance') return 'finance';
    if (mod === 'lead-tracking' || mod === 'lead-track') return 'leads';
    if (isSystemMessage(m) || isSystemMessage(message)) return 'system';
    return null;
  }
  isHiddenActivity(message: any): boolean {
    const k = this.activityKindOf(message);
    return !!k && this.hiddenActivity[k];
  }
  toggleActivity(kind: ActivityKind): void {
    this.hiddenActivity = { ...this.hiddenActivity, [kind]: !this.hiddenActivity[kind] };
    try { localStorage.setItem(this.activityStorageKey(), JSON.stringify(this.hiddenActivity)); } catch { /* ignore */ }
  }
  /** The filter bar shows only when the open chat carries activity lines. */
  hasActivity(messages: any[]): boolean {
    return Array.isArray(messages) && messages.some((m) => this.activityKindOf(m) !== null);
  }
  hiddenActivityCount(messages: any[]): number {
    return Array.isArray(messages) ? messages.filter((m) => this.isHiddenActivity(m)).length : 0;
  }
  isURL = isURL;
  isValidImageUrl = isValidImageUrl;
  getMessageMeta = getMessageMeta;
  isVoiceMessage = isVoiceMessage;

  hasLinkMeta(message: any): boolean {
    const target = (message.message && typeof message.message === 'object') ? message.message : message;
    const meta = getMessageMeta(target);
    if (!meta) return false;

    const title = (meta.title || '').trim();
    const description = (meta.description || '').trim();
    const image = (meta.image || '').trim();
    const icon = (meta.icon || '').trim();

    const isImageValid = !!(image && !meta.imageBroken);
    const isIconValid = !!(icon && !meta.iconBroken);

    return !!(title || description || isImageValid || isIconValid);
  }

  isIconMode(message: any): boolean {
    const target = (message.message && typeof message.message === 'object') ? message.message : message;
    const meta = getMessageMeta(target);
    if (!meta) return false;

    const image = (meta.image || '').trim();
    const icon = (meta.icon || '').trim();

    const isImageValid = !!(image && !meta.imageBroken);
    const isIconValid = !!(icon && !meta.iconBroken);

    if (!isIconValid) return false;
    if (!isImageValid) return true;
    if (image === icon) return true;
    const lowerImage = image.toLowerCase();
    if (lowerImage.includes('favicon') || lowerImage.endsWith('.ico')) return true;

    return false;
  }

  onPreviewImageError(message: any) {
    const target = (message.message && typeof message.message === 'object') ? message.message : message;
    if (target.meta) {
      target.meta.imageBroken = true;
      this.cdRef.detectChanges();
    }
  }

  onPreviewIconError(message: any) {
    const target = (message.message && typeof message.message === 'object') ? message.message : message;
    if (target.meta) {
      target.meta.iconBroken = true;
      this.cdRef.detectChanges();
    }
  }

  openUrl(url: string) {
    if (!url) return;
    window.open(url, '_blank');
  }




  activeAudio: HTMLAudioElement | null = null;
  currentlyPlayingId: string | null = null;
  audioProgress: { [key: string]: { currentTime: number, duration: number } } = {};

  // Conversation Cache
  cachedSingleMessages: { [channelId: string]: any[] } = {};
  cachedGroupMessages: { [channelId: string]: any[] } = {};
  cachedSingleHasMore: { [channelId: string]: boolean } = {};
  cachedGroupHasMore: { [channelId: string]: boolean } = {};
  cachedSingleOldestCreatedAt: { [channelId: string]: string | null } = {};
  cachedGroupOldestCreatedAt: { [channelId: string]: string | null } = {};
  cachedSingleScrollReady: { [channelId: string]: boolean } = {};
  cachedGroupScrollReady: { [channelId: string]: boolean } = {};
  cachedSingleChannels: { [channelId: string]: any } = {};
  cachedGroupChannels: { [channelId: string]: any } = {};
  cachedPinnedMessages: { [channelId: string]: any[] } = {};
  cachedStarredMessages: { [channelId: string]: any[] } = {};

  updateCacheForActiveChannel() {
    if (this.singleChatBox && this.activeChannel) {
      if (this.singleChatMessages && this.singleChatMessages.length > 0) {
        this.cachedSingleMessages[this.activeChannel] = this.singleChatMessages;
        this.cachedSingleHasMore[this.activeChannel] = this.hasMoreSingleMessages;
        this.cachedSingleOldestCreatedAt[this.activeChannel] = this.oldestSingleMessageCreatedAt;
        this.cachedSingleScrollReady[this.activeChannel] = this.scrollReadySingle;
        if (this.chatCacheService) {
          this.chatCacheService.saveMessages(this.activeChannel, this.singleChatMessages);
        }
      }
    } else if (this.groupChatBox && this.memberChannelId) {
      if (this.groupChatMessages && this.groupChatMessages.length > 0) {
        this.cachedGroupMessages[this.memberChannelId] = this.groupChatMessages;
        this.cachedGroupHasMore[this.memberChannelId] = this.hasMoreGroupMessages;
        this.cachedGroupOldestCreatedAt[this.memberChannelId] = this.oldestMessageCreatedAt;
        this.cachedGroupScrollReady[this.memberChannelId] = this.scrollReady;
        if (this.chatCacheService) {
          this.chatCacheService.saveMessages(this.memberChannelId, this.groupChatMessages);
        }
      }
    }
  }

  appendMessageToBackgroundCache(channelId: string, msg: any, isGroup: boolean) {
    if (!channelId) return;
    const cachedMsgs = isGroup ? this.cachedGroupMessages[channelId] : this.cachedSingleMessages[channelId];
    if (!cachedMsgs) return;

    const incoming = (msg?.message && typeof msg.message === 'object') ? msg.message : msg;
    const messageId = incoming?._id || incoming?.id || msg?._id || msg?.id || incoming?.messageId || msg?.messageId;
    const meta = incoming?.meta || msg?.meta || {};
    const senderId = msg?.sender_id ?? incoming?.sender_id ?? incoming?.sid ?? msg?.sid;

    const metaGroupId = meta?.groupId || meta?.data?.groupId;
    const isGroupedUpload = meta?.isGroupedUpload || !!metaGroupId;
    const groupId = metaGroupId;

    // Check duplicate
    if (messageId) {
      const exists = cachedMsgs.some((m: any) => (m._id || m.id || m.messageId) === messageId);
      if (exists) return;
    }

    const tempId = meta?.tempId || msg?.tempId || msg?.message?.meta?.tempId;
    if (tempId) {
      const idx = cachedMsgs.findIndex((m: any) => m.meta?.tempId === tempId || m.tempId === tempId);
      if (idx !== -1) {
        const isSysMsg = isGroup ? (meta?.type === 'system' || meta?.subType) : isSystemMessage(incoming || msg);
        let messageContent = "";
        if (typeof incoming.message === 'string') {
          messageContent = incoming.message;
        } else if (incoming.message && typeof incoming.message === 'object') {
          messageContent = incoming.message.message || incoming.message.text || incoming.message.content || "";
        } else {
          messageContent = incoming.text || incoming.content || "";
        }
        if (isSysMsg && messageContent) {
          messageContent = this.reformatPinSystemMessage(messageContent);
        }
        let fileUrl = meta?.fileUrl || incoming?.url || (typeof incoming?.file === 'string' ? incoming.file : incoming?.file?.url);
        if (!fileUrl && typeof incoming.message === 'object' && incoming.message !== null) {
          fileUrl = incoming.message.url || incoming.message.file || incoming.message.fileUrl;
        }
        let previewMessage = messageContent;
        const hasFiles = incoming.files && incoming.files.length > 0;
        const effectiveUrl = fileUrl || (hasFiles ? incoming.files[0].fileUrl : null);
        const isGif = this.isGifMessage(incoming);
        if ((messageContent === 'Sent an attachment' || messageContent.toLowerCase() === 'sent an attachment') && effectiveUrl) {
          previewMessage = this.generateFilePreview(effectiveUrl);
          messageContent = isGif ? "" : messageContent;
        }
        const filesList = hasFiles ? incoming.files : (fileUrl ? [{
          fileUrl: fileUrl,
          type: getFileType(fileUrl),
          name: getCleanFileName(messageContent || 'File', fileUrl)
        }] : []);
        const existing = cachedMsgs[idx];
        const updatedMsg = {
          ...existing,
          ...incoming,
          _id: messageId,
          id: messageId,
          message: messageContent || existing.message,
          preview: previewMessage || existing.preview,
          sender_id: senderId,
          sid: senderId,
          createdAt: incoming.createdAt || existing.createdAt,
          updatedAt: incoming.updatedAt || new Date().toISOString(),
          meta: this.normalizeLinkMeta({ ...existing.meta, ...meta }),
          files: (filesList && filesList.length) ? filesList : existing.files,
          isUploading: false,
        };
        const newCachedMsgs = [...cachedMsgs];
        newCachedMsgs[idx] = updatedMsg;
        if (isGroup) {
          this.cachedGroupMessages[channelId] = newCachedMsgs;
        } else {
          this.cachedSingleMessages[channelId] = newCachedMsgs;
        }
        this.updateLocalChatLastMessage(channelId, updatedMsg, isGroup);
        return;
      }
    }

    if (groupId) {
      const idx = cachedMsgs.findIndex((m: any) => {
        const mGroupId = m.meta?.groupId || m.meta?.data?.groupId;
        return mGroupId && String(mGroupId) === String(groupId);
      });
      if (idx !== -1) {
        const isSysMsg = isGroup ? (meta?.type === 'system' || meta?.subType) : isSystemMessage(incoming || msg);
        let messageContent = "";
        if (typeof incoming.message === 'string') {
          messageContent = incoming.message;
        } else if (incoming.message && typeof incoming.message === 'object') {
          messageContent = incoming.message.message || incoming.message.text || incoming.message.content || "";
        } else {
          messageContent = incoming.text || incoming.content || "";
        }
        if (isSysMsg && messageContent) {
          messageContent = this.reformatPinSystemMessage(messageContent);
        }
        let fileUrl = meta?.fileUrl || incoming?.url || (typeof incoming?.file === 'string' ? incoming.file : incoming?.file?.url);
        if (!fileUrl && typeof incoming.message === 'object' && incoming.message !== null) {
          fileUrl = incoming.message.url || incoming.message.file || incoming.message.fileUrl;
        }
        let previewMessage = messageContent;
        const hasFiles = incoming.files && incoming.files.length > 0;
        const effectiveUrl = fileUrl || (hasFiles ? incoming.files[0].fileUrl : null);
        const isGif = this.isGifMessage(incoming);
        if ((messageContent === 'Sent an attachment' || messageContent.toLowerCase() === 'sent an attachment') && effectiveUrl) {
          previewMessage = this.generateFilePreview(effectiveUrl);
          messageContent = isGif ? "" : messageContent;
        }
        const filesList = hasFiles ? incoming.files : (fileUrl ? [{
          fileUrl: fileUrl,
          type: getFileType(fileUrl),
          name: getCleanFileName(messageContent || 'File', fileUrl)
        }] : []);
        const existing = cachedMsgs[idx];
        const updatedMsg = {
          ...existing,
          ...incoming,
          _id: messageId,
          id: messageId,
          message: messageContent || existing.message,
          preview: previewMessage || existing.preview,
          sender_id: senderId,
          sid: senderId,
          createdAt: incoming.createdAt || existing.createdAt,
          updatedAt: incoming.updatedAt || new Date().toISOString(),
          meta: this.normalizeLinkMeta({ ...existing.meta, ...meta }),
          files: (filesList && filesList.length) ? filesList : existing.files,
          isUploading: false,
        };
        const newCachedMsgs = [...cachedMsgs];
        newCachedMsgs[idx] = updatedMsg;
        if (isGroup) {
          this.cachedGroupMessages[channelId] = newCachedMsgs;
        } else {
          this.cachedSingleMessages[channelId] = newCachedMsgs;
        }
        this.updateLocalChatLastMessage(channelId, updatedMsg, isGroup);
        return;
      }
    }

    const isSysMsg = isGroup ? (meta?.type === 'system' || meta?.subType) : isSystemMessage(incoming || msg);

    let messageContent = "";
    if (typeof incoming.message === 'string') {
      messageContent = incoming.message;
    } else if (incoming.message && typeof incoming.message === 'object') {
      messageContent = incoming.message.message || incoming.message.text || incoming.message.content || "";
    } else {
      messageContent = incoming.text || incoming.content || "";
    }

    if (isSysMsg && messageContent) {
      messageContent = this.reformatPinSystemMessage(messageContent);
    }

    let fileUrl = meta?.fileUrl || incoming?.url || (typeof incoming?.file === 'string' ? incoming.file : incoming?.file?.url);
    if (!fileUrl && typeof incoming.message === 'object' && incoming.message !== null) {
      fileUrl = incoming.message.url || incoming.message.file || incoming.message.fileUrl;
    }

    let previewMessage = messageContent;
    const hasFiles = incoming.files && incoming.files.length > 0;
    const effectiveUrl = fileUrl || (hasFiles ? incoming.files[0].fileUrl : null);
    const isGif = this.isGifMessage(incoming);

    if ((messageContent === 'Sent an attachment' || messageContent.toLowerCase() === 'sent an attachment') && effectiveUrl) {
      previewMessage = this.generateFilePreview(effectiveUrl);
      messageContent = isGif ? "" : messageContent;
    }

    // Handle Grouped Upload / Carousel
    if (isGroupedUpload && groupId && !isGif) {
      const existingGroupedMsgIdx = cachedMsgs.findIndex(m => {
        const mGroupId = m.meta?.groupId || m.meta?.data?.groupId;
        return mGroupId && String(mGroupId) === String(groupId);
      });

      if (existingGroupedMsgIdx !== -1) {
        const existingGroupedMsg = cachedMsgs[existingGroupedMsgIdx];
        if (effectiveUrl) {
          const alreadyExists = existingGroupedMsg.files.some((f: any) => f.fileUrl === effectiveUrl);
          if (!alreadyExists) {
            const fileName = getCleanFileName('', effectiveUrl);
            const updatedFiles = [...existingGroupedMsg.files, {
              fileUrl: effectiveUrl,
              type: getFileType(effectiveUrl),
              name: fileName,
            }];
            const existingIds: string[] = existingGroupedMsg._allIds || [];
            if (messageId && !existingIds.includes(messageId)) {
              existingIds.push(messageId);
            }
            const updatedMsg = {
              ...existingGroupedMsg,
              _id: messageId,
              id: messageId,
              files: updatedFiles,
              _allIds: existingIds
            };
            if (incoming.meta?.caption) {
              if (!updatedMsg.meta) updatedMsg.meta = {};
              updatedMsg.meta = { ...updatedMsg.meta, caption: incoming.meta.caption };
            }
            const newCachedMsgs = [...cachedMsgs];
            newCachedMsgs[existingGroupedMsgIdx] = updatedMsg;
            if (isGroup) {
              this.cachedGroupMessages[channelId] = newCachedMsgs;
            } else {
              this.cachedSingleMessages[channelId] = newCachedMsgs;
            }
          }
        }
        return;
      }
    }

    const filesList = hasFiles ? incoming.files : (fileUrl ? [{
      fileUrl: fileUrl,
      type: getFileType(fileUrl),
      name: getCleanFileName(messageContent || 'File', fileUrl)
    }] : []);

    const newMsg = {
      ...incoming,
      _id: messageId,
      id: messageId,
      message: messageContent,
      preview: previewMessage,
      sender_id: senderId,
      sid: senderId,
      createdAt: incoming.createdAt || new Date().toISOString(),
      updatedAt: incoming.updatedAt || incoming.createdAt || new Date().toISOString(),
      profileImage: this.profileImageMapping[senderId] ?? null,
      name: this.userNameMapping[senderId] ?? null,
      displayName: this.getDisplayName(senderId),
      meta: this.normalizeLinkMeta(meta),
      files: filesList,
      currentSlideIndex: 0,
      reactions: incoming.reactions || incoming.meta?.reactions || []
    };

    if (isGroupedUpload && groupId && !isGif) {
      newMsg._allIds = [messageId];
    }

    if (isGroup) {
      this.cachedGroupMessages[channelId] = [...cachedMsgs, newMsg];
    } else {
      this.cachedSingleMessages[channelId] = [...cachedMsgs, newMsg];
    }

    // Also update sidebar last message
    this.updateLocalChatLastMessage(channelId, newMsg, isGroup);
    if (this.chatCacheService && channelId) {
      this.chatCacheService.saveMessage(channelId, newMsg);
    }
  }


  togglePlay(event: MouseEvent, message: any) {
    event.stopPropagation();
    const id = message.messageId || message._id || message.id;
    const files = getFilesFromMessage(message);
    const url = files[0]?.fileUrl;

    if (!url) return;

    if (this.currentlyPlayingId === id) {
      if (this.activeAudio) {
        if (this.activeAudio.paused) {
          this.activeAudio.play();
        } else {
          this.activeAudio.pause();
        }
      }
    } else {
      if (this.activeAudio) {
        this.activeAudio.pause();
        this.activeAudio.src = '';
        this.activeAudio.load();
      }
      this.activeAudio = new Audio(url);
      this.currentlyPlayingId = id;

      this.activeAudio.addEventListener('timeupdate', () => {
        if (this.activeAudio && this.currentlyPlayingId === id) {
          this.audioProgress[id] = {
            currentTime: this.activeAudio.currentTime,
            duration: this.activeAudio.duration
          };
          this.cdRef.detectChanges();
        }
      });

      this.activeAudio.addEventListener('ended', () => {
        if (this.currentlyPlayingId === id) {
          this.currentlyPlayingId = null;
          if (this.audioProgress[id]) this.audioProgress[id].currentTime = 0;
          this.cdRef.detectChanges();
        }
      });

      this.activeAudio.play();
    }
  }

  formatDuration(seconds: number): string {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  }

  // Tasks (14 Oct 2026): any chat message can become a task, pre-filled and linked back to this chat.
  private readonly taskComposer = inject(TaskComposerService);
  /** Chat shortcut: "/task <title> [today|tomorrow|mon…sun|next week|in N days]". The task goes to the person you are chatting with; in a group you pick. */
  private pendingChatTask: { channelId: string; at: number } | null = null;
  private chatTaskSub?: Subscription;
  /** Open a chat (single or group) from its channel id, the way a task's "Open chat" and the /task line link to it. */
  private openByChannelId(channelId: string): boolean {
    if (!channelId) return false;
    const cleanId = String(channelId).trim();

    // 1. Try finding in chatMap
    const chatFromMap = this.chatMap?.[cleanId];
    if (chatFromMap) {
      if (chatFromMap.chatType === 'group' || chatFromMap.type === 'group' || chatFromMap.groupName) {
        this.memberChannelId = cleanId;
        this.showGroupChat(chatFromMap);
        return true;
      }
      const uid = chatFromMap.userId || chatFromMap._id || chatFromMap.id ||
        (chatFromMap.members?.find((m: any) => m.userId !== this.userId)?.userId);
      if (uid) {
        this.startSingleChat(uid);
        return true;
      }
    }

    // 2. Try finding in allSingleUsers or sortedChats
    const single = (this.allSingleUsers || []).find((x: any) =>
      String(x.channelId || x.channel_id || '').trim() === cleanId
    ) || (this.sortedChats || []).find((x: any) =>
      (x.chatType === 'single' || x.type === 'single' || (!x.groupName && !x.members && x.userId)) &&
      String(x.channelId || x.channel_id || '').trim() === cleanId
    );
    if (single) {
      const uid = single.userId || single._id || single.id ||
        (single.members?.find((m: any) => m.userId !== this.userId)?.userId);
      if (uid) {
        this.startSingleChat(uid);
        return true;
      }
    }

    // 3. Try finding in groups
    const g: any = this.findGroupByChannelId(cleanId);
    if (g) {
      this.memberChannelId = cleanId;
      this.showGroupChat(g);
      return true;
    }

    console.warn("Could not find chat for channelId:", cleanId);
    return false;
  }

  private waitForChatsTimer: ReturnType<typeof setInterval> | null = null;
  private routeNavTimer: any = null;

  scheduleRouteNavigation() {
    if (this.routeNavTimer) clearTimeout(this.routeNavTimer);
    this.routeNavTimer = setTimeout(() => {
      const checkAndNavigate = () => {
        if (this.sortedChats.length > 0 || !this.chatListLoading) {
          if (this.waitForChatsTimer) {
            clearInterval(this.waitForChatsTimer);
            this.waitForChatsTimer = null;
          }
          this.handleRouteNavigation();
        } else {
          if (!this.waitForChatsTimer) {
            this.waitForChatsTimer = setInterval(() => {
              if (this.sortedChats.length > 0 || !this.chatListLoading) {
                if (this.waitForChatsTimer) {
                  clearInterval(this.waitForChatsTimer);
                  this.waitForChatsTimer = null;
                }
                this.handleRouteNavigation();
              }
            }, 150);
          }
        }
      };
      checkAndNavigate();
    }, 60);
  }

  private lastHandledRouteKey: string | null = null;

  private handleRouteNavigation() {
    const queryParams = this.route.snapshot.queryParams;
    const chatType = queryParams["chatType"];
    const userId = queryParams["userId"];
    const channelId = queryParams["channelId"];
    const pathChannel = this.route.snapshot.paramMap.get("channelId") || "";

    const routeKey = JSON.stringify({ pathChannel, queryParams });
    if (routeKey === this.lastHandledRouteKey) return;

    if (pathChannel || channelId || (chatType === "single" && userId)) {
      this.openedFromChatList = false;
    }

    let opened = false;
    if (chatType === "single" && userId) {
      this.startSingleChat(userId);
      opened = true;
    } else if (chatType === "group" && channelId) {
      const activeGroup = this.findGroupByChannelId(channelId);
      if (activeGroup) {
        this.memberChannelId = channelId;
        this.showGroupChat(activeGroup);
        opened = true;
      } else {
        opened = this.openByChannelId(channelId);
      }
    } else if (pathChannel) {
      opened = this.openByChannelId(pathChannel);
    } else if (channelId) {
      opened = this.openByChannelId(channelId);
    }

    if (opened) {
      this.lastHandledRouteKey = routeKey;
    } else if (this.isMobileView && this.showChatWindow && !pathChannel && !channelId && !userId) {
      this.closeChatWindow(true);
      this.lastHandledRouteKey = routeKey;
      this.cdRef.detectChanges();
    }
  }

  private findGroupByChannelId(channelId: string): any {
    const cleanId = String(channelId).trim();
    const match = (group: any) =>
      String(group.channelId || group.channel_id || group._id || '').trim() === cleanId;
    return (this.allGroupUsers || []).find(match) ||
      (this.filteredGroupUsers || []).find(match) ||
      (this.sortedChats || []).find((c: any) => (c.chatType === 'group' || c.type === 'group' || c.groupName) && match(c));
  }

  startTaskCommand(raw: string) {
    const single = !!(this.singleChatBox && this.singleUser);
    const channelId = String(single ? this.singleUser?.channelId || "" : this.memberChannelId || "");
    const other = single ? this.singleUser?.displayName || this.singleUser?.userName || "" : "";
    const otherId = single ? String(this.singleUser?.userId || this.singleUser?._id || this.singleUser?.id || "") : "";
    const { title, dueDate } = this.parseTaskWords(raw);
    this.pendingChatTask = { channelId, at: Date.now() };
    if (!this.chatTaskSub) {
      this.chatTaskSub = this.taskComposer.created$.subscribe((t: any) => {
        const p = this.pendingChatTask;
        if (!p || Date.now() - p.at > 15 * 60 * 1000) return;
        if (t?.source !== "chat" || !String(t?.link?.url || "").endsWith(p.channelId)) return;
        this.pendingChatTask = null;
        void this.postTaskLine(t);
      });
    }
    this.taskComposer.open({
      title: title || "",
      assigneeId: otherId && otherId !== String(this.userId) ? otherId : undefined,
      dueDate: dueDate ?? undefined,
      link: { type: "chat", id: channelId, label: single ? `Chat with ${other}` : `Group chat${this.groupName ? ": " + this.groupName : ""}`, url: channelId ? `/main-chat/${channelId}` : "/main-chat" },
      source: "chat",
      sourceRef: { channelId, command: true },
    });
  }
  /** Pull a date out of the words: today, tomorrow, a weekday, next week, in N days. */
  private parseTaskWords(raw: string): { title: string; dueDate: string | null } {
    const days = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    const ymd = (offset: number) => { const d = new Date(); d.setDate(d.getDate() + offset); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
    let title = raw.trim();
    let dueDate: string | null = null;
    const take = (re: RegExp, offset: (m: RegExpMatchArray) => number) => {
      const m = title.match(re);
      if (!m) return false;
      dueDate = ymd(offset(m));
      title = title.replace(re, " ").replace(/\s+/g, " ").trim();
      return true;
    };
    take(/\b(by |on |for )?(today|tonight)\b/i, () => 0) ||
      take(/\b(by |on |for )?(tomorrow|tmrw|tmr)\b/i, () => 1) ||
      take(/\bnext week\b/i, () => 7) ||
      take(/\bin (\d{1,2}) days?\b/i, (m) => Number(m[1])) ||
      take(/\b(by |on |for |next )?(sun|mon|tue|tues|wed|thu|thur|thurs|fri|sat)(day|sday|nesday|rsday|urday)?\b/i, (m) => {
        const want = days.findIndex((d) => d.startsWith(m[2].toLowerCase().slice(0, 3)));
        const todayDow = new Date().getDay();
        let diff = (want - todayDow + 7) % 7;
        if (diff === 0 || /next/i.test(m[1] || "")) diff += diff === 0 ? 7 : 0;
        return diff;
      });
    return { title: title.replace(/^[,.\-–]\s*/, ""), dueDate };
  }
  /** After the task exists, one line into the chat so both sides see it. */
  private async postTaskLine(t: any) {
    const who = t?.isGroup ? `${t.groupSize || t?.childStats?.total || ""} people` : t?.assignee?.name || (t?.department ? `${t.department} queue` : "me");
    const when = t?.dueDate ? ` · due ${taskDueLabel(t.dueDate)}${t.dueTimeLabel ? " " + t.dueTimeLabel : ""}` : "";
    const link = `${window.location.origin}/#/tasks?task=${t?.id}`;
    this.newMessage = `📌 Task for ${who}: ${t?.title}${when}\n${link}`;
    await this.sendChatMessage();
  }

  makeTaskFromMessage(message: any) {
    if (this.isWriteBlockedInCurrentChat()) return;
    const text = String(message?.message || '').replace(/\s+/g, ' ').trim();
    const title = text.length > 140 ? text.slice(0, 139) + '…' : text;
    const senderId = String(message?.sender_id || message?.senderId || '');
    const isMine = senderId === String(this.userId);
    const single = !!(this.singleChatBox && this.singleUser);
    const channelId = single ? this.singleUser?.channelId : this.memberChannelId;
    const other = single ? (this.singleUser?.displayName || this.singleUser?.userName) : '';
    const messageId = String(message?._id || message?.id || message?.messageId || '');
    this.taskComposer.open({
      title: title || 'Follow up on a chat message',
      details: text.length > 140 ? text : '',
      // My own message to someone is usually an ask of them; their message to me is usually for me.
      assigneeId: single && isMine && this.singleUser?.userId ? String(this.singleUser.userId) : undefined,
      link: { type: 'chat', id: messageId, label: single ? `Chat with ${other}` : 'Group chat', url: channelId ? `/main-chat/${channelId}` : '/main-chat' },
      source: 'chat',
      sourceRef: { channelId, messageId, senderId },
    });
  }

  constructor(
    private sanitizer: DomSanitizer,
    private chatService: ChatService,
    private chatDraftService: ChatDraftService,
    private api: UserService,
    private OnlineUsersService: OnlineUsersService,
    private router: Router,
    private route: ActivatedRoute,
    private store: Store<{ chat: any }>,
    private cdRef: ChangeDetectorRef,
    private zone: NgZone,
    private location: Location,
    private userService: UserService,
    private dialog: MatDialog,
    private klipyService: KlipyService,
    private soundService: NotificationSoundService,
    private chatCacheService: ChatCacheService,
    private pwaService: PwaService,
  ) { }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;

    // Mention tag click handling
    const mentionEl = target.closest('.mention-highlight') as HTMLElement;
    if (mentionEl) {
      event.preventDefault();
      event.stopPropagation();
      const type = mentionEl.getAttribute('data-mention-type');
      if (type === 'all' || mentionEl.textContent?.trim().toLowerCase() === '@all') {
        this.onAllMentionClick();
      } else {
        const userId = mentionEl.getAttribute('data-user-id') || '';
        const name = mentionEl.getAttribute('data-mention-name') || mentionEl.textContent?.replace('@', '').trim() || '';
        this.onUserMentionClick(userId || name);
      }
      return;
    }

    // Check if click is inside a menu trigger, quick reaction menu, or dropdown menu
    const isInsideMenuTrigger = target.closest('.menu-trigger');
    const isInsideQuickReactionMenu = target.closest('.quick-reaction-menu');
    const isInsideDropdownMenu = target.closest('.dropdown-menu');
    const isInsideReactionPicker = target.closest('.reaction-picker-overlay');
    const isInsideReactionDetailsModal = target.closest('.reaction-details-modal');
    const isInsideMessageOptions = target.closest('.message-options');

    // Close Emoji Picker on outside click
    if (!target.closest('.emoji-button') && !target.closest('.emoji-btn') && !target.closest('emoji-mart') && !target.closest('.emoji-panel')) {
      this.showEmojiPicker = false;
    }

    // Close Chat Menu on outside click
    if (!target.closest('.chat-header-icon') && !target.closest('.dropdown-menu.three-dot')) {
      this.showChatMenuDropdown = false;
    }

    // Close Pinned Messages list on outside click
    if (this.showPinnedMessages &&
      !target.closest('.pinned-list') &&
      !target.closest('.pinned-messages-bar')) {
      this.showPinnedMessages = false;
    }

    // Close Mention dropdown on outside click
    if (this.showMentionList && !target.closest('.mention-dropdown') && !target.closest('textarea')) {
      this.showMentionList = false;
    }

    // Close Suggestion Box on outside click
    if (this.isSuggestionBoxVisible && !target.closest('.filter-data') && !target.closest('textarea')) {
      this.isSuggestionBoxVisible = false;
    }

    if (this.isSuggestionBoxVisible2 && !target.closest('.filter-data') && !target.closest('textarea')) {
      this.isSuggestionBoxVisible2 = false;
    }

    if (this.isNotificationSettingsOpen && !target.closest('.notification-settings-panel') && !target.closest('.mention-all-modal')) {
      this.isNotificationSettingsOpen = false;
    }

    // Don't close menus if clicking inside them
    if (isInsideMenuTrigger || isInsideQuickReactionMenu || isInsideDropdownMenu ||
      isInsideReactionPicker || isInsideReactionDetailsModal || isInsideMessageOptions) {
      return;
    }

    this.activeMessageMenuId = null;
    this.reactingToMessageId = null;
    this.showQuickReactionMenuId = null;
    this.showReactionEmojiPicker = false;
    this.unlockAudio();
  }

  showMentionAllModal: boolean = false;
  isNotificationSettingsOpen: boolean = false;

  get isDarkMode(): boolean {
    if (typeof document === 'undefined') return true;
    return (
      document.body.classList.contains('dark-theme') ||
      document.documentElement.classList.contains('dark-theme') ||
      !document.body.classList.contains('light-theme')
    );
  }

  onAllMentionClick(): void {
    this.showMentionAllModal = true;
    this.cdRef.detectChanges();
  }

  closeMentionAllModal(): void {
    this.showMentionAllModal = false;
    this.cdRef.detectChanges();
  }

  openNotificationSettingsFromModal(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.showMentionAllModal = false;
    this.isNotificationSettingsOpen = true;
    this.cdRef.detectChanges();
  }

  openNotificationSettings(): void {
    this.isNotificationSettingsOpen = true;
    this.cdRef.detectChanges();
  }

  closeNotificationSettings(): void {
    this.isNotificationSettingsOpen = false;
    this.cdRef.detectChanges();
  }

  onUserMentionClick(identifier: string): void {
    if (!identifier) return;

    // 1. Check if identifier matches a member in allGroupMembers by userId
    let member = (this.allGroupMembers || []).find(
      (m: any) => String(m.userId).trim() === String(identifier).trim()
    );

    // 2. If not found, check by name / userName / displayName in allGroupMembers
    if (!member) {
      const lower = identifier.toLowerCase().trim();
      member = (this.allGroupMembers || []).find((m: any) => {
        const n = (m.name || m.displayName || m.userName || '').toLowerCase().trim();
        return n === lower;
      });
    }

    if (member?.userId) {
      if (String(member.userId).trim() !== String(this.userId).trim()) {
        this.startSingleChat(member.userId);
      }
      return;
    }

    // 3. Check in single users lists
    const singleUsers = this.allSingleUsers || this.filteredSingleUsers || [];
    let user = singleUsers.find((u: any) => String(u.userId).trim() === String(identifier).trim());
    if (!user) {
      const lower = identifier.toLowerCase().trim();
      user = singleUsers.find((u: any) => {
        const n = (u.name || u.displayName || u.userName || '').toLowerCase().trim();
        return n === lower;
      });
    }

    if (user?.userId) {
      if (String(user.userId).trim() !== String(this.userId).trim()) {
        this.startSingleChat(user.userId);
      }
      return;
    }

    // 4. Check in userNameMapping
    for (const [uid, name] of Object.entries(this.userNameMapping || {})) {
      if (String(name || '').toLowerCase().trim() === identifier.toLowerCase().trim()) {
        if (String(uid).trim() !== String(this.userId).trim()) {
          this.startSingleChat(uid);
        }
        return;
      }
    }

    // 5. Direct fallback if identifier looks like a userId
    if (identifier && identifier !== this.userId && !identifier.includes(' ')) {
      this.startSingleChat(identifier);
    }
  }

  onMemberClick(userId: string): void {
    if (!userId || String(userId) === String(this.userId)) return;
    this.startSingleChat(userId);
  }

  async onReplyPrivately(message: any): Promise<void> {
    if (!message) return;
    const senderId = message.sender_id || message.senderId || message.user_id || message.sid;
    if (!senderId || String(senderId) === String(this.userId)) return;

    const sourceGroupName = this.groupName || this.groupChatName ||
      this.allGroupUsers?.find((g: any) => g.channelId === (message.meta?.channel_id || this.memberChannelId))?.groupName ||
      'Group';
    const sourceGroupId = this.memberChannelId || this.selectedChatGroupId || message.meta?.channel_id || '';
    const senderDisplayName = this.userNameMapping?.[senderId] || message.senderName || message.name || 'User';

    const replyContext = {
      ...message,
      id: message._id || message.id || message.messageId,
      _id: message._id || message.id || message.messageId,
      message: message.message || (message.meta?.caption ? message.meta.caption : 'Media'),
      senderName: senderDisplayName,
      sourceGroupName: sourceGroupName,
      sourceGroupId: sourceGroupId,
      meta: {
        ...(message.meta || {}),
        sourceGroupName: sourceGroupName,
        sourceGroupId: sourceGroupId
      }
    };

    // Open personal chat with sender
    await this.startSingleChat(senderId);

    // Set replyingToMessage on personal chat input
    this.replyingToMessage = replyContext;
    this.activeMessageMenuId = null;

    setTimeout(() => {
      this.focusMessageInput();
    }, 150);
  }

  showCreateGroupModal: boolean = false;
  showChatMenuDropdown: boolean = false;
  currentGroupCreator: string = "";

  toggleChatWindowMenu() {
    this.showChatMenuDropdown = !this.showChatMenuDropdown;
    if (this.showChatMenuDropdown) {
      this.showPinnedMessages = false;
    }
  }

  // Emoji Picker Methods
  toggleEmojiPicker() {
    this.showEmojiPicker = !this.showEmojiPicker;
  }

  addEmoji(event: any) {
    const emoji = event.emoji.native;
    const textarea = this.messageInputRef?.nativeElement;

    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = textarea.value;
      const before = text.substring(0, start);
      const after = text.substring(end, text.length);
      textarea.value = before + emoji + after;
      textarea.selectionStart = textarea.selectionEnd = start + emoji.length;
      // Update the model
      const inputEvent = new Event('input', { bubbles: true });
      textarea.dispatchEvent(inputEvent);
      // Focus back on textarea
      textarea.focus();
    }
  }



  readonly MAX_PINNED_CHATS = 10;

  get isCurrentChatPinned(): boolean {
    const channelId = this.singleChatBox ? this.activeChannel : this.memberChannelId;
    return !!(channelId && this.chatMap[channelId]?.isPinned);
  }

  // Pins/unpins the given chat (sidebar menu), or the open chat when none is given (header menu)
  async toggleChatPin(chat?: any) {
    this.showChatMenuDropdown = false;
    const channelId = chat?.channelId || (this.singleChatBox ? this.activeChannel : this.memberChannelId);
    if (!channelId || !this.projectInstance) {
      console.warn('toggleChatPin: No channelId or projectInstance found');
      return;
    }

    const shouldPin = !this.chatMap[channelId]?.isPinned;
    if (shouldPin) {
      const pinnedCount = [...this.allSingleUsers, ...this.allGroupUsers].filter(c => c.isPinned).length;
      if (pinnedCount >= this.MAX_PINNED_CHATS) {
        alertModalWithoutConfirm('warning', `You can only pin up to ${this.MAX_PINNED_CHATS} chats.`);
        return;
      }
    }
    try {
      if (shouldPin) {
        await this.projectInstance.addPinChannel({ channel_id: channelId });
      } else {
        await this.projectInstance.unPinChannel({ channel_id: channelId });
      }
      this.setChatPinned(channelId, shouldPin);
    } catch (err) {
      console.error('Error toggling chat pin:', err);
      alertModalWithoutConfirm('error', `Unable to ${shouldPin ? 'pin' : 'unpin'} chat. Please try again.`);
    }
  }

  private setChatPinned(channelId: string, isPinned: boolean) {
    // A fresh pin outranks every existing one, so it lands at the top of the pinned chats
    const pinRank = isPinned ? Date.now() : undefined;
    const update = (list: any[]) => list.map(c => (c.channelId === channelId ? { ...c, isPinned, pinRank } : c));
    this.allSingleUsers = update(this.allSingleUsers);
    this.filteredSingleUsers = update(this.filteredSingleUsers);
    this.allGroupUsers = update(this.allGroupUsers);
    this.filteredGroupUsers = update(this.filteredGroupUsers);
    this.sortAllChatsByRecentMessages();

    const updatedChat = this.chatMap[channelId];
    if (updatedChat && this.chatCacheService) {
      this.chatCacheService.saveConversation(updatedChat);
    }
  }

  clearChat() {
    this.showPinnedMessages = false;
    let chatId = this.singleChatBox ? this.activeChannel : this.memberChannelId;
    if (!chatId) {
      console.warn('clearChat: No chatId found');
      return;
    }
    this.showChatMenuDropdown = false;
    alertModalClearChat("Clear chat?").then((confirmed) => {
      if (confirmed) {
        this.chatLoader = true;
        this.chatService.clearChat(chatId).subscribe({
          next: (res) => {
            console.log("Chat cleared:", res);
            this.chatLoader = false;
            // Persist the cleared channel so last message stays hidden after refresh
            const clearedChannelId = this.singleChatBox ? this.activeChannel : this.memberChannelId;
            // Grab the original lastMessage time so sort order is preserved after clearing
            const chatForTime = this.sortedChats.find(c => c.channelId === clearedChannelId);
            const lastMsgTime = chatForTime?.lastMessage?.createdAt || chatForTime?.lastMessage?.updatedAt || chatForTime?.updatedAt || null;
            console.log('[clearChat] Marking as cleared, channelId:', clearedChannelId, 'lastMsgTime:', lastMsgTime);
            this.markChatAsCleared(clearedChannelId, lastMsgTime);
            // Also remove this chat's cached last message so it can't be restored from chatMetadata
            try {
              const chatMeta = JSON.parse(localStorage.getItem('chatMetadata') || '{}');
              if (chatMeta[clearedChannelId]) {
                delete chatMeta[clearedChannelId];
                localStorage.setItem('chatMetadata', JSON.stringify(chatMeta));
              }
            } catch (e) { console.error('Error clearing chatMetadata entry', e); }

            if (this.singleChatBox) {
              this.singleChatMessages = [];
              this.AllSingleChatMsg = { read: [], unread: [] };
              this.pinnedMessages = [];
              this.showPinnedMessages = false;

              const chatIndex = this.sortedChats.findIndex(c => c.chatType === 'single' && c.userId === this.chatUserId);
              if (chatIndex !== -1) {
                this.sortedChats[chatIndex].lastMessage = null;
                this.sortedChats[chatIndex].lastMessageTimeAgo = "";
              }

              const allIndex = this.allSingleUsers.findIndex(u => u.userId === this.chatUserId);
              if (allIndex !== -1) {
                this.allSingleUsers[allIndex].lastMessage = null;
                this.allSingleUsers[allIndex].lastMessageTimeAgo = "";
              }

              const filterIndex = this.filteredSingleUsers.findIndex(u => u.userId === this.chatUserId);
              if (filterIndex !== -1) {
                this.filteredSingleUsers[filterIndex].lastMessage = null;
                this.filteredSingleUsers[filterIndex].lastMessageTimeAgo = "";
              }

              if (this.singleUser && this.singleUser.userId === this.chatUserId) {
                this.singleUser.lastMessage = null;
              }

              // Force new array reference so Angular change detection re-renders the chat list
              // Also stamp isCleared on the in-memory entry
              const ci = this.sortedChats.findIndex(c => c.chatType === 'single' && c.userId === this.chatUserId);
              if (ci !== -1) this.sortedChats[ci].isCleared = true;
              this.sortedChats = [...this.sortedChats];

            } else {
              this.groupChatMessages = [];
              this.AllGroupChatMsg = { read: [], unread: [] };
              this.pinnedMessages = [];
              this.showPinnedMessages = false;

              const chatIndex = this.sortedChats.findIndex(c => c.chatType === 'group' && c.channelId === this.memberChannelId);
              if (chatIndex !== -1) {
                this.sortedChats[chatIndex].lastMessage = null;
                this.sortedChats[chatIndex].lastMessageTimeAgo = "";
              }

              const allIndex = this.allGroupUsers.findIndex(g => g.channelId === this.memberChannelId);
              if (allIndex !== -1) {
                this.allGroupUsers[allIndex].lastMessage = null;
                this.allGroupUsers[allIndex].lastMessageTimeAgo = "";
              }

              const filterIndex = this.filteredGroupUsers.findIndex(g => g.channelId === this.memberChannelId);
              if (filterIndex !== -1) {
                this.filteredGroupUsers[filterIndex].lastMessage = null;
                this.filteredGroupUsers[filterIndex].lastMessageTimeAgo = "";
              }

              // Force new array reference so Angular change detection re-renders the chat list
              // Also stamp isCleared on the in-memory entry
              const gi = this.sortedChats.findIndex(c => c.chatType === 'group' && c.channelId === this.memberChannelId);
              if (gi !== -1) this.sortedChats[gi].isCleared = true;
              this.sortedChats = [...this.sortedChats];
            }
            this.updateCacheForActiveChannel();
          },
          error: (err) => {
            console.error("Failed to clear chat:", err);
            this.chatLoader = false;
            alertModalWithoutConfirm("error", "Failed to clear chat");
          }
        });

        // Save updated unread counts to localStorage
        this.saveUnreadCountsToLocalStorage();
      }
    });
  }

  openCreateGroupModal() {
    this.showCreateGroupModal = true;
  }

  closeCreateGroupModal() {
    this.showCreateGroupModal = false;
  }




  onGroupCreated(newGroupObj: any) {
    // Update profile mappings for the new group
    if (newGroupObj.channelId) {
      this.profileImageMapping[newGroupObj.channelId] = newGroupObj.groupImg;
      this.userNameMapping[newGroupObj.channelId] = newGroupObj.groupName;
    }

    // Update profile mappings for all group members
    if (newGroupObj.members && Array.isArray(newGroupObj.members)) {
      newGroupObj.members.forEach((member: any) => {
        this.profileImageMapping[member.userId] = member.profileImage;
        this.userNameMapping[member.userId] = member.userName;
      });
    }

    // Add to local lists at the TOP 
    if (newGroupObj.channelId && !this.allGroupUsers.find(g => g.channelId === newGroupObj.channelId)) {
      this.allGroupUsers = [newGroupObj, ...this.allGroupUsers];
      this.filteredGroupUsers = [newGroupObj, ...this.filteredGroupUsers];

      // Update sortedChats and chatMap
      this.filteredSingleUsers = [...this.allSingleUsers];
      this.sortedChats = [...this.filteredSingleUsers, ...this.filteredGroupUsers];

      this.sortedChats.forEach((chat) => {
        if (chat.channelId) {
          this.chatMap[chat.channelId] = chat;
        }
      });

      // Re-sort 
      this.sortAllChatsByRecentMessages();
    }
    // Add chatType as 'group' and switch to this chat
    newGroupObj.chatType = 'group';
    this.onChatSelected(newGroupObj);

    if (newGroupObj.lastMessage?.message) {
      setTimeout(() => {
        if (this.memberChannelId === newGroupObj.channelId && this.groupChatMessages.length === 0) {
          const creationMsg = {
            _id: 'local_created_' + Date.now(),
            message: newGroupObj.lastMessage.message,
            sender_id: 'system',
            createdAt: newGroupObj.lastMessage.createdAt || new Date().toISOString(),
            systemText: newGroupObj.lastMessage.message,
            meta: { type: 'system', isSystem: true }
          };
          this.groupChatMessages = [creationMsg];
          this.processGroupMessages();
          this.cdRef.detectChanges();
        }
      }, 500);
    }
  }

  onChatSelected(chat: any) {
    this.openedFromChatList = true;
    this.showPinnedMessages = false;
    this.showStarredMessages = false;
    this.isStarredMessagesPanelOpen = false;
    if (!chat.fromSearch && !chat.fromStarred) {
      this.targetSearchMessageId = null;
      this.quickSearchComp?.resetSearch();
      if (this.isMobileView) {
        this.isQuickSearchOpen = false;
      }
    } else {
      this.isQuickSearchOpen = false;
    }
    this.isNavigatedFromSearch = !!chat.fromSearch;
    this.isNavigatedFromStarred = !!chat.fromStarred;
    this.isLastNavAdvancedSearch = !!chat.isAdvancedSearch;

    // Keep advanced search sidebar/filters open when selecting a chat from search results
    // so that the state is preserved and back button works correctly
    if (chat.isAdvancedSearch && !chat.fromStarred) {
      this.showAdvancedSearchResults = false;
      this.showAdvancedSearchFilters = true;
    } else {
      this.showAdvancedSearchResults = false;
      this.showMobileSearchResults = false;
      this.showAdvancedSearchFilters = false;
    }


    let target = chat;

    // From search, we have a message object. Look up the actual chat metadata in memory.
    if (chat.fromSearch && (chat.channel_id || chat.channelId)) {
      const channelId = chat.channel_id || chat.channelId;
      const foundChat = this.chatMap[channelId];
      if (foundChat) {
        target = { ...foundChat, fromSearch: true, lastMessage: chat.lastMessage };
      }
    }

    const scrollToMessageId = target.fromSearch ? (target.lastMessage?._id || target.lastMessage?.id || target.lastMessage?.messageId) : null;
    if (scrollToMessageId) {
      this.targetSearchMessageId = scrollToMessageId;
    } else {
      this.targetSearchMessageId = null;
    }
    if (target.chatType === 'single' || target.type === 'single' || (!target.groupName && !target.members && target.userId)) {
      this.startSingleChat(target.userId, scrollToMessageId);
    } else {
      this.showGroupChat(target, scrollToMessageId);
    }
  }

  private _pendingOpenChannelId: string | null = null;
  private _pendingOpenUserId: string | null = null;
  private openChatInterval: any = null;

  private clearOpenChatInterval() {
    if (this.openChatInterval) {
      clearInterval(this.openChatInterval);
      this.openChatInterval = null;
    }
    this._pendingOpenChannelId = null;
    this._pendingOpenUserId = null;
  }

  openChatByChannelId(channelId: string) {
    if (!channelId || channelId === 'undefined' || channelId === 'null') return;

    if (this.activeChannel === channelId && (this.singleChatBox || this.groupChatBox)) {
      return;
    }

    if (this._pendingOpenChannelId === channelId) {
      return;
    }
    this.clearOpenChatInterval();
    this._pendingOpenChannelId = channelId;

    let attempts = 0;
    const maxAttempts = 60; // Up to ~9 seconds
    this.openChatInterval = setInterval(() => {
      attempts++;

      if (this.activeChannel === channelId && (this.singleChatBox || this.groupChatBox)) {
        this.clearOpenChatInterval();
        return;
      }

      let foundChat = this.chatMap ? this.chatMap[channelId] : null;

      if (!foundChat) {
        foundChat = (this.allGroupUsers || []).find((g: any) => String(g.channelId).trim() === channelId) ||
          (this.allSingleUsers || []).find((u: any) => String(u.channelId).trim() === channelId) ||
          (this.sortedChats || []).find((c: any) => String(c.channelId).trim() === channelId);
      }

      if (foundChat) {
        this.clearOpenChatInterval();
        this.onChatSelected(foundChat);
        return;
      }

      if (attempts >= maxAttempts) {
        this.clearOpenChatInterval();
        console.warn(`[MainChat] Could not find chat for channelId: ${channelId} after ${maxAttempts} attempts`);
      }
    }, 150);
  }

  openChatByUserId(userId: string) {
    if (!userId || userId === 'undefined' || userId === 'null') return;

    if (this.chatUserId === userId && this.singleChatBox) {
      return;
    }

    if (this._pendingOpenUserId === userId) {
      return;
    }
    this.clearOpenChatInterval();
    this._pendingOpenUserId = userId;

    let attempts = 0;
    const maxAttempts = 60;
    this.openChatInterval = setInterval(() => {
      attempts++;

      if (this.chatUserId === userId && this.singleChatBox) {
        this.clearOpenChatInterval();
        return;
      }

      const foundUser = (this.allSingleUsers || []).find((u: any) => u.userId === userId) ||
        (this.filteredSingleUsers || []).find((u: any) => u.userId === userId);

      if (foundUser || (this.sortedChats && this.sortedChats.length > 0)) {
        this.clearOpenChatInterval();
        this.startSingleChat(userId);
        return;
      }

      if (attempts >= maxAttempts) {
        this.clearOpenChatInterval();
        this.startSingleChat(userId);
      }
    }, 150);
  }

  handleSearchBack() {
    if (this.isNavigatedFromStarred) {
      this.isNavigatedFromStarred = false;
      this.showStarredMessages = true;
      if (this.isMobileView) {
        this.showChatWindow = false;
        this.chatService.isChatOpenMobile$.next(false);
      }
      this.showAdvancedSearchFilters = false;
      this.showAdvancedSearchResults = false;
      this.starredMessages = [];
      this.starredMessagesPage = 1;
      this.hasMoreStarredMessages = true;
      this.loadStarredMessages();
    } else if (this.isLastNavAdvancedSearch) {
      // Return to full advanced search results view
      this.isNavigatedFromSearch = false;
      this.showAdvancedSearchResults = true;
      this.showAdvancedSearchFilters = true;
      if (this.isMobileView) {
        this.showMobileSearchResults = true;
        this.showChatWindow = false;
        this.chatService.isChatOpenMobile$.next(false);
      }
    } else {
      // Normal sidebar search - just hide the back button as results are in the sidebar
      this.isNavigatedFromSearch = false;
    }
    this.targetSearchMessageId = null;
    this.cdRef.detectChanges();
  }

  toggleAdvancedSearch() {
    this.showAdvancedSearchFilters = !this.showAdvancedSearchFilters;
    if (!this.showAdvancedSearchFilters) {
      this.showAdvancedSearchResults = false;
      this.showMobileSearchResults = false;
      this.targetSearchMessageId = null;
    }
  }

  onAdvancedSearchResults(event: any) {
    this.targetSearchMessageId = null;
    console.log("Advanced Search Results Received:", {
      resultsCount: event.results?.length,
      isMobile: this.isMobileView,
      showChatWindow: this.showChatWindow,
      showAdvancedSearchFilters: this.showAdvancedSearchFilters
    });
    this.advancedSearchResults = event.results || [];
    this.advancedSearchMatches = event.matchesCount || this.advancedSearchResults.length;
    this.advancedSearchFilters = event.filters || {};
    this.showAdvancedSearchResults = true;
    if (this.isMobileView) {
      this.showMobileSearchResults = true;
      console.log("Setting showMobileSearchResults = true for mobile view");
    }
    this.cdRef.detectChanges();
  }

  closeAdvancedSearch() {
    // If a chat was open via search results, we must refresh it to the latest messages
    // before closing the search panel, or close it entirely to avoid stale states.
    if (this.isNavigatedFromSearch) {
      if (this.singleChatBox && this.selectedChatUserId) {
        const userId = this.selectedChatUserId;
        this.chatUserId = null; // Force reload by bypassing early return
        this.startSingleChat(userId);
      } else if (this.groupChatBox && this.selectedChatGroupId) {
        const groupId = this.selectedChatGroupId;
        const activeGroup = this.allGroupUsers.find(g => g.channelId === groupId);
        if (activeGroup) {
          this.memberChannelId = null; // Force reload
          this.showGroupChat(activeGroup);
        }
      }
    }

    this.showAdvancedSearchFilters = false;
    this.showAdvancedSearchResults = false;
    this.showMobileSearchResults = false;
    this.isNavigatedFromSearch = false;
    this.isNavigatedFromStarred = false;
    this.targetSearchMessageId = null;
    this.advancedSearchResults = [];
  }

  onSearchResultsScrolled() {
    if (this.advancedSearchComp) {
      this.advancedSearchComp.searchNextPage();
    }
  }

  getSidebarClass() {
    if (this.isMobileView) return 'col-12 chat-list-window';
    return 'col-md-4 col-lg-3 col-xl-3 chat-list-window';
  }

  getChatWindowClass() {
    if (this.isMobileView) return 'col-12 chat-open-window';
    return 'col-md-8 col-lg-9 col-xl-9 chat-open-window';
  }

  shouldHideSidebar() {
    if (!this.isMobileView) return false;
    // On mobile, hide sidebar if chat window is open
    if (this.showChatWindow) return true;
    // On mobile, show sidebar if we are in advanced search mode
    if (this.showAdvancedSearchFilters) return false;
    // On mobile, hide sidebar if Starred Messages is open
    if (this.showStarredMessages) return true;
    return false;
  }

  shouldHideChatWindow() {
    if (!this.isMobileView) return false;
    if (this.showChatWindow) return false;
    if (this.showStarredMessages) return false;
    if (this.showAdvancedSearchFilters) return true;
    return !this.showChatWindow;
  }

  onOpenMediaPreview(event: { chat: any, allMedia: any[] }) {
    console.log("Main Chat Received Open Media Preview:", event);
    const { chat, allMedia } = event;

    // Map all results to the required ImageFile format for the dialog
    const mediaFiles = allMedia.map(item => {
      const userId = item.sender_id || item.senderId || item.userId;
      const originalUrl = item.url || item.meta?.fileUrl || item.fileUrl || item.message;
      return {
        fileUrl: originalUrl,
        url: item.meta?.compressedImgUrl || item.meta?.thumbnailUrl || originalUrl,
        name: item.displayName || item.name || getCleanFileName(item),
        senderName: item.displayName || item.userName || item.name || this.userNameMapping[userId] || "Unknown",
        date: item.createdAt || item.date || item.lastMessageTime,
        message: item.message || item.lastMessage?.message || "",
        profileImg: item.profileImg || item.profileImage || this.profileImageMapping[userId] || "assets/user-icon.png",
        thumbnailUrl: item.meta?.thumbnailUrl
      };
    });

    const startIndex = allMedia.findIndex(m => (m._id || m.id) === (chat._id || chat.id));

    this.dialog.open(ImageViewerDialogComponent, {
      data: {
        files: mediaFiles,
        startIndex: startIndex >= 0 ? startIndex : 0
      },
      panelClass: 'full-screen-dialog',
      maxWidth: '100vw',
      maxHeight: '100vh',
      width: '100vw',
      height: '100vh'
    });
  }



  // Reaction Details Modal
  showReactionDetailsModal: boolean = false;
  selectedMessageIDForDetails: string = "";
  activeReactionTab: string = 'All';
  reactionDetailsList: any[] = []; // { user: { name, image, id }, emoji: string }
  reactionTabs: { label: string, count: number, emoji?: string }[] = [];

  openReactionDetails(message: any) {
    if (!message || !message.reactions || message.reactions.length === 0) return;

    this.showReactionDetailsModal = true;
    this.selectedMessageIDForDetails = message._id || message.id;
    this.activeReactionTab = 'All';

    // Process all reactions
    const allReactions: any[] = [];
    const emojiCounts: { [emoji: string]: number } = {};

    message.reactions.forEach((r: any) => {
      const emoji = r.emoji || r.emote; // Handle different property names if any
      const userId = r.userId || r.user_id || r.sender_id;

      if (!emoji || !userId) return;

      if (!emojiCounts[emoji]) emojiCounts[emoji] = 0;
      emojiCounts[emoji]++;

      // Resolve User Info
      let userName = this.userNameMapping[userId] || "Unknown User";
      let userImage = this.profileImageMapping[userId] || "assets/user-icon.png";

      // Fallback for current user and other user in single chat if mapping misses
      if (userId === this.userId) {
        userName = "You";
        userImage = this.profileImg; // Current user profile img
      } else if (this.singleChatBox && (userId === this.selectedChatUserId || userId === this.selectedChatGroupId)) {
        // Try to find in single users first
        const chatUser = this.allSingleUsers.find((u: any) => u.userId === userId) ||
          this.allGroupUsers.find((g: any) => g.channelId === userId); // Fallback to group if mixed up

        userName = chatUser?.displayName || chatUser?.userName || chatUser?.groupName || "Unknown";
        userImage = chatUser?.profileImage || chatUser?.groupImg || "assets/user-icon.png";
      } else {
        // Try to find in filtered users (global list)
        const globalUser = this.allSingleUsers.find((u: any) => u.userId === userId);
        if (globalUser) {
          userName = globalUser.displayName || globalUser.userName;
          userImage = globalUser.profileImage;
        }
      }

      allReactions.push({
        emoji: emoji,
        userId: userId,
        userName: userName,
        userImage: userImage
      });
    });

    this.reactionDetailsList = allReactions;

    // Build Tabs
    this.reactionTabs = [
      { label: 'All', count: allReactions.length }
    ];

    Object.keys(emojiCounts).forEach(emoji => {
      this.reactionTabs.push({
        label: emoji,
        emoji: emoji,
        count: emojiCounts[emoji]
      });
    });

    console.log("Reaction Details Opened:", this.reactionDetailsList, this.reactionTabs);
  }


  closeReactionDetails() {
    this.showReactionDetailsModal = false;
    this.selectedMessageIDForDetails = "";
    this.reactionDetailsList = [];
    this.reactionTabs = [];
  }

  switchReactionTab(tabLabel: string) {
    this.activeReactionTab = tabLabel;
  }

  get filteredReactionDetails() {
    if (this.activeReactionTab === 'All') {
      return this.reactionDetailsList;
    }
    return this.reactionDetailsList.filter(r => r.emoji === this.activeReactionTab);
  }

  removeSelfReaction(item: any) {
    if (!item || !this.selectedMessageIDForDetails) return;
    this.reactingToMessageId = this.selectedMessageIDForDetails;
    this.submitReaction(item.emoji);
    this.closeReactionDetails();
  }





  onlineUserSub: Subscription;
  initialListSub: Subscription;
  private lastSeenTimer: any;
  readonly defaultProfileImage = "assets/user-icon.png";

  ngOnInit() {
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('online', this.onOnline);
    window.addEventListener('offline', this.onOffline);
    window.addEventListener('focus', this.onFocus);
    this.loadStarredMessages(100);
    // Initialize notification sound with max volume
    // this.initNotificationSound();

    // Load deleted message IDs from localStorage
    this.loadDeletedForMeMessageIds();

    this.initialListSub = this.chatService.onlineUser$.subscribe((list) => {
      this.zone.run(() => {
        // const user = list.find(u => u.userId === this.chatUserId);
        if (list && list.data) {
          this.updateChatUserStatus(list.data);
          this.cdRef.detectChanges();
        }
      });
    });
    // Starred message listeners
    this.chatService.starMessage$.subscribe((data: any) => {
      this.zone.run(() => this.handleStarMessageEvent(data));
    });
    this.chatService.unStarMessage$.subscribe((data: any) => {
      this.zone.run(() => this.handleUnStarMessageEvent(data));
    });
    this.chatService.editStarMessage$.subscribe((data: any) => {
      this.zone.run(() => this.handleEditStarMessageEvent(data));
    });

    // 1. NEW-GROUP
    this.chatService.newGroup$.subscribe((payload: any) => {
      this.zone.run(() => {
        console.log("Socket: NEW-GROUP Payload:", payload);
        const data = payload.data || payload;
        const channelId = data.channelId || data.chatId;
        if (!data.members) data.members = [];
        const creatorId = data.creator;
        const creatorInMembers = data.members.some((m: any) => m === creatorId || m.userId === creatorId || m.user_id === creatorId);

        if (!creatorInMembers && creatorId) {
          let creatorInfo = this.allSingleUsers.find(u => u.userId === creatorId);

          if (creatorInfo) {
            data.members.push({
              userId: creatorInfo.userId,
              userName: creatorInfo.userName,
              displayName: creatorInfo.displayName || creatorInfo.userName,
              profileImage: creatorInfo.profileImage,
              isAdmin: true
            });
          } else if (creatorId === this.userId) {
            data.members.push({
              userId: this.userId,
              userName: this.senderUsername,
              displayName: this.senderUsername,
              profileImage: this.profileImg,
              isAdmin: true
            });
          }
        }
        const isMember = data.members.some((m: any) => m === this.userId || m.userId === this.userId || m.user_id === this.userId);
        console.log("NEW-GROUP Member Check:", { isMember, myId: this.userId, members: data.members });

        if (!isMember) return;

        const exists = this.allGroupUsers.find(g => g.channelId === channelId);
        if (!exists) {
          // Determine creator name
          let creatorDisplayName = "Someone";
          if (creatorId === this.userId) {
            creatorDisplayName = "You";
          } else {
            const creatorInfo = this.allSingleUsers.find(u => u.userId === creatorId);
            if (creatorInfo) {
              creatorDisplayName = creatorInfo.displayName || creatorInfo.userName || "Someone";
            } else {
              const memberCreator = data.members.find((m: any) => m.userId === creatorId || m.user_id === creatorId || m === creatorId);
              if (memberCreator && (memberCreator.displayName || memberCreator.userName)) {
                creatorDisplayName = memberCreator.displayName || memberCreator.userName;
              }
            }
          }

          const groupNameStr = data.groupName || "Group";

          const newGroupObj = {
            ...data,
            channelId: channelId,
            type: 'group',
            groupImg: data.groupImg || "assets/group_chat.png",
            unreadCount: 0,
            lastMessage: {
              message: `${creatorDisplayName} created the group ${groupNameStr}`,
              createdAt: data.createdAt || new Date().toISOString()
            },
            lastMessageTimeAgo: "Just now",
            createdAt: data.createdAt || new Date().toISOString(),
            isExited: false
          };

          // Add to group lists immutably
          this.allGroupUsers = [newGroupObj, ...this.allGroupUsers];
          this.filteredGroupUsers = [newGroupObj, ...this.filteredGroupUsers];

          // Update Mappings
          this.profileImageMapping[newGroupObj.channelId] = newGroupObj.groupImg;
          this.userNameMapping[newGroupObj.channelId] = newGroupObj.groupName;
          data.members.forEach((m: any) => {
            this.profileImageMapping[m.userId] = m.profileImage;
            this.userNameMapping[m.userId] = m.displayName || m.userName;
          });

          // Update Combined Sorted List
          this.filteredSingleUsers = [...this.allSingleUsers];
          this.sortedChats = [...this.allGroupUsers, ...this.allSingleUsers];
          this.sortAllChatsByRecentMessages();

          // Update Chat Map
          this.chatMap[channelId] = newGroupObj;

          // Play sound & Force Update
          if (creatorId !== this.userId) {
            this.soundService.playChat();
          }
          this.cdRef.detectChanges();
        }
      });
    });


    //2. NEW-CHAT
    this.chatService.newChat$.subscribe((payload: any) => {
      this.zone.run(() => {
        console.log("Socket: NEW-CHAT Payload:", payload);
        const data = payload.data || payload;

        // 1. Membership check: return early unless current user is in data.members
        const isMember = data.members?.some((m: any) => m === this.userId || m.userId === this.userId || m.user_id === this.userId);
        if (!isMember) return;

        const channelId = data.channelId || data.chatId;
        const creatorId = data.creator || data.sender_id || data.senderId;

        // 2. Keep the existing creatorId && creatorId !== this.userId condition. creatorId resolves from data.creator
        if (creatorId && creatorId !== this.userId) {
          console.log("New chat received from:", creatorId);

          // 3. Build the chat object from the OTHER member, not from top-level fields
          const other = data.members?.find((m: any) => (m.userId || m.user_id || m) !== this.userId);
          if (!other) return;

          const otherUserId = other.userId || other.user_id || other;
          const otherUserName = other.userName || '';
          const otherDisplayName = other.displayName || other.userName || '';
          const otherProfileImage = other.profileImage || '';

          // Map userNameMapping / profileImageMapping for both channelId and other.userId from other
          this.profileImageMapping[otherUserId] = otherProfileImage;
          this.userNameMapping[otherUserId] = otherDisplayName;
          this.profileImageMapping[channelId] = otherProfileImage;
          this.userNameMapping[channelId] = otherDisplayName;

          if (data.members && Array.isArray(data.members)) {
            data.members.forEach((m: any) => {
              const mUserId = m.userId || m.user_id;
              if (mUserId) {
                this.profileImageMapping[mUserId] = m.profileImage || '';
                this.userNameMapping[mUserId] = m.displayName || m.userName || '';
              }
            });
          }

          // Create new chat object from the other member
          const newChatObj = {
            channelId: channelId,
            chatId: channelId,
            type: data.type || 'single',
            userId: otherUserId,
            userName: otherUserName,
            displayName: otherDisplayName,
            profileImage: otherProfileImage,
            unreadCount: 0,
            lastMessage: null,
            lastMessageTimeAgo: "",
            createdAt: data.createdAt || new Date().toISOString(),
            isExited: false,
            fcmTokens: data.fcmTokens || [],
            members: data.members || [],
            isDeleted: data.isDeleted || false,
            isPermanentlyDeleted: data.isPermanentlyDeleted || false
          };

          // Add to chat lists
          if (newChatObj.type === 'single') {
            // 4. Use other member's userId (not creatorId) in existing-chat lookup
            const existingChat = this.allSingleUsers.find(u =>
              (u.channelId && u.channelId === channelId) ||
              (u.userId && u.userId === otherUserId)
            );

            if (!existingChat) {
              this.allSingleUsers = [newChatObj, ...this.allSingleUsers];
              this.filteredSingleUsers = [newChatObj, ...this.filteredSingleUsers];
              this.sortedChats = [...this.allGroupUsers, ...this.allSingleUsers];
              this.sortAllChatsByRecentMessages();
              console.log("Added new single chat to list:", newChatObj);
            } else if (!existingChat.channelId) {
              // Update existing contact entry with new channelId
              existingChat.channelId = channelId;
              existingChat.chatId = channelId;
              this.sortedChats = [...this.allGroupUsers, ...this.allSingleUsers];
              this.sortAllChatsByRecentMessages();
              console.log("Updated existing chat entry with real channelId:", existingChat);
            }
          } else if (newChatObj.type === 'group') {
            // Check if group already exists
            const existingGroup = this.allGroupUsers.find(g => g.channelId === channelId);
            if (!existingGroup) {
              this.allGroupUsers = [newChatObj, ...this.allGroupUsers];
              this.filteredGroupUsers = [newChatObj, ...this.filteredGroupUsers];
              this.sortedChats = [...this.allGroupUsers, ...this.allSingleUsers];
              this.sortAllChatsByRecentMessages();
              console.log("Added new group chat to list:", newChatObj);
            }
          }

          // Update chat map
          this.chatMap[channelId] = newChatObj;

          this.soundService.playChat();
          this.cdRef.detectChanges();
        }
      });
    });


    this.chatService.deleteChat$.subscribe((payload: any) => {
      this.zone.run(() => {
        console.log("Socket: DELETE-CHAT Payload:", payload);
        const data = payload?.data || payload;
        let channelId = data?.channelId || data?.chatId;

        // If payload is a direct string or channelId wasn't found in object
        if (!channelId && typeof payload === 'string') {
          channelId = payload;
        } else if (!channelId && typeof data === 'string') {
          channelId = data;
        }

        if (channelId) {
          channelId = String(channelId).trim();
          this.allGroupUsers = this.allGroupUsers.filter(group => String(group.channelId || '').trim() !== channelId);
          this.filteredGroupUsers = this.filteredGroupUsers.filter(group => String(group.channelId || '').trim() !== channelId);

          this.allSingleUsers = this.allSingleUsers.filter(u => String(u.channelId || '').trim() !== channelId && String(u.userId || '').trim() !== channelId);
          this.filteredSingleUsers = this.filteredSingleUsers.filter(u => String(u.channelId || '').trim() !== channelId && String(u.userId || '').trim() !== channelId);

          this.sortedChats = [...this.allGroupUsers, ...this.allSingleUsers];
          this.sortAllChatsByRecentMessages();

          delete this.chatMap[channelId];

          // If active chat is deleted, clear chat box
          const activeChannel = String(this.memberChannelId || this.activeChannel || this.chatUserId || '').trim();
          if (activeChannel === channelId) {
            this.cancelReply();
            this.cancelEdit();
            this.groupChatBox = false;
            this.singleChatBox = false;
            this.memberChannelId = '';
            this.activeChannel = '';
            this.chatUserId = '';
            this.groupChatMessages = [];
            this.singleChatMessages = [];
            this.pinnedMessages = [];
            this.showPinnedMessages = false;
          }

          this.cdRef.detectChanges();
        }
      });
    });


    // 2. GROUP-CHAT-UPDATES
    this.chatService.groupChatUpdates$.subscribe((payload: any) => {
      this.zone.run(() => {
        console.log("Socket: GROUP-CHAT-UPDATES Payload:", payload);

        const data = payload.data || payload;
        const channelId = data.channelId || data.chatId;
        const { groupName, groupDescription, groupImg } = data;

        this.allGroupUsers = this.allGroupUsers.map(g => {
          if (g.channelId === channelId) {
            const updated = { ...g };
            if (groupName) updated.groupName = groupName;
            // Allow empty string (description removed)
            if (groupDescription !== undefined) updated.groupDescription = groupDescription;
            if (groupImg) updated.groupImg = groupImg;

            // Update Chat Map entry as well
            if (this.chatMap[channelId]) {
              this.chatMap[channelId] = updated;
            }
            return updated;
          }
          return g;
        });

        // Sync filtered lists
        this.filteredGroupUsers = [...this.allGroupUsers];
        this.sortedChats = [...this.allGroupUsers, ...this.allSingleUsers];
        this.sortAllChatsByRecentMessages();

        // Update mappings
        if (groupName) this.userNameMapping[channelId] = groupName;
        if (groupImg) {
          const previousImg = this.profileImageMapping[channelId];
          this.profileImageMapping[channelId] = groupImg;
          if (previousImg && previousImg !== groupImg && this.memberChannelId === channelId && this.groupChannel) {
            console.log("Group Icon changed via socket, fetching updated native messages to show the system notification...");
            this.getAllGroupMessages(this.groupChannel, false, channelId);
          }
        }
        if (this.memberChannelId === channelId || this.activeChannel === channelId) {
          if (groupName) this.groupChatName = groupName;
          // Allow empty description (clearing it)
          if (groupDescription !== undefined) this.groupChatDescription = groupDescription;
          if (groupImg) this.groupChatProfileImg = groupImg;
        }
        this.cdRef.detectChanges();
      });
    });


    // 3. MEMBERS-ADDED
    this.chatService.membersAdded$.subscribe((payload: any) => {
      this.zone.run(() => {
        console.log("Socket: MEMBERS-ADDED Payload:", payload);

        const data = payload.data || payload;
        const channelId = data.channelId || data.chatId;
        const members = data.members || [];

        let group = this.allGroupUsers.find(g => g.channelId === channelId);

        if (!group) {
          // If group not found locally, check if current user is among the added members
          const amIAdded = members.some((m: any) =>
            String(m.userId || m.user_id || m).trim() === String(this.userId).trim()
          );

          if (amIAdded) {
            console.log("Current user added to a new group. Refreshing chat list to show it in sidebar...");
            this.soundService.playChat();
            setTimeout(() => {
              this.getAllUsers();
            }, 500);
            return;
          }
        }

        if (group) {
          if (!group.members) group.members = [];
          members.forEach((nm: any) => {
            const existingMember = group.members.find((em: any) => em.userId === nm.userId);

            if (existingMember) {
              console.log("Reactivating existing member:", existingMember.userName);
              // Reactivate
              existingMember.isRemoved = false;
              existingMember.leftGroup = false;
              existingMember.exitedAt = null;

              // Update details
              if (nm.displayName) existingMember.displayName = nm.displayName;
              if (nm.userName) existingMember.userName = nm.userName;
              if (nm.profileImage) existingMember.profileImage = nm.profileImage;
              if (nm.isAdmin !== undefined) existingMember.isAdmin = nm.isAdmin;
            } else {
              // Add new
              console.log("Adding new member to list:", nm.userName);
              group.members.push(nm);
            }

            // Update mapping
            this.profileImageMapping[nm.userId] = nm.profileImage;
            this.userNameMapping[nm.userId] = nm.displayName || nm.userName;
            if (nm.userId === this.userId) {
              if (!group.isExited) {
                console.log("User already active in group, skipping 'You were added' self-message");
                return;
              }
              console.log("Current user added back to group - resetting isExited status");
              group.isExited = false;
              if (this.exitedGroups) this.exitedGroups.delete(channelId);
              this.chatService.removeExitedGroup(channelId);
              const now = new Date().toISOString();
              const sysMsgText = "You were added";
              group.lastMessage = {
                ...group.lastMessage,
                message: sysMsgText,
                sender_id: 'system',
                isSystemMessage: true,
                systemText: sysMsgText,
                updatedAt: now,
                createdAt: now
              };
              group.lastMessageTimeAgo = getTimeAgo(now);
              if (this.memberChannelId === channelId) {
                const lastMsg = this.groupChatMessages[this.groupChatMessages.length - 1];
                const lastText = lastMsg?.systemText || lastMsg?.message || '';
                if (lastText !== sysMsgText) {
                  this.groupChatMessages.push({
                    _id: 'local_sys_' + Date.now(),
                    message: sysMsgText,
                    sender_id: 'system',
                    isSystemMessage: true,
                    systemText: sysMsgText,
                    createdAt: now,
                    updatedAt: now,
                    meta: { type: 'system' }
                  });
                  this.processGroupMessages();
                  this.scrollToBottom();
                }
              }
              this.sortAllChatsByRecentMessages();
              this.cdRef.detectChanges();
            }
          });
          if (String(this.memberChannelId || '').trim() === String(channelId || '').trim()) {
            this.allGroupMembers = group.members || [];
            const active = this.allGroupMembers.filter(m => !m.isRemoved && !m.leftGroup);
            this.totalGroupMembers = active.length;
            this.groupMembersDisplayNames = active.map((m: any) => String(m.userId).trim() === String(this.userId).trim() ? 'You' : (m.displayName || m.userName));
            this.cdRef.detectChanges();
          }
          if (this.showSeenInfoModal && this.currentSeenInfoMessage) {
            this.refreshSeenInfoModal();
          }
        }
      });
    });


    // 4. MEMBER-REMOVED & 5. MEMBER-LEFT 
    const handleMemberExit = (payload: any, type: 'removed' | 'left') => {
      this.zone.run(() => {
        console.log(`Socket: MEMBER-${type.toUpperCase()} Payload:`, payload);

        const data = payload.data || payload;
        const channelId = String(data.channelId || data.chatId || '').trim();
        if (!channelId) return;

        const memberId = data.memberId || data.userId || data.user_id;
        const now = new Date().toISOString();

        // Find group in all lists for thorough update
        const allLists = [this.allGroupUsers, this.filteredGroupUsers, this.sortedChats];
        let groupObj: any = null;

        allLists.forEach(list => {
          if (!list) return;
          const g = list.find(chat => chat.channelId === channelId);
          if (g) {
            groupObj = g;
            if (memberId === this.userId) {
              g.isExited = true;
              g.unreadCount = 0;
            }
            if (g.members) {
              const m = g.members.find((member: any) => member.userId === memberId);
              if (m) {
                if (type === 'removed') m.isRemoved = true;
                else m.leftGroup = true;
                m.exitedAt = now;
              }
            }
          }
        });

        const activeChannelTrimmed = String(this.memberChannelId || '').trim();

        if (memberId === this.userId) {
          if (this.exitedGroups) this.exitedGroups.add(channelId);
          this.chatService.addExitedGroup(channelId);

          if (activeChannelTrimmed === channelId) {
            this.isGroupAdmin = false;
            this.cancelEdit();
            this.cancelReply();
            this.closeReactionPicker();
            this.activeMessageMenuId = null;
          }

          const removerId = data.adminId || data.removedBy || data.by || data.admin || data.performedBy || data.removerId || data.sender_id || data.senderId;
          const removerName = data.adminName || data.byName || data.removerName || data.adminUsername || data.remover;
          const memberName = data.memberName || data.memberUsername || data.name || (memberId === this.userId ? 'You' : undefined);

          // Generate system message for sidebar preview
          const messageText = type === 'removed' ? (removerName ? `${removerName} removed You` : "You were removed") : "You left";
          const systemMessage = {
            message: messageText,
            sender_id: type === 'removed' ? (removerId && removerId !== memberId ? removerId : 'system') : memberId,
            createdAt: now,
            meta: {
              type: 'system',
              subType: type === 'removed' ? 'Group Member Removed' : 'Member Left',
              channel_id: channelId,
              isSystem: true,
              removedMember: memberName || 'You',
              removedBy: removerName
            }
          };

          // Update sidebar and persistence
          this.updateSidebarForIncomingMessage(systemMessage, channelId, 'group', false);
        }

        // Update UI components if this group is currently open
        if (activeChannelTrimmed === channelId && groupObj) {
          this.allGroupMembers = groupObj.members || this.allGroupMembers;
          const active = this.allGroupMembers.filter(m => !m.isRemoved && !m.leftGroup);
          this.totalGroupMembers = active.length;
          this.groupMembersDisplayNames = active.map((m: any) => String(m.userId).trim() === String(this.userId).trim() ? 'You' : (m.displayName || m.userName));
        }

        if (this.showSeenInfoModal && this.currentSeenInfoMessage) {
          this.refreshSeenInfoModal();
        }

        // Always sort and detect changes to reflect status updates
        this.sortAllChatsByRecentMessages();
        this.cdRef.detectChanges();
      });
    };

    this.chatService.memberRemoved$.subscribe((data: any) => handleMemberExit(data, 'removed'));
    this.chatService.memberLeft$.subscribe((data: any) => handleMemberExit(data, 'left'));


    // 6. ADMIN-ASSIGNED & 7. ADMIN-REMOVED
    const handleAdminUpdate = (payload: any, isAdmin: boolean) => {
      this.zone.run(() => {
        console.log(`Socket: ADMIN-${isAdmin ? 'ASSIGNED' : 'REMOVED'} Payload:`, payload);
        const data = payload.data || payload;
        const channelId = data.channelId || data.chatId;
        const memberId = data.memberId || data.userId;

        const group = this.allGroupUsers.find(g => g.channelId === channelId);
        if (group && group.members) {
          // FIX: Don't update if I've exited or if this is my own admin removal (redundant with 'You left' / 'You were removed')
          if (group.isExited || this.hasExitedGroup(channelId)) return;

          // Skip if it's me losing admin status (usually happens because I left/was removed)
          if (memberId === this.userId && !isAdmin) {
            console.log("Skipping self-admin removal message as it is redundant");
            return;
          }

          const member = group.members.find((m: any) => m.userId === memberId);

          // Skip update for user who just left/removed and is losing admin status
          if (member && !isAdmin && (member.leftGroup || member.isRemoved)) {
            return;
          }

          if (member) {
            member.isAdmin = isAdmin;
          }

          let name = member?.displayName || member?.userName || this.userNameMapping[memberId] || 'User';
          if (memberId === this.userId) name = 'You';

          const msgText = isAdmin ? `${name} became an admin` : `${name} is no longer an admin`;
          const now = new Date().toISOString();

          // Fix duplicate/stale messages in open chat
          if (this.memberChannelId === channelId) {
            const currentMember = this.allGroupMembers.find(m => m.userId === memberId);
            if (currentMember) {
              currentMember.isAdmin = isAdmin;
            }

            const lastMsg = this.groupChatMessages[this.groupChatMessages.length - 1];
            const lastText = lastMsg?.systemText || lastMsg?.message || '';

            if (lastText === msgText) {
              console.log("Duplicate admin message detected locally, skipping push");
            } else if (lastText.toLowerCase() === 'admin changed') {
              if (lastMsg) {
                lastMsg.message = msgText;
                lastMsg.systemText = msgText;
                lastMsg.updatedAt = now;
              }
            }
          }

          // Generate system message for sidebar preview and persistence
          const systemMessage = {
            message: msgText,
            sender_id: memberId || 'system',
            createdAt: now,
            meta: {
              type: 'system',
              subType: isAdmin ? 'Admin Assigned' : 'Admin Removed',
              channel_id: channelId,
              isSystem: true,
              newAdmin: isAdmin ? name : undefined,
              removedAdmin: !isAdmin ? name : undefined
            }
          };

          // Update sidebar preview and trigger sorting
          this.updateSidebarForIncomingMessage(systemMessage, channelId, 'group', false);
          this.sortAllChatsByRecentMessages();
          this.cdRef.detectChanges();
        }
      });
    };

    this.chatService.adminAssigned$.subscribe((data: any) => handleAdminUpdate(data, true));
    this.chatService.adminRemoved$.subscribe((data: any) => handleAdminUpdate(data, false));



    this.onlineUserSub = this.chatService.onlineUser$.subscribe(
      (userUpdate) => {
        this.zone.run(() => {
          if (!userUpdate) return;

          const idx = this.onlineUserList.findIndex(
            (u) => u.userId === userUpdate.userId
          );
          if (idx !== -1) {
            this.onlineUserList[idx] = {
              ...this.onlineUserList[idx],
              ...userUpdate,
            };
          } else {
            this.onlineUserList.push(userUpdate);
          }

          if (this.chatUserId === userUpdate.userId) {
            this.updateChatUserStatus(userUpdate);
          }
          this.cdRef.detectChanges();
        });
      }
    );
    this.userId = localStorage.getItem("guestID");
    this.senderUsername = localStorage.getItem("username");
    this.restoreOutbox();

    // Handle query parameters and deep link route navigation
    this.route.queryParams.subscribe(() => {
      this.scheduleRouteNavigation();
    });
    this.route.paramMap.subscribe(() => {
      this.scheduleRouteNavigation();
    });

    this.getAllUsers();
    // this.getUnreadCount();
    this.initiatChat();
    this.listenForMessages();
    this.checkMobileView();
    if (this.isMobileView) {
      this.setupMobileHistory();
    }
    this.locationSubscription = this.location.subscribe((popEvent: any) => {
      this.handlePopState(popEvent);
    });
    window.addEventListener("resize", this.checkMobileView.bind(this));

    // Cleanup typing status periodically
    setInterval(() => {
      const now = Date.now();
      let changed = false;
      this.typingUsers.forEach((value, key) => {
        if (now - value.timestamp > 5000) {
          this.typingUsers.delete(key);
          changed = true;
        }
      });
      if (changed) this.cdRef.detectChanges();
    }, 5000);
  }
  getSafeIcon = (type: 'delete' | 'pin' | 'unpin' | 'forward' | 'reply' | 'menu' | 'forwarded' | 'pinned', color: string): SafeHtml => {
    return this.sanitizer.bypassSecurityTrustHtml(
      getIcon(type, color, '20px')
    );
  }


  showVideoOverlay = true;

  onVideoClick(event: Event): void {
    if (this.showVideoOverlay) {
      event.preventDefault();
      event.stopPropagation();

      const active = document.activeElement as HTMLElement;
      if (
        active &&
        (active.tagName === "TEXTAREA" || active.tagName === "INPUT")
      ) {
        active.blur();
      }

      this.showVideoOverlay = false;
    }
  }

  async disconnectSingleChat() {
    console.log("🔌 Attempting to disconnect previous single chat");
    console.log("Current active channel:", this.activeChannel);
    console.log("Current channel object:", this.singleChannel);

    if (this.singleChannel && this.activeChannel) {
      try {
        const status = await this.converse.closeChannel(this.activeChannel);
        console.log(" Disconnected channel:", this.activeChannel, status);
      } catch (err) {
        console.error(" Error disconnecting channel:", err);
      } finally {
        this.singleChannel = null;
        this.activeChannel = null;
      }
    } else {
      console.warn("No active channel to disconnect", {
        chan: this.singleChannel,
        activeChannel: this.activeChannel,
        singleUser: this.singleUser,
      });
    }
  }

  disconnectGroupChat() {
    if (this.groupChannel && this.activeChannel) {
      try {
        this.converse.closeChannel(this.activeChannel);
        console.log("Group channel disconnected:", this.activeChannel);
      } catch (err) {
        console.error("Error disconnecting group channel:", err);
      }
      this.groupChannel = null;
    } else {
      console.warn(
        "No active group channel to disconnect",
        this.groupChannel,
        this.activeChannel
      );
    }
  }

  private updateChatUserStatus(user: any) {
    if (!user) return;

    if (user.isActive) {
      this.isChatUserOnline = true;
      this.chatUserStatus = "Online";
    } else {
      this.isChatUserOnline = false;
      this.chatUserStatus = `Last seen ${this.formatLastActive(
        user.lastActiveAt
      )}`;
    }
  }

  @HostListener('window:beforeunload')
  onBeforeUnload(): void {
    this.saveCurrentChatDraft();
  }

  ngOnDestroy() {
    if (this.locationSubscription) {
      this.locationSubscription.unsubscribe();
      this.locationSubscription = null;
    }
    this.disarmBackTrail();
    this.clearOpenChatInterval();
    clearTimeout(this.outboxRetryTimer);
    this.saveCurrentChatDraft();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('online', this.onOnline);
    window.removeEventListener('offline', this.onOffline);
    window.removeEventListener('focus', this.onFocus);
    clearTimeout(this.resyncTimer);
    clearTimeout(this.routeNavTimer);
    if (this.waitForChatsTimer) clearInterval(this.waitForChatsTimer);
    this.chatService.isChatOpenMobile$.next(false);
    this.onlineUserSub?.unsubscribe();
    if (this.statusSubscription) {
      this.statusSubscription.unsubscribe();
    }
    if (this.lastSeenTimer) clearInterval(this.lastSeenTimer);
    console.log("Component is being destroyed");

    // Disconnect all cached single channels
    if (this.converse) {
      for (const channelId of Object.keys(this.cachedSingleChannels)) {
        try {
          this.converse.closeChannel(channelId);
          console.log("Closed cached single channel:", channelId);
        } catch (err) {
          console.error("Error closing cached single channel:", channelId, err);
        }
      }
      // Disconnect all cached group channels
      for (const channelId of Object.keys(this.cachedGroupChannels)) {
        try {
          this.converse.closeChannel(channelId);
          console.log("Closed cached group channel:", channelId);
        } catch (err) {
          console.error("Error closing cached group channel:", channelId, err);
        }
      }
    }

    this.disconnectSingleChat();
    this.disconnectGroupChat();
  }

  private waitForImagesToLoad(container: HTMLElement): Promise<void> {
    const images = container.querySelectorAll('img');
    const imagePromises: Promise<void>[] = [];

    images.forEach((img) => {
      if (img.complete) {
        return;
      }
      imagePromises.push(
        new Promise<void>((resolve) => {
          img.addEventListener('load', () => resolve());
          img.addEventListener('error', () => resolve());
          setTimeout(() => resolve(), 200);
        })
      );
    });

    if (imagePromises.length === 0) {
      return Promise.resolve();
    }

    return Promise.race<any>([
      Promise.all(imagePromises).then(() => { }),
      new Promise<void>(resolve => setTimeout(() => resolve(), 150))
    ]);
  }

  ngAfterViewChecked() {
    // Single chat scroll
    if (this.needsScrollToBottomSingle && this.singleChatBox && !this.isInitialLoadingChat) {
      const singleContainer = document.getElementById("singleChatContainer");
      if (singleContainer) {
        singleContainer.scrollTop = singleContainer.scrollHeight;
      }
      this.needsScrollToBottomSingle = false;
      this.waitForImagesToLoad(singleContainer!).then(() => {
        if (singleContainer) {
          singleContainer.scrollTop = singleContainer.scrollHeight;
        }
        this.scrollReadySingle = true;
        this.chatLoader = false;
      });
    }

    // Group chat scroll
    if (this.needsScrollToBottomGroup && this.groupChatBox && !this.isInitialLoadingChat) {
      const groupContainer = document.getElementById("groupChatContainer");
      if (groupContainer) {
        groupContainer.scrollTop = groupContainer.scrollHeight;
      }
      this.needsScrollToBottomGroup = false;
      this.waitForImagesToLoad(groupContainer!).then(() => {
        if (groupContainer) {
          groupContainer.scrollTop = groupContainer.scrollHeight;
        }
        this.scrollReady = true;
        this.chatLoader = false;
      });
    }
  }

  //LISTEN NOTIFICATION  MESSAGE
  listenForMessages() {
    console.log("Listening for messages");

    try {
      onMessage(messaging, (payload) => {
        console.log("Foreground message received:", payload);

        let parsed: any = {};
        try {
          parsed = JSON.parse(payload?.data?.payload || "{}");
          console.log("Parsed notification payload:", parsed);
        } catch (error) {
          console.error("Failed to parse notification payload:", error);
          return;
        }

        if (!parsed) return;

        if (parsed.type === "Chat-Read") {
          closeChatNotifications([parsed.channelId]);
          return;
        }

        if (parsed.sender_id === this.userId) {
          console.log("Notification skipped: Sent by self");
          return;
        }

        if (parsed.user_id && parsed.user_id !== this.userId) {
          console.log("Notification skipped: Not intended for current user");
          return;
        }

        const isChatNotification = parsed.redirectUrl?.includes('/main-chat?chatType=');
        if (isChatNotification) {
          this.soundService.playChat();

        }

        const isMentioned = parsed.user_id === this.userId;
        let title = "Notification";

        if (isMentioned) {
          title = parsed.groupName
            ? `${parsed.title} mentioned you in ${parsed.groupName}`
            : `${parsed.title} mentioned you`;
        } else {
          title = parsed.groupName && parsed.title !== parsed.groupName
            ? `${parsed.title} (${parsed.groupName})`
            : (parsed.title || "Notification");
        }

        let notificationBody = parsed.body || parsed.message || "";
        if (isAudio(notificationBody)) {
          notificationBody = "sent an audio";
        } else if (isImage(notificationBody)) {
          notificationBody = "sent a photo";
        } else if (isVideo(notificationBody)) {
          notificationBody = "sent a video";
        } else if (isDocument(notificationBody)) {
          notificationBody = "sent a document";
        }

        const options: NotificationOptions = {
          body: notificationBody,
          icon:
            parsed.icon ||
            "https://link.iiclab.com/assets/login/header-logo.png",
          data: {
            url: parsed.redirectUrl,
            redirectUrl: parsed.redirectUrl,
          },
        };

        // new Notification() throws "Illegal constructor" unconditionally on Android
        // Chrome — only a service worker may show a notification there. This runs on
        // every platform, so it replaces the constructor rather than branching on it.
        // Clicks are handled by the worker's existing notificationclick listener,
        // which reads this same data.redirectUrl key.
        getFcmRegistration().then((registration) => {
          registration?.showNotification(title, options);
        });
      });
    } catch (error) {
      console.error("Failed to set up foreground message listener:", error);
    }
  }

  // Mobile navigation history & back-flow management
  private isChatOpenInHistory: boolean = false;
  private locationSubscription: any = null;
  private openedFromChatList: boolean = false;

  private isNotificationNavigation(): boolean {
    if (this.route.snapshot.queryParams['n']) return true;
    if (window.history.state?.fromNotification) return true;
    try {
      const fcmNavTime = sessionStorage.getItem('fcm_notification_navigation');
      if (fcmNavTime && (Date.now() - Number(fcmNavTime) < 30000)) return true;
    } catch (e) { }
    return false;
  }

  private setupMobileHistory() {
    if (!this.isMobileView) return;

    const rawPath = this.location.path() || '';
    const currentPath = rawPath.split('?')[0];
    const navId = (window.history.state && window.history.state.navigationId) || 0;
    const isFromNotification = this.isNotificationNavigation();

    const isChannelRoute = (currentPath.startsWith('/main-chat/') && currentPath.length > '/main-chat/'.length) ||
      rawPath.includes('chatType=') || rawPath.includes('userId=') || rawPath.includes('channelId=');

    if (isChannelRoute) {
      if (isFromNotification || navId <= 1 || !this.openedFromChatList) {
        if (this.canUseBackTrail(navId)) {
          this.armBackTrail('chat');
          try {
            sessionStorage.removeItem('fcm_notification_navigation');
          } catch (e) { }
          return;
        }
        // Deep-linked or opened directly from push notification on a specific chat:
        // Synthesize stack: /home -> /main-chat -> active chat route
        this.location.replaceState('/home');
        this.location.go('/main-chat');
        this.location.go(rawPath);
        this.isChatOpenInHistory = true;
        this.openedFromChatList = false;
        try {
          sessionStorage.removeItem('fcm_notification_navigation');
        } catch (e) { }
      } else {
        this.isChatOpenInHistory = true;
      }
    } else if (currentPath === '/main-chat' || currentPath.startsWith('/main-chat')) {
      if (navId <= 1) {
        if (this.canUseBackTrail(navId)) {
          this.armBackTrail('list');
          return;
        }
        // Direct launch to main-chat listing on mobile:
        // Synthesize stack: /home -> /main-chat
        this.location.replaceState('/home');
        this.location.go('/main-chat');
        this.isChatOpenInHistory = false;
      }
    }
  }

  // Cold start on mobile (a push notification opening the closed PWA, or a direct link): the tab has a
  // single history entry and no user gesture yet, and Chrome skips entries pushed without a gesture, so
  // the synthesized /home -> /main-chat stack above is jumped over and Back exits the app. Android Back
  // still reaches a CloseWatcher without a gesture, so this tab walks chat -> list -> /home with one instead.
  private backTrail: 'chat' | 'list' | null = null;
  private backWatcher: any = null;

  private canUseBackTrail(navId: number): boolean {
    if (this.backTrail) return true;
    return typeof (window as any).CloseWatcher === 'function' && navId <= 1 && !(navigator as any).userActivation?.hasBeenActive;
  }

  private armBackTrail(step: 'chat' | 'list') {
    if (this.backTrail === step && this.backWatcher) return;
    this.disarmBackTrail();
    try {
      const watcher = new (window as any).CloseWatcher();
      watcher.addEventListener('close', () => {
        if (this.backWatcher !== watcher) return;
        this.backWatcher = null;
        this.onBackTrailClose(step);
      });
      this.backWatcher = watcher;
      this.backTrail = step;
    } catch (e) { }
  }

  private disarmBackTrail() {
    const watcher = this.backWatcher;
    this.backWatcher = null;
    this.backTrail = null;
    watcher?.destroy();
  }

  private onBackTrailClose(step: 'chat' | 'list') {
    if (step === 'list') {
      this.backTrail = null;
      void this.router.navigateByUrl('/home', { replaceUrl: true });
      return;
    }
    // A panel over the conversation closes first, as it does with history Back.
    if (this.isGroupInfoOpen || this.isStarredMessagesPanelOpen || this.isQuickSearchOpen || this.showPinnedMessages) {
      this.isGroupInfoOpen = false;
      this.isStarredMessagesPanelOpen = false;
      this.isQuickSearchOpen = false;
      this.showPinnedMessages = false;
      this.armBackTrail('chat');
    } else {
      this.closeChatWindow(true);
    }
    this.cdRef.detectChanges();
  }

  private handlePopState(popEvent?: any) {
    if (!this.isMobileView) return;

    const currentPath = this.location.path() || '';

    // If an overlay/sub-panel is open on mobile, close it first and keep chat window open
    if (this.isGroupInfoOpen) {
      this.isGroupInfoOpen = false;
      this.cdRef.detectChanges();
      const activeCh = this.activeChannel || this.memberChannelId || (this.singleUser && this.singleUser.channelId);
      if (activeCh) {
        this.location.go('/main-chat/' + activeCh);
      }
      return;
    }
    if (this.isStarredMessagesPanelOpen) {
      this.isStarredMessagesPanelOpen = false;
      this.cdRef.detectChanges();
      const activeCh = this.activeChannel || this.memberChannelId || (this.singleUser && this.singleUser.channelId);
      if (activeCh) {
        this.location.go('/main-chat/' + activeCh);
      }
      return;
    }
    if (this.isQuickSearchOpen) {
      this.isQuickSearchOpen = false;
      this.cdRef.detectChanges();
      const activeCh = this.activeChannel || this.memberChannelId || (this.singleUser && this.singleUser.channelId);
      if (activeCh) {
        this.location.go('/main-chat/' + activeCh);
      }
      return;
    }
    if (this.showPinnedMessages) {
      this.showPinnedMessages = false;
      this.cdRef.detectChanges();
      const activeCh = this.activeChannel || this.memberChannelId || (this.singleUser && this.singleUser.channelId);
      if (activeCh) {
        this.location.go('/main-chat/' + activeCh);
      }
      return;
    }

    // If chat conversation window is open on mobile
    if (this.showChatWindow || this.singleChatBox || this.groupChatBox) {
      const isChannelRoute = currentPath.startsWith('/main-chat/') && currentPath.length > '/main-chat/'.length;
      if (!isChannelRoute) {
        this.closeChatWindow(true);
        this.cdRef.detectChanges();
      }
    }
  }

  private updateChatRoute(channelId: string) {
    if (!channelId) return;
    const targetPath = '/main-chat/' + channelId;

    if (this.isMobileView && this.backTrail) {
      // Back is handled by the CloseWatcher trail, so the single history entry is only renamed.
      this.location.replaceState(targetPath);
      this.isChatOpenInHistory = false;
      this.openedFromChatList = false;
      this.armBackTrail('chat');
      try {
        sessionStorage.removeItem('fcm_notification_navigation');
      } catch (e) { }
    } else if (this.isMobileView) {
      const isFromNotification = this.isNotificationNavigation();
      const navId = (window.history.state && window.history.state.navigationId) || 0;
      const isDirectOrNotification = !this.openedFromChatList && (!this.isChatOpenInHistory || isFromNotification || navId <= 1);

      if (isDirectOrNotification) {
        // Chat was opened directly (push notification, deep link, or direct navigation):
        // Ensure the history stack has /home and /main-chat behind this chat
        this.location.replaceState('/home');
        this.location.go('/main-chat');
        this.location.go(targetPath);
        this.isChatOpenInHistory = true;
        this.openedFromChatList = false;
        try {
          sessionStorage.removeItem('fcm_notification_navigation');
        } catch (e) { }
      } else if (this.openedFromChatList) {
        // Chat was opened normally from the chat listing
        this.location.go(targetPath);
        this.isChatOpenInHistory = true;
        this.openedFromChatList = false;
      } else if (this.isChatOpenInHistory) {
        // Already inside a conversation and switching: replace current conversation in history
        this.location.replaceState(targetPath);
      } else {
        this.location.go(targetPath);
        this.isChatOpenInHistory = true;
      }
    } else {
      this.location.replaceState(targetPath);
    }
  }

  //Mobile Chat Ui
  checkMobileView() {
    const wasMobile = this.isMobileView;
    this.isMobileView = window.innerWidth < 768;
    if (!wasMobile && this.isMobileView) {
      this.setupMobileHistory();
    }
  }

  showBackButton() {
    this.closeChatWindow();
  }

  allSingleUsers: any[] = [];
  allGroupUsers: any[] = [];
  filteredGroupUsers: any[] = [];
  filteredSingleUsers: any[] = [];
  chatListLoading: boolean = true;
  skeletonPlaceholders = Array.from({ length: 12 });
  totalGroupMembers: any;
  memberChannelId: any;
  allGroupMembers: any[] = [];
  profileImageMapping = {};
  userNameMapping = {};
  groupChatProfileImg: any;
  groupChatName: any;
  groupMembersDisplayNames: string[] = [];
  chatMap: { [channelId: string]: any } = {};



  private getContentCategorization(fileName: string, fileType: string = '') {
    const lowerName = (fileName || "").toLowerCase();
    const lowerType = (fileType || "").toLowerCase();

    let contentType = 'other';
    let contentSubtype = 'other';

    if (DOCUMENT_EXTENSIONS.some(ext => lowerName.endsWith(ext.toLowerCase()))) {
      contentType = 'doc';
      const ext = lowerName.split('.').pop() || 'doc';
      if (lowerName.endsWith('.pdf')) contentSubtype = 'pdf';
      else if (lowerName.endsWith('.doc') || lowerName.endsWith('.docx')) contentSubtype = 'doc';
      else if (lowerName.endsWith('.ppt') || lowerName.endsWith('.pptx')) contentSubtype = 'ppt';
      else if (lowerName.endsWith('.xls') || lowerName.endsWith('.xlsx') || lowerName.endsWith('.csv') || lowerName.endsWith('.tsv')) contentSubtype = 'excel';
      else if (['js', 'ts', 'jsx', 'tsx', 'json', 'py', 'java', 'html', 'css', 'scss', 'sql', 'cpp', 'c', 'cs', 'php', 'rb', 'go', 'rs', 'swift', 'kt', 'sh', 'xml', 'yaml', 'yml'].includes(ext)) contentSubtype = ext;
      else contentSubtype = ext || 'other';
    } else if (lowerType.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(lowerName)) {
      contentType = 'image';
      if (lowerName.endsWith('.jpg') || lowerName.endsWith('.jpeg')) contentSubtype = 'jpg';
      else if (lowerName.endsWith('.png')) contentSubtype = 'png';
      else if (lowerType.includes('jpeg')) contentSubtype = 'jpg';
      else if (lowerType.includes('png')) contentSubtype = 'png';
      else contentSubtype = 'other';
    } else if (lowerType.startsWith('video/') || /\.(mp4|webm|ogg|mov|avi|mkv)(\?.*)?$/i.test(lowerName)) {
      if (isVoiceMessage(lowerName)) {
        contentType = 'audio';
        contentSubtype = 'mp3';
      } else {
        contentType = 'video';
        if (lowerName.endsWith('.mp4')) contentSubtype = 'mp4';
        else if (lowerName.endsWith('.mov')) contentSubtype = 'mov';
        else if (lowerName.endsWith('.webm')) contentSubtype = 'webm';
        else if (lowerName.endsWith('.ogg')) contentSubtype = 'ogg';
        else if (lowerName.endsWith('.avi')) contentSubtype = 'avi';
        else if (lowerName.endsWith('.mkv')) contentSubtype = 'mkv';
        else if (lowerType.includes('mp4')) contentSubtype = 'mp4';
        else if (lowerType.includes('quicktime')) contentSubtype = 'mov';
        else contentSubtype = 'other';
      }
    } else if (lowerType.startsWith('audio/') || /\.(mp3|wav|aac|ogg|flac)$/i.test(lowerName) || isVoiceMessage(lowerName)) {
      contentType = 'audio';
      if (lowerName.endsWith('.mp3') || isVoiceMessage(lowerName)) contentSubtype = 'mp3';
      else if (lowerType.includes('mpeg') || lowerType.includes('mp3')) contentSubtype = 'mp3';
      else contentSubtype = 'other';
    }

    return { contentType, contentSubtype };
  }

  private getLinkContentCategorization(url: string) {
    const lowerUrl = (url || "").toLowerCase();
    let contentSubtype = 'url';

    const cleanUrl = lowerUrl.split('?')[0].split('#')[0];

    if (lowerUrl.includes('docs.google.com/document')) {
      contentSubtype = 'doc';
    }
    else if (lowerUrl.includes('docs.google.com/spreadsheets')) {
      contentSubtype = 'excel';
    }
    else if (lowerUrl.includes('docs.google.com/presentation')) {
      contentSubtype = 'ppt';
    }
    else if (lowerUrl.includes('docs.google.com/videos')) {
      contentSubtype = 'video';
    }
    else if (lowerUrl.includes('drive.google.com/file')) {
      contentSubtype = 'url';
    }


    else if (
      (lowerUrl.includes('office.com') || lowerUrl.includes('onedrive.live.com')) &&
      lowerUrl.includes('word')
    ) {
      contentSubtype = 'doc';
    }
    else if (
      (lowerUrl.includes('office.com') || lowerUrl.includes('onedrive.live.com')) &&
      lowerUrl.includes('excel')
    ) {
      contentSubtype = 'excel';
    }
    else if (
      (lowerUrl.includes('office.com') || lowerUrl.includes('onedrive.live.com')) &&
      lowerUrl.includes('powerpoint')
    ) {
      contentSubtype = 'ppt';
    }

    else if (
      lowerUrl.includes('youtube.com') ||
      lowerUrl.includes('youtu.be') ||
      lowerUrl.includes('vimeo.com') ||
      lowerUrl.includes('loom.com') ||
      lowerUrl.includes('dailymotion.com')
    ) {
      contentSubtype = 'video';
    }


    else if (lowerUrl.includes('photos.google.com')) {
      contentSubtype = lowerUrl.includes('/video/') ? 'video' : 'image';
    }

    else if (/\.(pdf|docx?|txt)$/i.test(cleanUrl)) {
      contentSubtype = 'doc';
    }
    else if (/\.(xlsx?|csv)$/i.test(cleanUrl)) {
      contentSubtype = 'excel';
    }
    else if (/\.(pptx?)$/i.test(cleanUrl)) {
      contentSubtype = 'ppt';
    }
    else if (/\.(mp4|webm|ogg|mov|avi|mkv)$/i.test(cleanUrl)) {
      contentSubtype = 'video';
    }
    else if (/\.(mp3|wav|m4a|aac)$/i.test(cleanUrl)) {
      contentSubtype = 'mp3';
    }
    else if (/\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(cleanUrl)) {
      contentSubtype = 'image';
    }

    else if (/\/(image|img|media|upload)s?\//.test(lowerUrl)) {
      contentSubtype = 'image';
    }
    else if (/\/(video|stream|watch)s?\//.test(lowerUrl)) {
      contentSubtype = 'video';
    }

    return { contentType: 'link', contentSubtype };
  }


  // Central Dispatcher for ALL incoming messages 
  processIncomingProjectMessage(msg: any) {
    console.log("RAW SOCKET MESSAGE:", msg);

    // Normalize incoming
    const incoming =
      (msg?.message && typeof msg.message === 'object')
        ? msg.message
        : msg;

    let meta = incoming?.meta || msg?.meta || {};
    if (typeof meta === 'string') {
      try { meta = JSON.parse(meta); } catch (e) { }
    }
    let channelId =
      meta?.channel_id ||
      meta?.channelId ||
      msg?.channelId ||
      msg?.channel_id ||
      msg?.data?.channelId ||
      msg?.data?.channel_id ||
      msg?.message?.channelId ||
      msg?.message?.channel_id ||
      incoming?.channelId ||
      incoming?.channel_id ||
      meta?.groupId;

    if (!channelId) {
      console.warn("ChannelId NOT found. Full msg:", msg);
      return;
    }

    // Normalize channelId
    channelId = String(channelId).trim();

    const senderId =
      msg?.sender_id ||
      incoming?.sender_id ||
      msg?.sid ||
      incoming?.sid;

    // --- FIX MISSING CHANNEL ID FOR MEDIA GROUPED UPLOADS ---
    if (channelId === String(meta?.groupId).trim() && senderId && senderId !== this.userId) {
      const isKnownChannel = this.chatMap?.[channelId] || this.allGroupUsers.some(g => String(g.channelId).trim() === channelId) || this.allSingleUsers.some(u => String(u.channelId).trim() === channelId);
      if (!isKnownChannel) {
        console.warn("processIncomingProjectMessage: channelId seems to be groupId. Attempting to rescue single chat via senderId.");
        const matchingUser = this.allSingleUsers.find(u => u.userId === senderId || u._id === senderId);
        if (matchingUser && matchingUser.channelId) {
          channelId = String(matchingUser.channelId).trim();
          if (msg) msg.channelId = channelId;
          if (incoming) incoming.channelId = channelId;
          if (meta) meta.channel_id = channelId;
          console.log("rescued missing channelId for grouped upload via senderId:", channelId);
        }
      }
    }

    console.log("✅ Extracted ChannelId:", channelId);

    // Check for realId (standard deduplication) - CRITICAL for preventing receiver-side duplication
    const realId = incoming?._id || incoming?.id || msg?._id || msg?.id || meta?._id || meta?.id || incoming?.messageId || msg?.messageId;
    // NOTE: Don't register message ID here - let individual handlers handle deduplication
    // to avoid blocking legitimate messages from being added to chat window
    let fileUrlForDedup = meta?.fileUrl || incoming?.url || msg?.url || (typeof incoming?.file === 'string' ? incoming.file : incoming?.file?.url);
    if (!fileUrlForDedup && incoming?.files?.length) {
      fileUrlForDedup = incoming.files[0].fileUrl || incoming.files[0].url;
    }
    if (Array.isArray(fileUrlForDedup)) {
      fileUrlForDedup = fileUrlForDedup[0] || "";
    }

    if (senderId !== this.userId && fileUrlForDedup && String(fileUrlForDedup).trim() !== "") {
      const mediaDedupKey = `mediaDedup:${senderId}:${channelId}:${fileUrlForDedup}`;
      if (this.receivedMessageIds.has(mediaDedupKey)) {
        console.warn("Duplicate media blocked by content guard in processIncomingProjectMessage:", mediaDedupKey);
        return;
      }
      this.receivedMessageIds.add(mediaDedupKey);
      // Keep guard for 4 seconds to catch rapid-fire duplicates
      setTimeout(() => this.receivedMessageIds.delete(mediaDedupKey), 4000);
    }

    // Check for tempId (for manually pushed messages from other tabs/users)
    const tempId = meta?.tempId || incoming?.tempId || msg?.meta?.tempId;
    if (tempId && senderId !== this.userId && this.receivedMessageIds.has(`temp:${tempId}`)) {
      console.warn("Duplicate message skipped (tempId) in processIncomingProjectMessage:", tempId);
      return;
    }

    // Check for groupId (for grouped uploads/carousels)
    const duplicateGroupId = meta?.groupId || meta?.data?.groupId || incoming?.groupId;
    if (duplicateGroupId && this.receivedMessageIds.has(`groupId:${duplicateGroupId}`)) {
      console.warn("Duplicate message skipped (groupId) in processIncomingProjectMessage:", duplicateGroupId);
      return;
    }

    const chatEntry = this.chatMap?.[channelId];

    const isGroupChannel =
      chatEntry?.type === 'group' ||
      this.allGroupUsers.some(g => String(g.channelId).trim() === channelId);

    const isSingleChannel =
      chatEntry?.type === 'single' ||
      this.allSingleUsers.some(u => String(u.channelId).trim() === channelId);

    const isSysMsg = isSystemMessage(incoming || msg);

    /* ======================================================
       SELF MESSAGE HANDLING (Replace Pending)
    ======================================================= */

    if (senderId === this.userId && !isSysMsg) {

      let pendingMessageReplaced = false;

      const realId =
        incoming?._id ||
        incoming?.id ||
        incoming?.messageId ||
        msg?._id ||
        msg?.id ||
        msg?.messageId;

      if (realId) {
        // Attempt to find pending message by tempId first, then by 'no-id' status
        const findPendingIn = (messages: any[], targetChannel: string, currentActiveChannel: string) => {
          if (!messages || !messages.length || String(targetChannel).trim() !== String(currentActiveChannel).trim()) return -1;

          // 1. Try tempId match (most reliable)
          const inMeta = getMessageMeta(incoming);
          const msgMeta = getMessageMeta(msg);
          const tempId = inMeta.tempId || incoming?.tempId || msgMeta.tempId;
          if (tempId) {
            const idx = messages.findIndex(m => {
              const mMeta = getMessageMeta(m);
              return (mMeta.tempId === tempId || m.tempId === tempId) && m.sender_id === this.userId;
            });
            if (idx !== -1) return idx;
          }

          // 2. Try file URL match (fallback for media)
          let fileUrl = inMeta.fileUrl || incoming.url || (incoming.files && incoming.files[0]?.fileUrl);
          if (Array.isArray(fileUrl)) fileUrl = fileUrl[0];
          if (fileUrl) {
            const idx = messages.findIndex(m => {
              const mMeta = getMessageMeta(m);
              let mFileUrl = mMeta.fileUrl || m.url || (m.files && m.files[0]?.fileUrl);
              if (Array.isArray(mFileUrl)) mFileUrl = mFileUrl[0];
              return mFileUrl === fileUrl && m.sender_id === this.userId;
            });
            if (idx !== -1) return idx;
          }

          // 3. Fallback to 'latest no-id' match
          return messages.slice().reverse().findIndex((m: any) =>
            ((!m._id && !m.id && !m.messageId) || (m._id && m._id.toString().length < 20)) &&
            m.sender_id === this.userId
          ) === -1 ? -1 : messages.length - 1 - messages.slice().reverse().findIndex((m: any) =>
            ((!m._id && !m.id && !m.messageId) || (m._id && m._id.toString().length < 20)) &&
            m.sender_id === this.userId
          );
        };

        // SINGLE
        if (this.singleChatBox) {
          const actualIndex = findPendingIn(this.singleChatMessages, channelId, this.singleUser?.channelId);
          if (actualIndex !== -1) {
            const existing = this.singleChatMessages[actualIndex];
            const resolvedReplyTo = incoming.replyTo || meta?.replyTo || existing.replyTo || existing.meta?.replyTo;
            this.singleChatMessages[actualIndex] = {
              ...existing,
              ...incoming,
              _id: realId,
              id: realId,
              sender_id: this.userId,
              isUploading: false,
              ...(resolvedReplyTo ? { replyTo: resolvedReplyTo } : {}),
              meta: {
                ...existing.meta,
                ...(meta || {}),
                ...(resolvedReplyTo ? { replyTo: resolvedReplyTo } : {})
              },
              // Preserve media if server echo lacks it
              files: (incoming.files && incoming.files.length) ? incoming.files : existing.files,
              message: existing.message || incoming.message
            };
            this.processMessages();
            this.cdRef.detectChanges();
            pendingMessageReplaced = true;
          }
        }

        // GROUP
        if (!pendingMessageReplaced && this.groupChatBox) {
          const actualIndex = findPendingIn(this.groupChatMessages, channelId, this.memberChannelId);
          if (actualIndex !== -1) {
            const existing = this.groupChatMessages[actualIndex];
            const resolvedReplyTo = incoming.replyTo || meta?.replyTo || existing.replyTo || existing.meta?.replyTo;
            this.groupChatMessages[actualIndex] = {
              ...existing,
              ...incoming,
              _id: realId,
              id: realId,
              sender_id: this.userId,
              isUploading: false,
              ...(resolvedReplyTo ? { replyTo: resolvedReplyTo } : {}),
              meta: {
                ...existing.meta,
                ...(meta || {}),
                ...(resolvedReplyTo ? { replyTo: resolvedReplyTo } : {})
              },
              files: (incoming.files && incoming.files.length) ? incoming.files : existing.files
            };
            this.processGroupMessages();
            this.cdRef.detectChanges();
            pendingMessageReplaced = true;
          }
        }
      }

      if (pendingMessageReplaced) {
        // Update sidebar
        if (isGroupChannel) {
          this.updateSidebarForIncomingMessage(msg, channelId, 'group', false);
        } else if (isSingleChannel) {
          this.updateSidebarForIncomingMessage(msg, channelId, 'single', false);
        }
        return;
      }
    }

    /* ======================================================
       GROUP MESSAGE HANDLING
    ======================================================= */

    if (isGroupChannel) {
      const isGroupChatOpen = this.groupChatBox && String(channelId).trim() === String(this.memberChannelId).trim();
      if (isGroupChatOpen) {

        // Chat is open
        this.inComingGroupListenerMessages(msg);
        this.updateSidebarForIncomingMessage(msg, channelId, 'group', false);

      } else {

        // Chat closed → increment unread
        if (senderId !== this.userId) {
          this.soundService.playChat();
        }
        // Keep cached conversation in sync so it's up to date when reopened
        this.appendMessageToBackgroundCache(channelId, msg, true);
        this.updateSidebarForIncomingMessage(msg, channelId, 'group');
      }

      return;
    }

    /* ======================================================
       SINGLE MESSAGE HANDLING
    ======================================================= */

    if (isSingleChannel) {

      // Check if the single chat is actually open (both channel ID and singleChatBox state)
      const isSingleChatOpen = this.singleChatBox && String(channelId).trim() === String(this.singleUser?.channelId).trim();

      if (isSingleChatOpen) {

        this.handleIncomingMessage(msg);
        this.updateSidebarForIncomingMessage(msg, channelId, 'single', false);

      } else {

        if (senderId !== this.userId) {
          this.soundService.playChat();
        }
        // Keep cached conversation in sync so it's up to date when reopened
        this.appendMessageToBackgroundCache(channelId, msg, false);
        this.updateSidebarForIncomingMessage(msg, channelId, 'single');
      }

      return;
    }

    console.warn(" Message received but channel not found:", channelId);
  }





  // Helper function to update sidebar 
  private updateSidebarForIncomingMessage(
    msg: any,
    channelId: string,
    chatType: 'single' | 'group',
    incrementUnread: boolean = true
  ) {
    if (!this.isValidChannelId(channelId)) return;

    const incoming =
      (msg?.message && typeof msg.message === 'object')
        ? msg.message
        : msg;

    const meta = incoming?.meta || msg?.meta || {};
    const senderId =
      msg?.sender_id ||
      incoming?.sender_id ||
      msg?.sid ||
      incoming?.sid;

    //  FIXES SAMURAIS
    const normalizedChannelId = String(channelId).trim();

    const isSysMsg = isSystemMessage(incoming || msg);
    if (senderId && !isSysMsg) {
      if (!incoming.sender_id) incoming.sender_id = senderId;
      if (this.userNameMapping[senderId]) {
        incoming.name = this.userNameMapping[senderId];
      }
    }

    // Do not increment unread for self message
    if (senderId === this.userId && !isSysMsg) {

      incrementUnread = false;
    }

    // Check if this is a grouped upload (carousel)
    const metaGroupId = meta?.groupId || meta?.data?.groupId;
    const isGroupedUpload = meta?.isGroupedUpload || !!metaGroupId;

    // For grouped uploads, only increment unread count once per groupId
    if (isGroupedUpload && metaGroupId && incrementUnread) {
      if (this.processedGroupIdsForUnread.has(metaGroupId)) {
        // Already counted this groupId, don't increment again
        incrementUnread = false;
      } else {
        // Mark this groupId as processed and allow increment
        this.processedGroupIdsForUnread.add(metaGroupId);
      }
    }

    /* =====================================================
       PREVIEW GENERATION
    ===================================================== */

    let fileUrl =
      incoming?.url ||
      (typeof incoming?.file === 'string'
        ? incoming.file
        : incoming?.file?.url) ||
      "";

    if (!fileUrl && incoming?.files?.length) {
      fileUrl = incoming.files[0].fileUrl || incoming.files[0].url || "";
    }

    if (!fileUrl && meta?.fileUrl) {
      fileUrl = meta.fileUrl;
    }

    if (Array.isArray(fileUrl)) {
      fileUrl = fileUrl[0] || "";
    } else if (typeof fileUrl === 'object' && fileUrl !== null) {
      fileUrl = fileUrl.fileUrl || fileUrl.url || "";
    }

    fileUrl = typeof fileUrl === 'string' ? fileUrl.toLowerCase() : "";

    let rawMessage = incoming?.message;

    if (typeof rawMessage === 'object' && rawMessage !== null) {
      rawMessage = rawMessage.text || rawMessage.content || "";
    }

    let preview = (rawMessage && String(rawMessage).trim() !== "") ? String(rawMessage).trim() : "";

    const formattedPreview = this.generatePreviewText(incoming);

    if (formattedPreview && formattedPreview.trim() !== "") {
      preview = formattedPreview;
    } else if (fileUrl && String(fileUrl).trim() !== "") {
      // Fallback if generatePreviewText didn't match but we have a fileUrl
      if (isImage(fileUrl)) {
        preview = isGifMessage(incoming) || fileUrl.endsWith('.gif') ? "sent a gif" : "sent a photo";
      } else if (isVideo(fileUrl))
        preview = " sent a video";
      else if (isAudio(fileUrl))
        preview = "sent an audio";
      else if (isDocument(fileUrl))
        preview = "sent a document";
      else
        preview = "sent a file";
    }

    let updated = false;

    const normalizedLastUrl = Array.isArray(incoming?.url)
      ? (incoming.url[0] || "")
      : (typeof incoming?.url === 'string' ? incoming.url : (fileUrl || ""));

    // Persist Cache Logic
    const lastMsgObj = {
      ...incoming,
      url: normalizedLastUrl,
      message: preview,
      updatedAt: incoming.createdAt || new Date().toISOString()
    };

    // Save to metadata cache immediately
    this.saveChatMetadata(normalizedChannelId, {
      lastMessage: lastMsgObj,
      lastMessageTimeAgo: getTimeAgo(lastMsgObj.updatedAt)
    });

    /* =====================================================
       SINGLE CHAT UPDATE
    ===================================================== */

    if (chatType === 'single') {

      this.allSingleUsers = this.allSingleUsers.map(chat => {

        const chatId = String(chat.channelId).trim();

        if (chatId === normalizedChannelId) {

          updated = true;

          // Reset isCleared flag when a new message arrives
          this.unmarkChatAsCleared(normalizedChannelId);

          return {
            ...chat,
            unreadCount: incrementUnread
              ? (chat.unreadCount || 0) + 1
              : (chat.unreadCount || 0),
            lastMessage: {
              ...incoming,
              url: normalizedLastUrl,
              message: preview,
              createdAt: incoming.createdAt
            },
            lastMessageTimeAgo: getTimeAgo(incoming.createdAt),
            isCleared: false
          };
        }

        return chat;
      });

      this.filteredSingleUsers = [...this.allSingleUsers];
    }

    /* =====================================================
       GROUP CHAT UPDATE (SAMURAIS FIX HERE)
    ===================================================== */

    if (chatType === 'group') {

      this.allGroupUsers = this.allGroupUsers.map(chat => {

        const chatId = String(chat.channelId).trim();

        if (chatId === normalizedChannelId) {

          //  not to update if user has exited the group
          if (chat.isExited) return chat;

          updated = true;

          // Reset isCleared flag when a new message arrives
          this.unmarkChatAsCleared(normalizedChannelId);

          return {
            ...chat,
            unreadCount: incrementUnread
              ? (chat.unreadCount || 0) + 1
              : (chat.unreadCount || 0),
            lastMessage: {
              ...incoming,
              url: normalizedLastUrl,
              message: preview,
              createdAt: incoming.createdAt
            },
            lastMessageTimeAgo: getTimeAgo(incoming.createdAt),
            isCleared: false
          };
        }

        return chat;
      });

      this.filteredGroupUsers = [...this.allGroupUsers];
    }

    /* =====================================================
       FINAL SYNC
    ===================================================== */

    if (updated) {

      this.sortAllChatsByRecentMessages();

      this.cdRef.detectChanges();

      this.saveUnreadCountsToLocalStorage();

      if (this.chatCacheService) {
        const updatedChat = this.chatMap[normalizedChannelId];
        if (updatedChat) {
          this.chatCacheService.saveConversation(updatedChat);
        }
        this.chatCacheService.saveMessage(normalizedChannelId, incoming);
      }

    } else {

      console.log("⚠️ Sidebar not updated. Channel not found:", {
        socketChannelId: normalizedChannelId,
        availableGroupIds: this.allGroupUsers.map(g => g.channelId)
      });

    }
  }

  //GET ALL USERS
  isValidChannelId(channelId: any): boolean {
    if (channelId === null || channelId === undefined) return false;
    const id = String(channelId).trim();
    return id !== '' && id !== 'undefined' && id !== 'null';
  }

  purgeInvalidChannelCache() {
    ['chatMetadata', 'unreadCounts', 'clearedChats'].forEach((key) => {
      try {
        const cache = JSON.parse(localStorage.getItem(key) || '{}');
        if (!cache || typeof cache !== 'object') return;
        let changed = false;
        Object.keys(cache).forEach((id) => {
          if (!this.isValidChannelId(id)) {
            delete cache[id];
            changed = true;
          }
        });
        if (changed) localStorage.setItem(key, JSON.stringify(cache));
      } catch (e) { console.error(`Error purging ${key}`, e); }
    });
  }

  markChatAsCleared(channelId: string, lastMsgTime?: string | null) {
    if (!this.isValidChannelId(channelId)) return;
    try {
      const cleared = JSON.parse(localStorage.getItem('clearedChats') || '{}');
      cleared[channelId] = { cleared: true, lastMsgTime: lastMsgTime || new Date().toISOString() };
      localStorage.setItem('clearedChats', JSON.stringify(cleared));
      if (this.chatCacheService) {
        this.chatCacheService.saveMetadata('clearedChats', cleared);
      }
    } catch (e) { console.error('Error saving clearedChats', e); }
  }

  unmarkChatAsCleared(channelId: string) {
    if (!this.isValidChannelId(channelId)) return;
    try {
      const cleared = JSON.parse(localStorage.getItem('clearedChats') || '{}');
      if (cleared[channelId]) {
        delete cleared[channelId];
        localStorage.setItem('clearedChats', JSON.stringify(cleared));
        if (this.chatCacheService) {
          this.chatCacheService.saveMetadata('clearedChats', cleared);
        }
      }
    } catch (e) { console.error('Error removing clearedChats entry', e); }
  }

  isChatCleared(channelId: string): boolean {
    try {
      const cleared = JSON.parse(localStorage.getItem('clearedChats') || '{}');
      return !!(cleared[channelId]?.cleared);
    } catch (e) { return false; }
  }

  // Helper to persist chat metadata (last message, time)
  saveChatMetadata(channelId: string, metadata: any) {
    if (!this.isValidChannelId(channelId)) return;
    try {
      const cache = JSON.parse(localStorage.getItem('chatMetadata') || '{}');
      cache[channelId] = { ...cache[channelId], ...metadata };
      localStorage.setItem('chatMetadata', JSON.stringify(cache));
      if (this.chatCacheService) {
        this.chatCacheService.saveMetadata('chatMetadata', cache);
      }
    } catch (e) {
      console.error("Error saving chat metadata", e);
    }
  }

  getChatMetadata(channelId: string) {
    if (!this.isValidChannelId(channelId)) return null;
    try {
      const cache = JSON.parse(localStorage.getItem('chatMetadata') || '{}');
      return cache[channelId];
    } catch (e) {
      return null;
    }
  }

  isNewChatPlaceholder(msg: any): boolean {
    return !msg?.sender_id && !msg?.sid && String(msg?.message || '').trim().toLowerCase() === 'new chat created!';
  }

  // Centralized helper to update chat last message in memory and cache
  updateLocalChatLastMessage(channelId: string, lastMessage: any, isGroup: boolean) {
    if (!this.isValidChannelId(channelId) || this.isNewChatPlaceholder(lastMessage)) return;

    const preview = this.generatePreviewText(lastMessage);
    const msgDate = lastMessage.createdAt || lastMessage.updatedAt || new Date().toISOString();
    const senderId = lastMessage.sender_id || lastMessage.sid;
    const isSys = isSystemMessage(lastMessage);
    if (senderId && !isSys) {
      if (!lastMessage.sender_id) lastMessage.sender_id = senderId;
      if (this.userNameMapping[senderId]) {
        lastMessage.name = this.userNameMapping[senderId];
      }
    }

    const updatedMetadata = {
      lastMessage: {
        ...lastMessage,
        sender_id: senderId,
        message: preview,
        createdAt: msgDate,
        updatedAt: msgDate
      },
      lastMessageTimeAgo: getTimeAgo(msgDate)
    };

    // Save to LS
    this.saveChatMetadata(channelId, updatedMetadata);

    // A new message arrived — this chat is no longer considered "cleared"
    this.unmarkChatAsCleared(channelId);

    if (isGroup) {
      if (this.hasExitedGroup(channelId)) {
        console.log("Blocking sidebar update for exited group:", channelId);
        return;
      }
      const idx = this.filteredGroupUsers.findIndex(g => g.channelId === channelId);
      if (idx !== -1) {
        this.filteredGroupUsers[idx] = { ...this.filteredGroupUsers[idx], ...updatedMetadata };
        const allIdx = this.allGroupUsers.findIndex(g => g.channelId === channelId);
        if (allIdx !== -1) this.allGroupUsers[allIdx] = { ...this.allGroupUsers[allIdx], ...updatedMetadata };
      }
    } else {
      const idx = this.filteredSingleUsers.findIndex(u => u.channelId === channelId);
      if (idx !== -1) {
        this.filteredSingleUsers[idx] = { ...this.filteredSingleUsers[idx], ...updatedMetadata };
        const allIdx = this.allSingleUsers.findIndex(u => u.channelId === channelId);
        if (allIdx !== -1) this.allSingleUsers[allIdx] = { ...this.allSingleUsers[allIdx], ...updatedMetadata };
      }
    }

    this.sortAllChatsByRecentMessages();
  }

  async loadInitialCachedData() {
    try {
      const cached = await this.chatCacheService?.getConversations();
      if (cached && cached.length > 0 && (!this.sortedChats || this.sortedChats.length === 0)) {
        console.log('[ChatCache] Loaded cached conversations from IndexedDB:', cached.length);
        this.processUserList(cached, true);
        this.chatListLoading = false;
        this.cdRef.detectChanges();
      }
    } catch (e) {
      console.warn('[ChatCache] Failed to load initial cached conversations:', e);
    }
  }

  //GET ALL USERS
  getAllUsers() {
    this.OnlineUsersService.getUsers().subscribe((res) => {
      this.onlineUserList = res.data || res;
    });
    if (!this.sortedChats || this.sortedChats.length === 0) {
      this.chatListLoading = true;
      this.loadInitialCachedData();
    }
    this.chatService.getChats().subscribe({
      next: (resp: any) => {
        // The API returns pinned chats first, most recently pinned on top. Keep that order as pinRank,
        // since the list is re-sorted by time below and the IndexedDB cache does not keep order.
        const apiUsers = resp.data || [];
        const pinnedTotal = apiUsers.filter((u: any) => u.isPinned).length;
        let pinnedSeen = 0;
        const users = apiUsers.map((u: any) =>
          u.isPinned ? { ...u, pinRank: pinnedTotal - pinnedSeen++ } : u
        );
        console.log("All users data from API:", users);
        this.processUserList(users, false);
        if (this.chatCacheService) {
          this.chatCacheService.saveConversations(users);
        }
        this.chatListLoading = false;
        this.cdRef.detectChanges();
      },
      error: (err) => {
        console.error("Failed to fetch chats:", err);
        this.chatListLoading = false;
      },
      complete: () => {
        this.chatListLoading = false;
      },
    });
  }

  processUserList(users: any[], fromCache: boolean = false) {
    if (!users || !users.length) return;
    if (!fromCache) {
      this.purgeInvalidChannelCache();
    }
    // Get saved unread counts from localStorage
    const savedUnreadCounts = JSON.parse(localStorage.getItem('unreadCounts') || '{}');
    const cachedMetadata = JSON.parse(localStorage.getItem('chatMetadata') || '{}');

    // Fresh API data is the source of truth so reads on other devices are reflected;
    // the per-device saved counts are only used when rendering from cache.
    const resolveUnread = (channelId: string, serverCount: number): number => {
      if (channelId && (channelId === this.activeChannel || channelId === this.memberChannelId)) return 0;
      const saved = savedUnreadCounts[channelId];
      // If server provides a valid positive count, that takes precedence
      if (typeof serverCount === 'number' && serverCount > 0) {
        return serverCount;
      }
      // If local device has a saved unread count > 0, preserve it so API resync doesn't wipe real-time counts
      if (typeof saved === 'number' && saved > 0) {
        return saved;
      }
      return serverCount || 0;
    };

    // Reset all
    this.allSingleUsers = [];
    this.allGroupUsers = [];
    this.filteredSingleUsers = [];
    this.filteredGroupUsers = [];
    this.sortedChats = [];
    this.chatMap = {};
    this.profileImageMapping = {};
    this.userNameMapping = {};
    if (this.exitedGroups) this.exitedGroups.clear();
    else this.exitedGroups = new Set();

    users.filter((u: any) => u.type === 'group').forEach((group: any) => {
      if (group.members) {
        group.members.forEach((member: any) => {
          this.profileImageMapping[member.userId] = member.profileImage;
          this.userNameMapping[member.userId] = member.displayName || member.userName;
        });
      }
    });

    let totalUnreadCount = 0;

    // Handle single chats
    const clearedChats = JSON.parse(localStorage.getItem('clearedChats') || '{}');
    const isClearedEntry = (entry: any): boolean => entry === true || (entry && entry.cleared === true);
    const getClearedMsgTime = (entry: any): string | null =>
      entry === true ? null : (entry?.lastMsgTime || null);

    console.log('[getAllUsers] clearedChats keys:', Object.keys(clearedChats));

    this.allSingleUsers = users
      .filter((u) => u.type !== "group")
      .map((user) => {
        // If this chat was cleared by the user, suppress last message but preserve sort time
        const channelId = user.channelId || user.channel_id;
        const clearedEntry = clearedChats[channelId];
        if (isClearedEntry(clearedEntry)) {
          console.log('[getAllUsers] Suppressing lastMessage for cleared single chat:', channelId);
          const unreadCount = resolveUnread(channelId, user.unreadCount);
          if (unreadCount > 0) totalUnreadCount += unreadCount;
          return {
            ...user,
            unreadCount: unreadCount,
            lastMessage: null,
            lastMessageTimeAgo: "",
            isCleared: true,
            _clearedMsgTime: getClearedMsgTime(clearedEntry),
          };
        }

        // Use cache if API lastMessage is missing
        let lastMsg = user.lastMessage;
        let lastTimeAgo = user.lastMessage?.createdAt
          ? getTimeAgo(user.lastMessage.createdAt)
          : (user.lastMessage?.updatedAt ? getTimeAgo(user.lastMessage.updatedAt) : "");

        if ((!lastMsg || !lastMsg.message) && cachedMetadata[channelId]?.lastMessage && !this.isNewChatPlaceholder(cachedMetadata[channelId].lastMessage)) {
          lastMsg = cachedMetadata[channelId].lastMessage;
          lastTimeAgo = cachedMetadata[channelId].lastMessageTimeAgo || lastTimeAgo;
        }

        // Restore persisted seen flag from cache so blue tick survives a refresh
        const cachedSeen = cachedMetadata[channelId]?.lastMessage?.seen;
        if (cachedSeen && lastMsg && !lastMsg.seen) {
          lastMsg = { ...lastMsg, seen: true };
        }

        const preview = this.generatePreviewText(lastMsg, user.members);

        const unreadCount = resolveUnread(channelId, user.unreadCount);

        if (unreadCount > 0) totalUnreadCount += unreadCount;

        return {
          ...user,
          chatType: 'single',
          unreadCount: unreadCount,
          lastMessage: { ...(lastMsg || {}), message: preview },
          lastMessageTimeAgo: lastTimeAgo,
        };
      })
      .sort((a, b) => {
        const timeA = new Date(a.lastMessage?.updatedAt || a.createdAt || 0).getTime();
        const timeB = new Date(b.lastMessage?.updatedAt || b.createdAt || 0).getTime();
        return timeB - timeA;
      });

    // Handle group chats
    this.allGroupUsers = users
      .filter((u: any) => u.type === "group")
      .map((user: any) => {
        const channelId = user.channelId || user.channel_id;
        const isLeadAssignmentGroup = user.meta?.type === 'Lead Assignment Group' || user.type === 'Lead Assignment Group' || user.subType === 'Lead Assignment Group' || user.meta?.subType === 'Lead Assignment Group';
        const groupImg = isLeadAssignmentGroup
          ? (user.groupImg || user.groupImage || user.profileImage || user.profileImg || user.image || "assets/group_chat.png")
          : (user.groupImg || "assets/group_chat.png");

        this.profileImageMapping[channelId] = groupImg;
        this.userNameMapping[channelId] = user.groupName;

        // Check if user has exited
        let isExited = user.isExited;
        let exitedAt: string | null = null;
        let isRemoved = false;

        let me: any = null;
        if (user.members) {
          me = user.members.find((m: any) => m.userId === this.userId);
          if (me) {
            if (me.leftGroup || me.isRemoved) {
              isExited = true;
              exitedAt = me.exitedAt;
              isRemoved = me.isRemoved;
            }
          }
        }

        // If this group chat was cleared, suppress last message but preserve sort time
        const clearedGroupEntry = clearedChats[channelId];
        if (isClearedEntry(clearedGroupEntry)) {
          console.log('[getAllUsers] Suppressing lastMessage for cleared group chat:', channelId);
          let unreadCount = resolveUnread(channelId, user.unreadCount);
          if (isExited) unreadCount = 0;
          if (unreadCount > 0) totalUnreadCount += unreadCount;
          return {
            ...user,
            isExited,
            unreadCount,
            lastMessage: null,
            lastMessageTimeAgo: "",
            isCleared: true,
            _clearedMsgTime: getClearedMsgTime(clearedGroupEntry),
          };
        }

        // Use cache if API lastMessage is missing
        let lastMsg = user.lastMessage;
        let lastTimeAgo = user.lastMessage?.createdAt
          ? getTimeAgo(user.lastMessage.createdAt)
          : (user.lastMessage?.updatedAt ? getTimeAgo(user.lastMessage.updatedAt) : "");

        if ((!lastMsg || !lastMsg.message) && cachedMetadata[channelId]?.lastMessage && !this.isNewChatPlaceholder(cachedMetadata[channelId].lastMessage)) {
          lastMsg = cachedMetadata[channelId].lastMessage;
          lastTimeAgo = cachedMetadata[channelId].lastMessageTimeAgo || lastTimeAgo;
        }

        // For group chats, only set seen = true if every active member except sender has seen it
        if (lastMsg && lastMsg.sender_id === this.userId) {
          const activeMembers = (user.members || []).filter((m: any) => !m.isRemoved && !m.leftGroup);
          const activeOtherMembers = activeMembers.filter(
            (m: any) => String(m.userId || m._id).trim() !== String(this.userId).trim()
          );
          const activeOtherMemberIds: Set<string> = new Set<string>(
            activeOtherMembers.map((m: any) => String(m.userId || m._id).trim())
          );

          const cachedLastMsg = cachedMetadata[channelId]?.lastMessage;
          const seenBy = Array.isArray(lastMsg.seenBy)
            ? lastMsg.seenBy
            : (Array.isArray(cachedLastMsg?.seenBy) ? cachedLastMsg.seenBy : []);
          const validSeenBy: string[] = Array.from(new Set(seenBy.map(String).filter((id: string) => activeOtherMemberIds.has(id))));

          const isEveryoneSeen = activeOtherMemberIds.size > 0 && Array.from(activeOtherMemberIds).every((id: string) => validSeenBy.includes(id));
          lastMsg = { ...lastMsg, seenBy: validSeenBy, seen: isEveryoneSeen };
        }

        // If exited, ensure we don't show messages newer than exit time
        if (isExited) {
          const exitTime = exitedAt ? new Date(exitedAt).getTime() : 0;
          const lastMsgTime = lastMsg?.updatedAt ? new Date(lastMsg.updatedAt).getTime() : 0;
          if (lastMsgTime >= exitTime || !lastMsg) {
            if (isRemoved) {
              const removerId = lastMsg?.sender_id || me?.removedBy || me?.adminId;
              const removerName = me?.removedByName || me?.adminName || (lastMsg?.sender_id && lastMsg?.sender_id !== this.userId ? this.userNameMapping[lastMsg.sender_id] : null);
              const messageText = removerName ? `${removerName} removed You` : "You were removed";
              lastMsg = {
                ...lastMsg,
                message: messageText,
                updatedAt: exitedAt || lastMsg?.updatedAt || new Date().toISOString(),
                sender_id: (removerId && removerId !== this.userId) ? removerId : 'system',
                meta: {
                  ...(lastMsg?.meta || {}),
                  type: 'system',
                  subType: 'Group Member Removed',
                  removedMember: 'You',
                  removedBy: removerName
                }
              };
            } else {
              const messageText = "You left";
              lastMsg = {
                ...lastMsg,
                message: messageText,
                updatedAt: exitedAt || lastMsg?.updatedAt || new Date().toISOString(),
                sender_id: this.userId,
                meta: {
                  ...(lastMsg?.meta || {}),
                  type: 'system',
                  subType: 'Member Left'
                }
              };
            }
            lastTimeAgo = exitedAt ? getTimeAgo(exitedAt) : lastTimeAgo;
          }
        }

        const preview = this.generatePreviewText(lastMsg, user.members);
        let unreadCount = resolveUnread(channelId, user.unreadCount);
        // Force unread count to 0 if exited
        if (isExited) {
          unreadCount = 0;
        }

        if (unreadCount > 0) totalUnreadCount += unreadCount;

        return {
          ...user,
          chatType: 'group',
          groupImg: groupImg,
          isExited: isExited,
          unreadCount: unreadCount,
          lastMessage: {
            ...(lastMsg || {}),
            message: preview,
            name: (lastMsg && (lastMsg.sender_id || lastMsg.sid)) ? (this.userNameMapping[lastMsg.sender_id || lastMsg.sid] || lastMsg.name) : (lastMsg?.name || "")
          },
          lastMessageTimeAgo: lastTimeAgo,
        };
      })
      .sort((a, b) => {
        const timeA = new Date(a.lastMessage?.updatedAt || a.createdAt || 0).getTime();
        const timeB = new Date(b.lastMessage?.updatedAt || b.createdAt || 0).getTime();
        return timeB - timeA;
      });

    this.applyOutboxToChatPreviews();

    // Merge and map
    this.filteredSingleUsers = [...this.allSingleUsers];
    this.filteredGroupUsers = [...this.allGroupUsers];


    this.filteredGroupUsers.forEach(group => {
      if (group.isExited) {
        this.exitedGroups.add(group.channelId || group.channel_id);
      }
    });

    // Sync exited groups to ChatService
    this.chatService.setExitedGroups(Array.from(this.exitedGroups));

    // Build chatMap from ALL users/groups, not just filtered ones
    [...this.allSingleUsers, ...this.allGroupUsers].forEach((chat) => {
      const channelId = chat.channelId || chat.channel_id;
      if (channelId) {
        this.chatMap[channelId] = chat;
      }
      if (chat.userId) {
        this.chatMap[chat.userId] = chat;
      }
      if (chat._id) {
        this.chatMap[chat._id] = chat;
      }
    });

    this.sortedChats = [
      ...this.filteredSingleUsers,
      ...this.filteredGroupUsers,
    ];

    console.log("Built chatMap with keys:", Object.keys(this.chatMap));
    this.sortAllChatsByRecentMessages();
    this.chatListLoading = false;
    this.store.dispatch(setUnreadCount({ unreadCount: totalUnreadCount }));

    // Save unread counts to localStorage for persistence
    this.saveUnreadCountsToLocalStorage();
    if (!fromCache) {
      // Dismiss this device's notifications for chats already read (possibly on another device)
      closeChatNotifications(
        [...this.allSingleUsers, ...this.allGroupUsers]
          .filter((c) => !c.unreadCount)
          .map((c) => c.channelId || c.channel_id)
      );
      this.syncMissingGroupLastMessages();
      // Pre-connect to recent channels for background typing indicators
      setTimeout(() => this.preConnectChannelsForTyping(), 1500);
    }

    // Trigger route navigation in case a deep link or notification was waiting for chat list to load
    this.scheduleRouteNavigation();
  }

  // Save unread counts to localStorage for persistence
  saveUnreadCountsToLocalStorage() {
    const unreadCounts: { [key: string]: number } = {};

    this.allSingleUsers.forEach((user) => {
      if (user.unreadCount > 0) {
        const channelId = user.channelId || user.channel_id;
        if (channelId) unreadCounts[channelId] = user.unreadCount;
      }
    });

    this.allGroupUsers.forEach((group) => {
      if (group.unreadCount > 0) {
        unreadCounts[group.channelId] = group.unreadCount;
      }
    });

    localStorage.setItem('unreadCounts', JSON.stringify(unreadCounts));
    if (this.chatCacheService) {
      this.chatCacheService.saveMetadata('unreadCounts', unreadCounts);
    }
    console.log('Saved unread counts to localStorage and IndexedDB:', unreadCounts);
  }

  //format Time


  //Initiate socket connection
  processedMessages: Set<string> = new Set();
  isMessageListenerAttached = false;
  private processedMessageIds = new Set<string>();
  async initiatChat(): Promise<void> {
    if (this.converseInitPromise) {
      return this.converseInitPromise;
    }

    this.converseInitPromise = (async () => {
      try {
        this.converse = new Converse();

        const fetchConverseToken = async (): Promise<string | null> => {
          const res = await fetch(environment.host + "chats/create-token", {
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("guestToken")}`,
            },
            method: "POST",
            body: JSON.stringify({
              projectId: environment.converseProjectId,
            }),
          });

          const jsonResponse = await res.json();
          const token = jsonResponse?.data?.token || jsonResponse?.token;
          if (token) {
            localStorage.setItem("converseToken", token);
          }
          return token || null;
        };

        const cachedToken = localStorage.getItem("converseToken");
        let converseToken = cachedToken || await fetchConverseToken();
        if (!converseToken) throw new Error("Failed to get converse token");

        let initResult = await this.converse.init({
          projectId: environment.converseProjectId,
          converseToken,
        }).catch((err: any) => {
          console.warn("Converse init failed:", err?.message || err);
          return null;
        });

        // A cached token may be expired/invalid — drop it and retry once with a fresh one
        if (!initResult && cachedToken) {
          localStorage.removeItem("converseToken");
          const freshToken = await fetchConverseToken();
          if (freshToken) {
            this.converse = new Converse();
            initResult = await this.converse.init({
              projectId: environment.converseProjectId,
              converseToken: freshToken,
            });
          }
        }

        if (!initResult) throw new Error("Init failed");
        this.watchConverseAuth((this.converse as any)._socketConnection);

        await new Promise<void>((resolve) => {
          const connTimeout = setTimeout(() => {
            console.warn("Converse listenConnectionCallback timed out after 8s, continuing with fallback");
            resolve();
          }, 8000);

          this.converse!.listenConnectionCallback(async () => {
            clearTimeout(connTimeout);
            if (this.converse) {
              try {
                this.projectInstance = await this.converse.connectProject();

                if (!this.isMessageListenerAttached) {
                  this.isMessageListenerAttached = true;

                  this.projectInstance.listenNewMessage((msg: any) => {
                    this.zone.run(() => {
                      this.processIncomingProjectMessage(msg);
                    });
                  });

                  // Only attach listeners if the methods exist
                  if (typeof (this.projectInstance as any).listenEditMessage === 'function') {
                    (this.projectInstance as any).listenEditMessage((data: any) => {
                      this.zone.run(() => {
                        this.handleMessageEdit(data);
                      });
                    });
                  }

                  if (typeof (this.projectInstance as any).listenMessageReaction === 'function') {
                    (this.projectInstance as any).listenMessageReaction((data: any) => {
                      this.zone.run(() => {
                        this.handleReactionUpdate(data);
                      });
                    });
                  }

                  if (typeof (this.projectInstance as any).listenMessageDelete === 'function') {
                    (this.projectInstance as any).listenMessageDelete((data: any) => {
                      this.zone.run(() => {
                        this.handleMessageDelete(data);
                      });
                    });
                  }

                  // Global project-level listener for message-seen to sync unread counts across devices
                  const projectSocket = (this.projectInstance as any).socket;
                  this.watchConverseAuth(projectSocket);
                  if (projectSocket && typeof projectSocket.on === 'function') {
                    projectSocket.on('message-seen', (data: any) => {
                      this.zone.run(() => {
                        console.log('Project-level message-seen received:', data);
                        this.handleMessageSeen(data);
                      });
                    });
                    // Opening a chat emits message-seen-all, so listen for it too
                    projectSocket.on('message-seen-all', (data: any) => {
                      this.zone.run(() => this.handleMessageSeenAll(data));
                    });

                    this.hasProjectConnectedOnce = projectSocket.connected;
                    projectSocket.on('connect', () => {
                      if (this.hasProjectConnectedOnce) this.scheduleResync();
                      this.hasProjectConnectedOnce = true;
                    });
                  }

                  console.log("✅ Socket listener attached (NewMessage, EditMessage, Reaction, Delete, MessageSeen)");
                }

                // Auto-recover active chat if one is currently selected and needs loading
                this.zone.run(() => {
                  if (this.singleChatBox && this.singleUser && (!this.singleChatMessages || this.singleChatMessages.length === 0)) {
                    console.log("Auto-recovering single chat upon Converse connection:", this.singleUser.channelId);
                    this.handleSingleChatConnection(this.singleUser);
                  } else if (this.groupChatBox && this.memberChannelId && (!this.groupChatMessages || this.groupChatMessages.length === 0)) {
                    console.log("Auto-recovering group chat upon Converse connection:", this.memberChannelId);
                    this.handleGroupChatConnection(this.memberChannelId);
                  }
                });

                resolve();
              } catch (err) {
                console.error("Failed to connect project:", err);
                resolve();
              }
            }
          });
        });

        console.log({ projectInstance: this.projectInstance });
        console.log("Converse connected!");
      } catch (err) {
        console.error("Converse setup failed:", err);
        this.converseInitPromise = null;
      }
    })();

    setInterval(() => this.processedMessages.clear(), 5 * 60 * 1000);
    // Clear processed groupIds for unread every 5 minutes to prevent memory issues
    setInterval(() => this.processedGroupIdsForUnread.clear(), 5 * 60 * 1000);
    // Clear received message IDs every 5 minutes to prevent memory leak
    setInterval(() => this.receivedMessageIds.clear(), 5 * 60 * 1000);

    return this.converseInitPromise;
  }

  async waitForConverseReady(timeoutMs: number = 7000): Promise<boolean> {
    if (this.converse && this.converse.checkConnection?.()) {
      return true;
    }
    if (!this.converseInitPromise) {
      this.initiatChat();
    }
    const startTime = Date.now();
    while (Date.now() - startTime < timeoutMs) {
      if (this.converse && this.converse.checkConnection?.()) {
        return true;
      }
      await new Promise((resolve) => setTimeout(resolve, 80));
    }
    return !!(this.converse && this.converse.checkConnection?.());
  }

  private resyncTimer: any = null;
  private hasProjectConnectedOnce = false;
  private isRefreshingConverseToken = false;
  private converseTokenRefreshedAt = 0;

  // converse.init() never fails on a bad token; the server rejects it later as a socket "Not Authorized" error.
  // The token is cached in localStorage, so without this the chat stays on "Connecting…" until a re-login.
  private watchConverseAuth(socket: any) {
    if (!socket || typeof socket.on !== 'function' || socket.hasAuthWatcher) return;
    socket.hasAuthWatcher = true;
    socket.on('connect_error', (err: any) => {
      if (/not authori[sz]ed/i.test(err?.message || String(err || ''))) {
        this.zone.run(() => this.refreshConverseSession());
      }
    });
  }

  private async refreshConverseSession() {
    // Throttled so a rejection that a new token doesn't fix can't loop
    if (this.isRefreshingConverseToken || Date.now() - this.converseTokenRefreshedAt < 30000) return;
    this.isRefreshingConverseToken = true;
    this.converseTokenRefreshedAt = Date.now();
    console.warn("Converse rejected the cached token, reconnecting with a fresh one");

    try {
      localStorage.removeItem("converseToken");
      try { this.converse?.shutdown(); } catch (_) { }
      this.converse = null;
      this.projectInstance = null;
      this.converseInitPromise = null;
      this.isMessageListenerAttached = false;
      this.hasProjectConnectedOnce = false;
      // Channels keep the token they were created with, so cached ones would reconnect with the rejected one
      this.cachedSingleChannels = {};
      this.cachedGroupChannels = {};
      this.backgroundTypingChannels.clear();

      await this.initiatChat();
      // Reopens the active chat's channel on the new connection and flushes the outbox
      this.scheduleResync();
    } finally {
      this.isRefreshingConverseToken = false;
    }
  }

  private onVisibilityChange = () => {
    if (document.visibilityState === 'visible') this.scheduleResync();
  };

  private onOnline = () => {
    this.cdRef.detectChanges();
    this.scheduleResync();
  };

  // Sockets keep reporting connected for ~20s after the network drops, and anything emitted into them is lost.
  // Close them now so the status is accurate and resyncAfterReconnect / the outbox open fresh ones when back online.
  private onOffline = () => {
    const closeSocket = (channel: any) => {
      try { channel?.socket?.disconnect(); } catch (_) { }
    };
    closeSocket(this.singleChannel);
    closeSocket(this.groupChannel);
    Object.values(this.cachedSingleChannels).forEach(closeSocket);
    Object.values(this.cachedGroupChannels).forEach(closeSocket);
    this.backgroundTypingChannels.forEach(closeSocket);
    // Drop the dead channels so the next open/flush reconnects instead of reusing them
    this.cachedSingleChannels = {};
    this.cachedGroupChannels = {};
    this.backgroundTypingChannels.clear();
    this.cdRef.detectChanges();
  };

  // Header status while the open chat can't send (WhatsApp-style); empty when connected
  get chatConnectionStatus(): string {
    if (!navigator.onLine) return "Waiting for network…";
    if (this.singleChatBox && this.isPendingSingleChat) return "";
    const channel = this.singleChatBox ? this.singleChannel : this.groupChannel;
    if (!channel || (channel.socket && !channel.socket.connected)) return "Connecting…";
    return "";
  }

  private onFocus = () => this.scheduleResync();

  scheduleResync() {
    clearTimeout(this.resyncTimer);
    this.resyncTimer = setTimeout(() => this.zone.run(() => this.resyncAfterReconnect()), 500);
  }

  private collectKnownMessageIds(messages: any[]): Set<string> {
    const known = new Set<string>();
    (messages || []).forEach((m: any) => {
      const id = m?._id || m?.id || m?.messageId;
      if (id) known.add(id);
      (m?._allIds || []).forEach((mid: string) => known.add(mid));
    });
    return known;
  }

  private clearInactiveMessageCaches() {
    for (const id of Object.keys(this.cachedSingleMessages)) {
      if (id === this.activeChannel) continue;
      delete this.cachedSingleMessages[id];
      delete this.cachedSingleHasMore[id];
      delete this.cachedSingleOldestCreatedAt[id];
      delete this.cachedSingleScrollReady[id];
    }
    for (const id of Object.keys(this.cachedGroupMessages)) {
      if (id === this.memberChannelId) continue;
      delete this.cachedGroupMessages[id];
      delete this.cachedGroupHasMore[id];
      delete this.cachedGroupOldestCreatedAt[id];
      delete this.cachedGroupScrollReady[id];
    }
  }

  async resyncAfterReconnect() {
    if (!this.converse) return;

    this.clearInactiveMessageCaches();

    try {
      if (this.singleChatBox && this.singleChannel && this.activeChannel) {
        if (!this.singleChannel.socket?.connected) {
          try {
            if (this.singleChannel.socket) {
              try { this.singleChannel.socket.disconnect(); } catch (_) { }
            }
            this.singleChannel = await this.converse.connectChannel({
              channelId: this.activeChannel,
              batch: 100,
            });
            this.singleChannel.hasCachedListeners = false;
            this.attachSingleChannelListeners(this.singleChannel, this.activeChannel);
            this.cachedSingleChannels[this.activeChannel] = this.singleChannel;
          } catch (e) {
            console.warn('[Resync] Failed to reconnect single channel socket:', e);
          }
        }
        await this.getAllMessages(this.singleChannel, false, this.activeChannel);
      } else if (this.groupChatBox && this.groupChannel && this.memberChannelId) {
        if (!this.groupChannel.socket?.connected) {
          try {
            if (this.groupChannel.socket) {
              try { this.groupChannel.socket.disconnect(); } catch (_) { }
            }
            this.groupChannel = await this.converse.connectChannel({
              channelId: this.memberChannelId,
              batch: 100,
            });
            this.groupChannel.hasCachedListeners = false;
            this.attachGroupChannelListeners(this.groupChannel, this.memberChannelId);
            this.cachedGroupChannels[this.memberChannelId] = this.groupChannel;
          } catch (e) {
            console.warn('[Resync] Failed to reconnect group channel socket:', e);
          }
        }
        await this.getAllGroupMessages(this.groupChannel, false, this.memberChannelId);
      }
    } catch (err) {
      console.error('Resync of active chat failed:', err);
    }

    this.flushOutbox();
    this.getAllUsers();
  }

  async syncMissingGroupLastMessages() {
    let retries = 0;
    while ((!this.converse || !this.projectInstance) && retries < 15) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      retries++;
    }

    if (!this.converse || !this.projectInstance) return;

    // Only target group chats that don't have a recent message and aren't exited
    const missingGroupChats = this.allGroupUsers.filter(g => !g.lastMessage?.message && !g.isExited && g.channelId);
    for (const group of missingGroupChats) {
      try {
        const channel = await this.converse.connectChannel({ channelId: group.channelId });
        const requestParams: any = { limit: 1 };
        const response = await channel.getMessages(requestParams);
        const messages = [...(response.msgs?.read || []), ...(response.msgs?.unread || [])];
        if (messages.length > 0) {
          messages.sort((a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
          const lastMsg = messages[messages.length - 1];
          this.zone.run(() => {
            this.updateLocalChatLastMessage(group.channelId, lastMsg, true);
          });
        }
      } catch (err) {
        console.error("Failed to sync message for group", group.channelId, err);
      }
    }
  }

  // Add this method to mark messages as read
  async markChatAsRead(channelId: string, isGroup: boolean = false) {
    if (isGroup) {
      this.allGroupUsers = this.allGroupUsers.map((chat) => {
        if (chat.channelId === channelId) {
          return { ...chat, unreadCount: 0 };
        }
        return chat;
      });
      this.filteredGroupUsers = this.filteredGroupUsers.map((chat) => {
        if (chat.channelId === channelId) {
          return { ...chat, unreadCount: 0 };
        }
        return chat;
      });
    } else {
      this.allSingleUsers = this.allSingleUsers.map((chat) => {
        if (chat.channelId === channelId) {
          return { ...chat, unreadCount: 0 };
        }
        return chat;
      });
      this.filteredSingleUsers = this.filteredSingleUsers.map((chat) => {
        if (chat.channelId === channelId) {
          return { ...chat, unreadCount: 0 };
        }
        return chat;
      });
    }

    this.sortAllChatsByRecentMessages();

    // Update Redux store immediately
    const totalUnreadCount = this.calculateTotalUnreadCount();
    this.store.dispatch(setUnreadCount({ unreadCount: totalUnreadCount }));
    this.saveUnreadCountsToLocalStorage();
    closeChatNotifications([channelId]);
  }


  //To calcute the unread count of Chats
  calculateTotalUnreadCount(): number {
    const single = this.filteredSingleUsers.reduce(
      (sum, c) => sum + (c.unreadCount || 0),
      0
    );
    const group = this.filteredGroupUsers.reduce(
      (sum, c) => sum + (c.unreadCount || 0),
      0
    );
    return single + group;
  }

  // Check if a single chat is selected
  isSingleChatSelected(userId: string): boolean {
    return this.selectedChatUserId === userId && this.selectedChatGroupId === "";
  }

  // Check if a group chat is selected
  isGroupChatSelected(channelId: string): boolean {
    return this.selectedChatGroupId === channelId && this.selectedChatUserId === "";
  }

  //SEARCH USERS
  searchTerm: string = "";
  chatMatches: any[] = [];
  searchUsers() {
    const term = this.searchTerm.trim().toLowerCase();

    if (!term) {
      this.chatMatches = [];
      this.filteredSingleUsers = [...this.allSingleUsers];
      this.filteredGroupUsers = [...this.allGroupUsers];
      this.sortedChats = [
        ...this.filteredSingleUsers,
        ...this.filteredGroupUsers,
      ];
    } else {
      this.filteredSingleUsers = this.allSingleUsers.filter((user) =>
        user.userName?.toLowerCase().includes(term)
      );

      this.filteredGroupUsers = this.allGroupUsers.filter((group) =>
        group.groupName?.toLowerCase().includes(term)
      );

      this.chatMatches = [
        ...this.filteredSingleUsers,
        ...this.filteredGroupUsers,
      ];
    }

    this.sortAllChatsByRecentMessages();
  }

  channelData: any[] = [];
  singleUserProfileImg: any;
  singleChatUsername: any;
  newReceiver: any;

  // START SINGLE CHAT
  singleChatBox: boolean = false;
  singleUser: any;
  chatUserId: any;
  chatLoader: boolean = false;
  isInitialLoadingChat: boolean = false;

  // Close Chat Window (Mobile/General)
  closeChatWindow(fromPopState: boolean = false) {
    this.saveCurrentChatDraft();
    this.updateCacheForActiveChannel();
    this.cancelReply();
    this.cancelEdit();
    this.showPinnedMessages = false;
    this.showChatWindow = false;
    this.isStarredMessagesPanelOpen = false;
    this.isQuickSearchOpen = false;
    this.targetSearchMessageId = null;
    this.quickSearchComp?.resetSearch();
    this.isGroupInfoOpen = false;
    this.singleChatBox = false;
    this.groupChatBox = false;
    this.chatLoader = false;
    this.isInitialLoadingChat = false;
    this.chatLoadingError = null;
    this.newMessage = "";
    this.newGroupMessage = "";
    this.memberChannelId = "";
    this.activeChannel = "";
    this.selectedChatGroupId = "";
    this.selectedChatUserId = "";
    this.chatUserId = null;
    this.chatService.isChatOpenMobile$.next(false);

    if (this.isMobileView && this.backTrail) {
      this.openedFromChatList = false;
      this.isChatOpenInHistory = false;
      this.armBackTrail('list');
    } else if (this.isMobileView) {
      this.openedFromChatList = false;
      if (!fromPopState && this.isChatOpenInHistory) {
        this.isChatOpenInHistory = false;
        this.location.back();
        return;
      }
      this.isChatOpenInHistory = false;
    }
    this.location.replaceState('/main-chat');
  }

  async startSingleChat(userId: string, scrollToMessageId?: any) {
    if (!userId) return;
    const chatKey = `single_${userId}`;
    if (this.activeChatLoadingKey === chatKey && this.activeChatLoadingPromise) {
      console.log("startSingleChat already loading for:", userId);
      return this.activeChatLoadingPromise;
    }
    this.activeChatLoadingKey = chatKey;
    const loadPromise = this._doStartSingleChat(userId, scrollToMessageId);
    this.activeChatLoadingPromise = loadPromise;
    try {
      await loadPromise;
    } finally {
      if (this.activeChatLoadingKey === chatKey) {
        this.activeChatLoadingPromise = null;
      }
    }
  }

  private async _doStartSingleChat(userId: string, scrollToMessageId?: any) {
    this.saveCurrentChatDraft();
    console.log("startSingleChat called with:", userId, scrollToMessageId);
    console.log('========== SINGLE CHAT ==========');
    console.log('User ID:', userId);
    console.log('=================================');
    this.updateCacheForActiveChannel();
    this.chatLoadingError = null;
    this.chatLoader = true;
    this.isInitialLoadingChat = true;
    this.cancelReply();
    this.cancelEdit();
    this.showClickForInfo = true;
    this.showMentionAllModal = false;
    this.isNotificationSettingsOpen = false;
    this.isGroupInfoOpen = false;
    this.selectedChatUserId = userId;
    this.selectedChatGroupId = "";
    this.memberChannelId = "";

    if (this.chatUserId !== userId) {
      if (!scrollToMessageId) {
        this.targetSearchMessageId = null;
      }
      this.quickSearchComp?.resetSearch();
    }

    // Immediate fallback assignment so header is NEVER blank upon click/navigation
    const fallbackChat = this.chatMap?.[userId] || (this.allSingleUsers || []).find((u: any) => u.userId === userId);
    const fallbackName = fallbackChat?.displayName || fallbackChat?.userName || fallbackChat?.name || this.userNameMapping?.[userId];
    const fallbackImg = fallbackChat?.profileImage || fallbackChat?.profileImg || this.profileImageMapping?.[userId];
    if (fallbackName) this.singleChatUsername = fallbackName;
    if (fallbackImg) this.singleUserProfileImg = fallbackImg;

    if (
      this.chatUserId === userId &&
      this.singleChatBox &&
      this.showChatWindow &&
      this.scrollReadySingle
    ) {
      if (this.singleChannel) {
        this.getAllMessages(this.singleChannel, false, this.activeChannel).catch(() => { });
      }
      setTimeout(() => {
        if (scrollToMessageId) {
          this.scrollToSearchedMessage(scrollToMessageId, "single");
        } else {
          this.needsScrollToBottomSingle = true;
          this.scrollToBottom();
        }
      }, 100);
      return;
    }
    if (this.chatUserId !== userId || !this.singleChatBox) {
      const selectedUser = this.filteredSingleUsers.find(u => u.userId === userId);
      const cid = selectedUser?.channelId || (this.chatMap[userId]?.channelId) || "";

      this.singleChatMessages = this.injectPendingUploads(cid, []);
      this.AllSingleChatMsg = { read: [], unread: [] };
      this._enrichingUrls.clear(); // reset for new chat context
      this.newMessage = this.chatDraftService.getDraft('single', userId, cid);
      this.groupChannel = null;
      this.chatLoader = true;
      this.isInitialLoadingChat = true;
      this.chatLoadingError = null;
      this.scrollReady = false;
      this.scrollReadySingle = false;
      this.hasMoreGroupMessages = true;
      this.oldestMessageCreatedAt = null;
      this.isLoadingGroupMessages = false;
      this.hasMoreSingleMessages = true;
      this.oldestSingleMessageCreatedAt = null;
      this.isLoadingSingleMessages = false;
      this.needsScrollToBottomSingle = false;
    }

    let selectedUserIndex = this.filteredSingleUsers.findIndex(
      (user) => user.userId === userId
    );
    if (selectedUserIndex === -1) {
      const foundInAll = (this.allSingleUsers || []).find((u: any) => u.userId === userId);
      if (foundInAll) {
        this.filteredSingleUsers.unshift(foundInAll);
        selectedUserIndex = 0;
      } else {
        const member = (this.allGroupMembers || []).find((m: any) => m.userId === userId);
        if (member) {
          const newSingleUser = {
            userId: member.userId,
            displayName: member.displayName || member.userName || member.name,
            userName: member.userName || member.name,
            profileImage: member.profileImage || member.profileImg || 'assets/user-icon.png',
            channelId: member.channelId || '',
            emailId: member.emailId || member.email || member.mail || '',
            email: member.email || member.emailId || member.mail || '',
            phone: member.phone || member.mobile || '',
            about: member.about || member.bio || ''
          };
          this.filteredSingleUsers.unshift(newSingleUser);
          if (this.allSingleUsers && !this.allSingleUsers.some((u: any) => u.userId === userId)) {
            this.allSingleUsers.unshift(newSingleUser);
          }
          selectedUserIndex = 0;
        } else {
          const chatMapUser = this.chatMap?.[userId];
          const knownName = this.userNameMapping?.[userId] || chatMapUser?.displayName || chatMapUser?.userName || chatMapUser?.name;
          const newSingleUser = {
            userId: userId,
            displayName: knownName || 'User',
            userName: knownName || 'User',
            profileImage: this.profileImageMapping?.[userId] || chatMapUser?.profileImage || chatMapUser?.profileImg || 'assets/user-icon.png',
            channelId: chatMapUser?.channelId || '',
            emailId: chatMapUser?.emailId || chatMapUser?.email || chatMapUser?.mail || '',
            email: chatMapUser?.email || chatMapUser?.emailId || chatMapUser?.mail || '',
            phone: chatMapUser?.phone || chatMapUser?.mobile || '',
            about: chatMapUser?.about || chatMapUser?.bio || ''
          };
          this.filteredSingleUsers.unshift(newSingleUser);
          if (this.allSingleUsers && !this.allSingleUsers.some((u: any) => u.userId === userId)) {
            this.allSingleUsers.unshift(newSingleUser);
          }
          selectedUserIndex = 0;
        }
      }
    }
    if (selectedUserIndex === -1) return;

    let selectedUser = this.filteredSingleUsers[selectedUserIndex];
    const resolvedEmail = selectedUser?.emailId || selectedUser?.email || selectedUser?.mail ||
      this.chatMap?.[userId]?.emailId || this.chatMap?.[userId]?.email ||
      (this.allSingleUsers || []).find((u: any) => u.userId === userId)?.emailId ||
      (this.allSingleUsers || []).find((u: any) => u.userId === userId)?.email ||
      (this.onlineUserList || []).find((u: any) => u.userId === userId)?.emailId ||
      (this.onlineUserList || []).find((u: any) => u.userId === userId)?.email ||
      (this.allGroupMembers || []).find((m: any) => m.userId === userId)?.emailId ||
      (this.allGroupMembers || []).find((m: any) => m.userId === userId)?.email;
    if (resolvedEmail) {
      selectedUser.emailId = selectedUser.emailId || resolvedEmail;
      selectedUser.email = selectedUser.email || resolvedEmail;
    }
    this.chatUserId = selectedUser.userId;
    this.activeChannel = selectedUser.channelId || "";

    // Set header and user immediately
    this.singleChatUsername = selectedUser.displayName || selectedUser.userName || selectedUser.name || this.userNameMapping?.[userId] || "User";
    this.singleUserProfileImg = selectedUser.profileImage || selectedUser.profileImg || this.profileImageMapping?.[userId] || "assets/user-icon.png";
    this.singleUser = selectedUser;

    const liveStatus = this.onlineUserList.find((u) => u.userId === userId);
    if (liveStatus) {
      this.updateChatUserStatus(liveStatus);
    } else {
      this.isChatUserOnline = false;
      this.chatUserStatus = "Offline";
    }

    if (this.statusSubscription) {
      this.statusSubscription.unsubscribe();
    }

    this.statusSubscription = this.chatService.onlineUserList$
      .pipe(
        filter((list) => !!list && list.length > 0),
        map((list) => list.find((u) => u.userId === userId)),
        distinctUntilChanged((prev, curr) => prev?.isActive === curr?.isActive)
      )
      .subscribe((updatedUser) => {
        if (updatedUser) this.updateChatUserStatus(updatedUser);
      });

    if (this.showSeenInfoModal) {
      this.closeSeenInfoModal();
    }
    this.singleChatBox = true;
    this.groupChatBox = false;
    if (this.isMobileView) {
      this.showChatWindow = true;
      this.chatService.isChatOpenMobile$.next(true);
    }

    let updatedUser = { ...selectedUser };

    try {
      if (!updatedUser.channelId) {
        // Prepare pending state without creating channel yet
        this.isPendingSingleChat = true;
        this.singleChatUsername = updatedUser.displayName || updatedUser.userName || updatedUser.name || "User";
        this.singleUserProfileImg = updatedUser.profileImage || updatedUser.profileImg || "assets/user-icon.png";
        this.newReceiver = updatedUser.userId;
        this.singleUser = updatedUser;
        this.singleChannel = null;
        this.activeChannel = "";
        this.newMessage = this.chatDraftService.getDraft('single', userId, updatedUser.channelId);

        this.singleChatMessages = [];
        this.AllSingleChatMsg = { read: [], unread: [] };

        this.chatLoader = false;
        this.isInitialLoadingChat = false;
        this.scrollReadySingle = true;
        this.chatLoadingError = null;
        this.showVideoOverlay = true;
        if (this.isMobileView && !this.isChatOpenInHistory) {
          this.location.go('/main-chat?chatType=single&userId=' + userId);
          this.isChatOpenInHistory = true;
        }

        setTimeout(() => {
          this.focusMessageInput();
          this.singleMessageInput?.adjustTextareaHeight();
        }, 100);
        this.createPendingSingleChat().then((success) => {
          if (success) console.log("Automatically created pending channel on chat open");
        });

        return;
      }

      // Race condition check before proceeding
      if (this.selectedChatUserId !== userId) return;

      this.isPendingSingleChat = false;

      this.singleUser = updatedUser;
      this.singleChatUsername = updatedUser.displayName || updatedUser.userName || updatedUser.name || this.userNameMapping?.[userId] || "User";
      this.singleUserProfileImg = updatedUser.profileImage || updatedUser.profileImg || this.profileImageMapping?.[userId] || "assets/user-icon.png";
      this.updateChatRoute(updatedUser.channelId);
      this.filteredSingleUsers = this.filteredSingleUsers.map((user) => {
        if (user.userId === userId) {
          return { ...user, unreadCount: 0 };
        }
        return user;
      });

      this.allSingleUsers = this.allSingleUsers.map((user) => {
        if (user.userId === userId) {
          return { ...user, unreadCount: 0 };
        }
        return user;
      });

      this.sortAllChatsByRecentMessages();

      const totalUnreadCount = this.calculateTotalUnreadCount();
      this.store.dispatch(setUnreadCount({ unreadCount: totalUnreadCount }));
      this.saveUnreadCountsToLocalStorage();

      await this.handleSingleChatConnection(updatedUser);
    } catch (error) {
      console.error("Error starting single chat:", error);
      if (this.selectedChatUserId === userId) {
        this.chatLoader = false;
        this.isInitialLoadingChat = false;
        this.chatLoadingError = "Failed to load chat. Tap to retry.";
      }
    }

    this.showVideoOverlay = true;

    setTimeout(() => {
      this.focusMessageInput();
      this.singleMessageInput?.adjustTextareaHeight();
      if (scrollToMessageId) {
        this.scrollToSearchedMessage(scrollToMessageId, "single");
      } else if (!this.isInitialLoadingChat) {
        this.needsScrollToBottomSingle = true;
        this.scrollToBottom();
      }
    }, 100);
  }

  private pendingChatCreationPromise: Promise<boolean> | null = null;
  isPendingSingleChat: boolean = false;

  async createPendingSingleChat(): Promise<boolean> {
    if (this.pendingChatCreationPromise) {
      console.log("Reusing existing single chat creation promise");
      return this.pendingChatCreationPromise;
    }

    this.pendingChatCreationPromise = this._doCreatePendingSingleChat();
    try {
      const result = await this.pendingChatCreationPromise;
      return result;
    } finally {
      this.pendingChatCreationPromise = null;
    }
  }

  private async _doCreatePendingSingleChat(): Promise<boolean> {
    if (!this.singleUser) return false;

    try {
      // this.chatLoader = true;
      const body = {
        members: [
          {
            userId: this.singleUser.userId,
            userName: this.singleUser.userName,
            profileImage: this.singleUser.profileImage,
            displayName: this.singleUser.displayName
          },
        ],
        type: "single",
      };

      console.log("Creating chat with body:", body);

      const res: any = await firstValueFrom(
        this.api.sendSingleChatUserData(body)
      );

      console.log("Chat creation API response:", res);

      // Handle different response structures
      let newChannelId = res?.data?.chat?.channelId || res?.data?.channelId || res?.data?.chatId || res?.channelId || res?.chatId || res?.data?.channel_id || res?.data?.chat_id || res?.channel_id || res?.chat_id || res?.data?._id || res?._id || res?.data?.id || res?.id;

      if (!newChannelId && typeof res?.data === 'string') {
        newChannelId = res.data;
      }
      if (!newChannelId && Array.isArray(res?.data) && res.data.length > 0) {
        const first = res.data[0];
        newChannelId = first?.channelId || first?.chatId || first?.channel_id || first?.chat_id || first?._id || first?.id;
      }
      if (!newChannelId && Array.isArray(res) && res.length > 0) {
        const first = res[0];
        newChannelId = first?.channelId || first?.chatId || first?.channel_id || first?.chat_id || first?._id || first?.id;
      }

      if (!newChannelId) {
        console.warn("No channelId in response, fetching chat list to find existing chat...");
        try {
          const resp: any = await firstValueFrom(this.chatService.getChats());
          const users = resp.data || [];

          let existingChat = users.find((u: any) => u.userId === this.singleUser.userId);

          if (existingChat && (existingChat.channelId || existingChat.chatId || existingChat._id)) {
            newChannelId = existingChat.channelId || existingChat.chatId || existingChat._id;
            console.log("Found existing chat with channelId:", newChannelId);
          } else {
            console.log("Chat not found in fresh list either. res object was:", JSON.stringify(res.data));
          }
        } catch (err) {
          console.error("Failed to refresh chat list:", err);
        }
      }

      if (newChannelId) {
        console.log("Using channelId:", newChannelId);

        // Update mappings
        this.profileImageMapping[newChannelId] = this.singleUser.profileImage || this.singleUser.profileImg || "assets/user-icon.png";
        this.userNameMapping[newChannelId] = this.singleUser.displayName || this.singleUser.userName || this.singleUser.name || "User";
        if (this.singleUser.userId) {
          this.profileImageMapping[this.singleUser.userId] = this.singleUser.profileImage || this.singleUser.profileImg || "assets/user-icon.png";
          this.userNameMapping[this.singleUser.userId] = this.singleUser.displayName || this.singleUser.userName || this.singleUser.name || "User";
        }

        // Update URL path without reloading
        this.updateChatRoute(newChannelId);

        // Update local user object
        this.singleUser = { ...this.singleUser, channelId: newChannelId };

        // Update lists
        const updateList = (list: any[]) => list.map(u =>
          u.userId === this.singleUser.userId ? { ...u, channelId: newChannelId } : u
        );
        this.filteredSingleUsers = updateList(this.filteredSingleUsers);
        this.allSingleUsers = updateList(this.allSingleUsers);

        // Update sortedChats as well
        this.sortedChats = this.sortedChats.map(chat =>
          chat.chatType === 'single' && chat.userId === this.singleUser.userId
            ? { ...chat, channelId: newChannelId }
            : chat
        );

        // Update chatMap
        if (newChannelId) {
          this.chatMap[newChannelId] = { ...this.singleUser, channelId: newChannelId };
        }

        this.activeChannel = newChannelId;

        // Connect
        await this.handleSingleChatConnection(this.singleUser);

        this.isPendingSingleChat = false;
        this.chatLoader = false;
        this.isInitialLoadingChat = false;

        return true;
      } else {
        console.error("Chat creation failed: No channelId in response", res);
        alertModalWithoutConfirm(
          "Failed to create chat",
          "Unable to create chat."
        );
      }
    } catch (error) {
      console.error("Error creating pending chat:", error);
      // Try to refresh chat list as fallback
      try {
        console.log("Attempting fallback: fetching chat list...");
        const resp: any = await firstValueFrom(this.chatService.getChats());
        const users = resp.data || [];
        const existingChat = users.find((u: any) => u.userId === this.singleUser.userId);

        if (existingChat && (existingChat.channelId || existingChat.chatId || existingChat._id)) {
          console.log("Found existing chat via fallback:", existingChat.channelId || existingChat.chatId || existingChat._id);
          const validChannelId = existingChat.channelId || existingChat.chatId || existingChat._id;

          this.profileImageMapping[validChannelId] = this.singleUser.profileImage || this.singleUser.profileImg || "assets/user-icon.png";
          this.userNameMapping[validChannelId] = this.singleUser.displayName || this.singleUser.userName || this.singleUser.name || "User";
          if (this.singleUser.userId) {
            this.profileImageMapping[this.singleUser.userId] = this.singleUser.profileImage || this.singleUser.profileImg || "assets/user-icon.png";
            this.userNameMapping[this.singleUser.userId] = this.singleUser.displayName || this.singleUser.userName || this.singleUser.name || "User";
          }
          this.updateChatRoute(validChannelId);

          this.singleUser = { ...this.singleUser, channelId: validChannelId };
          this.activeChannel = validChannelId;
          await this.handleSingleChatConnection(this.singleUser);

          // Apply to UI state
          this.filteredSingleUsers = this.filteredSingleUsers.map((u: any) =>
            u.userId === this.singleUser.userId ? { ...u, channelId: validChannelId } : u
          );

          this.isPendingSingleChat = false;
          this.chatLoader = false;
          this.isInitialLoadingChat = false;

          return true;
        }
      } catch (fallbackError) {
        console.error("Fallback also failed:", fallbackError);
      }
      // Show error to user
      this.chatLoader = false;
      this.isInitialLoadingChat = false;
      alertModalWithoutConfirm(
        "Failed to create chat",
        "Unable to create chat. Please try again or refresh the page."
      );
      return false;
    }

    this.chatLoader = false;
    this.isInitialLoadingChat = false;
    return false;
  }

  private attachSingleChannelListeners(channel: any, channelId: string) {
    this.setupSingleChannelSocketAndListeners(channel, channelId);
  }

  private attachGroupChannelListeners(channel: any, channelId: string) {
    this.setupGroupChannelSocketAndListeners(channel, channelId);
  }

  //Create chat channel connection
  async handleSingleChatConnection(singleUser: any) {
    if (!singleUser) return;
    const channelId = singleUser.channelId;
    if (!channelId) return;

    this.chatLoadingError = null;
    this.chatLoader = true;
    this.isInitialLoadingChat = true;
    this.scrollReadySingle = false;
    this.activeChannel = channelId;
    this.singleChatUsername = singleUser.displayName || singleUser.userName || singleUser.name || this.userNameMapping?.[singleUser.userId] || "User";
    this.singleUserProfileImg = singleUser.profileImage || singleUser.profileImg || this.profileImageMapping?.[singleUser.userId] || "assets/user-icon.png";
    this.newReceiver = singleUser.userId;
    this.singleChatBox = true;

    const isConverseReady = await this.waitForConverseReady();
    if (!isConverseReady) {
      console.warn("Converse SDK not ready within timeout, retrying initialization...");
      await this.initiatChat();
    }

    try {
      if (this.cachedSingleChannels[channelId]) {
        this.singleChannel = this.cachedSingleChannels[channelId];
      } else {
        let channel: any = null;
        let lastErr: any = null;
        for (let attempt = 1; attempt <= 3; attempt++) {
          try {
            if (!this.converse?.checkConnection?.()) {
              await this.waitForConverseReady(3000);
            }
            channel = await this.converse?.connectChannel({
              channelId: channelId,
              batch: 100,
            });
            if (channel) break;
          } catch (err: any) {
            lastErr = err;
            console.warn(`[handleSingleChatConnection] attempt ${attempt} connectChannel failed:`, err?.message || err);
            if (attempt < 3) {
              await new Promise((r) => setTimeout(r, attempt * 400));
            }
          }
        }
        if (!channel) {
          throw lastErr || new Error("Failed to connect channel");
        }
        this.cachedSingleChannels[channelId] = channel;
        if (this.activeChannel !== channelId) return;
        this.singleChannel = channel;
      }

      if (this.activeChannel !== channelId) return;

      if (this.singleChannel) {
        if (!this.channelData.includes(this.singleChannel)) {
          this.channelData.push(this.singleChannel);
        }

        this.scrollReadySingle = false;
        this.chatLoader = true;
        this.isInitialLoadingChat = true;
        await this.getAllMessages(this.singleChannel, false, channelId);
        if (this.activeChannel !== channelId) return;

        this.cachedSingleMessages[channelId] = this.singleChatMessages;
        this.cachedSingleScrollReady[channelId] = true;
        this.cachedSingleHasMore[channelId] = this.hasMoreSingleMessages;
        this.cachedSingleOldestCreatedAt[channelId] = this.oldestSingleMessageCreatedAt;

        if (this.cachedPinnedMessages[channelId]) {
          this.pinnedMessages = this.cachedPinnedMessages[channelId];
          if (this.pinnedMessages.length === 0) {
            this.showPinnedMessages = false;
          }
        } else {
          this.pinnedMessages = [];
          this.fetchPinnedMessages();
        }

        if (this.cachedStarredMessages[channelId]) {
          this.starredMessages = this.cachedStarredMessages[channelId];
        } else {
          this.starredMessages = [];
          this.refreshStarredMessages(100);
        }

        // Setup listeners and socket in background
        this.setupSingleChannelSocketAndListeners(this.singleChannel, channelId);
      }
    } catch (error: any) {
      console.error("handleSingleChatConnection failed:", error);
      if (channelId === this.activeChannel) {
        this.chatLoader = false;
        this.isInitialLoadingChat = false;
        this.scrollReadySingle = true;
        this.chatLoadingError = "Failed to load chat. Tap to retry.";
        this.cdRef.detectChanges();
      }
    }
  }

  private async setupSingleChannelSocketAndListeners(channel: any, channelId: string) {
    if (!channel) return;
    this.watchConverseAuth(channel.socket);

    if (!channel.hasCachedListeners) {
      channel.hasCachedListeners = true;
      channel.listenMessageDelete?.((data: any) => this.zone.run(() => this.handleMessageDelete(data)));
      channel.listenMessageDeleteMe?.((data: any) => this.zone.run(() => this.handleMessageDelete(data)));
      channel.listenUpdateMessagReaction?.((data: any) => this.zone.run(() => this.handleReactionUpdate(data)));
      channel.listenPinMessage?.((data: any) => this.zone.run(() => this.handlePinMessage(data)));
      channel.listenUnPinMessage?.((data: any) => this.zone.run(() => this.handleUnPinMessage(data)));
      if (channel.socket && typeof channel.socket.on === 'function') {
        channel.socket.on('react-message', (data: any) => {
          this.zone.run(() => {
            console.log('Socket event react-message received in single chat:', data);
            this.handleReactionUpdate(data);
          });
        });
        channel.socket.on('message-seen', (data: any) => {
          this.zone.run(() => {
            console.log('Socket event message-seen received in single chat:', data);
            this.handleMessageSeen(data);
          });
        });
        channel.socket.on('message-seen-all', (data: any) => {
          this.zone.run(() => this.handleMessageSeenAll(data, channelId));
        });
      }

      channel.listenTyping?.((data: any) => {
        this.zone.run(() => {
          const payload = data?.meta || data || {};
          const userId = payload.userId || payload.sender_id;
          const userName = payload.userName || payload.name || 'Someone';
          const cId = payload.channelId || this.activeChannel;
          if (!userId || userId === this.userId) return;
          const key = `${cId}_${userId}`;
          this.typingUsers.set(key, { userId, userName, timestamp: Date.now() });
          this.cdRef.detectChanges();
        });
      });

      channel.listenStopTyping?.((data: any) => {
        this.zone.run(() => {
          const payload = data?.meta || data || {};
          const userId = payload.userId || payload.sender_id;
          const cId = payload.channelId || this.activeChannel;
          if (!userId) return;
          const key = `${cId}_${userId}`;
          this.typingUsers.delete(key);
          this.cdRef.detectChanges();
        });
      });

      if (!channel.hasListener) {
        channel.listenMessage?.(this.handleIncomingMessage.bind(this));
        channel.hasListener = true;
      }

      channel.listenEditMessage?.((data: any) => this.zone.run(() => this.handleMessageEdit(data)));
    }

    this.waitForChannelConnection(channel, channelId, 10, 300).then((connected) => {
      if (connected && channel.socket && typeof channel.socket.emit === "function") {
        channel.socket.emit("message-seen-all", {});
        this.chatService.emitChatRead(channelId);
        console.log("message-seen-all event emitted via socket for single chat:", channelId);
      }
    });
  }

  // Helper method to wait for channel socket connection
  private async waitForChannelConnection(channel: any, channelId: string, maxRetries: number = 20, retryDelay: number = 250): Promise<boolean> {
    for (let i = 0; i < maxRetries; i++) {
      if (channel && channel.socket && channel.socket.connected) {
        console.log(`Channel ${channelId} socket connected successfully`);
        return true;
      }
      await new Promise(resolve => setTimeout(resolve, retryDelay));
    }
    console.warn(`Channel ${channelId} socket connection timed out after ${maxRetries} attempts`);
    return false;
  }

  //Create group chat channel connection
  groupChatUsername: any;
  groupUserProfileImg: any;
  async handleGroupChatConnection(channelId: string) {
    if (!channelId) return;
    this.chatLoadingError = null;
    this.chatLoader = true;
    this.isInitialLoadingChat = true;
    this.scrollReady = false;
    this.activeChannel = channelId;
    this.memberChannelId = channelId;
    this.groupChatBox = true;

    const isConverseReady = await this.waitForConverseReady();
    if (!isConverseReady) {
      console.warn("Converse SDK not ready within timeout, retrying initialization...");
      await this.initiatChat();
    }

    try {
      if (this.cachedGroupChannels[channelId]) {
        this.groupChannel = this.cachedGroupChannels[channelId];
      } else {
        let channel: any = null;
        let lastErr: any = null;
        for (let attempt = 1; attempt <= 3; attempt++) {
          try {
            if (!this.converse?.checkConnection?.()) {
              await this.waitForConverseReady(3000);
            }
            channel = await this.converse?.connectChannel({ channelId });
            if (channel) break;
          } catch (err: any) {
            lastErr = err;
            console.warn(`[handleGroupChatConnection] attempt ${attempt} connectChannel failed:`, err?.message || err);
            if (attempt < 3) {
              await new Promise((r) => setTimeout(r, attempt * 400));
            }
          }
        }
        if (!channel) {
          throw lastErr || new Error("Failed to connect group channel");
        }
        this.cachedGroupChannels[channelId] = channel;
        if (this.memberChannelId !== channelId) return;
        this.groupChannel = channel;
      }

      if (this.memberChannelId !== channelId) return;

      if (this.groupChannel) {
        this.scrollReady = false;
        this.chatLoader = true;
        this.isInitialLoadingChat = true;
        await this.getAllGroupMessages(this.groupChannel, false, channelId);
        if (this.memberChannelId !== channelId) return;

        this.cachedGroupMessages[channelId] = this.groupChatMessages;
        this.cachedGroupScrollReady[channelId] = true;
        this.cachedGroupHasMore[channelId] = this.hasMoreGroupMessages;
        this.cachedGroupOldestCreatedAt[channelId] = this.oldestMessageCreatedAt;

        if (this.cachedPinnedMessages[channelId]) {
          this.pinnedMessages = this.cachedPinnedMessages[channelId];
          if (this.pinnedMessages.length === 0) {
            this.showPinnedMessages = false;
          }
        } else {
          this.pinnedMessages = [];
          this.fetchPinnedMessages();
        }

        if (this.cachedStarredMessages[channelId]) {
          this.starredMessages = this.cachedStarredMessages[channelId];
        } else {
          this.starredMessages = [];
          this.refreshStarredMessages(100);
        }

        // Setup listeners and socket in background
        this.setupGroupChannelSocketAndListeners(this.groupChannel, channelId);
      }
    } catch (error: any) {
      console.error("handleGroupChatConnection failed:", error);
      if (channelId === this.memberChannelId) {
        this.chatLoader = false;
        this.isInitialLoadingChat = false;
        this.scrollReady = true;
        this.chatLoadingError = "Failed to load group chat. Tap to retry.";
        this.cdRef.detectChanges();
      }
    }
  }

  private async setupGroupChannelSocketAndListeners(channel: any, channelId: string) {
    if (!channel) return;
    this.watchConverseAuth(channel.socket);

    if (!channel.hasCachedListeners) {
      channel.hasCachedListeners = true;
      channel.listenMessageDelete?.((data: any) => this.zone.run(() => this.handleMessageDelete(data)));
      channel.listenMessageDeleteMe?.((data: any) => this.zone.run(() => this.handleMessageDelete(data)));
      channel.listenUpdateMessagReaction?.((data: any) => this.zone.run(() => this.handleReactionUpdate(data)));
      channel.listenPinMessage?.((data: any) => this.zone.run(() => this.handlePinMessage(data)));
      channel.listenUnPinMessage?.((data: any) => this.zone.run(() => this.handleUnPinMessage(data)));
      if (channel.socket && typeof channel.socket.on === 'function') {
        channel.socket.on('react-message', (data: any) => {
          this.zone.run(() => {
            console.log('Socket event react-message received in group chat:', data);
            this.handleReactionUpdate(data);
          });
        });
        channel.socket.on('message-seen', (data: any) => {
          this.zone.run(() => {
            console.log('Socket event message-seen received in group chat:', data);
            this.handleMessageSeen(data);
          });
        });
        channel.socket.on('message-seen-all', (data: any) => {
          this.zone.run(() => this.handleMessageSeenAll(data, channelId));
        });
      }

      channel.listenTyping?.((data: any) => {
        this.zone.run(() => {
          const payload = data?.meta || data || {};
          const userId = payload.userId || payload.sender_id;
          const userName = payload.userName || payload.name || 'Someone';
          const cId = payload.channelId || this.memberChannelId;
          if (!userId || userId === this.userId) return;
          const key = `${cId}_${userId}`;
          this.typingUsers.set(key, { userId, userName, timestamp: Date.now() });
          this.cdRef.detectChanges();
        });
      });

      channel.listenStopTyping?.((data: any) => {
        this.zone.run(() => {
          const payload = data?.meta || data || {};
          const userId = payload.userId || payload.sender_id;
          const cId = payload.channelId || this.memberChannelId;
          if (!userId) return;
          const key = `${cId}_${userId}`;
          this.typingUsers.delete(key);
          this.cdRef.detectChanges();
        });
      });

      channel.listenMessage?.((data: any) => {
        this.zone.run(() => {
          console.log("Group channel direct message event:", data);
          const normalizedMsg = {
            message: data,
            channelId: channelId,
            sender_id: data.sid || data.sender_id
          };
          this.inComingGroupListenerMessages(normalizedMsg);
        });
      });

      channel.listenEditMessage?.((data: any) => this.zone.run(() => this.handleMessageEdit(data)));
    }

    this.waitForChannelConnection(channel, channelId, 10, 300).then((connected) => {
      if (connected && channel.socket && typeof channel.socket.emit === "function") {
        channel.socket.emit("message-seen-all", {});
        this.chatService.emitChatRead(channelId);
        console.log("message-seen-all event emitted via socket for group chat:", channelId);
      }
    });
  }

  retryCurrentChat() {
    this.chatLoadingError = null;
    this.chatLoader = true;
    this.isInitialLoadingChat = true;
    if (this.singleChatBox && this.singleUser) {
      this.handleSingleChatConnection(this.singleUser);
    } else if (this.groupChatBox && this.memberChannelId) {
      this.handleGroupChatConnection(this.memberChannelId);
    } else {
      this.chatLoader = false;
      this.isInitialLoadingChat = false;
    }
  }

  async showGroupChat(group: any, scrollToMessageId?: string) {
    if (!group) return;
    const gid = group.channelId || group._id;
    const chatKey = `group_${gid}`;
    if (this.activeChatLoadingKey === chatKey && this.activeChatLoadingPromise) {
      console.log("showGroupChat already loading for:", gid);
      return this.activeChatLoadingPromise;
    }
    this.activeChatLoadingKey = chatKey;
    const loadPromise = this._doShowGroupChat(group, scrollToMessageId);
    this.activeChatLoadingPromise = loadPromise;
    try {
      await loadPromise;
    } finally {
      if (this.activeChatLoadingKey === chatKey) {
        this.activeChatLoadingPromise = null;
      }
    }
  }

  private async _doShowGroupChat(group: any, scrollToMessageId?: string) {
    this.saveCurrentChatDraft();
    if (scrollToMessageId) {
      this.targetSearchMessageId = scrollToMessageId;
    }
    this.updateCacheForActiveChannel();
    this.chatLoadingError = null;
    this.chatLoader = true;
    this.isInitialLoadingChat = true;
    this.groupName = group.groupName || this.userNameMapping?.[group.channelId] || "Group";
    this.groupChatUsername = this.groupName;
    this.groupChatName = this.groupName;
    this.cancelReply();
    this.cancelEdit();
    this.showClickForInfo = true;

    // Track selected chat
    this.selectedChatGroupId = group.channelId || group._id;
    this.selectedChatUserId = "";
    this.singleChannel = null;
    this.scrollReadySingle = false;
    this.hasMoreSingleMessages = true;
    this.oldestSingleMessageCreatedAt = null;
    this.isLoadingSingleMessages = false;
    const groupChannelId = group.channelId || group._id;
    const isSameGroup = this.memberChannelId === groupChannelId && this.groupChatBox;
    if (!isSameGroup) {
      if (!scrollToMessageId) {
        this.targetSearchMessageId = null;
      }
      this.quickSearchComp?.resetSearch();
    }
    const updateUnread = (g: any) => {
      if (g.channelId === groupChannelId) {
        return { ...g, unreadCount: 0 };
      }
      return g;
    };

    this.filteredGroupUsers = this.filteredGroupUsers.map(updateUnread);
    this.allGroupUsers = this.allGroupUsers.map(updateUnread);
    this.sortAllChatsByRecentMessages();

    const totalUnreadCount = this.calculateTotalUnreadCount();
    this.store.dispatch(setUnreadCount({ unreadCount: totalUnreadCount }));
    this.saveUnreadCountsToLocalStorage();

    if (
      isSameGroup &&
      this.groupChatBox &&
      this.groupChatMessages.length > 0 &&
      this.groupChannel &&
      this.scrollReady
    ) {
      this.getAllGroupMessages(this.groupChannel, false, groupChannelId).catch(() => { });
      setTimeout(() => {
        if (scrollToMessageId) {
          this.targetSearchMessageId = scrollToMessageId;
          this.scrollToSearchedMessage(scrollToMessageId, "group");
        } else {
          this.needsScrollToBottomGroup = true;
          this.scrollToBottom();
        }
      }, 100);
      return;
    }

    const gid = group.channelId || group._id;
    this.groupChannel = null;
    this.scrollReady = false;
    this.hasMoreGroupMessages = true;
    this.oldestMessageCreatedAt = null;
    this.isLoadingGroupMessages = false;
    this.chatLoader = true;
    this.isInitialLoadingChat = true;
    this.chatLoadingError = null;
    this.needsScrollToBottomGroup = false;

    this.groupChatMessages = this.injectPendingUploads(gid, []);
    this.AllGroupChatMsg = { read: [], unread: [] };
    this._enrichingUrls.clear(); // reset for new chat context

    // Restore group draft on chat switch
    this.newGroupMessage = this.chatDraftService.getDraft('group', gid);
    this.showMentionList = false;
    this.mentionUsers = [];
    this.mentionSearchTerm = "";
    this.currentMentionPosition = 0;
    this.messageMeta = null;
    this.MetaDataDetails2 = null;
    this.MedataLength2 = 0;
    this.isSuggestionBoxVisible2 = false;

    if (this.showSeenInfoModal) {
      this.closeSeenInfoModal();
    }
    // Toggle visibility
    this.singleChatBox = false;
    this.groupChatBox = true;
    this.memberChannelId = group.channelId || group._id || groupChannelId;
    this.groupChatUsername = group.groupName;
    this.groupChatName = group.groupName;
    this.groupDescription = group.groupDescription || group.description || "";
    this.groupChatDescription = this.groupDescription;
    const isLeadAssignmentGroup = group.meta?.type === 'Lead Assignment Group' || group.type === 'Lead Assignment Group' || group.subType === 'Lead Assignment Group' || group.meta?.subType === 'Lead Assignment Group';
    const resolvedGroupImg = isLeadAssignmentGroup
      ? (group.groupImg || group.groupImage || this.profileImageMapping[group.channelId] || "assets/group_chat.png")
      : (group.groupImg || this.profileImageMapping[group.channelId] || "assets/group_chat.png");

    this.groupUserProfileImg = resolvedGroupImg;
    this.groupChatProfileImg = resolvedGroupImg;
    this.cdRef.detectChanges();
    this.currentGroupCreator = group.creator || "";

    // Set group members info for the modal
    this.allGroupMembers = group.members || [];
    const activeMembers = this.allGroupMembers.filter((m: any) => !m.isRemoved && !m.leftGroup);
    this.totalGroupMembers = activeMembers.length;

    this.groupMembersDisplayNames = activeMembers.map((m: any) =>
      m.userId === this.userId
        ? "You"
        : (m.displayName || m.userName)
    );


    const finalChannelId = group.channelId || group._id || groupChannelId;
    if (!finalChannelId || finalChannelId === 'undefined') {
      console.error("Error: Cannot connect to group chat, invalid channel ID.", group);
      return;
    }

    try {
      this.updateChatRoute(finalChannelId);
      await this.handleGroupChatConnection(finalChannelId);
    } catch (error) {
      console.error("Error connecting to group chat:", error);
      if (this.selectedChatGroupId === (group.channelId || group._id)) {
        this.chatLoader = false;
        this.isInitialLoadingChat = false;
        this.chatLoadingError = "Failed to load group chat. Tap to retry.";
      }
    }

    if (this.isMobileView) {
      this.showChatWindow = true;
      this.chatService.isChatOpenMobile$.next(true);
    }

    setTimeout(() => {
      this.focusMessageInput();
      this.groupMessageInput?.adjustTextareaHeight();
      if (scrollToMessageId) {
        this.targetSearchMessageId = scrollToMessageId;
        this.scrollToSearchedMessage(scrollToMessageId, "group");
      } else if (!this.isInitialLoadingChat) {
        this.needsScrollToBottomGroup = true;
        this.scrollToBottom();
      }
    }, 300);
  }

  isWriteBlockedInCurrentChat(): boolean {
    return !this.singleChatBox && this.isCurrentGroupExited;
  }

  // Check if current user has exited the current group
  get isCurrentGroupExited(): boolean {
    const channelId = String(this.memberChannelId || '').trim();
    if (!channelId) return false;
    if (this.exitedGroups && this.exitedGroups.has(channelId)) return true;
    if (!this.allGroupMembers || this.allGroupMembers.length === 0) {
      // Check if this group is in allGroupUsers and flagged as exited
      const cachedGroup = this.allGroupUsers.find(g => String(g.channelId || '').trim() === channelId);
      if (cachedGroup && cachedGroup.isExited) return true;
      return false;
    }
    const me = this.allGroupMembers.find((m) => String(m.userId).trim() === String(this.userId).trim());
    return !!(me && (me.leftGroup || me.isRemoved));
  }

  highlightMentions(message: string, messageId?: string, senderId?: string): string {
    if (!message) return "";

    let meta = null;
    let foundMessage = null;

    if (messageId) {
      foundMessage = this.groupChatMessages.find(m => m.id === messageId);
      meta = foundMessage?.meta;
    }

    if (!meta && senderId) {
      foundMessage = this.groupChatMessages.find(m => m.message === message && m.sender_id === senderId);
      meta = foundMessage?.meta;
    }

    if (!meta) {
      foundMessage = this.groupChatMessages.find(m => m.message === message);
      meta = foundMessage?.meta;
    }

    if (meta?.mentions?.length > 0) {
      console.log(' Using meta mentions:', meta.mentions);
      return this.applyMentionsFromMeta(message, meta.mentions);
    }

    return this.applyMentionsFromText(message);
  }

  private applyMentionsFromMeta(message: string, mentions: any[]): string {
    let highlighted = this.escapeHtml(message);
    const validMentions = (mentions || []).filter((mention) => {
      if (!mention || !mention.name || typeof mention.position !== 'number') return false;
      if (mention.position > 0 && /[\w.+-]/.test(message[mention.position - 1])) {
        return false;
      }
      return true;
    });

    [...validMentions].reverse().forEach((mention) => {
      const mentionText = `@${mention.name}`;
      const mentionHtml = `<span class="mention-highlight">${mentionText}</span>`;

      if (mention.position >= 0 && mention.position <= highlighted.length) {
        highlighted =
          highlighted.substring(0, mention.position) +
          mentionHtml +
          highlighted.substring(mention.position + mention.length);
      }
    });

    return highlighted;
  }

  private applyMentionsFromText(message: string): string {
    if (!message) return "";
    let highlighted = this.escapeHtml(message);

    // 1. Identify email addresses and replace with placeholders so they are never treated as mentions
    const emails: { placeholder: string; text: string }[] = [];
    const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;

    highlighted = highlighted.replace(emailRegex, (email) => {
      const placeholder = `___EMAIL_PLACEHOLDER_${emails.length}___`;
      emails.push({ placeholder, text: email });
      return placeholder;
    });

    // 2. Highlight genuine @mentions that have a proper boundary (not part of an email or word)
    const mentionRegex = /(^|[^\w.+-])@([a-zA-Z0-9_]+(?:\.[a-zA-Z0-9_]+)*)\b/g;

    highlighted = highlighted.replace(mentionRegex, (match, prefix, username) => {
      if (!username) return match;
      const lower = username.toLowerCase();
      // Do not treat domain names / email fragments as user mentions
      if (/\.(com|org|net|edu|gov|io|co|in|ai|app|dev|xyz|me|info|biz|uk|us|ca|au|de|fr|tech|site|online)$/i.test(lower)) {
        return match;
      }
      if (/\.[a-z]{2,6}$/i.test(lower)) {
        return match;
      }
      if (lower === 'all') {
        return `${prefix}<span class="mention-highlight mention-all-highlight" data-mention-type="all">@${this.escapeHtml(username)}</span>`;
      }
      return `${prefix}<span class="mention-highlight" data-mention-type="user" data-mention-name="${this.escapeHtml(username)}">@${this.escapeHtml(username)}</span>`;
    });

    // 3. Restore protected emails as normal plain text
    emails.forEach(email => {
      highlighted = highlighted.split(email.placeholder).join(email.text);
    });

    return highlighted;
  }

  escapeHtml(text: string): string {
    if (!text) return "";
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  //Last Active to show online offline
  formatLastActive(dateString: string): string {
    if (!dateString) return "a while ago";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);

    if (diffSec < 10) return "a few seconds ago";
    if (diffSec < 60) return `${diffSec}s ago`;
    if (diffMin < 60) return `${diffMin} min ago`;
    if (diffHr < 24) return `${diffHr} hr ago`;
    if (diffDay === 1) return "yesterday";
    if (diffDay < 7) return `${diffDay} days ago`;

    const options: Intl.DateTimeFormatOptions = {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return date.toLocaleString("en-US", options);
  }

  getFilePreview(fileName: string): string {
    if (fileName.includes('voice_message_')) {
      return "🎤 Voice message";
    } else if (/\.(jpg|jpeg|png|gif|webp|svg)$/i.test(fileName)) {
      return " sent a photo";
    } else if (/\.(mp4|mov|avi|mkv|webm)$/i.test(fileName)) {
      return "sent a video";
    } else if (DOCUMENT_EXTENSIONS.some(ext => (fileName || '').toLowerCase().endsWith(ext.toLowerCase()))) {
      return "sent a file";
    } else {
      return fileName;
    }
  }

  AllSingleChatMsg: any = {};
  singleChatMessages: any;
  AllGroupChatMsg: any = {};
  groupChatMessages: any;
  projectInstance: Project | null = null;
  oldestSingleMessageCreatedAt: string | null = null;
  isLoadingSingleMessages = false;
  hasMoreSingleMessages = true;
  scrollReadySingle = false;
  showJumpToLatestSingle = false;

  @ViewChild("singleScrollContainer") singleScrollContainer!: ElementRef;
  @Input() project: string = "";
  private needsScrollToBottomSingle = false;
  private needsScrollToBottomGroup = false;

  // GET ALL SINGLE CHAT MESSAGES
  async getAllMessages(channel: any, loadMore = false, targetChannelId?: string) {
    if (!channel) {
      console.warn(" Channel not available for getAllMessages");
      return;
    }

    if (!loadMore) {
      this.hasMoreSingleMessages = true;
      this.oldestSingleMessageCreatedAt = null;
    }
    if (this.isLoadingSingleMessages || (loadMore && !this.hasMoreSingleMessages)) {
      console.log(" Skipping single message fetch (loading or no more)");
      return;
    }

    const channelId = String(
      targetChannelId ||
      channel?.channel_id ||
      channel?.channelId ||
      channel?._id ||
      (this.singleChatBox ? this.activeChannel : '') ||
      ''
    ).trim();

    this.isLoadingSingleMessages = true;

    try {
      const requestParams: any = {
        limit: 100,
      };

      if (loadMore && this.oldestSingleMessageCreatedAt) {
        requestParams.time = this.oldestSingleMessageCreatedAt;
      }

      const messages = await channel.getMessages(requestParams);

      const read = (messages.msgs.read || []).map((msg: any) => ({ ...msg, seen: true }));
      const unread = messages.msgs.unread || [];
      const newMessages = [...read, ...unread];

      if (unread.length > 0) {
        const seenParams = unread.map((msg: any) => ({
          senderId: msg.sender_id || msg.sid,
          messageId: msg._id || msg.id || msg.messageId
        })).filter((p: any) => p.senderId && p.messageId && p.senderId !== this.userId);

        if (seenParams.length > 0) {
          try {
            channel.seenMessage(seenParams);
          } catch (err) {
            console.error("Failed to mark single messages as seen:", err);
          }
        }
      }

      if (newMessages.length === 0) {
        this.hasMoreSingleMessages = false;
        if (channelId === this.activeChannel) {
          this.singleChatMessages = [];
          this.chatLoadingError = null;
          this.isInitialLoadingChat = false;
          this.chatLoader = false;
          this.scrollReadySingle = true;
        }
        if (!channel.hasListener) {
          channel.listenMessage(this.handleIncomingMessage.bind(this));
          channel.hasListener = true;
          console.log('listenMessage registered for new empty channel:', channel);
        }
        return;
      }

      newMessages.sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );

      // Fix hasMore logic - should be based on raw response length
      if (read.length + unread.length < 100) {
        this.hasMoreSingleMessages = false;
      }

      const newOldestCreatedAt = newMessages[0]?.createdAt;
      if (loadMore) {
        if (
          !newOldestCreatedAt ||
          newOldestCreatedAt === this.oldestSingleMessageCreatedAt
        ) {
          this.hasMoreSingleMessages = false;
          return;
        }
      }

      if (!loadMore || !this.oldestSingleMessageCreatedAt) {
        this.oldestSingleMessageCreatedAt = newOldestCreatedAt;
      }

      // Group messages by groupId
      const groupedMessages: { [groupId: string]: any[] } = {};
      const seenMessageIds = new Set<string>();

      newMessages.forEach((msg) => {
        const msgId = msg._id || msg.id || msg.messageId;
        const groupId = msg.meta?.groupId;

        // Skip duplicate messages by ID
        if (msgId && seenMessageIds.has(msgId)) {
          return;
        }
        if (msgId) {
          seenMessageIds.add(msgId);
        }

        if (groupId) {
          if (!groupedMessages[groupId]) {
            groupedMessages[groupId] = [];
          }
          groupedMessages[groupId].push(msg);
        }
      });

      const processedGroups = new Set();

      const processedMessages = newMessages
        .map((msg) => {
          const msgId = msg._id || msg.id || msg.messageId;
          const groupId = msg.meta?.groupId;

          // Skip if already processed as duplicate
          if (msgId && !seenMessageIds.has(msgId)) {
            return null;
          }

          if (groupId && processedGroups.has(groupId)) {
            return null;
          }

          // Filter out generic admin change messages
          if (msg.message && msg.message.toLowerCase().trim() === 'admin changed' && !msg.meta?.subType) {
            return null;
          }

          if (groupId && groupedMessages[groupId]?.length > 1) {
            processedGroups.add(groupId);

            const uniqueFiles = new Map();
            const allRx: any[] = [];
            const seenRx = new Set();
            const _allIds: string[] = [];
            groupedMessages[groupId].forEach((groupedMsg) => {
              const fileUrl = groupedMsg?.url || groupedMsg?.meta?.fileUrl;
              if (fileUrl && !uniqueFiles.has(fileUrl)) {
                uniqueFiles.set(fileUrl, {
                  fileUrl: fileUrl,
                  type: getFileType(fileUrl),
                  name: groupedMsg?.message || "File",
                });
              }
              // Collect individual message IDs for carousel mass-deletion
              const mid = groupedMsg._id || groupedMsg.id || groupedMsg.messageId;
              if (mid && !_allIds.includes(mid)) _allIds.push(mid);
              const rx = groupedMsg.reactions || groupedMsg.meta?.reactions || [];
              rx.forEach((r: any) => {
                const uId = r.userId || r.user_id || r.sender_id;
                if (uId && !seenRx.has(uId)) {
                  seenRx.add(uId);
                  allRx.push(r);
                }
              });
            });

            return {
              ...msg,
              meta: this.normalizeLinkMeta(msg.meta),
              files: Array.from(uniqueFiles.values()),
              _allIds,
              currentSlideIndex: 0,
              reactions: allRx
            };
          }

          const fileUrl = msg.url || msg.meta?.fileUrl;
          return {
            ...msg,
            meta: this.normalizeLinkMeta(msg.meta),
            files: fileUrl ? [{
              fileUrl: fileUrl,
              type: getFileType(fileUrl),
              name: msg.message || "File"
            }] : [],
            profileImage: this.profileImageMapping[msg.sender_id] || "",
            reactions: msg.reactions || msg.meta?.reactions || []
          };
        })
        .filter((msg) => msg !== null);

      if (loadMore) {
        // Support scroll restoration for single chat
        const container = this.singleScrollContainer?.nativeElement || document.getElementById('singleChatContainer');
        let prevScrollHeight = 0, prevScrollTop = 0;
        if (container) {
          prevScrollHeight = container.scrollHeight;
          prevScrollTop = container.scrollTop;
        }

        this.singleChatMessages = [
          ...processedMessages,
          ...this.singleChatMessages,
        ];

        if (container && processedMessages.length > 0) {
          this.cdRef.detectChanges();
          const newScrollHeight = container.scrollHeight;
          container.scrollTop = prevScrollTop + (newScrollHeight - prevScrollHeight);
        }
      } else {
        const isCurrentActive = this.singleChatBox && Boolean(channelId && channelId === this.activeChannel);
        const mergedMessages = processedMessages;

        if (isCurrentActive) {
          this.singleChatMessages = this.injectPendingUploads(this.activeChannel, mergedMessages);
          console.log("Single chat messages fetched:", this.singleChatMessages);
          this.chatLoadingError = null;
          this.isInitialLoadingChat = false;
          this.chatLoader = false;
          this.needsScrollToBottomSingle = true;
          this.cdRef.detectChanges();
          const container = this.singleScrollContainer?.nativeElement || document.getElementById('singleChatContainer');
          if (container) {
            container.scrollTop = container.scrollHeight;
          }
          this.scrollReadySingle = true;
          if (this.singleChatMessages.length > 0) {
            const lastMsg = this.singleChatMessages[this.singleChatMessages.length - 1];
            this.updateLocalChatLastMessage(this.activeChannel, lastMsg, false);
          }
        }

        if (channelId) {
          this.cachedSingleMessages[channelId] = mergedMessages;
          this.cachedSingleScrollReady[channelId] = true;
          if (this.chatCacheService && mergedMessages.length > 0) {
            this.chatCacheService.saveMessages(channelId, mergedMessages);
          }
        }
      }
      this.processMessages();

      if (!channel.hasListener) {
        channel.listenMessage(this.handleIncomingMessage.bind(this));
        channel.hasListener = true;
      }
      this.updateCacheForActiveChannel();
    } catch (error) {
      console.error("Error fetching messages:", error);
      if (!loadMore && channelId === this.activeChannel) {
        this.chatLoadingError = "Failed to load messages. Tap to retry.";
      }
      this.scrollReadySingle = true;
    } finally {
      this.isLoadingSingleMessages = false;
      if (!loadMore && channelId === this.activeChannel) {
        this.isInitialLoadingChat = false;
        this.chatLoader = false;
      }
    }
  }

  getProjectFromMessage(message: any): string | null {
    const meta = message?.meta || {};
    if (meta?.project) return meta.project;
    if (meta?.projectName) return meta.projectName;
    const text = message?.message || message?.systemText || "";
    if (typeof text !== 'string') return null;
    const upperText = text.toUpperCase();
    if (upperText.includes('IIC-LAB') || upperText.includes('IIC')) return 'IIC-LAB';
    if (upperText.includes('V-RETAIL') || upperText.includes('RETAIL')) return 'V-RETAIL';
    if (upperText.includes('V-ESTATE') || upperText.includes('ESTATE')) return 'V-ESTATE';
    if (upperText.includes('OUTBOUND')) return 'OUTBOUND';
    return null;
  }

  //Assigned leads click function
  onSystemMessageClick(event: Event, message: any): void {
    event?.stopPropagation();
    const rawRedirectUrl = message?.meta?.redirectUrl || message?.redirectUrl;
    if (rawRedirectUrl) {
      let url = rawRedirectUrl;
      try {
        url = decodeURIComponent(rawRedirectUrl);
      } catch (e) {
        url = rawRedirectUrl;
      }
      if (url.startsWith('http://') || url.startsWith('https://')) {
        window.location.href = url;
        return;
      }
      const baseUrl = environment.redirectUrl || '';
      if (url.startsWith('#')) {
        window.location.href = `${baseUrl}${url}`;
        return;
      }
      if (url.startsWith('/')) {
        window.location.href = `${baseUrl}#${url}`;
        return;
      }
      window.location.href = `${baseUrl}#/${url}`;
      return;
    }
    const project = this.getProjectFromMessage(message) || message?.meta?.project || '';
    const redirectUrl = this.getKanbanRedirectUrlByProject(project);
    window.location.href = redirectUrl;
  }

  getKanbanRedirectUrlByProject(project: string): string {
    const projectPathMap: Record<string, string> = {
      'V-RETAIL': 'vretail-leads',
      'V-ESTATE': 'vestate-leads',
      'IIC-LAB': 'iic-leads',
      'OUTBOUND': 'outbound-leads',
    };

    const path = projectPathMap[project] || 'all-leads';

    return `${environment.redirectUrl}#/lead-track/${path}?view=kanban`;
  }

  //Pagination of single chat
  onSingleChatScroll(event: any) {
    if (this.isProgrammaticScroll) return;
    const el = event.target;
    const scrollTop = el.scrollTop;
    const scrollHeight = el.scrollHeight;
    const clientHeight = el.clientHeight;

    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    this.showJumpToLatestSingle = distanceFromBottom > 400;

    // Only attempt to load older messages if conditions allow
    if (
      !this.scrollReadySingle ||
      this.isLoadingSingleMessages ||
      !this.hasMoreSingleMessages
    ) {
      return;
    }

    const percentFromTop = (scrollTop / (scrollHeight - clientHeight)) * 100;
    if (percentFromTop <= 5) {
      console.log("Scrolled near top — loading older single messages...");
      this.getAllMessages(this.singleChannel, true, this.activeChannel);
    }
  }

  // GROUP CHAT INCOMING MESSAGES
  unreadMessageCount: any;
  isLoadingGroupMessages = false;
  hasMoreGroupMessages = true;
  oldestMessageId: string | null = null;
  oldestMessageCreatedAt: string | null = null;
  scrollReady: boolean = false;
  async getAllGroupMessages(channel: any, loadMore = false, targetChannelId?: string) {
    if (!channel) {
      console.log("Channel not available for getAllGroupMessages");
      return;
    }

    if (!loadMore) {
      this.hasMoreGroupMessages = true;
      this.oldestMessageCreatedAt = null;
    }

    if (this.isLoadingGroupMessages || (loadMore && !this.hasMoreGroupMessages)) {
      console.log("Skipping group message fetch");
      return;
    }

    const channelId = String(
      targetChannelId ||
      channel?.channel_id ||
      channel?.channelId ||
      channel?._id ||
      (this.groupChatBox ? this.memberChannelId : '') ||
      ''
    ).trim();

    this.isLoadingGroupMessages = true;
    try {
      const requestParams: any = { limit: 100 };
      if (loadMore && this.oldestMessageCreatedAt) {
        requestParams.time = this.oldestMessageCreatedAt;
      } else if (!loadMore) {
        // Check if current user has exited the group
        const currentGroup = this.allGroupUsers.find(g => g.channelId === this.memberChannelId);
        if (currentGroup && currentGroup.members) {
          const currentUserMember = currentGroup.members.find((m: any) => m.userId === this.userId);
          if (currentUserMember && (currentUserMember.leftGroup || currentUserMember.isRemoved) && currentUserMember.exitedAt) {
            console.log("Fetching messages up to exit time:", currentUserMember.exitedAt);
            const exitTime = new Date(currentUserMember.exitedAt);
            exitTime.setSeconds(exitTime.getSeconds() + 1);
            requestParams.time = exitTime.toISOString();
          }
        }
      }
      const response = await channel.getMessages(requestParams);
      const rawRead = response.msgs?.read || [];
      const rawUnread = response.msgs?.unread || [];

      // For messages where sender_id === this.userId, do not set seen from read list.
      // The read list describes the current user's own read status, not whether group members have read it.
      const read = rawRead.map((msg: any) => ({
        ...msg,
        seen: msg.sender_id === this.userId ? false : true
      }));
      const unread = rawUnread.map((msg: any) => ({
        ...msg,
        seen: false
      }));
      // Filter out messages that have been "deleted for me" by the current user
      const filteredRead = read.filter((msg: any) => {
        const msgId = msg._id || msg.id || msg.messageId;
        return !this.deletedForMeMessageIds.has(msgId);
      });
      const filteredUnread = unread.filter((msg: any) => {
        const msgId = msg._id || msg.id || msg.messageId;
        return !this.deletedForMeMessageIds.has(msgId);
      });
      const newMessages = [...filteredRead, ...filteredUnread];

      if (unread.length > 0 && this.groupChannel) {
        const seenParams = unread.map((msg: any) => ({
          senderId: msg.sender_id || msg.sid,
          messageId: msg._id || msg.id || msg.messageId
        })).filter((p: any) => p.senderId && p.messageId && p.senderId !== this.userId);

        if (seenParams.length > 0) {
          try {
            this.groupChannel.seenMessage(seenParams);
          } catch (err) {
            console.error("Failed to mark group messages as seen:", err);
          }
        }
      }

      // Fix hasMore logic - should be based on raw response length
      if (filteredUnread.length + filteredRead.length < 100 && (read.length + unread.length < 100)) {
        this.hasMoreGroupMessages = false;
      }

      if (newMessages.length === 0) {
        this.hasMoreGroupMessages = false;
        if (channelId === this.memberChannelId) {
          this.groupChatMessages = [];
          this.chatLoadingError = null;
          this.chatLoader = false;
          this.isInitialLoadingChat = false;
          this.scrollReady = true;
        }
        return;
      }

      newMessages.sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );

      // Fetch message receipts for the page to compute seen and seenBy for sender's messages
      const hasMyMessages = newMessages.some((m: any) => m.sender_id === this.userId);
      if (hasMyMessages && channel && typeof channel.getMessagesReceipts === 'function') {
        try {
          const receiptsTime = (newMessages.length > 0 ? newMessages[newMessages.length - 1]?.createdAt : undefined)
            || requestParams.time
            || new Date().toISOString();

          let receiptsResponse: any = null;
          try {
            receiptsResponse = await channel.getMessagesReceipts({
              time: receiptsTime,
              fetchAll: true
            });
          } catch (e) {
            receiptsResponse = await channel.getMessagesReceipts({ time: receiptsTime });
          }

          const rawList = Array.isArray(receiptsResponse)
            ? receiptsResponse
            : (Array.isArray(receiptsResponse?.receipts) ? receiptsResponse.receipts : []);

          const receiptsByMsgId = new Map<string, { seenUserIds: string[], seenReceipts: { [uId: string]: string } }>();

          rawList.forEach((item: any) => {
            const mId = String(item._id || item.id || item.message_id || item.messageId || '').trim();
            if (!mId) return;

            const innerReceipts = Array.isArray(item.receipts)
              ? item.receipts
              : (Array.isArray(item.seen) ? item.seen : (Array.isArray(item) ? item : []));

            const seenUserIds: string[] = [];
            const seenReceipts: { [uId: string]: string } = {};

            innerReceipts.forEach((r: any) => {
              const uId = r.user_id || r.userId || r._id;
              const rTime = r.time || r.seenAt || r.timestamp || r.updatedAt || r.createdAt;
              if (uId && (r.status === 'seen' || !r.status)) {
                const strUId = String(uId).trim();
                if (!seenUserIds.includes(strUId)) {
                  seenUserIds.push(strUId);
                }
                if (rTime) {
                  seenReceipts[strUId] = rTime;
                }
              }
            });

            receiptsByMsgId.set(mId, { seenUserIds, seenReceipts });
          });

          const currentGroup = this.allGroupUsers.find(g => g.channelId === this.memberChannelId);
          const members = (this.allGroupMembers && this.allGroupMembers.length > 0)
            ? this.allGroupMembers
            : (currentGroup?.members || []);
          const activeMembers = members.filter((m: any) => !m.isRemoved && !m.leftGroup);
          const activeOtherMembers = activeMembers.filter(
            (m: any) => String(m.userId || m._id).trim() !== String(this.userId).trim()
          );
          const activeOtherMemberIds: Set<string> = new Set<string>(
            activeOtherMembers.map((m: any) => String(m.userId || m._id).trim())
          );

          newMessages.forEach((msg: any) => {
            if (msg.sender_id === this.userId) {
              const msgId = String(msg._id || msg.id || msg.messageId || '').trim();
              let receiptEntry = receiptsByMsgId.get(msgId);
              if (!receiptEntry && msgId) {
                for (const [k, v] of receiptsByMsgId.entries()) {
                  if (k === msgId || k.endsWith(msgId) || msgId.endsWith(k)) {
                    receiptEntry = v;
                    break;
                  }
                }
              }

              const seenBySet = new Set<string>();
              if (Array.isArray(msg.seenBy)) {
                msg.seenBy.forEach((id: any) => seenBySet.add(String(id).trim()));
              }
              if (receiptEntry?.seenUserIds) {
                receiptEntry.seenUserIds.forEach((id: string) => seenBySet.add(id));
              }

              const validSeenBy: string[] = Array.from(seenBySet).filter((id: string) => activeOtherMemberIds.has(id));
              const isSeen = activeOtherMemberIds.size > 0 && Array.from(activeOtherMemberIds).every((id: string) => validSeenBy.includes(id));

              msg.seenBy = validSeenBy;
              msg.seen = isSeen;
              if (receiptEntry?.seenReceipts) {
                msg.seenReceipts = { ...(msg.seenReceipts || {}), ...receiptEntry.seenReceipts };
              }
            }
          });
        } catch (err) {
          console.error("Error computing group message seen receipts:", err);
        }
      }

      const newOldestCreatedAt = newMessages[0]?.createdAt;

      if (loadMore) {
        if (
          !newOldestCreatedAt ||
          newOldestCreatedAt === this.oldestMessageCreatedAt
        ) {
          this.hasMoreGroupMessages = false;
          return;
        }
      }

      if (!loadMore || !this.oldestMessageCreatedAt) {
        this.oldestMessageCreatedAt = newOldestCreatedAt;
      }

      const groupedMessages: { [groupId: string]: any[] } = {};
      newMessages.forEach((msg) => {
        const groupId = msg.meta?.groupId;
        if (groupId) {
          if (!groupedMessages[groupId]) {
            groupedMessages[groupId] = [];
          }
          groupedMessages[groupId].push(msg);
        }
      });

      const processedGroups = new Set();
      const processedMessages = newMessages
        .map((msg) => {
          const groupId = msg.meta?.groupId;
          const msgId = msg._id || msg.id || msg.messageId;

          // Skip if this message or any message in its group has been "deleted for me"
          if (groupId && this.deletedForMeMessageIds.has(groupId)) {
            return null;
          }
          if (this.deletedForMeMessageIds.has(msgId)) {
            return null;
          }

          if (groupId && processedGroups.has(groupId)) {
            return null;
          }

          // Filter out generic admin change messages
          if (msg.message && msg.message.toLowerCase().trim() === 'admin changed' && !msg.meta?.subType) {
            return null;
          }

          const isMediaMessage =
            msg.url &&
            (isImage(msg.url) ||
              isVideo(msg.url) ||
              isAudio(msg.url) ||
              isDocument(msg.url));

          if (groupId || isMediaMessage) {
            const messagesToProcess = groupId
              ? groupedMessages[groupId]
              : [msg];

            if (groupId) {
              processedGroups.add(groupId);
            }

            const allRx: any[] = [];
            const seenRx = new Set();
            messagesToProcess.forEach((m: any) => {
              const rx = m.reactions || m.meta?.reactions || [];
              rx.forEach((r: any) => {
                const uId = r.userId || r.user_id || r.sender_id;
                if (uId && !seenRx.has(uId)) {
                  seenRx.add(uId);
                  allRx.push(r);
                }
              });
            });

            // Collect all individual message IDs for carousel mass-deletion
            const _allIds = messagesToProcess
              .map((m: any) => m._id || m.id || m.messageId)
              .filter(Boolean);

            return {
              ...msg,
              meta: this.normalizeLinkMeta(msg.meta),
              files: messagesToProcess.map((m) => ({
                fileUrl: m.url,
                type: getFileType(m.url),
                name: m.message || "File",
              })),
              _allIds,
              currentSlideIndex: 0,
              profileImage: this.profileImageMapping[msg.sender_id] || "",
              name: this.userNameMapping[msg.sender_id] || "User",
              displayName: this.getDisplayName(msg.sender_id),
              url: undefined,
              reactions: allRx
            };
          }

          // Regular text message
          return {
            ...msg,
            meta: this.normalizeLinkMeta(msg.meta),
            profileImage: this.profileImageMapping[msg.sender_id] || "",
            name: this.userNameMapping[msg.sender_id] || "User",
            displayName: this.getDisplayName(msg.sender_id),
            reactions: msg.reactions || msg.meta?.reactions || []
          };
        })
        .filter((msg) => msg !== null);


      if (loadMore) {
        // Support scroll restoration for group chat
        const container = this.scrollContainer?.nativeElement || document.getElementById('groupChatContainer');
        let prevScrollHeight = 0, prevScrollTop = 0;
        if (container) {
          prevScrollHeight = container.scrollHeight;
          prevScrollTop = container.scrollTop;
        }

        this.groupChatMessages = [
          ...processedMessages,
          ...this.groupChatMessages,
        ];

        if (container && processedMessages.length > 0) {
          this.cdRef.detectChanges();
          const newScrollHeight = container.scrollHeight;
          container.scrollTop = prevScrollTop + (newScrollHeight - prevScrollHeight);
        }
      } else {
        const isCurrentActive = this.groupChatBox && Boolean(channelId && channelId === this.memberChannelId);
        const mergedMessages = processedMessages;

        if (isCurrentActive) {
          this.groupChatMessages = this.injectPendingUploads(this.memberChannelId, mergedMessages);
          this.chatLoadingError = null;
          this.chatLoader = false;
          this.isInitialLoadingChat = false;
          this.needsScrollToBottomGroup = true;
          this.cdRef.detectChanges();
          const container = this.scrollContainer?.nativeElement || document.getElementById('groupChatContainer');
          if (container) {
            container.scrollTop = container.scrollHeight;
          }
          this.scrollReady = true;
          if (this.groupChatMessages.length > 0) {
            const lastMsg = this.groupChatMessages[this.groupChatMessages.length - 1];
            this.updateLocalChatLastMessage(this.memberChannelId, lastMsg, true);
          }
        }

        if (channelId) {
          this.cachedGroupMessages[channelId] = mergedMessages;
          this.cachedGroupScrollReady[channelId] = true;
          if (this.chatCacheService && mergedMessages.length > 0) {
            this.chatCacheService.saveMessages(channelId, mergedMessages);
          }
        }
      }

      this.unreadMessageCount = unread.length;
      this.processGroupMessages();
      this.updateCacheForActiveChannel();
    } catch (err) {
      console.error("Error fetching messages:", err);
      if (!loadMore && channelId === this.memberChannelId) {
        this.chatLoadingError = "Failed to load messages. Tap to retry.";
      }
    } finally {
      this.isLoadingGroupMessages = false;
      if (!loadMore && channelId === this.memberChannelId) {
        this.isInitialLoadingChat = false;
        this.chatLoader = false;
        this.scrollReady = true;
      }
    }
  }

  //Pagination of Group chat
  showJumpToLatest = false;
  scrollThrottleTimer: any;
  @ViewChild("scrollContainer") scrollContainer!: ElementRef;
  threshold = 95;
  onGroupChatScroll(event: any) {
    if (this.isProgrammaticScroll) return;
    const el = event.target;
    const scrollTop = el.scrollTop;
    const scrollHeight = el.scrollHeight;
    const clientHeight = el.clientHeight;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    this.showJumpToLatest = distanceFromBottom > 400;

    if (
      !this.scrollReady ||
      this.isLoadingGroupMessages ||
      !this.hasMoreGroupMessages
    )
      return;

    const nearTopThreshold = 100;
    if (scrollTop <= nearTopThreshold) {
      console.log(
        `Scrolled near top (${scrollTop.toFixed(2)}px), loading more...`
      );
      this.getAllGroupMessages(this.groupChannel, true, this.memberChannelId); // loadMore = true
    }
  }

  isProgrammaticScroll: boolean = false;

  //for self user
  getOtherUserIdFromChannel(channelId: string, selfId: string): string | null {
    const chat = this.chatMap[channelId];
    const members = chat?.members || [];
    const other = members.find((m) => m.userId !== selfId);
    return other?.userId || null;
  }

  //Redirection of Searched Message
  scrollToSearchedMessage(messageId: string, type: "single" | "group", attempt: number = 0) {
    if (!messageId) return;

    if (attempt === 0) {
      this.targetSearchMessageId = messageId;
    }

    // Ensure we don't conflict with normal scroll to bottom
    this.needsScrollToBottomSingle = false;
    this.needsScrollToBottomGroup = false;

    if (attempt > 35) {
      console.warn("Message not found after maximum loading attempts for ID:", messageId);
      if (this.targetSearchMessageId === messageId) {
        this.targetSearchMessageId = null;
      }
      return;
    }

    // Check if we are still looking for THIS message
    if (this.targetSearchMessageId !== messageId) {
      console.log("Stopping scroll animation: search target changed or cancelled for ID:", messageId);
      return;
    }

    const checkAndScroll = () => {
      // Re-check target inside closure
      if (this.targetSearchMessageId !== messageId) return;

      let targetDOMId = messageId;
      const tArray = type === "single" ? this.singleChatMessages : this.groupChatMessages;

      // 1. Check if the message is already in memory, or nested inside a carousel/grouped metadata
      const localMsg = tArray.find(m =>
        (m._id && String(m._id) === String(messageId)) ||
        (m.id && String(m.id) === String(messageId)) ||
        (m.messageId && String(m.messageId) === String(messageId)) ||
        (m._allIds && m._allIds.some((id: any) => String(id) === String(messageId)))
      );

      if (localMsg) {
        // If it was grouped, the DOM ID uses the parent message's ID
        targetDOMId = localMsg._id || localMsg.id || localMsg.messageId || targetDOMId;
      }

      const el = document.getElementById("msg-" + targetDOMId);
      if (el) {
        console.log("Found message element, scrolling to ID:", targetDOMId);
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Suspend pagination trigger during the smooth travel
        this.isProgrammaticScroll = true;
        setTimeout(() => this.isProgrammaticScroll = false, 1500);

        this.highlightMessage(el);
        this.targetSearchMessageId = null;
      } else {
        if (localMsg) {
          // We found it in the Javascript array, but Angular hasn't updated the DOM yet. 
          // Wait for the render cycle without triggering more API requests.
          console.log("Message found in data array but DOM not ready. Waiting for render...", targetDOMId);
          this.cdRef.detectChanges();
          setTimeout(() => this.scrollToSearchedMessage(messageId, type, attempt), 200);
          return;
        }

        console.warn("Message not loaded. Attempting to load more... (Attempt " + attempt + ") for ID:", messageId);

        const channel = type === "single" ? this.singleChannel : this.groupChannel;
        // If channel is still connecting, wait for it
        if (!channel && attempt < 15) {
          console.log(`Channel for ${type} chat still connecting (attempt ${attempt}). Retrying...`);
          setTimeout(() => this.scrollToSearchedMessage(messageId, type, attempt + 1), 400);
          return;
        }

        let shouldTryAgain = false;
        let isLoading = (type === "single") ? this.isLoadingSingleMessages : this.isLoadingGroupMessages;
        let hasMore = (type === "single") ? this.hasMoreSingleMessages : this.hasMoreGroupMessages;

        if (hasMore) {
          shouldTryAgain = true;
          if (!isLoading && channel) {
            if (type === "single") {
              this.getAllMessages(this.singleChannel, true, this.activeChannel);
            } else {
              this.getAllGroupMessages(this.groupChannel, true, this.memberChannelId);
            }
          }
        }

        if (shouldTryAgain) {
          // If a network request is loading, wait longer between retries to avoid wasting attempts
          const retryDelay = isLoading ? 700 : 350;
          setTimeout(() => this.scrollToSearchedMessage(messageId, type, attempt + 1), retryDelay);
        } else {
          console.warn("No more messages to load, or correct channel not found for ID:", messageId);
          if (this.targetSearchMessageId === messageId) {
            this.targetSearchMessageId = null;
          }
        }
      }
    };

    // If it's the first attempt, try immediately without timeout to feel faster
    if (attempt === 0) {
      checkAndScroll();
    } else {
      setTimeout(checkAndScroll, 200);
    }
  }

  highlightMessage(el: HTMLElement) {
    if (!el) return;
    el.classList.remove('highlight-message');
    // Force DOM reflow to restart animation reliably
    void el.offsetWidth;
    el.classList.add('highlight-message');
    setTimeout(() => {
      el.classList.remove('highlight-message');
    }, 2600);
  }

  activeSingleChannelId: string | null = null;
  private receivedMessageIds = new Set<string>();
  // Helper to update local group data
  updateLocalGroupData(channelId: string, data: any) {
    if (!channelId) return;
    // Update in all lists
    const updateList = (list: any[]) => {
      const index = list.findIndex(g => g.channelId === channelId);
      if (index !== -1) {
        list[index] = { ...list[index], ...data };
      }
    };

    updateList(this.allGroupUsers);
    updateList(this.filteredGroupUsers);
    updateList(this.sortedChats);

    // Update active chat variables if this is the current group
    if (this.memberChannelId === channelId) {
      if (data.groupName) {
        this.groupName = data.groupName;
        this.groupChatName = data.groupName;
        this.groupChatUsername = data.groupName;
      }
      if (data.groupDescription !== undefined) {
        this.groupDescription = data.groupDescription;
        this.groupChatDescription = data.groupDescription;
      }
      if (data.groupImg) {
        const img = data.groupImg;
        this.groupChatProfileImg = img + (img.includes('?') ? '&' : '?') + 't=' + Date.now();
        this.groupUserProfileImg = this.groupChatProfileImg;
      }

      // Live update for Edit Mode if open
      // Edit mode updates are now handled by the group-info-panel component
    }
  }

  markMessageAsSeen(message: any, isGroup: boolean = false) {
    if (!message) return;
    const senderId = message.sender_id || message.sid;
    const messageId = message._id || message.id || message.messageId;

    if (!messageId || !senderId) return;

    if (senderId === this.userId) return;

    const params = [{
      senderId: senderId,
      messageId: messageId
    }];

    // Verify the message belongs to the currently active channel before emitting seen
    const messageSenderId = message.sender_id || message.sid;
    const messageChannelId = message.meta?.channel_id || message.meta?.channelId || message.channelId || message.channel_id;

    try {
      if (isGroup && this.groupChatBox && this.groupChannel) {
        // Guard: only mark seen for the active group channel
        if (messageChannelId && String(messageChannelId).trim() !== String(this.memberChannelId).trim()) {
          console.warn(`markMessageAsSeen: Skipping seen for group message from channel ${messageChannelId} — currently on ${this.memberChannelId}`);
          return;
        }
        this.groupChannel.seenMessage(params);
        this.chatService.emitChatRead(this.memberChannelId);
      } else if (!isGroup && this.singleChatBox && this.singleChannel) {
        // Guard: only mark seen when the sender is the active single chat user
        const activeOtherUser = this.singleUser?.userId || this.singleUser?._id || this.singleUser?.id;
        if (messageChannelId && String(messageChannelId).trim() !== String(this.singleUser?.channelId || this.activeChannel).trim()) {
          console.warn(`markMessageAsSeen: Skipping seen for single message from channel ${messageChannelId} — currently on ${this.singleUser?.channelId}`);
          return;
        }
        this.singleChannel.seenMessage(params);
        this.chatService.emitChatRead(this.singleUser?.channelId || this.activeChannel);
        // Manually emit message-seen for single chat to ensure real-time update for sender
        if (this.singleChannel.socket && typeof this.singleChannel.socket.emit === 'function') {
          this.singleChannel.socket.emit('message-seen', {
            ...params[0],
            channelId: this.singleUser?.channelId || this.activeChannel
          });
        }
      }
    } catch (err) {
      console.error("Error marking message as seen:", err);
    }
  }

  inComingGroupListenerMessages(msg: any) {
    console.log("Incoming listener Message", msg)
    const incoming = (msg?.message && typeof msg.message === 'object') ? msg.message : msg;
    const meta = incoming?.meta || msg?.meta || {};
    const messageId = incoming?._id || incoming?.id || msg?._id || msg?.id || incoming?.messageId || msg?.messageId;
    const actualGroupChannelId =
      msg?.channelId ||
      msg?.channel_id ||
      msg?.data?.channelId ||
      msg?.data?.channel_id ||
      msg?.message?.channelId ||
      msg?.message?.channel_id ||
      incoming?.channelId ||
      incoming?.channel_id ||
      meta?.channelId ||
      meta?.channel_id;

    if (actualGroupChannelId && String(actualGroupChannelId).trim() !== String(this.memberChannelId).trim()) {
      console.warn(`inComingGroupListenerMessages: Skipping message for different group. Expected ${this.memberChannelId}, got ${actualGroupChannelId}`);
      this.appendMessageToBackgroundCache(actualGroupChannelId, msg, true);
      return;
    } else if (!actualGroupChannelId) {
      if (msg.meta?.isGroupedUpload || msg.message?.meta?.isGroupedUpload) {
        console.warn("Allowing grouped upload fallback due to missing channelId in group chat");
      } else {
        // System messages (pin/unpin) and other messages with no channelId:
        // validate the sender is a member of the currently active group.
        const sysMsg = incoming || msg;
        const sysSenderId = sysMsg?.sender_id ?? sysMsg?.sid ?? msg?.sender_id;
        const isGroupMember = sysSenderId && this.allGroupMembers?.some((m: any) => String(m.userId) === String(sysSenderId));
        const isSelf = String(sysSenderId) === String(this.userId);
        if (!isGroupMember && !isSelf) {
          console.warn(`inComingGroupListenerMessages: Skipping no-channelId message from ${sysSenderId} — not a member of active group ${this.memberChannelId}`);
          return;
        }
        console.warn(`inComingGroupListenerMessages: Allowing no-channelId message from group member ${sysSenderId}`);
      }
    }

    // Check for tempId first (for manually pushed forwarded messages)
    const tempId = meta?.tempId || incoming?.tempId;
    if (tempId && this.receivedMessageIds.has(`temp:${tempId}`)) {
      console.warn("Duplicate message skipped (tempId):", tempId);
      return;
    }

    // Check for groupId (for grouped uploads/carousels)
    const duplicateGroupId = meta?.groupId || meta?.data?.groupId;
    if (duplicateGroupId && this.receivedMessageIds.has(`groupId:${duplicateGroupId}`)) {
      console.warn("Duplicate message skipped (groupId):", duplicateGroupId);
      return;
    }

    // For pin/unpin system messages the SDK sets messageId = the PINNED message's ID.
    // Using that as the dedup key means rapid pins of different messages all share
    // the same key → 2nd, 3rd… pins are wrongly dropped. Dedup by own _id instead.
    const isGroupSysPin =
      meta?.subType === 'Message Pinned' ||
      meta?.subType === 'Message UnPinned' ||
      !!meta?.pinnedBy;
    const groupDedupId = isGroupSysPin
      ? (incoming?._id || incoming?.id || msg?._id || msg?.id)
      : messageId;

    if (groupDedupId && this.receivedMessageIds.has(groupDedupId)) {
      console.warn("Duplicate message skipped:", groupDedupId);
      return;
    }
    if (groupDedupId) {
      this.receivedMessageIds.add(groupDedupId);
    }

    const senderId =
      msg?.sender_id ??
      incoming?.sender_id ??
      incoming?.sid ??
      msg?.sid;

    if (senderId && this.allGroupMembers && this.allGroupMembers.length > 0) {
      const isMember = this.allGroupMembers.some((m: any) => String(m.userId) === String(senderId));
      if (String(senderId) !== String(this.userId) && !isMember) {
        console.warn(`inComingGroupListenerMessages: Skipping message because it's from ${senderId} who is not a member of this active group chat. Message doesn't belong to this active chat.`);
        return;
      }
    }

    const isSysMsg =
      meta?.type === 'system' ||
      meta?.subType;

    // Extract content early for duplicate check
    let messageContent = "";
    if (typeof incoming.message === 'string') {
      messageContent = incoming.message;
    } else if (incoming.message && typeof incoming.message === 'object') {
      messageContent = incoming.message.message || incoming.message.text || incoming.message.content || "";
    } else {
      messageContent = incoming.text || incoming.content || "";
    }

    // Reformat pin system messages that contain raw filenames
    if (isSysMsg && messageContent) {
      messageContent = this.reformatPinSystemMessage(messageContent);

      // Deduplicate system messages by content and time
      const incomingPinnedId = meta?.messageId || incoming?.messageId || meta?.data?.messageId || meta?.pinnedMessageId;

      const exists = this.groupChatMessages.some((m: any) => {
        const isMSystem = m.meta?.type === 'system' || m.meta?.subType || m.meta?.pinnedBy;
        if (!isMSystem || m.message !== messageContent) return false;

        const existingPinnedId = m.meta?.messageId || m.messageId || m.meta?.data?.messageId || m.meta?.pinnedMessageId;

        if (incomingPinnedId && existingPinnedId) {
          return incomingPinnedId === existingPinnedId;
        }

        return Math.abs(new Date(m.createdAt || 0).getTime() - new Date(incoming.createdAt || incoming.updatedAt || Date.now()).getTime()) < 5000;
      });

      if (exists) {
        console.warn("Duplicate system message blocked by content deduplication in Group Chat:", messageContent, "PinnedId:", incomingPinnedId);
        return;
      }
    }

    if (senderId === this.userId && !isSysMsg) {

      const tempId = meta?.tempId || incoming?.tempId;
      if (tempId) {
        const localMsg = this.groupChatMessages.find((m: any) => m.meta?.tempId === tempId || m.tempId === tempId);
        if (localMsg) {
          console.log("Matched local message by tempId:", tempId);
          localMsg._id = messageId;
          localMsg.id = messageId;
          if (incoming.createdAt) localMsg.createdAt = incoming.createdAt;
          this.receivedMessageIds.add(messageId);
          return;
        }
      }

      const echoFileUrl = meta?.fileUrl || incoming?.url || (incoming.files && incoming.files[0]?.fileUrl);
      if (echoFileUrl) {
        const localMsg = this.groupChatMessages.find((m: any) =>
          (m.url === echoFileUrl || m.meta?.fileUrl === echoFileUrl || (m.files && m.files[0]?.fileUrl === echoFileUrl)) &&
          m.sender_id === this.userId
        );
        if (localMsg) {
          console.log('Matched own group file echo by URL, updating _id:', messageId);
          localMsg._id = messageId;
          localMsg.id = messageId;
          if (incoming.createdAt) localMsg.createdAt = incoming.createdAt;
          const echoReplyTo = incoming.replyTo || meta?.replyTo;
          if (echoReplyTo && !localMsg.replyTo) {
            localMsg.replyTo = echoReplyTo;
            localMsg.meta = { ...(localMsg.meta || {}), replyTo: echoReplyTo };
          }
          this.receivedMessageIds.add(messageId);
          return;
        }
      }

      const exists = this.groupChatMessages.some((m: any) =>
        (m._id && m._id === messageId) || (m.id && m.id === messageId) || (m.messageId && m.messageId === messageId) ||
        (!m._id && !m.id && !m.messageId &&
          m.message === messageContent &&
          Math.abs(new Date(m.createdAt).getTime() - new Date(incoming.createdAt || incoming.updatedAt).getTime()) < 5000)
      );
      if (exists) {
        const localMsg = this.groupChatMessages.find((m: any) => !m._id && !m.id && !m.messageId && m.message === messageContent);
        if (localMsg) {
          localMsg._id = messageId;
          localMsg.id = messageId;
          this.receivedMessageIds.add(messageId);
        }
        return;
      }
    }

    if (isSysMsg) {
      const systemMeta = meta.data || meta;
      const updates: any = {};

      const newName = meta.groupName || meta.data?.groupName;
      const newDesc = meta.groupDescription || meta.data?.groupDescription || meta.description || meta.data?.description;
      const newImg = meta.groupImg || meta.data?.groupImg;

      if (newName) updates.groupName = newName;
      const descKey = ['groupDescription', 'description'].find(k => meta[k] !== undefined || meta.data?.[k] !== undefined);
      const resolvedDesc = descKey ? (meta[descKey] ?? meta.data?.[descKey]) : undefined;
      if (resolvedDesc !== undefined) updates.groupDescription = resolvedDesc;
      if (newImg) updates.groupImg = newImg;

      if (Object.keys(updates).length > 0) {
        const chId = msg.channelId || meta.channelId || this.memberChannelId;
        if (updates.groupName === true) delete updates.groupName;
        if (updates.groupDescription === true) delete updates.groupDescription;

        this.updateLocalGroupData(chId, updates);
      }
    }

    const currentGroup = this.allGroupUsers.find(g => g.channelId === this.memberChannelId);
    if (currentGroup && currentGroup.members) {
      const currentUserMember = currentGroup.members.find((m: any) => m.userId === this.userId);
      if (currentUserMember && (currentUserMember.leftGroup || currentUserMember.isRemoved)) {
        const messageCreatedAt = new Date(incoming.createdAt || incoming.updatedAt);
        const exitedAt = currentUserMember.exitedAt ? new Date(currentUserMember.exitedAt) : null;
        if (exitedAt && messageCreatedAt > exitedAt) {
          console.log(`Skipping message - sent after user exited`);
          return;
        }
        if (!exitedAt && (currentUserMember.leftGroup || currentUserMember.isRemoved)) {
          console.log("Skipping message - user has exited group");
          return;
        }
      }
    }

    let fileUrl = meta?.fileUrl || incoming?.url || (typeof incoming?.file === 'string' ? incoming.file : incoming?.file?.url);
    if (!fileUrl && typeof incoming.message === 'object' && incoming.message !== null) {
      fileUrl = incoming.message.url || incoming.message.file || incoming.message.fileUrl;
    }
    const metaGroupId = meta?.groupId || meta?.data?.groupId;
    const isGroupedUpload = meta?.isGroupedUpload || !!metaGroupId;
    const groupId = metaGroupId;

    let previewMessage = messageContent;
    const hasFiles = incoming.files && incoming.files.length > 0;

    if ((messageContent === 'Sent an attachment' || messageContent.toLowerCase() === 'sent an attachment') && !fileUrl && !hasFiles) {
      if (messageId) {
        this.receivedMessageIds.delete(messageId);
      }
      console.log("Received placeholder without URL in Group Chat. Skipping to avoid reload.");
      return;
    }

    const effectiveUrl = fileUrl || (hasFiles ? incoming.files[0].fileUrl : null);
    const isGif = this.isGifMessage(incoming);

    if ((messageContent === 'Sent an attachment' || messageContent.toLowerCase() === 'sent an attachment') && effectiveUrl) {
      previewMessage = this.generateFilePreview(effectiveUrl);
      // Set messageContent to filename if it was "Sent an attachment"
      if (isGif) {
        messageContent = (messageContent === 'Sent an attachment' || messageContent.toLowerCase() === 'sent an attachment') ? "" : messageContent;
      } else {
        messageContent = "";
      }
    }

    if (isGroupedUpload && groupId && !isGif) {
      // Robust find with String comparison
      const existingGroupedMsg = this.groupChatMessages.find(m => {
        const mGroupId = m.meta?.groupId || m.meta?.data?.groupId;
        return mGroupId && String(mGroupId) === String(groupId);
      });

      if (existingGroupedMsg) {
        const filesToAdd: any[] = (incoming?.files && incoming.files.length > 0)
          ? incoming.files
          : ((meta?.files && meta.files.length > 0)
            ? meta.files
            : (effectiveUrl ? (Array.isArray(effectiveUrl) ? effectiveUrl : [effectiveUrl]) : []));

        let anyAppended = false;
        for (const item of filesToAdd) {
          const itemUrl = typeof item === 'string' ? item : (item?.fileUrl || item?.url);
          if (!itemUrl) continue;
          const itemName = stripTimestampPrefix((typeof item === 'object' && (item?.name || item?.ogFileName)) ? (item.name || item.ogFileName) : getCleanFileName('', itemUrl));
          const itemType = (typeof item === 'object' && item?.type) ? item.type : getFileType(itemUrl);

          const alreadyExists = existingGroupedMsg.files.some((f: any) => f.fileUrl === itemUrl);
          if (!alreadyExists) {
            const localFileIdx = existingGroupedMsg.files.findIndex((f: any) => f.isLocal && (f.name === itemName || f.fileUrl === itemUrl));
            if (localFileIdx !== -1) {
              existingGroupedMsg.files[localFileIdx] = {
                fileUrl: itemUrl,
                type: itemType,
                name: itemName,
                size: item.size
              };
            } else {
              const firstLocalIdx = existingGroupedMsg.files.findIndex((f: any) => f.isLocal);
              if (firstLocalIdx !== -1) {
                existingGroupedMsg.files[firstLocalIdx] = {
                  fileUrl: itemUrl,
                  type: itemType,
                  name: itemName,
                  size: item.size
                };
              } else {
                existingGroupedMsg.files.push({
                  fileUrl: itemUrl,
                  type: itemType,
                  name: itemName,
                  size: item.size
                });
              }
            }
            anyAppended = true;
          }
        }

        if (anyAppended || (incoming.meta?.caption && (!existingGroupedMsg.meta?.caption || existingGroupedMsg.meta.caption !== incoming.meta.caption))) {
          if (incoming.meta?.caption && (!existingGroupedMsg.meta?.caption || existingGroupedMsg.meta.caption !== incoming.meta.caption)) {
            if (!existingGroupedMsg.meta) {
              existingGroupedMsg.meta = {};
            }
            existingGroupedMsg.meta = { ...existingGroupedMsg.meta, caption: incoming.meta.caption };
          }
          delete existingGroupedMsg._processedFiles;
          // Track all individual message IDs for carousel mass-deletion
          const existingIds: string[] = existingGroupedMsg._allIds || [];
          if (messageId && !existingIds.includes(messageId)) {
            existingIds.push(messageId);
          }
          const incomingReplyTo = incoming.replyTo || meta?.replyTo || existingGroupedMsg.replyTo || existingGroupedMsg.meta?.replyTo;
          const updatedMsg = {
            ...existingGroupedMsg,
            _id: messageId,
            id: messageId,
            _allIds: existingIds,
            ...(incomingReplyTo ? { replyTo: incomingReplyTo } : {})
          };
          if (incomingReplyTo) {
            updatedMsg.meta = { ...(updatedMsg.meta || {}), replyTo: incomingReplyTo };
          }
          updatedMsg.files = [...existingGroupedMsg.files];

          const msgIndex = this.groupChatMessages.indexOf(existingGroupedMsg);
          if (msgIndex !== -1) {
            this.groupChatMessages[msgIndex] = updatedMsg;
            this.groupChatMessages = [...this.groupChatMessages];
          }

          console.log("File appended to existing grouped message");
          this.processGroupMessages();
          this.cdRef.detectChanges();
          setTimeout(() => this.scrollToBottom(), 50);
        }
        return;
      }
      const incomingGroupFiles = (incoming?.files && incoming.files.length > 0)
        ? incoming.files
        : ((meta?.files && meta.files.length > 0)
          ? meta.files
          : (fileUrl ? (Array.isArray(fileUrl) ? fileUrl.map((u: any) => ({
            fileUrl: typeof u === 'string' ? u : (u?.fileUrl || u?.url),
            type: (typeof u === 'object' && u?.type) ? u.type : getFileType(typeof u === 'string' ? u : (u?.fileUrl || u?.url)),
            name: stripTimestampPrefix((typeof u === 'object' && (u?.name || u?.ogFileName)) ? (u.name || u.ogFileName) : getCleanFileName('', typeof u === 'string' ? u : (u?.fileUrl || u?.url))),
            size: typeof u === 'object' ? u?.size : undefined
          })) : [{
            fileUrl: fileUrl,
            type: getFileType(fileUrl),
            name: stripTimestampPrefix(getCleanFileName('', fileUrl))
          }]) : []));

      const incomingReplyTo = incoming.replyTo || meta?.replyTo;
      const newMsg = {
        ...incoming,
        _id: messageId,
        id: messageId,
        // Only set messageContent if it's not "Sent an attachment"
        message: "",
        preview: previewMessage,
        sender_id: senderId,
        sid: senderId,
        createdAt: incoming.createdAt,
        updatedAt: incoming.updatedAt || incoming.createdAt,
        profileImage: this.profileImageMapping[senderId] ?? null,
        name: this.userNameMapping[senderId] ?? null,
        displayName: this.getDisplayName(senderId),
        meta: incomingReplyTo ? { ...meta, replyTo: incomingReplyTo } : meta,
        ...(incomingReplyTo ? { replyTo: incomingReplyTo } : {}),
        files: incomingGroupFiles,
        currentSlideIndex: 0,
        reactions: incoming.reactions || incoming.meta?.reactions || []
      };
      this.groupChatMessages = [...this.groupChatMessages, newMsg];
    } else if ((fileUrl || hasFiles) && !isGif) {
      if (!hasFiles) {
        if (messageContent === 'Sent an attachment' || messageContent.toLowerCase() === 'sent an attachment') {
          messageContent = getCleanFileName('', fileUrl);
        }
        previewMessage = this.generateFilePreview(fileUrl);
      }
      const fileNameForSingle = hasFiles && incoming.files[0].name ? incoming.files[0].name : (messageContent || "File");

      const incomingGroupFiles = (incoming?.files && incoming.files.length > 0)
        ? incoming.files
        : ((meta?.files && meta.files.length > 0)
          ? meta.files
          : (fileUrl ? (Array.isArray(fileUrl) ? fileUrl.map((u: any) => ({
            fileUrl: typeof u === 'string' ? u : (u?.fileUrl || u?.url),
            type: (typeof u === 'object' && u?.type) ? u.type : getFileType(typeof u === 'string' ? u : (u?.fileUrl || u?.url)),
            name: (typeof u === 'object' && (u?.name || u?.ogFileName)) ? (u.name || u.ogFileName) : getCleanFileName('', typeof u === 'string' ? u : (u?.fileUrl || u?.url)),
            size: typeof u === 'object' ? u?.size : undefined
          })) : [{
            fileUrl: fileUrl,
            type: getFileType(fileUrl),
            name: getCleanFileName(fileNameForSingle, fileUrl),
          }]) : []));

      const incomingReplyTo = incoming.replyTo || meta?.replyTo;
      const newMsg = {
        ...incoming,
        _id: messageId,
        id: messageId,
        message: messageContent,
        preview: previewMessage,
        sender_id: senderId,
        sid: senderId,
        createdAt: incoming.createdAt,
        updatedAt: incoming.updatedAt || incoming.createdAt,
        profileImage: this.profileImageMapping[senderId] ?? null,
        name: this.userNameMapping[senderId] ?? null,
        displayName: this.getDisplayName(senderId),
        meta: incomingReplyTo ? { ...this.normalizeLinkMeta(meta), replyTo: incomingReplyTo } : this.normalizeLinkMeta(meta),
        ...(incomingReplyTo ? { replyTo: incomingReplyTo } : {}),
        files: incomingGroupFiles,
        currentSlideIndex: 0,
        url: Array.isArray(fileUrl) ? (fileUrl[0] || "") : fileUrl,
        reactions: incoming.reactions || incoming.meta?.reactions || []
      };
      this.groupChatMessages = [...this.groupChatMessages, newMsg];
    } else {
      if (messageContent && messageContent.toLowerCase().trim() === 'admin changed') {
        console.log("Skipping generic 'admin changed' message in favor of structured updates");
        return;
      }

      const incomingReplyTo = incoming.replyTo || meta?.replyTo;
      const newMsg = {
        ...incoming,
        _id: messageId,
        id: messageId,
        message: messageContent,
        preview: previewMessage,
        sender_id: senderId,
        sid: senderId,
        createdAt: incoming.createdAt,
        updatedAt: incoming.updatedAt || incoming.createdAt,
        profileImage: this.profileImageMapping[senderId] ?? null,
        name: this.userNameMapping[senderId] ?? null,
        displayName: this.getDisplayName(senderId),
        meta: incomingReplyTo ? { ...this.normalizeLinkMeta(meta), replyTo: incomingReplyTo } : this.normalizeLinkMeta(meta),
        ...(incomingReplyTo ? { replyTo: incomingReplyTo } : {}),
        reactions: incoming.reactions || incoming.meta?.reactions || []
      };
      this.groupChatMessages = [...this.groupChatMessages, newMsg];
    }

    console.log("New group message added");
    this.processGroupMessages();
    this.cdRef.detectChanges();
    setTimeout(() => this.scrollToBottom(), 50);
    this.markMessageAsSeen(incoming, true);
    this.cdRef.detectChanges();

  }

  // SINGLE CHAT INCOMING MESSAGES
  handleIncomingMessage = (msg: any) => {
    console.log("handleIncomingMessage received:", msg);
    const _incoming = (msg?.message && typeof msg.message === 'object') ? msg.message : msg;
    const messageId = _incoming?._id || _incoming?.id || _incoming?.messageId || msg?._id || msg?.id || msg?.messageId;
    // Detect system messages (pin, unpin)
    const _meta = _incoming?.meta || msg?.meta || {};
    const _senderId0 = msg?.sender_id ?? _incoming?.sender_id ?? _incoming?.sid ?? msg?.sid;
    // For pin/unpin system messages the SDK often puts the PINNED message's ID in
    // `_incoming.messageId` (i.e. meta.messageId). If we use that as the dedup key,
    // the 2nd, 3rd… rapid pin events are incorrectly treated as duplicates of the
    // first one (same pinned-msg-id == same key). Instead we only dedup by the
    // system-notification's own `_id` / `id` field, and skip `messageId` for that lookup.
    const isSysMsgEarly = (
      _meta?.subType === 'Message Pinned' ||
      _meta?.subType === 'Message UnPinned' ||
      !!_meta?.pinnedBy
    );
    // Build the dedup ID: for system pin msgs use only _id/id, not messageId.
    const dedupId = isSysMsgEarly
      ? (_incoming?._id || _incoming?.id || msg?._id || msg?.id)
      : messageId;

    if (dedupId) {
      const dedupKey = `msg:${dedupId}`;
      if (this.receivedMessageIds.has(dedupKey)) {
        console.warn("Duplicate receiver message skipped (messageId):", dedupId);
        return;
      }
      this.receivedMessageIds.add(dedupKey);
    }
    const tempId = _meta?.tempId || msg?.tempId || msg?.message?.meta?.tempId;
    if (tempId && this.receivedMessageIds.has(`temp:${tempId}`)) {
      console.warn("Duplicate message skipped (tempId) in single chat:", tempId);
      return;
    }

    // Check for groupId (for grouped uploads/carousels)
    const duplicateGroupId = _meta?.groupId || _meta?.data?.groupId || msg?.meta?.groupId;
    if (duplicateGroupId && this.receivedMessageIds.has(`groupId:${duplicateGroupId}`)) {
      console.warn("Duplicate message skipped (groupId) in single chat:", duplicateGroupId);
      return;
    }

    const isSysMsg = isSystemMessage(_incoming || msg);

    let senderId =
      msg?.sender_id ??
      _incoming?.sender_id ??
      _incoming?.sid ??
      msg?.sid;
    const tempIdForSelfCheck = _meta?.tempId || msg?.tempId || msg?.message?.meta?.tempId;
    if (tempIdForSelfCheck) {
      const isMine = this.singleChatMessages.some((m: any) =>
        (m.meta?.tempId === tempIdForSelfCheck || m.tempId === tempIdForSelfCheck) && m.sender_id === this.userId
      );
      if (isMine) {
        senderId = this.userId;
      }
    }

    if (senderId && this.singleUser) {
      const allowedSender1 = this.userId;
      const allowedSender2 = this.singleUser.userId || this.singleUser._id || this.singleUser.id;
      const isForward = _meta?.isForwarded === true || _meta?.isForwarded === 'true' || msg?.isForwarded || _incoming?.isForwarded;

      if (senderId !== allowedSender1 && senderId !== allowedSender2 && !isSysMsg && !isForward) {
        console.warn(`handleIncomingMessage: Skipping message because it's from ${senderId} which is neither me nor the current single user ${allowedSender2}. Message doesn't belong to this active chat.`);
        return;
      }
    }

    if (senderId === this.userId && !isSysMsg) {

      if (!messageId) return;
      const tempId = msg.meta?.tempId || msg.tempId || msg.message?.meta?.tempId;
      if (tempId) {
        const localMsg = this.singleChatMessages.find((m: any) => m.meta?.tempId === tempId || m.tempId === tempId);
        if (localMsg) {
          localMsg._id = messageId;
          localMsg.id = messageId;
          if (msg.createdAt) localMsg.createdAt = msg.createdAt;
          return;
        }
      }
      const echoFileUrl = msg.meta?.fileUrl || msg.url;
      if (echoFileUrl) {
        const localMsg = this.singleChatMessages.find((m: any) =>
          (m.url === echoFileUrl || m.meta?.fileUrl === echoFileUrl) &&
          Math.abs(new Date(m.createdAt).getTime() - new Date(msg.createdAt || msg.updatedAt || Date.now()).getTime()) < 15000
        );
        if (localMsg) {
          console.log('Matched own file echo by URL, updating _id:', messageId);
          localMsg._id = messageId;
          localMsg.id = messageId;
          if (msg.createdAt) localMsg.createdAt = msg.createdAt;
          return;
        }
      }
      const contentStr = typeof msg.message === 'string' ? msg.message : (msg.message?.text || msg.message?.content || '');
      const exists = this.singleChatMessages.some((m: any) =>
        (m._id && m._id === messageId) || (m.id && m.id === messageId) || (m.messageId && m.messageId === messageId) ||
        (!m._id && !m.id && !m.messageId &&
          m.message === contentStr &&
          Math.abs(new Date(m.createdAt).getTime() - new Date(msg.createdAt || msg.updatedAt).getTime()) < 5000)
      );
      if (exists) {
        const localMsg = this.singleChatMessages.find((m: any) => !m._id && !m.id && !m.messageId && m.message === contentStr);
        if (localMsg) {
          localMsg._id = messageId;
          localMsg.id = messageId;
        }
        return;
      }
    } else if (senderId !== this.userId && !isSysMsg && messageId) {
      const existingReceiverIdx = this.singleChatMessages.findIndex((m: any) =>
        (m._id && m._id === messageId) || (m.id && m.id === messageId) || (m.messageId && m.messageId === messageId)
      );
      if (existingReceiverIdx !== -1) {
        console.log('Matched existing receiver message, updating:', messageId);
        const incoming = (msg?.message && typeof msg.message === 'object') ? msg.message : msg;
        const existing = this.singleChatMessages[existingReceiverIdx];
        const updatedMsg = {
          ...existing,
          ...incoming,
          _id: messageId,
          id: messageId,
          meta: this.normalizeLinkMeta(msg.meta || incoming.meta || existing.meta),
          // Preserve the message text if the incoming is just a placeholder
          message: (incoming.message === 'Sent an attachment' || incoming.message?.toLowerCase?.() === 'sent an attachment')
            ? (existing.message || '')
            : (incoming.message ?? existing.message),
          files: (incoming.files && incoming.files.length) ? incoming.files : existing.files,
        };
        this.singleChatMessages = [
          ...this.singleChatMessages.slice(0, existingReceiverIdx),
          updatedMsg,
          ...this.singleChatMessages.slice(existingReceiverIdx + 1)
        ];
        this.processMessages();
        this.cdRef.detectChanges();
        return;
      }
    }


    const incoming = (msg?.message && typeof msg.message === 'object') ? msg.message : msg;
    let meta = incoming?.meta || msg?.meta || {};
    if (typeof meta === 'string') {
      try { meta = JSON.parse(meta); } catch (e) { }
    }
    let actualChannelId = meta?.channel_id || meta?.channelId || msg?.channelId || msg?.channel_id || msg?.data?.channelId || msg?.data?.channel_id || msg?.message?.channelId || msg?.message?.channel_id || incoming?.channelId || incoming?.channel_id;
    // NOTE: Do NOT fall back to singleUser.channelId for system messages (pin/unpin).
    // Pin events from other channels arrive with no channelId; applying the fallback here
    // would make them look like they belong to the active chat and bypass the channel guard.
    if (!actualChannelId && !isSysMsg && this.singleUser?.channelId) {
      actualChannelId = this.singleUser.channelId;
    }

    if (actualChannelId && String(actualChannelId).trim() !== String(this.singleUser?.channelId).trim()) {
      console.warn(`handleIncomingMessage: Skipping message for different single chat. Expected ${this.singleUser?.channelId}, got ${actualChannelId}`);
      this.appendMessageToBackgroundCache(actualChannelId, msg, false);
      return;
    } else if (!actualChannelId) {
      if (msg.meta?.isGroupedUpload || msg.message?.meta?.isGroupedUpload) {
        console.warn("Allowing grouped upload fallback in single chat due to missing channelId");
      } else if (isSysMsg) {
        // System messages (pin/unpin) without channelId — validate via sender scope.
        // The sender must be the other party in the active single chat or ourselves.
        const sysSenderId = msg?.sender_id ?? _incoming?.sender_id ?? _incoming?.sid ?? msg?.sid;
        const expectedOther = this.singleUser?.userId || this.singleUser?._id || this.singleUser?.id;
        if (sysSenderId && expectedOther && String(sysSenderId).trim() !== String(expectedOther).trim() && String(sysSenderId).trim() !== String(this.userId).trim()) {
          console.warn(`handleIncomingMessage: Skipping system message from ${sysSenderId} — not related to active single chat with ${expectedOther} (Max Cooper fix)`);
          return;
        }
        console.log("Allowing system message (pin/unpin) through in single chat — sender matches active chat");
      } else {
        console.warn(`handleIncomingMessage: Skipping message because msgChannelId is falsy:`, msg);
        return;
      }
    }

    this.markMessageAsSeen(incoming, false);

    let fileUrl = msg.meta?.fileUrl || msg.url || (typeof msg.file === 'string' ? msg.file : msg.file?.url);
    if (!fileUrl && typeof msg.message === 'object' && msg.message !== null) {
      fileUrl = msg.message.url || msg.message.file || msg.message.fileUrl;
    }
    const isGroupedUpload = msg.meta?.isGroupedUpload || msg.meta?.data?.groupId;
    const groupId = msg.meta?.groupId || msg.meta?.data?.groupId;

    let messageContent = "";
    if (typeof msg.message === 'string') {
      messageContent = msg.message;
    } else if (msg.message && typeof msg.message === 'object') {
      messageContent = msg.message.message || msg.message.text || msg.message.content || "";
    } else {
      messageContent = msg.text || msg.content || "";
    }

    // Reformat pin system messages that contain raw filenames
    if (isSysMsg && messageContent) {
      messageContent = this.reformatPinSystemMessage(messageContent);

      // Deduplicate system messages by content and time (catch dual pinning notifications)
      // For pins, we also check the pinned message ID to allow rapid sequential pinning of DIFFERENT messages.
      const incomingPinnedId = _meta?.messageId || msg?.messageId || _meta?.data?.messageId || _meta?.pinnedMessageId;

      const exists = this.singleChatMessages.some((m: any) => {
        if (!isSystemMessage(m) || m.message !== messageContent) return false;

        const existingPinnedId = m.meta?.messageId || m.messageId || m.meta?.data?.messageId || m.meta?.pinnedMessageId;

        // If we have pinned message IDs for both, only deduplicate if the pinned ID matches.
        // This allows pinning different messages rapidly even if the system text is identical.
        if (incomingPinnedId && existingPinnedId) {
          return incomingPinnedId === existingPinnedId;
        }

        // Fallback for cases without pinned IDs: deduplicate by content if within 5 seconds.
        return Math.abs(new Date(m.createdAt || 0).getTime() - new Date(msg.createdAt || msg.updatedAt || Date.now()).getTime()) < 5000;
      });

      if (exists) {
        console.warn("Duplicate system message blocked by content deduplication:", messageContent, "PinnedId:", incomingPinnedId);
        return;
      }
    }
    const hasFiles = msg.files && msg.files.length > 0;
    if ((messageContent === 'Sent an attachment' || messageContent.toLowerCase() === 'sent an attachment') && !fileUrl && !hasFiles) {
      if (messageId) {
        this.receivedMessageIds.delete(`msg:${messageId}`);
      }
      console.log("Received placeholder without URL in Single Chat. Skipping to avoid reload.");
      return;
    }

    let previewMessage = messageContent;

    if (fileUrl) {
      if (messageContent === 'Sent an attachment' || messageContent.toLowerCase() === 'sent an attachment') {
        messageContent = "";
      }
      previewMessage = this.generateFilePreview(fileUrl);
    }

    if (isGroupedUpload && groupId) {
      let existingMsg = this.singleChatMessages.find(
        m => m.meta?.groupId === groupId
      );

      if (!existingMsg) {
        const initialReplyTo = msg.replyTo || incoming?.replyTo || meta?.replyTo;
        existingMsg = {
          sender_id: senderId,
          sid: senderId,
          createdAt: msg.createdAt || new Date().toISOString(),
          updatedAt: msg.updatedAt || msg.createdAt || new Date().toISOString(),
          meta: { ...msg.meta, ...(initialReplyTo ? { replyTo: initialReplyTo } : {}) },
          replyTo: initialReplyTo,
          files: [],
          message: "",
          currentSlideIndex: 0,
        };
        // Explicitly preserve isForwarded if it was in the WS message or linkPreviewMeta
        if (msg.meta?.isForwarded || msg.isForwarded) {
          existingMsg.meta.isForwarded = true;
        }
        if (msg.meta?.forwardedFrom || msg.forwardedFrom) {
          existingMsg.meta.forwardedFrom = msg.meta?.forwardedFrom || msg.forwardedFrom;
        }

        this.singleChatMessages = [...this.singleChatMessages, existingMsg];
        this.cdRef.detectChanges();
      } else {
        if (!existingMsg.message) {
          existingMsg.message = messageContent;
        }
        // Always preserve isForwarded tag when updating existing message
        if (existingMsg.meta?.isForwarded) {
          msg.meta = msg.meta || {};
          msg.meta.isForwarded = true;
          msg.meta.forwardedFrom = existingMsg.meta.forwardedFrom || msg.meta.forwardedFrom;
        }
      }


      const filesToAdd: any[] = (incoming?.files && incoming.files.length > 0)
        ? incoming.files
        : ((meta?.files && meta.files.length > 0)
          ? meta.files
          : (fileUrl ? (Array.isArray(fileUrl) ? fileUrl.map((u: any) => ({
            fileUrl: typeof u === 'string' ? u : (u?.fileUrl || u?.url),
            type: (typeof u === 'object' && u?.type) ? u.type : getFileType(typeof u === 'string' ? u : (u?.fileUrl || u?.url)),
            name: (typeof u === 'object' && (u?.name || u?.ogFileName)) ? (u.name || u.ogFileName) : getCleanFileName('', typeof u === 'string' ? u : (u?.fileUrl || u?.url)),
            size: typeof u === 'object' ? u?.size : undefined
          })) : [{
            fileUrl,
            type: getFileType(fileUrl),
            name: getCleanFileName('', fileUrl)
          }]) : []));

      let anyAppended = false;
      for (const item of filesToAdd) {
        const itemUrl = typeof item === 'string' ? item : (item?.fileUrl || item?.url);
        if (!itemUrl) continue;
        const itemName = (typeof item === 'object' && (item?.name || item?.ogFileName)) ? (item.name || item.ogFileName) : getCleanFileName('', itemUrl);
        const itemType = (typeof item === 'object' && item?.type) ? item.type : getFileType(itemUrl);

        const alreadyExists = existingMsg.files.some((f: any) => f.fileUrl === itemUrl);
        if (!alreadyExists) {
          const localFileIdx = existingMsg.files.findIndex((f: any) => f.isLocal && (f.name === itemName || f.fileUrl === itemUrl));
          if (localFileIdx !== -1) {
            existingMsg.files[localFileIdx] = {
              fileUrl: itemUrl,
              type: itemType,
              name: itemName,
              size: item.size
            };
          } else {
            const firstLocalIdx = existingMsg.files.findIndex((f: any) => f.isLocal);
            if (firstLocalIdx !== -1) {
              existingMsg.files[firstLocalIdx] = {
                fileUrl: itemUrl,
                type: itemType,
                name: itemName,
                size: item.size
              };
            } else {
              existingMsg.files.push({
                fileUrl: itemUrl,
                type: itemType,
                name: itemName,
                size: item.size
              });
            }
          }
          anyAppended = true;
        }
      }

      if (anyAppended || (msg.meta?.caption && (!existingMsg.meta?.caption || existingMsg.meta.caption !== msg.meta.caption))) {
        if (msg.meta?.caption && (!existingMsg.meta?.caption || existingMsg.meta.caption !== msg.meta.caption)) {
          if (!existingMsg.meta) {
            existingMsg.meta = {};
          }

          existingMsg.meta = { ...existingMsg.meta, caption: msg.meta.caption };
        }
        delete existingMsg._processedFiles;

        const existingIds: string[] = existingMsg._allIds || [];
        if (messageId && !existingIds.includes(messageId)) {
          existingIds.push(messageId);
        }

        const resolvedReplyTo = existingMsg.replyTo || msg.replyTo || incoming?.replyTo || meta?.replyTo;
        const updatedMsg = {
          ...existingMsg,
          _id: messageId,
          id: messageId,
          replyTo: resolvedReplyTo,
          meta: this.normalizeLinkMeta({
            ...existingMsg.meta,
            ...(resolvedReplyTo ? { replyTo: resolvedReplyTo } : {})
          }),
          _allIds: existingIds
        };
        updatedMsg.files = [...existingMsg.files];

        const msgIndex = this.singleChatMessages.indexOf(existingMsg);
        if (msgIndex !== -1) {
          this.singleChatMessages[msgIndex] = updatedMsg;
          this.singleChatMessages = [...this.singleChatMessages];
        }

        console.log("Single chat grouped media appended:", updatedMsg);
        this.processMessages();
        this.cdRef.detectChanges();
        setTimeout(() => this.scrollToBottom(), 50);
      }
      return;
    }
    const incomingFiles = (incoming?.files && incoming.files.length > 0)
      ? incoming.files
      : ((meta?.files && meta.files.length > 0)
        ? meta.files
        : (fileUrl ? (Array.isArray(fileUrl) ? fileUrl.map((u: any) => ({
          fileUrl: typeof u === 'string' ? u : (u?.fileUrl || u?.url),
          type: (typeof u === 'object' && u?.type) ? u.type : getFileType(typeof u === 'string' ? u : (u?.fileUrl || u?.url)),
          name: (typeof u === 'object' && (u?.name || u?.ogFileName)) ? (u.name || u.ogFileName) : getCleanFileName('', typeof u === 'string' ? u : (u?.fileUrl || u?.url)),
          size: typeof u === 'object' ? u?.size : undefined
        })) : [{
          fileUrl,
          type: getFileType(fileUrl),
          name: getCleanFileName('', fileUrl),
        }]) : []));

    if (incomingFiles.length > 0) {
      const resolvedReplyTo = msg.replyTo || incoming?.replyTo || meta?.replyTo;
      const newMsg = {
        ...msg,
        ...incoming,
        _id: messageId,
        id: messageId,
        message: messageContent,
        preview: previewMessage,
        sender_id: senderId,
        sid: senderId,
        createdAt: msg.createdAt || incoming?.createdAt || new Date().toISOString(),
        updatedAt: msg.updatedAt || msg.createdAt || incoming?.updatedAt || incoming?.createdAt || new Date().toISOString(),
        replyTo: resolvedReplyTo,
        files: incomingFiles,
        url: Array.isArray(fileUrl) ? (fileUrl[0] || "") : fileUrl,
        meta: this.normalizeLinkMeta({
          ...meta,
          ...(resolvedReplyTo ? { replyTo: resolvedReplyTo } : {})
        }),
      };

      this.singleChatMessages = [...this.singleChatMessages, newMsg];
      this.processMessages();
      this.cdRef.detectChanges();
      setTimeout(() => this.scrollToBottom(), 50);
      this.sortAllChatsByRecentMessages();
      return;
    }

    const newMsg = {
      ...msg,
      ...incoming,
      _id: messageId,
      id: messageId,
      message: messageContent,
      preview: previewMessage,
      sender_id: senderId,
      sid: senderId,
      createdAt: msg.createdAt || incoming?.createdAt || new Date().toISOString(),
      updatedAt: msg.updatedAt || msg.createdAt || incoming?.updatedAt || incoming?.createdAt || new Date().toISOString(),
      meta: this.normalizeLinkMeta(meta),
    };

    this.singleChatMessages = [...this.singleChatMessages, newMsg];
    this.processMessages();
    this.cdRef.detectChanges();
    setTimeout(() => this.scrollToBottom(), 50);
    this.sortAllChatsByRecentMessages();
  };

  processMessages() {
    this.singleChatMessages = this.singleChatMessages || [];
    this.sanitizeReactions(this.singleChatMessages);
    this.processSingleChatMessages(this.singleChatMessages);
    this.enrichForwardedUrlMessages(this.singleChatMessages);
  }

  private _enrichingUrls = new Set<string>();
  private _failedUrlEnrichments = new Set<string>();
  normalizeLinkMeta(meta: any): any {
    if (!meta) return {};
    let parsed = meta;
    if (typeof meta === 'string') {
      try {
        parsed = JSON.parse(meta);
      } catch (e) {
        return { message: meta }; // Fallback
      }
    }
    const nested = parsed?.data?.meta || {};
    return {
      ...parsed,
      title: (parsed.title || nested.title || '').trim(),
      description: (parsed.description || nested.description || '').trim(),
      image: (parsed.image || nested.image || '').trim(),
      icon: (parsed.icon || nested.icon || '').trim(),
      version: parsed.version || nested.version || '',
    };
  }


  enrichForwardedUrlMessages(messages: any[]) {
    if (!messages || !Array.isArray(messages)) return;
    let anyNormalized = false;
    // Pass 1: normalize any nested meta structure synchronously
    messages.forEach((msg, idx) => {
      const text = typeof msg.message === 'string' ? msg.message : '';
      if (!text || !isURL(text)) return;
      if (!msg.meta) return;
      const hasMeta = msg.meta.title || msg.meta.description || msg.meta.image || msg.meta.version;
      if (hasMeta) return;
      const normalised = this.normalizeLinkMeta(msg.meta);
      if (normalised.title || normalised.description || normalised.image) {
        messages[idx] = { ...msg, meta: { ...msg.meta, ...normalised } };
        anyNormalized = true;
      }
    });
    // If sync normalization changed anything, reassign array reference & detect
    if (anyNormalized) {
      if (messages === this.singleChatMessages) {
        this.singleChatMessages = [...messages];
      } else if (messages === this.groupChatMessages) {
        this.groupChatMessages = [...messages];
      }
      this.cdRef.detectChanges();
    }

    // Pass 2: async-fetch for messages that still lack any preview meta
    messages.forEach((msg) => {
      const text = typeof msg.message === 'string' ? msg.message : '';
      if (!text || !isURL(text)) return;
      const hasMeta = msg.meta?.title || msg.meta?.description || msg.meta?.image || msg.meta?.version;
      if (hasMeta) return;
      const msgKey = (msg._id || msg.id || msg.messageId || text) + '|' + text;
      if (this._enrichingUrls.has(msgKey) || this._failedUrlEnrichments.has(msgKey)) return;
      this._enrichingUrls.add(msgKey);
      const channelId = this.singleUser?.channelId || this.memberChannelId || '';
      this.OnlineUsersService.isUrlValid({ url: text, channelId })
        .subscribe((res: any) => {
          this._enrichingUrls.delete(msgKey);
          if (!res?.data) return;
          const preview = res.data;
          const title = preview.meta?.title || '';
          const description = preview.meta?.description || '';
          const image = preview.meta?.image || '';
          const icon = preview.meta?.icon || '';
          const version = preview.version || msg.meta?.version || '';
          if (!title && !description && !image && !icon) {
            this._failedUrlEnrichments.add(msgKey);
            return;
          }
          const applyToLiveArray = (arr: any[]): boolean => {
            let changed = false;
            arr.forEach((m, mIdx) => {
              if (
                m.message === text &&
                !(m.meta?.title || m.meta?.description || m.meta?.image || m.meta?.icon)
              ) {
                arr[mIdx] = {
                  ...m,
                  meta: {
                    ...(m.meta || {}),
                    title,
                    description,
                    image,
                    icon,
                    version: version || m.meta?.version
                  }
                };
                changed = true;
              }
            });
            return changed;
          };
          let fetchChanged = false;
          if (applyToLiveArray(this.singleChatMessages)) {
            this.singleChatMessages = [...this.singleChatMessages];
            fetchChanged = true;
          }
          if (applyToLiveArray(this.groupChatMessages)) {
            this.groupChatMessages = [...this.groupChatMessages];
            fetchChanged = true;
          }
          if (fetchChanged) this.cdRef.detectChanges();
        }, (error) => {
          this._enrichingUrls.delete(msgKey);
        });
    });
  }



  // Helper to ensure only the latest reaction per user is kept
  sanitizeReactions(messages: any[]) {
    if (!messages || !Array.isArray(messages)) return;
    messages.forEach(message => {
      if (message.reactions && Array.isArray(message.reactions) && message.reactions.length > 0) {
        const uniqueReactions = new Map<string, any>();
        const sortedReactions = [...message.reactions].sort((a: any, b: any) => {
          const timeA = a.reactionTime ? new Date(a.reactionTime).getTime() : 0;
          const timeB = b.reactionTime ? new Date(b.reactionTime).getTime() : 0;
          return timeA - timeB;
        });
        sortedReactions.forEach((reaction: any) => {
          const userId = reaction.userId || reaction.user_id || reaction.sender_id;
          if (userId) {
            uniqueReactions.set(userId, reaction);
          }
        });
        message.reactions = Array.from(uniqueReactions.values());
      }
    });
  }

  processSingleChatMessages(chatMessages: any[]) {
    if (!Array.isArray(chatMessages)) {
      console.error(
        "Expected an array of messages, but received:",
        chatMessages
      );
      return;
    }
    // sort by createdAt (NOT updatedAt)
    chatMessages.sort((a, b) => {
      const aDate = new Date(a.createdAt).getTime();
      const bDate = new Date(b.createdAt).getTime();
      return aDate - bDate;
    });
    let lastDisplayedDate: string | null = null;
    chatMessages.forEach((message) => {
      const date = new Date(message.createdAt);
      if (isNaN(date.getTime())) {
        console.warn("Invalid createdAt:", message);
        return;
      }
      const formattedDate = date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      // New header only when the date changes
      message.showDateHeader = formattedDate !== lastDisplayedDate;
      lastDisplayedDate = formattedDate;
      // System Message Detection & Formatting
      const systemText = this.detectAndFormatSystemMessage(message);
      if (systemText) {
        message['isSystemMessage'] = true;
        message['systemText'] = systemText;
      }
    });
  }

  processGroupMessages() {
    this.sanitizeReactions(this.groupChatMessages);
    let lastDisplayedDate: string | null = null;
    let lastSenderId: string | null = null;
    this.groupChatMessages.forEach((message, index) => {
      const messageDate = new Date(message.createdAt).toLocaleDateString(
        "en-US",
        { month: "short", day: "numeric", year: "numeric" }
      );
      message["showGroupDateHeader"] = lastDisplayedDate !== messageDate;
      lastDisplayedDate = messageDate;

      const currentSenderId = message.sender_id || message.sid;
      message["showAvatarAndName"] =
        index === 0 || lastSenderId !== currentSenderId;
      lastSenderId = currentSenderId;
      // To Check if sender has left the group
      const senderMember = this.allGroupMembers.find(
        (m) => m.userId === currentSenderId
      );
      message["isSenderLeft"] = senderMember ? senderMember.leftGroup : false;
      const systemText = this.detectAndFormatSystemMessage(message);
      if (systemText) {
        message['isSystemMessage'] = true;
        message['systemText'] = systemText;
        message['showAvatarAndName'] = false;
        lastSenderId = null;
      }
    });
    this.enrichForwardedUrlMessages(this.groupChatMessages);
  }

  getPinnedMessageType(message: any): string {
    if (!message) return 'message';
    // Check for GIF
    if (message.meta?.mediaType === 'gif' || message.meta?.gifUrl) {
      return 'GIF';
    }

    // Check for Voice Message
    if (isVoiceMessage(message)) {
      return 'voice message';
    }

    // Check file URL
    let fileUrl = message.url || message.meta?.fileUrl || message.meta?.url;
    if (!fileUrl && typeof message.file === 'string') fileUrl = message.file;
    if (!fileUrl && message.file?.url) fileUrl = message.file.url;

    if (fileUrl && typeof fileUrl === 'string') {
      const lowerUrl = fileUrl.split('?')[0].toLowerCase();
      if (/\.(jpg|jpeg|png|webp|svg|heic|heif|bmp|tiff?|gif)$/i.test(lowerUrl)) {
        return 'photo';
      }
      if (/\.(mp4|mov|avi|mkv|webm|flv|wmv|m4v|3gp)$/i.test(lowerUrl)) {
        return 'video';
      }
      if (/\.(pdf|docx?|xlsx?|pptx?|txt|csv|js|git|html|angular|zip|rar|7z|tar|gz)$/i.test(lowerUrl)) {
        return 'document';
      }
      if (/\.(mp3|wav|ogg|aac|m4a|flac|wma|opus)$/i.test(lowerUrl)) {
        return 'audio';
      }
    }

    // Check meta fileType
    if (message.meta?.fileType?.startsWith('image/')) {
      return 'photo';
    }
    if (message.meta?.fileType?.startsWith('video/')) {
      return 'video';
    }

    // Check files array
    if (message.files && message.files.length > 0) {
      const file = message.files[0];
      if (file.type?.startsWith('image/')) return 'photo';
      if (file.type?.startsWith('video/')) return 'video';
      if (file.type?.startsWith('audio/')) return 'audio';
      return 'document';
    }

    // Check if message text itself is just a file extension 
    const isTextFilename = typeof message.message === 'string' && message.meta?.isForwarded === true && isURL(message.message);
    if (isTextFilename) {
      const lowerText = message.message.split('?')[0].toLowerCase();
      if (/\.(jpg|jpeg|png|webp|svg|heic|heif|bmp|tiff?|gif)$/i.test(lowerText)) return 'photo';
      if (/\.(mp4|mov|avi|mkv|webm|flv|wmv|m4v|3gp)$/i.test(lowerText)) return 'video';
      if (/\.(pdf|docx?|xlsx?|pptx?|txt|csv|js|git|html|angular|zip|rar|7z|tar|gz)$/i.test(lowerText)) return 'document';
      if (/\.(mp3|wav|ogg|aac|m4a|flac|wma|opus)$/i.test(lowerText)) return 'audio';
    }

    return 'message';
  }


  forwardedIcon = `
<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24">
<path fill="#217fb5" d="M14 5v4C7 10 4 15 3 20c2.5-3.5 6-5.1 11-5.1V19l7-7zm2 4.83L18.17 12L16 14.17V12.9h-2c-2.07 0-3.93.38-5.66.95c1.4-1.39 3.2-2.48 5.94-2.85l1.72-.27z" stroke-width="0.5" stroke="#217fb5"/>
</svg>
`;
  generatePreviewText(message: any, groupMembers: any[] = []): string {
    if (!message) return "";
    // Check for system message first
    const systemText = this.detectAndFormatSystemMessage(message, groupMembers);
    if (systemText) return systemText;
    let rawMsg = message.message;
    if (typeof rawMsg === 'object' && rawMsg !== null) {
      rawMsg = rawMsg.text || rawMsg.content || "";
    }
    const isPlaceholder = rawMsg && (String(rawMsg).toLowerCase().trim() === 'sent an attachment');
    let preview = (rawMsg && String(rawMsg).trim() !== "" && !isPlaceholder) ? rawMsg : "";

    // Extract file info
    let fileUrl = typeof message.file === 'string' ? message.file : (message.url || message.file?.url || "");
    if (!fileUrl && message.files && message.files.length > 0) {
      fileUrl = message.files[0].fileUrl || message.files[0].url || "";
    }
    const meta = message.meta || {};
    if (!fileUrl && meta.fileUrl) {
      fileUrl = meta.fileUrl;
    }

    if (Array.isArray(fileUrl)) {
      fileUrl = fileUrl[0] || "";
    } else if (typeof fileUrl === 'object' && fileUrl !== null) {
      fileUrl = fileUrl.fileUrl || fileUrl.url || "";
    }

    const hasFiles = Array.isArray(message.files) && message.files.length > 0;
    const firstFileType = hasFiles ? (message.files[0].type || '') : '';
    const firstFileName = hasFiles ? (message.files[0].name || message.files[0].fileName || '') : '';
    const firstFileUrl = hasFiles ? (message.files[0].fileUrl || message.files[0].url || '') : '';

    const hasAttachmentOrMedia = !!(
      (typeof fileUrl === 'string' && fileUrl.trim() !== '') ||
      hasFiles ||
      meta.mediaType ||
      meta.fileType ||
      meta.gifUrl ||
      meta.isVoice ||
      message.isVoice
    );

    // Exact media placeholder strings (emitted when sending media without text)
    const lowerMsg = (rawMsg && typeof rawMsg === 'string') ? rawMsg.toLowerCase().trim() : '';
    const exactMediaPlaceholders: { [key: string]: string } = {
      'sent a photo': 'sent a photo',
      '📷 photo': 'sent a photo',
      'sent a video': 'sent a video',
      '🎥 video': 'sent a video',
      'sent a document': 'sent a document',
      '📄 document': 'sent a document',
      'sent an audio': 'sent an audio',
      'sent a audio': 'sent an audio',
      'sent a file': 'sent a file',
      '📎 file': 'sent a file',
      'sent a gif': 'sent a gif'
    };

    if (isVoiceMessage(message)) {
      preview = "🎤 Voice message";
    } else if (hasAttachmentOrMedia) {
      const cleanUrl = String(fileUrl || '').toLowerCase().split('?')[0];
      const isGif = meta.mediaType === 'gif' || meta.gifUrl || cleanUrl.endsWith('.gif');
      const photoFound = meta.mediaType === 'image' || meta.fileType?.startsWith('image/') || isImage(fileUrl) ||
        firstFileType.startsWith('image/') || isImage(firstFileUrl);
      const videoFound = meta.mediaType === 'video' || meta.fileType?.startsWith('video/') || isVideo(fileUrl) ||
        firstFileType.startsWith('video/') || isVideo(firstFileUrl);
      const audioFound = meta.mediaType === 'audio' || meta.fileType?.startsWith('audio/') || isAudio(fileUrl) ||
        firstFileType.startsWith('audio/') || isAudio(firstFileUrl);
      const docFound = meta.mediaType === 'document' || meta.fileType?.startsWith('application/') || isDocument(fileUrl) ||
        firstFileType.startsWith('application/') || isDocument(firstFileUrl) || isDocument(firstFileName);

      const isExactPlaceholder = !!exactMediaPlaceholders[lowerMsg] || lowerMsg === '🎤 voice message';
      if (!preview || isExactPlaceholder) {
        if (isGif) {
          preview = "sent a gif";
        } else if (photoFound) {
          preview = "sent a photo";
        } else if (videoFound) {
          preview = "sent a video";
        } else if (audioFound) {
          preview = "sent an audio";
        } else if (docFound) {
          preview = "sent a document";
        } else if (fileUrl && typeof fileUrl === 'string' && fileUrl.trim() !== "") {
          preview = "sent a file";
        }
      }
    } else if (exactMediaPlaceholders[lowerMsg]) {
      preview = exactMediaPlaceholders[lowerMsg];
    } else if (lowerMsg === '🎤 voice message' && (meta.duration || message.duration)) {
      preview = "🎤 Voice message";
    }

    if (meta.isForwarded || meta.forwardedFrom || message.forwardedFrom) {
      if (preview && preview.trim() !== "") {
        if (!preview.startsWith("↪ ")) {
          preview = "↪ " + preview;
        }
      } else {
        preview = "↪ Forwarded message";
      }
    }

    if (preview && typeof preview === 'string') {
      const hasFwd = preview.startsWith('↪ ');
      const content = hasFwd ? preview.slice(2).trim() : preview;
      if (isCodeOrHtml(content)) {
        const formatted = formatCodeOrHtmlPreview(content);
        preview = hasFwd ? ('↪ ' + formatted) : formatted;
      }
    }

    return preview;
  }

  detectAndFormatSystemMessage(message: any, groupMembers: any[] = []): string | null {
    if (!message) return null;
    const meta = message.meta || {};
    const content = message.message || "";
    const sysMsgFound = isSystemMessage(message);

    if (meta?.subType === 'Message Pinned' || meta?.pinnedBy) {
      return this.formatPinnedMessage(message);
    }

    // Only continue if it was explicitly flagged as a system message
    if (!sysMsgFound) return null;

    // Do NOT perform 'You' replacement on Alert messages; keep the original lead/user name from the API response
    if (content.trim().startsWith('Alert:') || meta?.subType === 'Lead Assigned' || content.includes('Alert:')) {
      return content;
    }

    // Truncate long quoted pinned messages if present in content
    const pinMatch = content.match(/^(.+?\bpinned\s+)["“]([^"”]+)["”](.*)$/i);
    if (pinMatch) {
      const prefix = pinMatch[1];
      const quoted = pinMatch[2];
      const suffix = pinMatch[3] || '';
      const maxLen = 80;
      const truncated = quoted.length > maxLen ? quoted.slice(0, maxLen).trim() + '...' : quoted;
      return `${prefix}"${truncated}"${suffix}`;
    }

    // Filter out generic admin change messages if they lack a specific subtype
    if (content.toLowerCase().trim() === 'admin changed' && !meta.subType) {
      return null;
    }

    const senderId = message.sender_id || message.sid;
    const isMe = senderId === this.userId;

    // Resolve all possible names for the current user to replace with 'You'
    const myMember = this.allGroupMembers?.find(m => m.userId === this.userId);
    const selfNames = [
      myMember?.userName,
      myMember?.displayName,
      this.senderUsername,
      localStorage.getItem('username'),
      localStorage.getItem('displayName'),
      localStorage.getItem('userName'),
      localStorage.getItem('name'),
      this.userNameMapping[this.userId]
    ].filter(Boolean).map(n => String(n).trim());

    // Helper to check if a name refers to the current user
    const isSelf = (name: string) => {
      if (!name) return false;
      const ln = name.toLowerCase().trim();
      return selfNames.some(own => own.toLowerCase() === ln);
    };

    const resolveSenderName = () => {
      if (isMe) return 'You';
      // 1. Try passed groupMembers
      if (groupMembers && groupMembers.length > 0) {
        const member = groupMembers.find(m => m.userId === senderId);
        if (member) return member.displayName || member.userName;
      }
      const member = this.allGroupMembers?.find(m => m.userId === senderId);
      if (member) return member.displayName || member.userName;
      if (
        this.singleChatBox &&
        this.singleUser &&
        (
          this.singleUser.userId === senderId ||
          this.singleUser._id === senderId ||
          this.singleUser.id === senderId
        )
      ) {
        return this.singleUser.displayName || this.singleUser.userName || this.singleUser.name;
      }
      const singleUser = this.allSingleUsers?.find(
        u => u.userId === senderId || u._id === senderId || u.id === senderId
      );
      if (singleUser) return singleUser.displayName || singleUser.userName || singleUser.name;
      return meta.removedBy || meta.adminName || message.name || 'Someone';
    };

    const senderName = resolveSenderName();
    const subType = meta?.subType?.trim();

    // STRUCTURED SYSTEM MESSAGES
    if (subType) {
      switch (subType) {
        case 'Group Creation': {
          const groupNameMatch = content.match(/: (.+)$/);
          const groupName = groupNameMatch ? groupNameMatch[1] : null;
          return groupName
            ? `${senderName} created the group "${groupName}"`
            : `${senderName} created the group`;
        }

        case 'Member Added': {
          const names = meta.memberNames || [];
          const formatted = names.map((n: string) => isSelf(n) ? 'You' : n);
          return `${senderName} added ${formatted.join(', ')}`;
        }
        case 'Group Member Removed': {
          const removed = meta.removedMember;
          const name = isSelf(removed) ? 'You' : (removed || 'Someone');
          return `${senderName} removed ${name}`;
        }
        case 'Member Left': {
          return `${senderName} left`;
        }
        case 'Group Name Changed':
          return meta.groupName
            ? `${senderName} changed the group name to "${meta.groupName}"`
            : `${senderName} changed the group name`;
        case 'Group Description Changed':
          return meta.groupDescription
            ? `${senderName} changed the group description`
            : `${senderName} changed the group description`;
        case 'Group Icon Changed':
          return `${senderName} changed the group icon`;
        case 'Admin Assigned':
        case 'Admin Added': {
          const admin = meta.newAdmin;
          const name = isSelf(admin) ? 'You' : (admin || 'Someone');
          return `${name} ${name === 'You' ? 'are' : 'is'} now an admin`;
        }
        case 'Admin Removed': {
          const removed = meta.removedAdmin || meta.adminRemoved;
          const name = isSelf(removed) ? 'You' : (removed || 'Someone');
          return `${name} ${name === 'You' ? 'are' : 'is'} no longer an admin`;
        }
      }
    }

    // UNSTRUCTURED FALLBACK
    let formattedText = content;
    // Sort names by length descending to replace "Anshu Rani" before "Anshu"
    const sortedSelfNames = Array.from(new Set(selfNames)).sort((a, b) => b.length - a.length);

    sortedSelfNames.forEach(name => {
      const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      // We use a case-insensitive regex but respect boundaries to avoid partial matches
      formattedText = formattedText.replace(
        new RegExp(`\\b${escaped}\\b`, 'gi'),
        'You'
      );
    });

    // Special cleanup for common cases like "Sender added You"
    formattedText = formattedText.replace(/added anshu rani/gi, 'added You');
    formattedText = formattedText.replace(/removed anshu rani/gi, 'removed You');
    formattedText = formattedText.replace(/You removed You/gi, 'You were removed');

    return formattedText;
  }

  private formatPinnedMessage(message: any): string {
    const meta = message.meta || {};
    const senderId = message.sender_id || message.sid;
    const isMe = senderId === this.userId;
    let pinnedBy = meta.pinnedBy || message.name || 'Someone';
    if (isMe) pinnedBy = 'You';
    const messageId = meta.messageId;
    let originalMessage: any = null;
    if (messageId) {
      originalMessage =
        this.singleChatMessages?.find(m => (m._id || m.id || m.messageId) === messageId) ||
        this.groupChatMessages?.find(m => (m._id || m.id || m.messageId) === messageId);
    }
    if (originalMessage) {
      const type = this.getPinnedMessageType(originalMessage);
      if (type === 'message') {
        const text =
          originalMessage.message?.text ||
          originalMessage.message ||
          '';
        const maxLen = 80;
        const truncated = text.length > maxLen ? text.slice(0, maxLen).trim() + '...' : text;
        return text
          ? `${pinnedBy} pinned "${truncated}"`
          : `${pinnedBy} pinned a message`;
      }
      return `${pinnedBy} pinned a ${type}`;
    }
    return `${pinnedBy} pinned a message`;
  }

  getDisplayName(userId: string): string {
    if (userId === this.userId) return "You";
    const groupMember = this.allGroupMembers?.find((m) => m.userId === userId);
    if (groupMember) return groupMember.displayName || groupMember.userName || groupMember.name;
    const singleUser = this.allSingleUsers?.find((u) => u.userId === userId);
    return singleUser?.displayName || singleUser?.userName || this.userNameMapping[userId] || "User";
  }

  shouldShowAvatarAndName(message: any, index: number): boolean {
    return message["showAvatarAndName"] ?? true;
  }

  isLastMessageFromUser(message: any, index: number): boolean {
    if (index >= this.groupChatMessages.length - 1) return true;
    const nextMessage = this.groupChatMessages[index + 1];
    const currentSenderId = message.sender_id || message.sid;
    const nextSenderId = nextMessage?.sender_id || nextMessage?.sid;
    return currentSenderId !== nextSenderId;
  }


  //SCROLL CHAT TO BOTTOM
  scrollToBottom() {
    const singleContainer = document.getElementById("singleChatContainer");
    if (singleContainer && this.singleChatBox) {
      singleContainer.scrollTop = singleContainer.scrollHeight;
    }
    const groupContainer = document.getElementById("groupChatContainer");
    if (groupContainer && this.groupChatBox) {
      groupContainer.scrollTop = groupContainer.scrollHeight;
    }
    this.showJumpToLatestSingle = false;
    this.showJumpToLatest = false;
  }

  //SCROLL CHAT TO BOTTOM (for jump-to-latest button)
  scrollToBottomSmooth() {
    const singleContainer = document.getElementById("singleChatContainer");
    if (singleContainer && this.singleChatBox) {
      singleContainer.scrollTo({
        top: singleContainer.scrollHeight,
        behavior: "smooth"
      });
    }

    const groupContainer = document.getElementById("groupChatContainer");
    if (groupContainer && this.groupChatBox) {
      groupContainer.scrollTo({
        top: groupContainer.scrollHeight,
        behavior: "smooth"
      });
    }

    this.showJumpToLatestSingle = false;
    this.showJumpToLatest = false;
  }

  scrollToBottomEdgeSafe() {
    const singleContainer = document.getElementById("singleChatContainer") as HTMLElement;
    const groupContainer = document.getElementById("groupChatContainer") as HTMLElement;

    if (singleContainer && this.singleChatBox) {
      singleContainer.scrollTop = singleContainer.scrollHeight;
    }

    if (groupContainer && this.groupChatBox) {
      groupContainer.scrollTop = groupContainer.scrollHeight;
    }

    this.showJumpToLatestSingle = false;
    this.showJumpToLatest = false;
  }

  //To remove file name from chatbox
  isMessageRedundantWithFile(message: any): boolean {
    const url = message.url || message.meta?.url || message.meta?.fileUrl;
    const msg = message.message;
    if (!url || !msg) return false;

    if (Array.isArray(url)) {
      return url.some(u => typeof u === 'string' && u.includes(msg));
    }

    const isFilename = typeof url === 'string' && url.includes(msg);
    return isFilename;
  }

  isGifMessage(message: any): boolean {
    return isGifMessage(message);
  }

  getFilesFromMessage(message: any): any[] {
    const processed = getFilesFromMessage(message);
    if (message && message.currentSlideIndex === undefined && processed.length > 1) {
      message.currentSlideIndex = 0;
    }
    return processed.sort((a, b) => {
      const aIsAudio = (a.type || '').startsWith('audio/');
      const bIsAudio = (b.type || '').startsWith('audio/');
      if (aIsAudio && !bIsAudio) return -1;
      if (!aIsAudio && bIsAudio) return 1;
      return 0;
    });
  }


  previousTimestamp: Date | null = null;
  isDifferentTimestamp(currentMessage: any): boolean {
    const currentTimestamp = new Date(currentMessage.createdAt);
    if (
      this.previousTimestamp &&
      currentTimestamp.getMinutes() === this.previousTimestamp.getMinutes()
    ) {
      return false;
    } else {
      // Update the previous timestamp
      this.previousTimestamp = currentTimestamp;
      return true;
    }
  }

  //Opening URL in new window
  openUrlInNewWindow(url: string): void {
    if (isURL(url)) {
      window.open(url, "_blank");
    }
  }





  // Unlock audio on user interaction 
  unlockAudio() {
    if (this.audioUnlocked) return;
    try {
      console.log(" Attempting to unlock audio via soundService...");
      this.soundService.unlock();
      this.audioUnlocked = true;
      console.log(" Audio unlocked for notifications!");
    } catch (error) {
      console.log(" Audio unlock attempt failed:", error);
    }
  }

  // Test function to manually test the sound
  testNotificationSound() {
    console.log("Testing notification sound...");
    // this.playMessageNotificationSound();
  }

  newMessage: string = "";
  sendfile: any;
  fileId: any;
  //SEND SINGLE CHAT MESSAGES
  async sendChatMessage(event?: any) {
    if (event) {
      (event as KeyboardEvent).preventDefault();
    }
    if (this.chatLoader || this.isSending) return;
    this.isSending = true;
    try {
      const messageText = this.newMessage?.trim();
      const fileToSend = this.sendfile;
      const gifToSend = this.selectedGifUrl;

      // "/task Call the vendor tomorrow" → the New-task window, pre-filled for this chat (17 Oct 2026).
      if (messageText && /^\/tasks?\b/i.test(messageText) && !this.editingMessage && !fileToSend && !gifToSend) {
        this.chatDraftService.clearDraft('single', this.chatUserId, this.singleUser?.channelId);
        this.newMessage = "";
        this.startTaskCommand(messageText.replace(/^\/tasks?\b/i, "").trim());
        return;
      }

      // Handle edit mode
      if (this.editingMessage) {
        const chatChannel = this.singleChannel;
        if (!chatChannel) return;

        const messageId = this.editingMessage._id || this.editingMessage.id || this.editingMessage.messageId;
        if (!messageId) {
          console.error("Cannot edit message: missing message ID");
          return;
        }

        // Use the converse SDK editMessage method
        try {
          const isMedia = !!(this.editingMessage.url || this.editingMessage.meta?.fileUrl || this.editingMessage.meta?.gifUrl || (this.editingMessage.files && this.editingMessage.files.length > 0));

          const editData: any = {
            message: isMedia ? this.editingMessage.message : messageText,
            meta: { ...(this.editingMessage.meta || {}), edited: true }
          };

          if (isMedia) {
            editData.meta.caption = messageText;
            // If the original message was just a placeholder like "sent a photo", 
            // and we now have a real caption, we might want to update the message field too
            // for better notifications/previews, but we should be careful not to break 
            // the "sent a photo" logic if needed. 
            // Most consistent is to update both if it's a media caption.
            if (messageText) {
              editData.message = messageText;
            }
          } else {
            editData.message = messageText;
          }

          await chatChannel.editMessage({
            message_id: messageId,
            data: editData
          });

          // Update the local message in the array
          const messageIndex = this.singleChatMessages.findIndex(
            (msg) => (msg._id || msg.id || msg.messageId) === messageId
          );
          if (messageIndex !== -1) {
            this.singleChatMessages[messageIndex].message = isMedia && messageText ? messageText : (isMedia ? this.singleChatMessages[messageIndex].message : messageText);
            this.singleChatMessages[messageIndex].updatedAt = new Date().toISOString();
            this.singleChatMessages[messageIndex].edited = true;
            this.singleChatMessages[messageIndex].meta = {
              ...(this.singleChatMessages[messageIndex].meta || {}),
              edited: true,
              ...(isMedia ? { caption: messageText } : {})
            };
          }

          // Update the last message in chat list
          const updatedMessageText = this.singleChatMessages[messageIndex].message;
          const userIndex = this.allSingleUsers.findIndex(
            (user) => user.userId === this.newReceiver
          );
          if (userIndex !== -1 && this.allSingleUsers[userIndex].lastMessage) {
            if ((this.allSingleUsers[userIndex].lastMessage._id || this.allSingleUsers[userIndex].lastMessage.id) === messageId) {
              this.allSingleUsers[userIndex].lastMessage.message = updatedMessageText;
            }
          }

          const filteredUserIndex = this.filteredSingleUsers.findIndex(
            (user) => user.userId === this.newReceiver
          );
          if (filteredUserIndex !== -1 && this.filteredSingleUsers[filteredUserIndex].lastMessage) {
            if ((this.filteredSingleUsers[filteredUserIndex].lastMessage._id || this.filteredSingleUsers[filteredUserIndex].lastMessage.id) === messageId) {
              this.filteredSingleUsers[filteredUserIndex].lastMessage.message = updatedMessageText;
            }
          }
        } catch (error) {
          console.error("Failed to edit message:", error);
          alertModalWithoutConfirm("Edit Failed", "Failed to edit message. Please try again.");
        }

        // Reset edit mode
        this.editingMessage = null;
        this.newMessage = this.chatDraftService.getDraft('single', this.chatUserId, this.singleUser?.channelId);
        setTimeout(() => this.singleMessageInput?.adjustTextareaHeight(), 0);
        this.cdRef.detectChanges();
        return;
      }
      // Lazy create channel if pending
      if (!this.singleChannel && this.isPendingSingleChat) {
        const created = await this.createPendingSingleChat();
        if (!created || !this.singleChannel) {
          console.error("Failed to create chat channel before sending");
          // Show error to user
          alertModalWithoutConfirm(
            "Failed to create chat",
            "Unable to create chat. Please try again."
          );
          return;
        }
      }

      const chatChannel = this.singleChannel;
      const receiverId = this.newReceiver;
      const sendChannelId = this.singleUser?.channelId;
      // When disconnected the message is shown with a clock and queued in the outbox instead of failing
      const isChannelConnected = this.canSendOnChannel(chatChannel);

      if (!messageText && !fileToSend && !gifToSend) return;

      let messageContent = messageText;
      let previewMessage = messageText;

      if (gifToSend) {
        previewMessage = "GIF";
      }

      if (fileToSend) {
        const lowerUrl = (fileToSend.fileUrl || "").toLowerCase();
        if (!messageText) {
          messageContent = "";
        }

        if (isVoiceMessage(fileToSend.fileUrl)) {
          previewMessage = `🎤 Voice message`;
          // Include media type in message field for receiver's preview
          if (!messageText) {
            messageContent = "🎤 Voice message";
          }
        } else if (/\.(jpg|jpeg|png|gif|webp|svg)$/.test(lowerUrl)) {
          previewMessage = `📷 Photo`;
          // Include media type in message field for receiver's preview
          if (!messageText) {
            messageContent = lowerUrl.endsWith('.gif') ? "sent a gif" : "sent a photo";
          }
        } else if (/\.(mp4|mov|avi|mkv|webm)$/.test(lowerUrl)) {
          previewMessage = `🎥 Video`;
          // Include media type in message field for receiver's preview
          if (!messageText) {
            messageContent = "sent a video";
          }
        } else if (DOCUMENT_EXTENSIONS.some(ext => lowerUrl.endsWith(ext.toLowerCase()))) {
          previewMessage = `📄 Document`;
          // Include media type in message field for receiver's preview
          if (!messageText) {
            messageContent = "sent a document";
          }
        } else {
          previewMessage = `📎 File`;
          // Include media type in message field for receiver's preview
          if (!messageText) {
            messageContent = "sent a file";
          }
        }
      }

      let contentType = 'text';
      let contentSubtype = 'text';

      if (gifToSend) {
        contentType = 'image';
        contentSubtype = 'other';
      } else if (fileToSend) {
        const categorization = this.getContentCategorization(fileToSend.fileUrl || fileToSend.name, fileToSend.type);
        contentType = categorization.contentType;
        contentSubtype = categorization.contentSubtype;
      } else if (isURL(messageText)) {
        const categorization = this.getLinkContentCategorization(messageText);
        contentType = categorization.contentType;
        contentSubtype = categorization.contentSubtype;
      }

      let newMessageObj: any = {
        message: messageContent,
        sender_id: this.userId,
        createdAt: new Date(),
        updatedAt: new Date().toISOString(),
        replyTo: this.replyingToMessage ? {
          id: this.replyingToMessage._id || this.replyingToMessage.id,
          message: this.replyingToMessage.message || "Media",
          sender_id: this.replyingToMessage.sender_id || this.replyingToMessage.senderId || this.replyingToMessage.user_id || this.replyingToMessage.sid,
          senderName: (this.replyingToMessage.sender_id && String(this.replyingToMessage.sender_id).trim() === String(this.userId).trim()) ? "You" : (this.replyingToMessage.senderName || this.replyingToMessage.name || (this.userNameMapping?.[this.replyingToMessage.sender_id] || "User")),
          sourceGroupName: this.replyingToMessage.sourceGroupName || this.replyingToMessage.meta?.sourceGroupName,
          sourceGroupId: this.replyingToMessage.sourceGroupId || this.replyingToMessage.meta?.sourceGroupId,
          url: this.replyingToMessage.url,
          files: this.replyingToMessage.files,
          meta: this.replyingToMessage.meta
        } : undefined,
        meta: {
          channel_id: this.singleUser.channelId,
          tempId: Date.now().toString(),
          mentions: this.messageMeta?.mentions || [],
          contentType: contentType,
          contentSubtype: contentSubtype,
          replyTo: this.replyingToMessage ? {
            id: this.replyingToMessage._id || this.replyingToMessage.id,
            message: this.replyingToMessage.message || "Media",
            sender_id: this.replyingToMessage.sender_id || this.replyingToMessage.senderId || this.replyingToMessage.user_id || this.replyingToMessage.sid,
            senderName: (this.replyingToMessage.sender_id && String(this.replyingToMessage.sender_id).trim() === String(this.userId).trim()) ? "You" : (this.replyingToMessage.senderName || this.replyingToMessage.name || (this.userNameMapping?.[this.replyingToMessage.sender_id] || "User")),
            sourceGroupName: this.replyingToMessage.sourceGroupName || this.replyingToMessage.meta?.sourceGroupName,
            sourceGroupId: this.replyingToMessage.sourceGroupId || this.replyingToMessage.meta?.sourceGroupId,
            url: this.replyingToMessage.url,
            files: this.replyingToMessage.files,
            meta: this.replyingToMessage.meta
          } : undefined
        },
      };

      // Handle GIF metadata
      if (gifToSend) {
        newMessageObj.url = gifToSend;
        newMessageObj.message = messageText || "";
        newMessageObj.meta = {
          ...newMessageObj.meta,
          gifUrl: gifToSend,
          mediaType: "gif",
          fileType: "image/gif",
        };
        newMessageObj.preview = "GIF";
      }

      // Handle URL metadata (skipped offline so the queued message shows instantly)
      if (isURL(messageText) && !gifToSend && navigator.onLine) {
        const body: any = {
          url: messageText,
          channelId: this.singleUser.channelId,
        };
        if (this.fileId) body.fileId = this.fileId;
        try {
          const res: any = await this.OnlineUsersService.isUrlValidSave(body);
          if (res?.data) {
            const metaTitle = res.data.meta?.title || '';
            const metaDesc = res.data.meta?.description || '';
            newMessageObj.meta = {
              ...newMessageObj.meta,
              title: metaTitle,
              description: metaDesc,
              image: res.data.meta?.image || '',
              icon: res.data.meta?.icon || '',
              version: res.data.version || '',
              thread: res.data.thread || [],
              fileId: res.data.fileId || body.fileId
            };
            // Store meta for backend full-text search
            const reqSearch: string[] = [];
            if (metaTitle) reqSearch.push(metaTitle);
            if (metaDesc) reqSearch.push(metaDesc);
            if (reqSearch.length > 0) {
              newMessageObj.reqSearch = reqSearch;
            }
          }
        } catch (err) {
          console.error('URL metadata fetch failed', err);
        }
      }

      if (fileToSend && !gifToSend) {
        newMessageObj.url = fileToSend.fileUrl;
        newMessageObj.meta = {
          ...newMessageObj.meta,
          caption: messageText || "",
          ...(messageText ? { captionContentType: isURL(messageText) ? 'url' : 'text' } : {}),
          fileUrl: fileToSend.fileUrl,
          fileType: fileToSend.type || "file",
          ...(fileToSend.duration ? { duration: fileToSend.duration } : {})
        };
        newMessageObj.files = [{
          fileUrl: fileToSend.fileUrl,
          type: getFileType(fileToSend.fileUrl),
          name: getCleanFileName('', fileToSend.fileUrl),
        }];
      }

      const outboxItem: OutboxItem = {
        chatType: "single",
        message: newMessageObj,
        preview: gifToSend ? "GIF" : previewMessage,
        notifications: [{
          channelId: sendChannelId,
          title: this.senderUsername,
          body: messageText || (gifToSend ? "Sent a GIF" : "Sent a file"),
          redirectUrl: `${environment.redirectUrl}#/main-chat?chatType=single&userId=${this.userId}`,
          icon: "https://link.iiclab.com/assets/login/header-logo.png",
          sender_id: this.userId,
        }],
      };
      let sent = false;
      if (isChannelConnected) {
        try {
          await chatChannel.sendMessage(newMessageObj);
          sent = true;
        } catch (err) {
          console.warn("sendMessage failed, queueing in outbox:", err);
        }
      }
      if (!sent) {
        newMessageObj.pending = true;
        this.queueOutboxMessage(sendChannelId, outboxItem);
      }

      this.isSuggestionBoxVisible = false;
      this.MetaDataDetails = null;
      this.MedataLength = 0;
      this.showEmojiPicker = false;
      this.singleChatMessages.push(newMessageObj);
      this.processMessages();
      setTimeout(() => this.scrollToBottom(), 50);

      if (sent) {
        this.onOutboxMessageDelivered(sendChannelId, outboxItem);
      }

      // Update user preview in both allSingleUsers and filteredSingleUsers
      const userIndex = this.allSingleUsers.findIndex(
        (user) => user.userId === receiverId
      );
      if (userIndex !== -1) {
        this.allSingleUsers[userIndex].lastMessage = {
          ...newMessageObj,
          message: gifToSend ? "GIF" : previewMessage,
          createdAt: newMessageObj.createdAt
        };
        this.allSingleUsers[userIndex].lastMessageTimeAgo = getTimeAgo(newMessageObj.createdAt);
      }

      const filteredUserIndex = this.filteredSingleUsers.findIndex(
        (user) => user.userId === receiverId
      );
      if (filteredUserIndex !== -1) {
        this.filteredSingleUsers[filteredUserIndex].lastMessage = {
          ...newMessageObj,
          message: gifToSend ? "GIF" : previewMessage,
          createdAt: newMessageObj.createdAt
        };
        this.filteredSingleUsers[filteredUserIndex].lastMessageTimeAgo = getTimeAgo(newMessageObj.createdAt);
      }

      this.sortAllChatsByRecentMessages();
      this.cdRef.detectChanges();

      const activeChat = this.filteredSingleUsers.find(
        (c) => c.channelId === this.singleUser.channelId
      );
      if (activeChat) activeChat.unreadCount = 0;

      this.chatDraftService.clearDraft('single', receiverId, this.singleUser?.channelId);
      this.newMessage = "";
      this.replyingToMessage = null;
      this.sendfile = null;
      this.fileId = null;
      this.selectedGifUrl = null;
      this.messageMeta = null;
      // this.resetTextareaHeight();
      this.cdRef.detectChanges();
      this.updateCacheForActiveChannel();

      // Ensure keyboard stays open on mobile
      if (this.singleMessageInput) {
        setTimeout(() => {
          this.singleMessageInput.focus();
        }, 50);
      }
    } catch (error) {
      console.error("sendChatMessage error:", error);
    } finally {
      this.isSending = false;
    }
  }

  //SEND GROUP CHAT MESSAGES
  newGroupMessage: string = "";
  newGroupReceiver: any;
  sendGroupFile: any;
  // GIF / Klipy UI state - Single Chat
  showGifPanel = false;
  gifQuery = "";
  gifResults: string[] = [];
  gifCurrentPage = 1;
  gifHasNext = false;
  isGifLoading = false;
  gifError: string | null = null;
  selectedGifUrl: string | null = null;

  // GIF / Klipy UI state - Group Chat
  showGifPanelGroup = false;
  gifQueryGroup = "";
  gifResultsGroup: string[] = [];
  gifCurrentPageGroup = 1;
  gifHasNextGroup = false;
  isGifLoadingGroup = false;
  gifErrorGroup: string | null = null;
  selectedGifUrlGroup: string | null = null;
  async sendGroupChatMessage(event?: any) {
    if (this.isCurrentGroupExited) {
      console.warn("User has exited this group. Sending disabled.");
      alertModalWithoutConfirm("Not a member", "You can't send messages because you have left or been removed from this group.");
      return;
    }
    if (event) {
      (event as KeyboardEvent).preventDefault();
    }
    if (this.isSending) return;
    this.isSending = true;
    try {
      const groupChatChannel = this.groupChannel;
      const gifToSend = this.selectedGifUrlGroup;

      // Handle edit mode
      if (this.editingMessage) {
        if (!groupChatChannel) return;

        const messageId = this.editingMessage._id || this.editingMessage.id || this.editingMessage.messageId;
        if (!messageId) {
          console.error("Cannot edit message: missing message ID");
          return;
        }

        const messageText = this.newGroupMessage?.trim();
        if (!messageText) return;

        // Use the converse SDK editMessage method
        try {
          const isMedia = !!(this.editingMessage.url || this.editingMessage.meta?.fileUrl || this.editingMessage.meta?.gifUrl || (this.editingMessage.files && this.editingMessage.files.length > 0));

          const editData: any = {
            message: isMedia ? this.editingMessage.message : messageText,
            meta: { ...(this.editingMessage.meta || {}), edited: true }
          };

          if (isMedia) {
            editData.meta.caption = messageText;
            if (messageText) {
              editData.message = messageText;
            }
          } else {
            editData.message = messageText;
          }

          await groupChatChannel.editMessage({
            message_id: messageId,
            data: editData
          });

          // Update the local message in the array
          const messageIndex = this.groupChatMessages.findIndex(
            (msg) => (msg._id || msg.id || msg.messageId) === messageId
          );
          if (messageIndex !== -1) {
            this.groupChatMessages[messageIndex].message = isMedia && messageText ? messageText : (isMedia ? this.groupChatMessages[messageIndex].message : messageText);
            this.groupChatMessages[messageIndex].updatedAt = new Date().toISOString();
            this.groupChatMessages[messageIndex].edited = true;
            this.groupChatMessages[messageIndex].meta = {
              ...(this.groupChatMessages[messageIndex].meta || {}),
              edited: true,
              ...(isMedia ? { caption: messageText } : {})
            };
          }

          // Update the last message in group list
          const updatedMessageText = this.groupChatMessages[messageIndex].message;
          const groupIndex = this.allGroupUsers.findIndex(
            (group) => group.channelId === this.memberChannelId
          );
          if (groupIndex !== -1 && this.allGroupUsers[groupIndex].lastMessage) {
            if ((this.allGroupUsers[groupIndex].lastMessage._id || this.allGroupUsers[groupIndex].lastMessage.id) === messageId) {
              this.allGroupUsers[groupIndex].lastMessage.message = updatedMessageText;
            }
          }

          const filteredGroupIndex = this.filteredGroupUsers.findIndex(
            (group) => group.channelId === this.memberChannelId
          );
          if (filteredGroupIndex !== -1 && this.filteredGroupUsers[filteredGroupIndex].lastMessage) {
            if ((this.filteredGroupUsers[filteredGroupIndex].lastMessage._id || this.filteredGroupUsers[filteredGroupIndex].lastMessage.id) === messageId) {
              this.filteredGroupUsers[filteredGroupIndex].lastMessage.message = updatedMessageText;
            }
          }
        } catch (error) {
          console.error("Failed to edit message:", error);
          alertModalWithoutConfirm("Edit Failed", "Failed to edit message. Please try again.");
        }

        // Reset edit mode
        this.editingMessage = null;
        this.newGroupMessage = this.chatDraftService.getDraft('group', this.memberChannelId);
        setTimeout(() => this.groupMessageInput?.adjustTextareaHeight(), 0);
        this.cdRef.detectChanges();
        return;
      }

      const sendChannelId = this.memberChannelId;
      if (!sendChannelId) {
        console.error("Error: Group channel is not selected.");
        return;
      }
      // When disconnected the message is shown with a clock and queued in the outbox instead of failing
      const isChannelConnected = this.canSendOnChannel(groupChatChannel);

      if (!this.newGroupMessage.trim() && !this.sendGroupFile && !gifToSend) {
        console.warn("Cannot send an empty message.");
        return;
      }

      let messageContent = this.newGroupMessage.trim();
      let previewMessage = this.newGroupMessage.trim();

      if (gifToSend) {
        previewMessage = "GIF";
      }

      if (this.sendGroupFile) {
        const fileUrlLower = this.sendGroupFile.fileUrl.toLowerCase();
        if (!messageContent) {
          messageContent = "";
        }

        // Generate preview only for UI
        if (isVoiceMessage(this.sendGroupFile.fileUrl)) {
          previewMessage = "🎤 Voice message";
          // Include media type in message field for receiver's preview
          if (!messageContent) {
            messageContent = "🎤 Voice message";
          }
        } else if (/\.(jpg|jpeg|png|gif|webp|svg)$/.test(fileUrlLower)) {
          previewMessage = "📷 Photo";
          // Include media type in message field for receiver's preview
          if (!messageContent) {
            messageContent = "sent a photo";
          }
        } else if (/\.(mp4|mov|avi|mkv|webm)$/.test(fileUrlLower)) {
          previewMessage = "🎥 Video";
          // Include media type in message field for receiver's preview
          if (!messageContent) {
            messageContent = "sent a video";
          }
        } else if (DOCUMENT_EXTENSIONS.some(ext => fileUrlLower.endsWith(ext.toLowerCase()))) {
          previewMessage = "📄 Document";
          // Include media type in message field for receiver's preview
          if (!messageContent) {
            messageContent = "sent a document";
          }
        } else {
          previewMessage = "📎 File";
          // Include media type in message field for receiver's preview
          if (!messageContent) {
            messageContent = "sent a file";
          }
        }
      }

      let contentType = 'text';
      let contentSubtype = 'text';

      if (gifToSend) {
        contentType = 'image';
        contentSubtype = 'other';
      } else if (this.sendGroupFile) {
        const categorization = this.getContentCategorization(this.sendGroupFile.fileUrl || this.sendGroupFile.name, this.sendGroupFile.type);
        contentType = categorization.contentType;
        contentSubtype = categorization.contentSubtype;
      } else if (isURL(this.newGroupMessage.trim())) {
        const categorization = this.getLinkContentCategorization(this.newGroupMessage.trim());
        contentType = categorization.contentType;
        contentSubtype = categorization.contentSubtype;
      }

      const tempId = Date.now().toString();
      let messageObject: any = {
        message: messageContent,
        sender_id: this.userId,
        createdAt: new Date().toISOString(),
        tempId: tempId, // Add tempId for deduplication
        replyTo: this.replyingToMessage ? {
          id: this.replyingToMessage._id || this.replyingToMessage.id,
          message: this.replyingToMessage.message || "Media",
          sender_id: this.replyingToMessage.sender_id || this.replyingToMessage.senderId || this.replyingToMessage.user_id || this.replyingToMessage.sid,
          senderName: (this.replyingToMessage.sender_id && String(this.replyingToMessage.sender_id).trim() === String(this.userId).trim()) ? "You" : (this.replyingToMessage.senderName || this.replyingToMessage.name || (this.userNameMapping?.[this.replyingToMessage.sender_id] || "User")),
          sourceGroupName: this.replyingToMessage.sourceGroupName || this.replyingToMessage.meta?.sourceGroupName,
          sourceGroupId: this.replyingToMessage.sourceGroupId || this.replyingToMessage.meta?.sourceGroupId,
          url: this.replyingToMessage.url,
          files: this.replyingToMessage.files,
          meta: this.replyingToMessage.meta
        } : undefined,
        meta: {
          tempId: tempId, // Ensure it's in meta too
          mentions: this.messageMeta?.mentions || [],
          contentType: contentType,
          contentSubtype: contentSubtype,
          replyTo: this.replyingToMessage ? {
            id: this.replyingToMessage._id || this.replyingToMessage.id,
            message: this.replyingToMessage.message || "Media",
            sender_id: this.replyingToMessage.sender_id || this.replyingToMessage.senderId || this.replyingToMessage.user_id || this.replyingToMessage.sid,
            senderName: (this.replyingToMessage.sender_id && String(this.replyingToMessage.sender_id).trim() === String(this.userId).trim()) ? "You" : (this.replyingToMessage.senderName || this.replyingToMessage.name || (this.userNameMapping?.[this.replyingToMessage.sender_id] || "User")),
            sourceGroupName: this.replyingToMessage.sourceGroupName || this.replyingToMessage.meta?.sourceGroupName,
            sourceGroupId: this.replyingToMessage.sourceGroupId || this.replyingToMessage.meta?.sourceGroupId,
            url: this.replyingToMessage.url,
            files: this.replyingToMessage.files,
            meta: this.replyingToMessage.meta
          } : undefined
        },
      };

      // Handle GIF metadata
      if (gifToSend) {
        messageObject.url = gifToSend;
        messageObject.message = messageContent || ""; // Include media type for receiver's preview
        messageObject.meta = {
          ...messageObject.meta,
          channel_id: this.memberChannelId,
          gifUrl: gifToSend,
          mediaType: "gif",
          fileType: "image/gif",
          fileUrl: gifToSend,
        };
        messageObject.preview = "GIF";
      }

      // Handle URL metadata (skipped offline so the queued message shows instantly)
      if (isURL(this.newGroupMessage.trim()) && !gifToSend && navigator.onLine) {
        let body: any = {
          url: this.newGroupMessage,
          channelId: this.memberChannelId,
        };
        if (this.fileId) body["fileId"] = this.fileId;

        const res: any = await this.OnlineUsersService.isUrlValidSave(body);
        if (res?.data) {
          const metaTitle = res.data.meta?.title || '';
          const metaDesc = res.data.meta?.description || '';
          messageObject.meta = {
            ...messageObject.meta,
            title: metaTitle,
            description: metaDesc,
            image: res.data.meta?.image || '',
            icon: res.data.meta?.icon || '',
            version: res.data.version || '',
            thread: res.data.thread || [],
            fileId: res.data.fileId || body.fileId
          };
          // Store meta for backend full-text search
          const reqSearch: string[] = [];
          if (metaTitle) reqSearch.push(metaTitle);
          if (metaDesc) reqSearch.push(metaDesc);
          if (reqSearch.length > 0) {
            messageObject.reqSearch = reqSearch;
          }
        }
      }

      // Handle file metadata
      if (this.sendGroupFile && !gifToSend) {
        messageObject.url = this.sendGroupFile.fileUrl;
        messageObject.meta = {
          ...messageObject.meta,
          caption: this.newGroupMessage || "",
          ...(this.newGroupMessage ? { captionContentType: isURL(this.newGroupMessage) ? 'url' : 'text' } : {}),
          fileUrl: this.sendGroupFile.fileUrl,
          fileType: this.sendGroupFile.type || "file",
          ...(this.sendGroupFile.duration ? { duration: this.sendGroupFile.duration } : {})
        };
        // Explicitly format files array for local display
        messageObject.files = [{
          fileUrl: this.sendGroupFile.fileUrl,
          type: this.sendGroupFile.type || "file",
          name: this.sendGroupFile.name || "File"
        }];
      }

      // Build push notifications up front so a queued message can emit them once it is delivered
      const group = this.filteredGroupUsers.find(
        (group) => group.channelId === sendChannelId
      );
      const groupName = group?.groupName || "Group";
      const senderName = this.userNameMapping[this.userId] || "User";
      const groupRedirectUrl = `${environment.redirectUrl}#/main-chat?chatType=group&channelId=${sendChannelId}`;
      const notifyMentions: any[] = [...(this.messageMeta?.mentions || [])];

      // If user typed @all directly into the message text, ensure @all mention is tracked
      const rawText = messageContent || "";
      const allMatch = /(^|[^\w.+-])@all\b/i.exec(rawText);
      if (allMatch && !notifyMentions.some((m: any) => m.type === 'all' || m.userId === 'all')) {
        notifyMentions.push({
          userId: 'all',
          name: 'all',
          displayName: 'All',
          position: allMatch.index + allMatch[1].length,
          length: 4,
          type: 'all'
        });
      }

      const notifications: any[] = [];
      if (notifyMentions.length) {
        // Check if this is an @all mention
        const hasAllMention = notifyMentions.some((m: any) => m.type === 'all' || m.userId === 'all');

        if (hasAllMention) {
          // Fan out to every active member except the sender
          const activeMembers = (this.allGroupMembers || []).filter(
            (m: any) => !m.isRemoved && !m.leftGroup && String(m.userId).trim() !== String(this.userId).trim()
          );
          activeMembers.forEach((member: any) => {
            notifications.push({
              channelId: sendChannelId,
              title: groupName,
              body: `${senderName} mentioned all members in ${groupName}`,
              redirectUrl: groupRedirectUrl,
              icon: "https://link.iiclab.com/assets/login/header-logo.png",
              sender_id: this.userId,
              user_id: member.userId,
              groupName: groupName,
            });
          });
        } else {
          notifyMentions.forEach((mention: any) => {
            notifications.push({
              channelId: sendChannelId,
              title: `${senderName}`,
              body: messageContent || (gifToSend ? "Sent a GIF" : "Sent a file"),
              redirectUrl: groupRedirectUrl,
              icon: "https://link.iiclab.com/assets/login/header-logo.png",
              sender_id: this.userId,
              user_id: mention.userId,
              groupName: groupName,
            });
          });
        }
      } else {
        notifications.push({
          channelId: sendChannelId,
          title: groupName,
          body: `${senderName}\n${messageContent || (gifToSend ? "Sent a GIF" : "Sent a file")}`,
          redirectUrl: groupRedirectUrl,
          icon: "https://link.iiclab.com/assets/login/header-logo.png",
          sender_id: this.userId,
          groupName: groupName,
        });
      }
      const outboxItem: OutboxItem = {
        chatType: "group",
        message: messageObject,
        notifications,
        preview: gifToSend ? "GIF" : previewMessage,
      };

      // Optimistic Push: Add to UI immediately before sending
      const tempPushId = messageObject.meta.tempId;
      this.groupChatMessages.push(messageObject);
      this.processGroupMessages();
      setTimeout(() => this.scrollToBottom(), 50);

      // Send the message, or queue it in the outbox if disconnected / the send fails
      let sent = false;
      if (isChannelConnected) {
        try {
          await groupChatChannel.sendMessage(messageObject);
          sent = true;
        } catch (sendError) {
          console.warn("Group sendMessage failed, queueing in outbox:", sendError);
        }
      }
      if (!sent) {
        messageObject.pending = true;
        this.queueOutboxMessage(sendChannelId, outboxItem);
      }

      this.isSuggestionBoxVisible2 = false;
      this.MetaDataDetails2 = null;
      this.MedataLength2 = 0;
      this.showEmojiPicker = false;
      const listenerAddedMsg = this.groupChatMessages.find((m: any) =>
        m !== messageObject && m.meta?.tempId === tempPushId
      );

      if (listenerAddedMsg) {
        console.log("Duplicate message detected after send, deduplicating...");
        const index = this.groupChatMessages.indexOf(messageObject);
        if (index > -1) {
          this.groupChatMessages.splice(index, 1);
        }
      }

      if (sent) {
        this.onOutboxMessageDelivered(sendChannelId, outboxItem);
      }

      // Update group preview in both allGroupUsers and filteredGroupUsers
      const groupIndex = this.allGroupUsers.findIndex(
        (group) => group.channelId === this.memberChannelId
      );
      if (groupIndex !== -1) {
        this.allGroupUsers[groupIndex].lastMessage = {
          ...messageObject,
          message: gifToSend ? "GIF" : previewMessage,
          createdAt: messageObject.createdAt
        };
        this.allGroupUsers[groupIndex].lastMessageTimeAgo = getTimeAgo(
          new Date(messageObject.createdAt)
        );
      }

      const filteredGroupIndex = this.filteredGroupUsers.findIndex(
        (group) => group.channelId === this.memberChannelId
      );
      if (filteredGroupIndex !== -1) {
        this.filteredGroupUsers[filteredGroupIndex].lastMessage = {
          ...messageObject,
          message: gifToSend ? "GIF" : previewMessage,
          createdAt: messageObject.createdAt
        };
        this.filteredGroupUsers[filteredGroupIndex].lastMessageTimeAgo = getTimeAgo(
          new Date(messageObject.createdAt)
        );
      }

      this.sortAllChatsByRecentMessages();
      this.cdRef.detectChanges();

      // Reset and clean up
      this.chatDraftService.clearDraft('group', this.memberChannelId);
      this.newGroupMessage = "";
      this.replyingToMessage = null;
      this.sendGroupFile = null;
      this.selectedGifUrlGroup = null;
      this.MetaDataDetails = null;
      this.MedataLength = 0;
      this.messageMeta = null;
      this.isSuggestionBoxVisible = false;
      this.lastProcessedUrl = "";

      // Reset textarea height to initial state
      // this.resetTextareaHeight();

      // Trigger change detection to ensure textarea is cleared
      this.cdRef.detectChanges();

      // Ensure keyboard stays open on mobile
      if (this.groupMessageInput) {
        setTimeout(() => {
          this.groupMessageInput.focus();
        }, 50);
      }

      this.groupMessagesByDate();
      this.updateCacheForActiveChannel();
    } catch (error) {
      console.error("Error sending group chat message:", error);
    } finally {
      this.isSending = false;
    }
  }

  // Helper function to generate file preview
  generateFilePreview(fileUrl: string): string {
    const lowerUrl = (fileUrl || "").toLowerCase();
    if (isVoiceMessage(fileUrl)) return "sent an audio";
    if (/\.(jpg|jpeg|png|gif|webp|svg)$/.test(lowerUrl)) {
      return "sent a photo";
    } else if (/\.(mp4|mov|avi|mkv|webm)$/.test(lowerUrl)) {
      return "sent a video";
    } else if (/\.(mp3|wav|ogg|m4a|aac|flac)$/.test(lowerUrl)) {
      return "sent an audio";
    } else if (DOCUMENT_EXTENSIONS.some(ext => lowerUrl.endsWith(ext.toLowerCase()))) {
      return "sent a document";
    }
    return "sent a file";
  }

  // ==================== GIF METHODS (SINGLE CHAT) ====================
  toggleGifPanel(): void {
    this.showGifPanel = !this.showGifPanel;
    if (!this.showGifPanel) {
      this.gifResults = [];
      this.gifQuery = "";
      this.gifError = null;
      this.gifCurrentPage = 1;
      this.gifHasNext = false;
    } else {
      if (this.gifQuery.trim()) {
        this.searchGifs();
      } else if (this.gifResults.length === 0) {
        this.loadTrendingGifs();
      }
    }
  }

  loadTrendingGifs(): void {
    this.isGifLoading = true;
    this.gifError = null;
    this.klipyService.getTrendingGifs(1, 20).subscribe({
      next: (res) => {
        this.gifResults = res.gifs;
        this.gifHasNext = res.hasNext;
        this.isGifLoading = false;
      },
      error: (err) => {
        console.error("Error loading trending GIFs", err);
        this.gifError = "Unable to load GIFs. Please try again.";
        this.isGifLoading = false;
      },
    });
  }

  searchGifs(): void {
    const q = this.gifQuery.trim();
    if (!q) {
      this.loadTrendingGifs();
      return;
    }
    this.gifCurrentPage = 1;
    this.isGifLoading = true;
    this.gifError = null;
    this.klipyService.searchGifs(q, 1, 20).subscribe({
      next: (res) => {
        this.gifResults = res.gifs;
        this.gifHasNext = res.hasNext;
        this.isGifLoading = false;
      },
      error: (err) => {
        console.error("Error searching Klipy GIFs", err);
        this.gifError = "Unable to load GIFs. Please try again.";
        this.isGifLoading = false;
      },
    });
  }

  loadMoreGifs(): void {
    if (!this.gifQuery.trim() || !this.gifHasNext) {
      return;
    }
    this.gifCurrentPage++;
    this.isGifLoading = true;
    this.klipyService.searchGifs(this.gifQuery.trim(), this.gifCurrentPage, 20).subscribe({
      next: (res) => {
        this.gifResults = [...this.gifResults, ...res.gifs];
        this.gifHasNext = res.hasNext;
        this.isGifLoading = false;
      },
      error: (err) => {
        console.error("Error loading more Klipy GIFs", err);
        this.gifError = "Unable to load more GIFs.";
        this.isGifLoading = false;
      },
    });
  }

  selectGif(gifUrl: string): void {
    this.selectedGifUrl = gifUrl;
    this.showGifPanel = false;
    // Send directly
    this.sendChatMessage();
  }

  clearSelectedGif(): void {
    this.selectedGifUrl = null;
  }

  // ==================== GIF METHODS (GROUP CHAT) ====================

  toggleGifPanelGroup(): void {
    this.showGifPanelGroup = !this.showGifPanelGroup;
    if (!this.showGifPanelGroup) {
      this.gifResultsGroup = [];
      this.gifQueryGroup = "";
      this.gifErrorGroup = null;
      this.gifCurrentPageGroup = 1;
      this.gifHasNextGroup = false;
    } else {
      // Auto-load trending GIFs when panel opens
      if (this.gifQueryGroup.trim()) {
        this.searchGifsGroup();
      } else if (this.gifResultsGroup.length === 0) {
        this.loadTrendingGifsGroup();
      }
    }
  }

  loadTrendingGifsGroup(): void {
    this.isGifLoadingGroup = true;
    this.gifErrorGroup = null;
    this.klipyService.getTrendingGifs(1, 20).subscribe({
      next: (res) => {
        this.gifResultsGroup = res.gifs;
        this.gifHasNextGroup = res.hasNext;
        this.isGifLoadingGroup = false;
      },
      error: (err) => {
        console.error("Error loading trending GIFs", err);
        this.gifErrorGroup = "Unable to load GIFs. Please try again.";
        this.isGifLoadingGroup = false;
      },
    });
  }

  searchGifsGroup(): void {
    const q = this.gifQueryGroup.trim();
    if (!q) {
      this.loadTrendingGifsGroup();
      return;
    }
    this.gifCurrentPageGroup = 1;
    this.isGifLoadingGroup = true;
    this.gifErrorGroup = null;
    this.klipyService.searchGifs(q, 1, 20).subscribe({
      next: (res) => {
        this.gifResultsGroup = res.gifs;
        this.gifHasNextGroup = res.hasNext;
        this.isGifLoadingGroup = false;
      },
      error: (err) => {
        console.error("Error searching Klipy GIFs", err);
        this.gifErrorGroup = "Unable to load GIFs. Please try again.";
        this.isGifLoadingGroup = false;
      },
    });
  }

  loadMoreGifsGroup(): void {
    if (!this.gifQueryGroup.trim() || !this.gifHasNextGroup) {
      return;
    }
    this.gifCurrentPageGroup++;
    this.isGifLoadingGroup = true;
    this.klipyService.searchGifs(this.gifQueryGroup.trim(), this.gifCurrentPageGroup, 20).subscribe({
      next: (res) => {
        this.gifResultsGroup = [...this.gifResultsGroup, ...res.gifs];
        this.gifHasNextGroup = res.hasNext;
        this.isGifLoadingGroup = false;
      },
      error: (err) => {
        console.error("Error loading more Klipy GIFs", err);
        this.gifErrorGroup = "Unable to load more GIFs.";
        this.isGifLoadingGroup = false;
      },
    });
  }

  selectGifGroup(gifUrl: string): void {
    // Store selected GIF URL with metadata
    this.selectedGifUrlGroup = gifUrl;
    this.showGifPanelGroup = false;
    // Send directly
    this.sendGroupChatMessage();
  }

  clearSelectedGifGroup(): void {
    this.selectedGifUrlGroup = null;
  }

  groupedMessages: any[] = [];
  chat_messages: any[] = [];
  groupMessagesByDate(): void {
    const messageMap = new Map<string, any[]>();

    this.chat_messages.forEach((message) => {
      const date = new Date(message.createdAt).toDateString();
      if (!messageMap.has(date)) {
        messageMap.set(date, []);
      }
      messageMap.get(date).push(message);
    });
    this.groupedMessages = [];
    messageMap.forEach((messages, dateString) => {
      this.groupedMessages.push({ date: new Date(dateString), messages });
    });
    this.groupedMessages.sort((a, b) => b.date.getTime() - a.date.getTime());
  }

  normalizeUrl(url: string): string {
    if (!url) return "";
    return url.trim().replace(/\/+$/, "");
  }

  // ==================== TYPING STATUS METHODS ====================
  async preConnectChannelsForTyping() {
    if (!this.converse) return;
    // Skip while Converse is disconnected — every connectChannel would just fail
    if (!this.converse.checkConnection?.()) return;

    // Connect to maximum 20 recent channels to listen for typing
    const channelsToConnect = this.sortedChats.slice(0, 20);

    await Promise.all(channelsToConnect.map(async (chat) => {
      if (!chat.channelId) return;

      // Skip if it's already the active one
      if (chat.channelId === this.activeChannel || chat.channelId === this.memberChannelId) return;

      try {
        if (!this.backgroundTypingChannels.has(chat.channelId)) {
          const channel = await this.converse.connectChannel({ channelId: chat.channelId });

          if (channel) {
            this.backgroundTypingChannels.set(chat.channelId, channel);

            // Register typing listeners
            channel.listenTyping((data: any) => {
              this.zone.run(() => {
                const payload = data?.meta || data || {};
                const userId = payload.userId || payload.sender_id;
                const userName = payload.userName || payload.name || 'Someone';
                const channelId = payload.channelId || chat.channelId;
                if (!userId || userId === this.userId) return;
                const key = `${channelId}_${userId}`;
                this.typingUsers.set(key, { userId, userName, timestamp: Date.now() });
                this.cdRef.detectChanges();
              });
            });

            channel.listenStopTyping((data: any) => {
              this.zone.run(() => {
                const payload = data?.meta || data || {};
                const userId = payload.userId || payload.sender_id;
                const channelId = payload.channelId || chat.channelId;
                if (!userId) return;
                const key = `${channelId}_${userId}`;
                this.typingUsers.delete(key);
                this.cdRef.detectChanges();
              });
            });
          }
        }
      } catch (err) {
        console.warn(`Failed to pre-connect background channel for typing: ${chat.channelId}`);
      }
    }));
  }

  sendTypingStart() {
    const channel = this.singleChatBox ? this.singleChannel : this.groupChannel;
    const channelId = this.activeChannel || this.memberChannelId;
    if (channel && channelId) {
      try {
        channel.startTyping({
          userId: this.userId,
          userName: this.senderUsername,
          channelId
        });
      } catch (e) {
        console.warn('startTyping error:', e);
      }
    }
  }

  sendTypingStop() {
    const channel = this.singleChatBox ? this.singleChannel : this.groupChannel;
    const channelId = this.activeChannel || this.memberChannelId;
    if (channel && channelId) {
      try {
        channel.stopTyping({
          userId: this.userId,
          channelId
        });
      } catch (e) {
        console.warn('stopTyping error:', e);
      }
    }
  }

  getCurrentTypingUsers(): Array<{ userId: string; userName: string }> {
    const channelId = this.singleChatBox ? this.activeChannel : this.memberChannelId;
    if (!channelId) return [];

    const result: Array<{ userId: string; userName: string }> = [];
    this.typingUsers.forEach((value, key) => {
      if (key.startsWith(channelId + '_')) {
        result.push({ userId: value.userId, userName: value.userName });
      }
    });
    return result;
  }

  saveCurrentChatDraft(): void {
    if (this.editingMessage) return;
    const targetUserId = this.chatUserId || this.selectedChatUserId || this.singleUser?.userId;
    const targetChannelId = this.singleUser?.channelId || this.activeChannel;
    const targetGroupId = this.memberChannelId || this.selectedChatGroupId || this.groupChannel?.channelId;

    if (this.singleChatBox && (targetUserId || targetChannelId)) {
      this.chatDraftService.setDraft('single', targetUserId, this.newMessage, targetChannelId);
      this.chatDraftService.flush();
    } else if (this.groupChatBox && targetGroupId) {
      this.chatDraftService.setDraft('group', targetGroupId, this.newGroupMessage);
      this.chatDraftService.flush();
    }
  }

  onSingleMessageChange(value: string): void {
    this.newMessage = value;
    const targetUserId = this.chatUserId || this.selectedChatUserId || this.singleUser?.userId;
    const targetChannelId = this.singleUser?.channelId || this.activeChannel;
    if (!this.editingMessage && (targetUserId || targetChannelId)) {
      this.chatDraftService.setDraft('single', targetUserId, value, targetChannelId);
    }
  }

  onGroupMessageChange(value: string): void {
    this.newGroupMessage = value;
    const targetGroupId = this.memberChannelId || this.selectedChatGroupId || this.groupChannel?.channelId;
    if (!this.editingMessage && targetGroupId) {
      this.chatDraftService.setDraft('group', targetGroupId, value);
    }
  }

  private isFirstTextTyping: boolean = true;
  skipApi: boolean = false;
  onInputChange(): void {
    // this.autoResizeTextarea();
    setTimeout(() => {
      if (!this.newMessage) {
        if (this.chatUserId && !this.editingMessage) {
          this.chatDraftService.clearDraft('single', this.chatUserId, this.singleUser?.channelId);
        }
        this.MetaDataDetails = null;
        this.MedataLength = 0;
        this.filterDataSearch = [];
        this.filterDataSearchLength = 0;
        this.isFirstTextTyping = true;
        this.skipApi = false;
        this.lastProcessedUrl = "";
        this.isSuggestionBoxVisible = false;
        return;
      }

      // Handle slash command filtering
      if (this.newMessage[0] === "/") {
        let newText = this.newMessage.slice(1);
        if (newText) {
          let text = newText.toLowerCase();
          let filterData = this.singleChatMessages.filter((msg: any) =>
            msg.message?.toLowerCase()?.includes(text)
          );
          this.filterDataSearch = filterData;
          this.filterDataSearchLength = filterData.length;
        }
      }

      const isUrl = isURL(this.newMessage);

      if (!isUrl) {
        this.MetaDataDetails = null;
        this.MedataLength = 0;
        this.skipApi = false;
        this.lastProcessedUrl = "";
        this.isSuggestionBoxVisible = false;
        return;
      }
      const normalizedInputUrl = this.normalizeUrl(this.newMessage);
      this.lastProcessedUrl = normalizedInputUrl;
      const body = {
        url: normalizedInputUrl,
        channelId: this.singleUser.channelId,
      };

      this.OnlineUsersService.isUrlValid(body).subscribe((res: any) => {
        if (res?.data) {
          const incomingFileUrl = this.normalizeUrl(res.data.fileUrl);
          this.MetaDataDetails = res.data;

          if (!this.MetaDataDetails.thread) {
            this.MetaDataDetails.thread = [];
          }

          this.MedataLength = this.MetaDataDetails.thread.length || 1;
          this.isSuggestionBoxVisible = true;
          this.isSuggestionBoxVisible2 = false;
        } else {
          this.MetaDataDetails = null;
          this.MedataLength = 0;
          this.isSuggestionBoxVisible = false;
        }
      });

      this.isFirstTextTyping = false;
    }, 0);
  }

  onPaste(event: ClipboardEvent, chatType: 'single' | 'group'): void {
    const items = event.clipboardData?.items;
    if (!items) return;
    const files: File[] = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file') {
        const file = items[i].getAsFile();
        if (file) {
          files.push(file);
        }
      }
    }

    if (files.length > 0) {
      if (files.length > 100) {
        alertModalWithoutConfirm('warning', 'File upload limit exceeded. Maximum 100 files allowed per upload.');
        files.splice(100);
      }
      event.preventDefault();
      const mockEvent = { target: { files: files, value: '' } };
      if (chatType === 'single') {
        this.handleFileInput(mockEvent);
      } else {
        this.handleGroupFileInput(mockEvent);
      }
    }
  }

  // SORT ALL CHATS BY LAST MESSAGES
  sortedChats: any[] = [];
  sortAllChatsByRecentMessages() {
    const allChats = [
      ...this.filteredSingleUsers.map((user) => ({
        ...user,
        chatType: "single",
        isPinned: !!user.isPinned
      })),
      ...this.filteredGroupUsers.map((group) => ({
        ...group,
        chatType: "group",
        isPinned: !!group.isPinned
      })),
    ];

    this.sortedChats = allChats.sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      // Pinned chats keep their pin order and don't move on new messages
      if (a.isPinned && b.isPinned) return (b.pinRank || 0) - (a.pinRank || 0);

      // Chats with no messages sink to the bottom (unless pinned)
      const aHasMessage = (a.lastMessage?.message && !a.isCleared) ? 1 : 0;
      const bHasMessage = (b.lastMessage?.message && !b.isCleared) ? 1 : 0;

      if (aHasMessage !== bHasMessage) {
        return bHasMessage - aHasMessage;
      }

      // Sort by time — cleared chats use _clearedMsgTime to keep original position
      const aTime = a.lastMessage?.createdAt
        ? new Date(a.lastMessage.createdAt).getTime()
        : (a.isCleared && a._clearedMsgTime
          ? new Date(a._clearedMsgTime).getTime()
          : (a.createdAt ? new Date(a.createdAt).getTime() : 0));
      const bTime = b.lastMessage?.createdAt
        ? new Date(b.lastMessage.createdAt).getTime()
        : (b.isCleared && b._clearedMsgTime
          ? new Date(b._clearedMsgTime).getTime()
          : (b.createdAt ? new Date(b.createdAt).getTime() : 0));

      return bTime - aTime;
    });

    // Synchronize chatMap with the latest chat data
    this.sortedChats.forEach((chat) => {
      if (chat.channelId) {
        this.chatMap[chat.channelId] = chat;
      }
    });

    this.cdRef.detectChanges();
  }

  onSuggestionSelectedAction(selectedSuggestion: string): void {
    console.log("this is selected suggestions", selectedSuggestion);
  }

  //URL SHARING SUGGESTION SELECT
  MetaDataDetails: any;
  MedataLength: any;
  private lastProcessedUrl: string = "";
  isSuggestionBoxVisible: boolean = true;
  filterDataSearch: any;
  filterDataSearchLength: any;
  selectSuggestion(selectedSuggestion: string, fileId?: any): void {
    this.newMessage = selectedSuggestion;
    if (!this.editingMessage && this.chatUserId) {
      this.chatDraftService.setDraft('single', this.chatUserId, selectedSuggestion, this.singleUser?.channelId);
    }
    this.fileId = fileId;
    this.filterDataSearch = [];
    this.filterDataSearchLength = 0;
    this.onSuggestionSelectedAction(selectedSuggestion);
  }

  //CLOSE URL SUGGESTION BOX
  closeSuggestionBox() {
    this.isSuggestionBoxVisible = false;
  }

  //UPLOADING MEDIA FILES
  uploadProgress: number = 0;
  isLoading: boolean = false;
  private getDynamicChunks(fileSize: number) {
    let chunks = [];
    let totalChunks = Math.ceil(fileSize / CHUNK_SIZE);

    for (let i = 0; i < totalChunks; i++) {
      let start = i * CHUNK_SIZE;
      let end = Math.min(start + CHUNK_SIZE, fileSize);
      if (
        i === totalChunks - 1 &&
        end - start < MIN_CHUNK_SIZE &&
        chunks.length > 0
      ) {
        let previousChunk = chunks.pop();
        start = previousChunk.start;
        end = fileSize;
        totalChunks--;
      }
      chunks.push({ start, end });
    }
    return { chunks, totalChunks };
  }

  //SINGLE CHAT
  pendingFiles: { file: File; progress: number }[] = [];
  currentUploadGroupId: string | null = null;
  async handleFileInput(event: any): Promise<void> {
    const files: FileList = event.target.files;
    if (!files || files.length === 0) return;

    const rawFilesArray = Array.from(files);
    const validFiles = rawFilesArray.filter((file) => this.isValidFileType(file));

    if (validFiles.length === 0) {
      alertModalWithoutConfirm("Warning", "Please drop valid files");
      if (event.target) event.target.value = '';
      return;
    }

    if (validFiles.length !== rawFilesArray.length) {
      alertModalWithoutConfirm("Warning", "Some files were skipped due to unsupported format");
    }

    let filesArray = validFiles;
    if (filesArray.length > 100) {
      alertModalWithoutConfirm('warning', 'Only 100 files are allowed at one time...');
      filesArray = filesArray.slice(0, 100);
    }

    // Client-side image compression for oversized photos before upload
    filesArray = await compressMultipleFiles(filesArray);

    // Build group-member list for mention picker (exclude sender, removed, left)
    const captionGroupMembers = this.groupChatBox
      ? (this.allGroupMembers || [])
        .filter((m: any) => !m.isRemoved && !m.leftGroup && m.userId !== this.userId)
      : [];
    const captionChannelId = this.groupChatBox
      ? this.memberChannelId
      : (this.singleUser?.channelId || this.activeChannel || '');

    // Open preview modal
    const replyToContext = this.replyingToMessage ? this.buildReplyToPayload(this.replyingToMessage) : null;
    const dialogRef = this.dialog.open(FilePreviewModalComponent, {
      width: "500px",
      panelClass: "file-preview-dialog-panel",
      data: {
        files: filesArray,
        isGroup: this.groupChatBox,
        groupMembers: captionGroupMembers,
        channelId: captionChannelId,
        replyTo: replyToContext
      },
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result === undefined) {
        if (event.target) event.target.value = "";
        return;
      }
      const filesToSend = result.files || Array.from(files);
      const additionalMessage = result.message || "";
      const captionMentions: any[] = result.mentions || [];
      const captionUrlMeta: any = result.urlMeta || null;
      const replyToSend = result.replyTo !== undefined ? result.replyTo : replyToContext;

      if (this.replyingToMessage) {
        this.cancelReply();
      }

      const uploadGroupId = Date.now().toString();
      this.currentUploadGroupId = uploadGroupId;
      const sessionFiles = filesToSend.map((file) => ({
        file,
        progress: 0,
      }));
      this.pendingFiles = sessionFiles; // Backward compatibility for any other parts
      this.isLoading = true;

      const tempGroupedMessage = {
        _id: uploadGroupId,
        sender_id: this.userId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        message: additionalMessage,
        files: filesToSend.map((f: File) => ({
          fileUrl: URL.createObjectURL(f),
          type: DOCUMENT_EXTENSIONS.some(ext => (f.name || '').toLowerCase().endsWith(ext.toLowerCase())) ? getFileType(f.name) : (f.type || getFileType(f.name)),
          name: f.name,
          size: f.size,
          isLocal: true
        })),
        url: undefined,
        currentSlideIndex: 0,
        replyTo: replyToSend || undefined,
        meta: {
          channel_id: this.singleUser?.channelId,
          isGroupedUpload: true,
          groupId: uploadGroupId,
          tempId: uploadGroupId,
          caption: additionalMessage,
          ...(replyToSend ? { replyTo: replyToSend } : {}),
          ...(additionalMessage ? { captionContentType: isURL(additionalMessage) ? 'url' : 'text' } : {}),
          ...(captionMentions.length > 0 ? { mentions: captionMentions } : {}),
          ...(captionUrlMeta ? {
            title: captionUrlMeta.title || '',
            description: captionUrlMeta.description || '',
            image: captionUrlMeta.image || '',
            icon: captionUrlMeta.icon || '',
            version: captionUrlMeta.version || '',
            thread: captionUrlMeta.thread || [],
            ...(captionUrlMeta.fileId ? { fileId: captionUrlMeta.fileId } : {})
          } : {})
        },
        name: this.userNameMapping[this.userId] || "User",
        profileImage: this.profileImageMapping[this.userId] || "",
        isUploading: true,
      };
      this.singleChatMessages = [...this.singleChatMessages, tempGroupedMessage];
      this.updateCacheForActiveChannel();
      this.scrollToBottom();

      // Track in background map for navigation persistence
      const uploadChannelId = this.activeChannel;
      if (uploadChannelId) {
        const pending = this.pendingUploadsByChannel.get(uploadChannelId) || [];
        this.pendingUploadsByChannel.set(uploadChannelId, [...pending, tempGroupedMessage]);
      }

      try {
        const uploadedFiles = await Promise.all(
          filesToSend.map((file) => this.uploadFile(file, additionalMessage, uploadChannelId, uploadGroupId, sessionFiles, captionMentions, captionUrlMeta, replyToSend))
        );
        const validId = uploadedFiles.find((f: any) => f._id)?._id;

        const finalizeMessage = (msgList: any[]) => {
          let messageIndex = msgList.findIndex((msg) => msg._id === uploadGroupId);
          if (messageIndex === -1 && uploadGroupId) {
            messageIndex = msgList.findIndex((msg) => msg.meta?.groupId === uploadGroupId);
          }
          if (messageIndex === -1 && validId) {
            messageIndex = msgList.findIndex((msg) => msg._id === validId || msg.id === validId || msg.messageId === validId);
          }

          if (messageIndex !== -1) {
            const updatedMessage = {
              ...msgList[messageIndex],
              message: additionalMessage,
              files: uploadedFiles,
              replyTo: replyToSend || msgList[messageIndex].replyTo,
              // Drop the cached blob: file list from the temp message so the real URLs are used
              _processedFiles: undefined,
              _allIds: uploadedFiles.map((f: any) => f._id).filter(id => !!id),
              ...(validId ? { _id: validId, id: validId } : {}),
              isUploading: false,
              meta: {
                ...msgList[messageIndex]?.meta,
                ...(replyToSend ? { replyTo: replyToSend } : {})
              }
            };
            return {
              list: [
                ...msgList.slice(0, messageIndex),
                updatedMessage,
                ...msgList.slice(messageIndex + 1),
              ],
              updatedMessage
            };
          }
          return { list: msgList, updatedMessage: null };
        };

        // 1. Update active UI if still in same chat
        if (this.activeChannel === uploadChannelId && this.singleChatBox) {
          const { list, updatedMessage } = finalizeMessage(this.singleChatMessages);
          this.singleChatMessages = list;
          if (updatedMessage) this.notifyReceiver(updatedMessage);
        }

        // 1.5. Update cache if it exists
        if (uploadChannelId && this.cachedSingleMessages[uploadChannelId]) {
          const { list } = finalizeMessage(this.cachedSingleMessages[uploadChannelId]);
          this.cachedSingleMessages[uploadChannelId] = list;
        }

        // 2. Update background map (and remove since finished)
        if (uploadChannelId) {
          const pending = this.pendingUploadsByChannel.get(uploadChannelId) || [];
          const remaining = pending.filter(m => m._id !== uploadGroupId && m.meta?.groupId !== uploadGroupId);
          if (remaining.length > 0) {
            this.pendingUploadsByChannel.set(uploadChannelId, remaining);
          } else {
            this.pendingUploadsByChannel.delete(uploadChannelId);
          }
        }

        this.sendfile = null;
        if (this.activeChannel === uploadChannelId) {
          setTimeout(() => this.scrollToBottom(), 50);
        }
      } catch (error) {
        console.error("Error uploading files:", error);
        // Remove from background on failure too
        if (uploadChannelId) {
          const pending = this.pendingUploadsByChannel.get(uploadChannelId) || [];
          this.pendingUploadsByChannel.set(uploadChannelId, pending.filter(m => m._id !== uploadGroupId));
        }
      } finally {
        this.isLoading = false;
        this.uploadProgress = 0;
        this.pendingFiles = [];
        if (this.currentUploadGroupId === uploadGroupId) {
          this.currentUploadGroupId = null;
        }
        if (event.target) event.target.value = "";
      }
    });
  }

  isDragOver = false;
  dragOverTimer: any;
  // Drag and Drop Methods
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    // Clear any existing timer
    if (this.dragOverTimer) {
      clearTimeout(this.dragOverTimer);
    }
    this.isDragOver = true;
    const textarea = event.target as HTMLElement;
    if (textarea.classList.contains("custom-textarea")) {
      textarea.classList.add("drag-over");
    }
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOverTimer = setTimeout(() => {
      this.isDragOver = false;
      const textarea = event.target as HTMLElement;
      if (textarea.classList.contains("custom-textarea")) {
        textarea.classList.remove("drag-over");
      }
    }, 100);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    // Clear the timer
    if (this.dragOverTimer) {
      clearTimeout(this.dragOverTimer);
    }

    this.isDragOver = false;

    // Remove visual feedback
    const textarea = event.target as HTMLElement;
    if (textarea.classList.contains("custom-textarea")) {
      textarea.classList.remove("drag-over");
    }

    // Get the dropped files
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleDroppedFiles(files);
    }
  }

  async handleDroppedFiles(files: FileList): Promise<void> {
    // Validate file types
    const validFiles = Array.from(files).filter((file) =>
      this.isValidFileType(file)
    );

    if (validFiles.length === 0) {
      alertModalWithoutConfirm("Warning", "Please drop valid files");
      return;
    }

    if (validFiles.length !== files.length) {
      alertModalWithoutConfirm("Warning", "Some files were skipped due to unsupported format");
    }

    // Reuse handleFileInput logic to show preview modal and handle upload
    const mockEvent = {
      target: {
        files: validFiles,
        value: ''
      }
    };
    await this.handleFileInput(mockEvent);
  }

  isValidFileType(file: File): boolean {
    return isValidFileType(file);
  }


  async uploadFile(file: File, caption: string = "", channelId: string, uploadGroupId: string, sessionFiles: any[], captionMentions: any[] = [], captionUrlMeta: any = null, replyTo: any = null): Promise<any> {
    return new Promise(async (resolve, reject) => {
      const fileBuffer = await file.arrayBuffer();
      const fileBytes = fileBuffer.byteLength;
      let { chunks, totalChunks } = this.getDynamicChunks(file.size);

      try {
        const response = await this.chatService
          .getPreSignedUrls(file.name, totalChunks)
          .toPromise();

        if (!response?.data) {
          console.error("Error: Invalid response from pre-signed URL API.");
          reject("Invalid response from server");
          return;
        }

        const { uploadId, preSignedUrls, fileName, fileUrl, compressedImgUrl, thumbnailUrl, ogFileName } = response.data;
        let chunkProgress: { [key: number]: number } = {};

        // Upload chunks in parallel and preserve order
        let uploadedParts: any[] = new Array(preSignedUrls.length);
        await Promise.all(
          preSignedUrls.map(async ({ partNumber, url }: any, index: number) => {
            const chunk = file.slice(chunks[index].start, chunks[index].end);

            try {
              const res = await axios.put(url, chunk, {
                headers: { "Content-Type": "application/octet-stream" },
                onUploadProgress: (progressEvent) => {
                  if (progressEvent.loaded && progressEvent.total) {
                    chunkProgress[partNumber] = progressEvent.loaded;
                    const totalUploaded = Object.values(chunkProgress).reduce(
                      (a, b) => a + b,
                      0
                    );
                    const progress = Math.floor(
                      (totalUploaded / fileBytes) * 100
                    );

                    const fileIndex = sessionFiles.findIndex(
                      (f) => f.file === file
                    );
                    if (fileIndex !== -1) {
                      sessionFiles[fileIndex].progress = progress;
                    }

                    this.zone.run(() => {
                      // Aggregate progress for all files in THIS session
                      const totalProgress = sessionFiles.reduce((acc, curr) => acc + curr.progress, 0);
                      const groupProgress = Math.floor(totalProgress / sessionFiles.length);

                      // Update global progress for backward compatibility (may flicker but that's okay now)
                      this.uploadProgress = groupProgress;

                      // Update progress in the message object itself for persistent UI
                      const updateProgressInList = (list: any[]) => {
                        const idx = list.findIndex(m => m._id === uploadGroupId || m.meta?.groupId === uploadGroupId);
                        if (idx !== -1) {
                          list[idx] = { ...list[idx], progress: groupProgress };
                        }
                      };

                      if (this.activeChannel === channelId && this.singleChatBox) {
                        updateProgressInList(this.singleChatMessages);
                      }

                      const pending = this.pendingUploadsByChannel.get(channelId) || [];
                      updateProgressInList(pending);

                      this.cdRef.detectChanges();
                    });
                  }
                },
              });

              const eTag = res.headers["etag"]?.replace(/^"(.*)"$/, "$1");
              if (eTag) {
                uploadedParts[index] = { PartNumber: partNumber, ETag: eTag };
              }
            } catch (error) {
              console.error(`Chunk ${partNumber} upload failed:`, error);
              throw error;
            }
          })
        );

        let fileType = DOCUMENT_EXTENSIONS.some(ext => (file.name || '').toLowerCase().endsWith(ext.toLowerCase()))
          ? getFileType(file.name)
          : (file.type || getFileType(file.name));
        if (DOCUMENT_EXTENSIONS.some(ext => (file.name || fileUrl || '').toLowerCase().endsWith(ext.toLowerCase()))) {
          fileType = getFileType(file.name || fileUrl);
        } else if (fileUrl && (fileType === 'application/octet-stream' || !fileType || fileType.startsWith('video/'))) {
          if (isVoiceMessage(fileUrl)) fileType = 'audio/mpeg';
          else if (isVideo(fileUrl)) fileType = 'video/mp4';
          else if (isImage(fileUrl)) fileType = 'image/jpeg';
          else if (isAudio(fileUrl)) fileType = 'audio/mpeg';
        }

        const completeRes: any = await this.chatService
          .completeUpload(
            fileName,
            uploadId,
            uploadedParts,
            file.size,
            channelId || this.activeChannel,
            {
              file: fileUrl,
              message: caption || ogFileName || file.name || fileName,
              replyTo: replyTo || undefined,
              meta: {
                isGroupedUpload: true,
                groupId: uploadGroupId,
                tempId: uploadGroupId,
                caption: caption || "",
                ...(replyTo ? { replyTo } : {}),
                ...(caption ? { captionContentType: isURL(caption) ? 'url' : 'text' } : {}),
                ...(captionMentions && captionMentions.length > 0 ? { mentions: captionMentions } : {}),
                ...(captionUrlMeta ? {
                  title: captionUrlMeta.title || '',
                  description: captionUrlMeta.description || '',
                  image: captionUrlMeta.image || '',
                  icon: captionUrlMeta.icon || '',
                  version: captionUrlMeta.version || '',
                  thread: captionUrlMeta.thread || [],
                  ...(captionUrlMeta.fileId ? { fileId: captionUrlMeta.fileId } : {})
                } : {}),
                fileType: fileType,
                ...this.getContentCategorization(file.name, fileType),
                ...(compressedImgUrl ? { compressedImgUrl } : {}),
                ...(thumbnailUrl ? { thumbnailUrl } : {}),
                ...((file as any).duration ? { duration: (file as any).duration } : {}),
                size: file.size,
                ogFileName: ogFileName || file.name
              },
              type: false,
            }
          )
          .toPromise();

        resolve({
          fileUrl,
          name: ogFileName || file.name,
          fileName: ogFileName || file.name,
          ogFileName: ogFileName || file.name,
          size: file.size,
          type: fileType,
          _id: completeRes?.data?._id || completeRes?._id
        });
      } catch (err) {
        console.error("Upload process failed:", err);
        reject(err);
      }
    });
  }



  private notifyReceiver(groupedMessage: any): void {
    let previewMessage = "Sent multiple files";

    if (groupedMessage.files?.length === 1) {
      previewMessage = this.getFilePreview(
        groupedMessage.files[0].fileUrl || ""
      );
    }

    // Update user preview
    const user = this.filteredSingleUsers.find(
      (user) => user.userId === this.newReceiver
    );
    if (user) {
      user.lastMessage = {
        ...groupedMessage,
        message: previewMessage,
      };
      user.lastMessageTimeAgo = getTimeAgo(groupedMessage.createdAt);
      this.sortAllChatsByRecentMessages();
    }

    // Send notification
    this.chatService.emitPushNotification({
      channelId: this.singleUser.channelId,
      title: this.senderUsername,
      body: previewMessage,
      redirectUrl: `${environment.redirectUrl}#/main-chat?chatType=single&userId=${this.userId}`,
      icon: "https://link.iiclab.com/assets/login/header-logo.png",
      sender_id: this.userId,
    });
  }

  currentSlideIndex = 0;
  currentMessageIdForCarousel: string | null = null;
  prevSlide(message: any) {
    const len = this.getFilesFromMessage(message).length;
    if (len <= 1) return;
    const current = message.currentSlideIndex || 0;
    message.currentSlideIndex = current > 0 ? current - 1 : len - 1;
  }

  nextSlide(message: any) {
    const len = this.getFilesFromMessage(message).length;
    if (len <= 1) return;
    const current = message.currentSlideIndex || 0;
    message.currentSlideIndex = current < len - 1 ? current + 1 : 0;
  }

  goToSlide(message: any, index: number) {
    if (this.getFilesFromMessage(message).length <= 1) return;
    message.currentSlideIndex = index;
  }

  isActiveSlide(message: any, index: number): boolean {
    return (
      this.currentMessageIdForCarousel === message._id &&
      this.currentSlideIndex === index
    );
  }

  //MENTION
  groupChatBox: boolean = false;
  mentionUsers: any[] = [];
  showMentionList: boolean = false;
  mentionSearchTerm: string = "";
  currentMentionPosition: number = 0;
  checkForMention(event: any) {
    const text = (event?.target && typeof event.target.value === 'string')
      ? event.target.value
      : (this.newGroupMessage || "");
    const cursorPos = event?.target?.selectionStart ?? text.length;

    const lastAtPos = text.lastIndexOf("@", cursorPos - 1);

    if (lastAtPos >= 0) {
      if (lastAtPos > 0 && /[\w.+-]/.test(text[lastAtPos - 1])) {
        this.showMentionList = false;
        return;
      }
      const mentionText = text.substring(lastAtPos + 1, cursorPos);

      const isMentioning = /^[a-zA-Z0-9_ ]*$/.test(mentionText);

      if (isMentioning) {
        // Filter out left/removed members AND the current user
        const mentionable = (this.allGroupMembers || []).filter(
          (m: any) => !m.isRemoved && !m.leftGroup && String(m.userId).trim() !== String(this.userId).trim()
        );

        // Prepend the synthetic @all entry
        const allEntry = {
          userId: '__all__',
          name: 'All',
          userName: 'all',
          displayName: 'All',
          profileImage: ''
        };
        const lowerSearch = mentionText.toLowerCase().trim();
        const showAll = 'all'.includes(lowerSearch) || lowerSearch === '';
        const matches = [
          ...(showAll ? [allEntry] : []),
          ...mentionable.filter((m: any) => {
            const n = (m.name || m.displayName || m.userName || '').toLowerCase();
            return n.includes(lowerSearch);
          })
        ];

        // Only open the dropdown if there are matches (either @all or members)
        if (matches.length === 0) {
          this.showMentionList = false;
          return;
        }

        this.showMentionList = true;
        this.currentMentionPosition = lastAtPos;
        this.mentionSearchTerm = mentionText;
        this.mentionUsers = matches;
      } else {
        this.showMentionList = false;
      }
    } else {
      this.showMentionList = false;
    }
  }

  selectMention(user: any) {
    const isAll = user.userId === '__all__';
    const userName = isAll ? 'all' : (user.name || user.userName || "");
    const currentText = this.newGroupMessage || "";
    const textBefore = currentText.substring(
      0,
      this.currentMentionPosition
    );
    const textAfter = currentText.substring(
      this.currentMentionPosition + 1 + this.mentionSearchTerm.length
    );
    const trailing = textAfter ? (textAfter.startsWith(' ') ? textAfter : ` ${textAfter}`) : ' ';
    this.newGroupMessage = `${textBefore}@${userName}${trailing}`;

    if (!this.messageMeta) {
      this.messageMeta = { mentions: [] };
    }
    if (!this.messageMeta.mentions) {
      this.messageMeta.mentions = [];
    }

    if (isAll) {
      // Store a single @all mention entry; notification fan-out happens at send time
      this.messageMeta.mentions.push({
        userId: 'all',
        name: 'all',
        displayName: 'All',
        position: this.currentMentionPosition,
        length: '@all'.length,
        type: 'all'
      } as any);
    } else {
      this.messageMeta.mentions.push({
        userId: user.userId,
        name: userName,
        position: this.currentMentionPosition,
        length: userName.length + 1,
      });
    }

    this.showMentionList = false;
    this.mentionSearchTerm = "";
    if (!this.editingMessage && this.memberChannelId) {
      this.chatDraftService.setDraft('group', this.memberChannelId, this.newGroupMessage);
    }
  }

  filterMentions(searchTerm: string) {
    this.mentionSearchTerm = searchTerm;
  }

  MetaDataDetails2: any = null;
  MedataLength2: number = 0;
  lastProcessedUrl2: string = "";
  isSuggestionBoxVisible2: boolean = false;
  onGroupInputChange(event?: any): void {
    if (event) {
      this.checkForMention(event);
    }
    setTimeout(() => {
      if (!this.newGroupMessage) {
        if (this.memberChannelId && !this.editingMessage) {
          this.chatDraftService.clearDraft('group', this.memberChannelId);
        }
        this.MetaDataDetails2 = null;
        this.MedataLength2 = 0;
        this.filterDataSearch = [];
        this.filterDataSearchLength = 0;
        this.isFirstTextTyping = true;
        this.skipApi = false;
        this.lastProcessedUrl2 = "";
        this.isSuggestionBoxVisible2 = false;
        return;
      }
      // Handle slash command filtering for group chat
      if (this.newGroupMessage[0] === "/") {
        let newText = this.newGroupMessage.slice(1);
        if (newText) {
          let text = newText.toLowerCase();
          let filterData = this.groupChatMessages.filter((msg: any) =>
            msg.message?.toLowerCase()?.includes(text)
          );
          this.filterDataSearch = filterData;
          this.filterDataSearchLength = filterData.length;
        }
      }
      const isUrl = isURL(this.newGroupMessage);
      if (!isUrl) {
        this.MetaDataDetails2 = null;
        this.MedataLength2 = 0;
        this.skipApi = false;
        this.lastProcessedUrl2 = "";
        this.isSuggestionBoxVisible2 = false;
        return;
      }
      const normalizedInputUrl = this.normalizeUrl(this.newGroupMessage);
      this.lastProcessedUrl2 = normalizedInputUrl;
      const body = {
        url: normalizedInputUrl,
        channelId: this.memberChannelId,
      };
      this.OnlineUsersService.isUrlValid(body).subscribe((res: any) => {
        if (res?.data) {
          const incomingFileUrl = this.normalizeUrl(res.data.fileUrl);
          this.MetaDataDetails2 = res.data;

          if (!this.MetaDataDetails2.thread) {
            this.MetaDataDetails2.thread = [];
          }
          this.MedataLength2 = this.MetaDataDetails2.thread.length || 1;
          this.isSuggestionBoxVisible2 = true;
          this.isSuggestionBoxVisible = false;
        } else {
          this.MetaDataDetails2 = null;
          this.MedataLength2 = 0;
          this.isSuggestionBoxVisible = false;
        }
      });
      this.isFirstTextTyping = false;
    }, 0);
  }

  closeSuggestionBox2() {
    this.isSuggestionBoxVisible2 = false;
  }

  //Group chat Upload
  isGroupLoading: boolean = false;
  uploadProgress2: number = 0;
  pendingGroupFiles: { file: File; progress: number }[] = [];
  uploadGroupId: string | null = null;
  selectedGroupMessage: any = null;
  selectedGroupSlideIndex: number = 0;
  async handleCameraCapture(file: File): Promise<void> {
    const mockEvent = {
      target: {
        files: [file]
      }
    };

    if (this.singleChatBox) {
      this.handleFileInput(mockEvent);
    } else {
      this.handleGroupFileInput(mockEvent);
    }
  }

  onCameraClosed() {
    this.showCamera = false;
  }

  async handleGroupFileInput(event: any): Promise<void> {
    const files: FileList = event.target.files;
    if (!files || files.length === 0) return;

    const rawFilesArray = Array.from(files);
    const validFiles = rawFilesArray.filter((file) => this.isValidFileType(file));

    if (validFiles.length === 0) {
      alertModalWithoutConfirm("Warning", "Please drop valid files");
      if (event.target) event.target.value = '';
      return;
    }

    if (validFiles.length !== rawFilesArray.length) {
      alertModalWithoutConfirm("Warning", "Some files were skipped due to unsupported format");
    }

    let filesArray = validFiles;
    if (filesArray.length > 100) {
      alertModalWithoutConfirm('warning', 'Only 100 files are allowed at one time...');
      filesArray = filesArray.slice(0, 100);
    }

    // Client-side image compression for oversized photos before upload
    filesArray = await compressMultipleFiles(filesArray);

    // Build group-member list for mention picker (exclude sender, removed, left)
    const captionGroupMembers = (this.allGroupMembers || [])
      .filter((m: any) => !m.isRemoved && !m.leftGroup && m.userId !== this.userId);
    const captionChannelId = this.memberChannelId;

    // Open preview modal
    const replyToContext = this.replyingToMessage ? this.buildReplyToPayload(this.replyingToMessage) : null;
    const dialogRef = this.dialog.open(FilePreviewModalComponent, {
      width: "500px",
      panelClass: "file-preview-dialog-panel",
      data: {
        files: filesArray,
        isGroup: true,
        groupMembers: captionGroupMembers,
        channelId: captionChannelId,
        replyTo: replyToContext
      },
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result === undefined) {
        // User cancelled - clear input
        if (event.target) event.target.value = "";
        return;
      }

      // User clicked send - result contains files and message
      const filesToSend = result.files || Array.from(files);
      const additionalMessage = result.message || "";
      const captionMentions: any[] = result.mentions || [];
      const captionUrlMeta: any = result.urlMeta || null;
      const replyToSend = result.replyTo !== undefined ? result.replyTo : replyToContext;

      if (this.replyingToMessage) {
        this.cancelReply();
      }

      const uploadGroupId = Date.now().toString();
      this.uploadGroupId = uploadGroupId;
      const sessionFiles = filesToSend.map((file) => ({
        file,
        progress: 0,
      }));
      this.pendingGroupFiles = sessionFiles;
      this.isGroupLoading = true;

      const tempGroupedMessage = {
        _id: uploadGroupId,
        sender_id: this.userId,
        createdAt: new Date(),
        updatedAt: new Date(),
        message: additionalMessage,
        files: filesToSend.map((f: File) => ({
          fileUrl: URL.createObjectURL(f),
          type: DOCUMENT_EXTENSIONS.some(ext => (f.name || '').toLowerCase().endsWith(ext.toLowerCase())) ? getFileType(f.name) : (f.type || getFileType(f.name)),
          name: f.name,
          size: f.size,
          isLocal: true
        })),
        url: undefined,
        currentSlideIndex: 0,
        replyTo: replyToSend || undefined,
        meta: {
          channel_id: this.memberChannelId,
          isGroupedUpload: true,
          groupId: uploadGroupId,
          tempId: uploadGroupId,
          caption: additionalMessage,
          ...(replyToSend ? { replyTo: replyToSend } : {}),
          ...(additionalMessage ? { captionContentType: isURL(additionalMessage) ? 'url' : 'text' } : {}),
          ...(captionMentions.length > 0 ? { mentions: captionMentions } : {}),
          ...(captionUrlMeta ? {
            title: captionUrlMeta.title || '',
            description: captionUrlMeta.description || '',
            image: captionUrlMeta.image || '',
            icon: captionUrlMeta.icon || '',
            version: captionUrlMeta.version || '',
            thread: captionUrlMeta.thread || [],
            ...(captionUrlMeta.fileId ? { fileId: captionUrlMeta.fileId } : {})
          } : {}),
          // Check the first file for duration (useful if this is a single voice message sent via group file input)
          ...((filesToSend[0] as any).duration ? { duration: (filesToSend[0] as any).duration } : {})
        },
        name: this.userNameMapping[this.userId] || "User",
        profileImage: this.profileImageMapping[this.userId] || "",
        showGroupDateHeader: this.shouldShowDateHeader(
          new Date(),
          this.groupChatMessages
        ),
        isUploading: true,
      };
      this.groupChatMessages = [...this.groupChatMessages, tempGroupedMessage];
      this.updateCacheForActiveChannel();
      this.scrollToBottom();

      // Track in background map for navigation persistence
      const uploadChannelId = this.memberChannelId;
      if (uploadChannelId) {
        const pending = this.pendingUploadsByChannel.get(uploadChannelId) || [];
        this.pendingUploadsByChannel.set(uploadChannelId, [...pending, tempGroupedMessage]);
      }

      try {
        const uploadedFiles = await Promise.all(
          filesToSend.map((file) => this.uploadGroupFile(file, additionalMessage, uploadChannelId, uploadGroupId, sessionFiles, captionMentions, captionUrlMeta, replyToSend))
        );
        const validId = uploadedFiles.find((f: any) => f._id)?._id;

        const finalizeMessage = (msgList: any[]) => {
          let messageIndex = msgList.findIndex((msg) => msg._id === uploadGroupId);
          if (messageIndex === -1 && uploadGroupId) {
            messageIndex = msgList.findIndex((msg) => msg.meta?.groupId === uploadGroupId);
          }
          if (messageIndex === -1 && validId) {
            messageIndex = msgList.findIndex((msg) => msg._id === validId || msg.id === validId || msg.messageId === validId);
          }

          if (messageIndex !== -1) {
            const updatedMessage = {
              ...msgList[messageIndex],
              message: additionalMessage,
              files: uploadedFiles,
              replyTo: replyToSend || msgList[messageIndex].replyTo,
              // Drop the cached blob: file list from the temp message so the real URLs are used
              _processedFiles: undefined,
              _allIds: uploadedFiles.map((f: any) => f._id).filter(id => !!id),
              ...(validId ? { _id: validId, id: validId } : {}),
              url: undefined,
              isUploading: false,
              meta: {
                ...msgList[messageIndex]?.meta,
                caption: additionalMessage,
                ...(replyToSend ? { replyTo: replyToSend } : {}),
                ...(captionMentions && captionMentions.length > 0 ? { mentions: captionMentions } : {}),
                ...(captionUrlMeta ? {
                  title: captionUrlMeta.title || '',
                  description: captionUrlMeta.description || '',
                  image: captionUrlMeta.image || '',
                  icon: captionUrlMeta.icon || '',
                  version: captionUrlMeta.version || '',
                  thread: captionUrlMeta.thread || [],
                  ...(captionUrlMeta.fileId ? { fileId: captionUrlMeta.fileId } : {})
                } : {})
              }
            };
            return {
              list: [
                ...msgList.slice(0, messageIndex),
                updatedMessage,
                ...msgList.slice(messageIndex + 1),
              ],
              updatedMessage
            };
          }
          return { list: msgList, updatedMessage: null };
        };

        // 1. Update active UI if still in same chat
        if (this.memberChannelId === uploadChannelId && this.groupChatBox) {
          const { list, updatedMessage } = finalizeMessage(this.groupChatMessages);
          this.groupChatMessages = list;
          if (updatedMessage) this.notifyGroupMembers(updatedMessage);
        }

        // 1.5. Update cache if it exists
        if (uploadChannelId && this.cachedGroupMessages[uploadChannelId]) {
          const { list } = finalizeMessage(this.cachedGroupMessages[uploadChannelId]);
          this.cachedGroupMessages[uploadChannelId] = list;
        }

        // 2. Update background map (and remove since finished)
        if (uploadChannelId) {
          const pending = this.pendingUploadsByChannel.get(uploadChannelId) || [];
          const remaining = pending.filter(m => m._id !== uploadGroupId && m.meta?.groupId !== uploadGroupId);
          if (remaining.length > 0) {
            this.pendingUploadsByChannel.set(uploadChannelId, remaining);
          } else {
            this.pendingUploadsByChannel.delete(uploadChannelId);
          }
        }

        if (this.memberChannelId === uploadChannelId) {
          setTimeout(() => this.scrollToBottom(), 50);
        }
      } catch (error) {
        console.error("Error uploading files:", error);
        // Remove from background on failure too
        if (uploadChannelId) {
          const pending = this.pendingUploadsByChannel.get(uploadChannelId) || [];
          this.pendingUploadsByChannel.set(uploadChannelId, pending.filter(m => m._id !== uploadGroupId));
        }
      } finally {
        this.isGroupLoading = false;
        this.uploadProgress2 = 0;
        this.pendingGroupFiles = [];
        if (this.uploadGroupId === uploadGroupId) {
          this.uploadGroupId = null;
        }
        if (event.target) event.target.value = "";
      }
    });
  }

  shouldShowDateHeader(date: Date, messages: any[]): boolean {
    if (messages.length === 0) return true;
    const lastMessage = messages[messages.length - 1];
    return (
      !lastMessage.createdAt ||
      new Date(lastMessage.createdAt).toDateString() !== date.toDateString()
    );
  }

  async uploadGroupFile(file: File, caption: string = "", channelId: string, uploadGroupId: string, sessionFiles: any[], captionMentions: any[] = [], captionUrlMeta: any = null, replyTo: any = null): Promise<any> {
    this.uploadProgress2 = 0;
    this.cdRef.detectChanges();

    return new Promise(async (resolve, reject) => {
      const fileBuffer = await file.arrayBuffer();
      const fileBytes = fileBuffer.byteLength;
      let { chunks, totalChunks } = this.getDynamicChunks(file.size);

      try {
        const response = await this.chatService
          .getPreSignedUrls(file.name, totalChunks)
          .toPromise();

        if (!response?.data) {
          console.error("Error: Invalid response from pre-signed URL API.");
          reject("Invalid response from server");
          return;
        }

        const { uploadId, preSignedUrls, fileName, fileUrl, compressedImgUrl, thumbnailUrl, ogFileName } = response.data;
        let chunkProgress: { [key: number]: number } = {};

        // Upload chunks in parallel and preserve order
        let uploadedParts: any[] = new Array(preSignedUrls.length);
        await Promise.all(
          preSignedUrls.map(async ({ partNumber, url }: any, index: number) => {
            const chunk = file.slice(chunks[index].start, chunks[index].end);
            try {
              const res = await axios.put(url, chunk, {
                headers: { "Content-Type": "application/octet-stream" },
                onUploadProgress: (progressEvent) => {
                  if (progressEvent.loaded && progressEvent.total) {
                    chunkProgress[partNumber] = progressEvent.loaded;
                    const totalUploaded = Object.values(chunkProgress).reduce(
                      (a, b) => a + b,
                      0
                    );
                    const progress = Math.floor(
                      (totalUploaded / fileBytes) * 100
                    );

                    const fileIndex = sessionFiles.findIndex(
                      (f) => f.file === file
                    );
                    if (fileIndex !== -1) {
                      sessionFiles[fileIndex].progress = progress;

                      this.zone.run(() => {
                        // Aggregate progress for all group files in THIS session
                        const totalProgress = sessionFiles.reduce((acc, curr) => acc + curr.progress, 0);
                        const groupProgress = Math.floor(totalProgress / sessionFiles.length);

                        this.uploadProgress2 = groupProgress;

                        // Update progress in the message object itself for persistent UI
                        const updateProgressInList = (list: any[]) => {
                          const idx = list.findIndex(m => m._id === uploadGroupId || m.meta?.groupId === uploadGroupId);
                          if (idx !== -1) {
                            list[idx] = { ...list[idx], progress: groupProgress };
                          }
                        };

                        if (this.memberChannelId === channelId && this.groupChatBox) {
                          updateProgressInList(this.groupChatMessages);
                        }

                        const pending = this.pendingUploadsByChannel.get(channelId) || [];
                        updateProgressInList(pending);

                        this.cdRef.detectChanges();
                      });
                    }
                  }
                },
              });
              const eTag = res.headers["etag"]?.replace(/^"(.*)"$/, "$1");
              if (eTag) {
                uploadedParts[index] = { PartNumber: partNumber, ETag: eTag };
              }
            } catch (error) {
              console.error(`Chunk ${partNumber} upload failed:`, error);
              throw error;
            }
          })
        );

        let fileType = DOCUMENT_EXTENSIONS.some(ext => (file.name || '').toLowerCase().endsWith(ext.toLowerCase()))
          ? getFileType(file.name)
          : (file.type || getFileType(file.name));
        if (DOCUMENT_EXTENSIONS.some(ext => (file.name || fileUrl || '').toLowerCase().endsWith(ext.toLowerCase()))) {
          fileType = getFileType(file.name || fileUrl);
        } else if (fileUrl && (fileType === 'application/octet-stream' || !fileType || fileType.startsWith('video/'))) {
          if (isVoiceMessage(fileUrl)) fileType = 'audio/mpeg';
          else if (isVideo(fileUrl)) fileType = 'video/mp4';
          else if (isImage(fileUrl)) fileType = 'image/jpeg';
          else if (isAudio(fileUrl)) fileType = 'audio/mpeg';
        }

        const completeRes: any = await this.chatService
          .completeUpload(
            fileName,
            uploadId,
            uploadedParts,
            file.size,
            channelId || this.memberChannelId,
            {
              file: fileUrl,
              message: caption || ogFileName || file.name || fileName,
              replyTo: replyTo || undefined,
              meta: {
                isGroupedUpload: true,
                groupId: uploadGroupId,
                tempId: uploadGroupId,
                caption: caption || "",
                ...(replyTo ? { replyTo } : {}),
                ...(caption ? { captionContentType: isURL(caption) ? 'url' : 'text' } : {}),
                ...(captionMentions && captionMentions.length > 0 ? { mentions: captionMentions } : {}),
                ...(captionUrlMeta ? {
                  title: captionUrlMeta.title || '',
                  description: captionUrlMeta.description || '',
                  image: captionUrlMeta.image || '',
                  icon: captionUrlMeta.icon || '',
                  version: captionUrlMeta.version || '',
                  thread: captionUrlMeta.thread || [],
                  ...(captionUrlMeta.fileId ? { fileId: captionUrlMeta.fileId } : {})
                } : {}),
                fileType: fileType,
                ...this.getContentCategorization(file.name, fileType),
                ...(compressedImgUrl ? { compressedImgUrl } : {}),
                ...(thumbnailUrl ? { thumbnailUrl } : {}),
                ...((file as any).duration ? { duration: (file as any).duration } : {}),
                size: file.size,
                ogFileName: ogFileName || file.name
              },
              type: false,
            }
          )
          .toPromise();

        resolve({
          fileUrl,
          type: fileType,
          name: ogFileName || file.name,
          fileName: ogFileName || file.name,
          ogFileName: ogFileName || file.name,
          size: file.size,
          _id: completeRes?.data?._id || completeRes?._id
        });
      } catch (err) {
        console.error("Upload process failed:", err);
        reject(err);
      }
    });
  }

  // GROUP DRAG AND DROP
  isGroupDragOver = false;
  dragGroupOverTimer: any;
  onGroupDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.dragGroupOverTimer) {
      clearTimeout(this.dragGroupOverTimer);
    }

    this.isGroupDragOver = true;

    const textarea = event.target as HTMLElement;
    if (textarea.classList.contains("custom-textarea")) {
      textarea.classList.add("drag-over");
    }
  }

  onGroupDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragGroupOverTimer = setTimeout(() => {
      this.isGroupDragOver = false;
    }, 100);
  }

  async onGroupDrop(event: DragEvent): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    if (this.dragGroupOverTimer) {
      clearTimeout(this.dragGroupOverTimer);
    }
    this.isGroupDragOver = false;
    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) return;

    // Validate file types
    const validFiles = Array.from(files).filter((file) =>
      this.isValidFileType(file)
    );

    if (validFiles.length === 0) {
      alertModalWithoutConfirm("Warning", "Please drop valid files");
      return;
    }

    if (validFiles.length !== files.length) {
      alertModalWithoutConfirm("Warning", "Some files were skipped due to unsupported format");
    }

    // Reuse handleGroupFileInput logic to show preview modal and handle upload
    const mockEvent = {
      target: {
        files: validFiles,
        value: ''
      }
    };
    await this.handleGroupFileInput(mockEvent);
  }



  // Carousel methods
  prevGroupSlide(message: any) {
    const files = this.getFilesFromMessage(message);
    if (!files || files.length <= 1) return;
    if (message.currentSlideIndex === undefined) {
      message.currentSlideIndex = 0;
    }
    message.currentSlideIndex =
      message.currentSlideIndex > 0
        ? message.currentSlideIndex - 1
        : files.length - 1;
  }

  nextGroupSlide(message: any) {
    const files = this.getFilesFromMessage(message);
    if (!files || files.length <= 1) return;
    // Ensure currentSlideIndex is initialized
    if (message.currentSlideIndex === undefined) {
      message.currentSlideIndex = 0;
    }
    message.currentSlideIndex =
      message.currentSlideIndex < files.length - 1
        ? message.currentSlideIndex + 1
        : 0;
  }

  goToGroupSlide(message: any, index: number) {
    const files = this.getFilesFromMessage(message);
    if (!files || files.length <= 1) return;
    message.currentSlideIndex = index;
  }

  isGroupActiveSlide(message: any, index: number): boolean {
    return (message.currentSlideIndex || 0) === index;
  }

  // Image modal methods for group chat
  openGroupImageModal(message: any, index: number) {
    const files = this.buildFileListFromMessage(message);
    if (!files.length) return;
    this.dialog.open(ImageViewerDialogComponent, {
      data: { files, startIndex: index },
      panelClass: "image-viewer-dialog-panel",
      backdropClass: "image-viewer-dialog-backdrop",
      height: "100vh",
      width: "100vw",
      maxHeight: "100vh",
      maxWidth: "100vw",
      autoFocus: false,
    });
  }

  closeGroupImageModal() {
    this.isImageModalOpen = false;
    this.selectedGroupMessage = null;
    this.selectedGroupSlideIndex = 0;
  }

  prevGroupModalSlide() {
    if (!this.selectedGroupMessage?.files) return;
    this.selectedGroupSlideIndex =
      this.selectedGroupSlideIndex > 0
        ? this.selectedGroupSlideIndex - 1
        : this.selectedGroupMessage.files.length - 1;
  }

  nextGroupModalSlide() {
    if (!this.selectedGroupMessage?.files) return;
    this.selectedGroupSlideIndex =
      this.selectedGroupSlideIndex < this.selectedGroupMessage.files.length - 1
        ? this.selectedGroupSlideIndex + 1
        : 0;
  }

  private notifyGroupMembers(groupedMessage: any): void {
    let previewMessage = "Sent multiple files";

    if (groupedMessage.files?.length === 1) {
      previewMessage = this.getFilePreview(
        groupedMessage.files[0].fileUrl || ""
      );
    }

    const group = this.filteredGroupUsers.find(
      (group) => group.channelId === this.memberChannelId
    );
    if (group) {
      group.lastMessage = {
        ...groupedMessage,
        message: previewMessage,
      };
      group.lastMessageTimeAgo = getTimeAgo(groupedMessage.createdAt);
      this.sortAllChatsByRecentMessages();
    }

    const groupName = group?.groupName || "Group";
    const senderName = this.userNameMapping[this.userId] || "User";
    const mentions = groupedMessage.meta?.mentions || [];
    const captionText = groupedMessage.meta?.caption || "";
    const hasAllMention = mentions.some((m: any) => m.type === 'all' || m.userId === 'all') || /(^|[^\w.+-])@all\b/i.test(captionText);
    if (hasAllMention) {
      const activeMembers = (this.allGroupMembers || []).filter(
        (m: any) => !m.isRemoved && !m.leftGroup && String(m.userId).trim() !== String(this.userId).trim()
      );
      activeMembers.forEach((member: any) => {
        this.chatService.emitPushNotification({
          channelId: this.memberChannelId,
          title: groupName,
          body: `${senderName} mentioned all members in ${groupName}`,
          redirectUrl: `${environment.redirectUrl}#/main-chat?chatType=group&channelId=${this.memberChannelId}`,
          icon: "https://link.iiclab.com/assets/login/header-logo.png",
          sender_id: this.userId,
          user_id: member.userId,
          groupName: groupName,
        });
      });
    } else if (mentions.length > 0) {
      mentions.forEach((mention: any) => {
        this.chatService.emitPushNotification({
          channelId: this.memberChannelId,
          title: senderName,
          body: previewMessage,
          redirectUrl: `${environment.redirectUrl}#/main-chat?chatType=group&channelId=${this.memberChannelId}`,
          icon: "https://link.iiclab.com/assets/login/header-logo.png",
          sender_id: this.userId,
          user_id: mention.userId,
          groupName: groupName,
        });
      });
    } else {
      this.chatService.emitPushNotification({
        channelId: this.memberChannelId,
        title: groupName,
        body: `${senderName} ${previewMessage}`,
        redirectUrl: `${environment.redirectUrl}#/main-chat?chatType=group&channelId=${this.memberChannelId}`,
        icon: "https://link.iiclab.com/assets/login/header-logo.png",
        sender_id: this.userId,
        groupName: groupName,
      });
    }
  }

  //Image Modal to show full screen

  selectedImageUrl: string | null = null;
  isImageModalOpen = false;
  selectedMessage: any = null;
  selectedSlideIndex: number = 0;


  openImageModal(message: any, index: number = 0): void {
    const files = this.buildFileListFromMessage(message);
    if (!files.length) return;

    this.dialog.open(ImageViewerDialogComponent, {
      data: { files, startIndex: index },
      panelClass: "image-viewer-dialog-panel",
      backdropClass: "image-viewer-dialog-backdrop",
      height: "100vh",
      width: "100vw",
      maxHeight: "100vh",
      maxWidth: "100vw",
      autoFocus: false,
    });
  }

  closeImageModal(): void {
    this.isImageModalOpen = false;
    this.selectedMessage = null;
    this.selectedSlideIndex = 0;
  }

  private buildFileListFromMessage(
    message: any
  ): Array<{ fileUrl: string; name?: string }> {
    const files = this.getFilesFromMessage(message);
    if (files && files.length > 0) {
      return files
        .map((f: any) => ({
          fileUrl: f.fileUrl || f.url,
          name: f.fileName || f.name || message?.message || 'file',
        }))
        .filter((f: any) => !!f.fileUrl);
    }
    return [];
  }

  prevModalSlide(): void {
    if (!this.selectedMessage) return;
    const len = this.selectedMessage.files?.length || 0;
    this.selectedSlideIndex = (this.selectedSlideIndex - 1 + len) % len;
  }

  nextModalSlide(): void {
    if (!this.selectedMessage) return;
    const len = this.selectedMessage.files?.length || 0;
    this.selectedSlideIndex = (this.selectedSlideIndex + 1) % len;
  }

  @HostListener('document:keydown.escape', ['$event'])
  handleEscapeKey(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;

    // 0. If already handled by child component or composing with IME
    if (keyboardEvent.defaultPrevented || keyboardEvent.isComposing) {
      return;
    }

    // 1. MatDialogs (Attachment previews, confirmation dialogs, etc.)
    if (this.dialog && this.dialog.openDialogs && this.dialog.openDialogs.length > 0) {
      keyboardEvent.preventDefault();
      this.dialog.closeAll();
      return;
    }

    // 2. Image preview modal
    if (this.isImageModalOpen) {
      keyboardEvent.preventDefault();
      this.closeImageModal();
      return;
    }

    // 3. Camera capture modal
    if (this.showCamera) {
      keyboardEvent.preventDefault();
      this.onCameraClosed();
      return;
    }

    // 4. Forward modal
    if (this.showForwardModal) {
      keyboardEvent.preventDefault();
      this.closeForwardModal();
      return;
    }

    // 5. Message seen info modal
    if (this.showSeenInfoModal) {
      keyboardEvent.preventDefault();
      this.closeSeenInfoModal();
      return;
    }

    // 6. Create group modal
    if (this.showCreateGroupModal) {
      keyboardEvent.preventDefault();
      this.closeCreateGroupModal();
      return;
    }

    // 7. Mention all modal
    if (this.showMentionAllModal) {
      keyboardEvent.preventDefault();
      this.closeMentionAllModal();
      return;
    }

    // 8. Reaction details modal
    if (this.showReactionDetailsModal) {
      keyboardEvent.preventDefault();
      this.closeReactionDetails();
      return;
    }

    // 9. Reaction emoji picker
    if (this.showReactionEmojiPicker) {
      keyboardEvent.preventDefault();
      this.closeReactionPicker();
      return;
    }

    // 10. Voice recording in progress
    if (this.singleMessageInput?.isRecordingVoice || this.groupMessageInput?.isRecordingVoice) {
      keyboardEvent.preventDefault();
      if (this.singleMessageInput?.isRecordingVoice) this.singleMessageInput.cancelVoiceRecording();
      if (this.groupMessageInput?.isRecordingVoice) this.groupMessageInput.cancelVoiceRecording();
      return;
    }

    // 11. Attachment menus (plus/paperclip popups)
    if (this.singleMessageInput?.showAttachMenu || this.groupMessageInput?.showAttachMenu) {
      keyboardEvent.preventDefault();
      if (this.singleMessageInput) this.singleMessageInput.showAttachMenu = false;
      if (this.groupMessageInput) this.groupMessageInput.showAttachMenu = false;
      return;
    }

    // 12. Mention suggestions dropdown
    if (this.showMentionList) {
      keyboardEvent.preventDefault();
      this.showMentionList = false;
      return;
    }

    // 13. Emoji / GIF popups
    if (this.showEmojiPicker || this.showGifPanel || this.showGifPanelGroup) {
      keyboardEvent.preventDefault();
      this.showEmojiPicker = false;
      this.showGifPanel = false;
      this.showGifPanelGroup = false;
      return;
    }

    // 14. Message context action menu
    if (this.activeMessageMenuId) {
      keyboardEvent.preventDefault();
      this.activeMessageMenuId = null;
      return;
    }

    // 15. Chat header dropdown menu
    if (this.showChatMenuDropdown) {
      keyboardEvent.preventDefault();
      this.showChatMenuDropdown = false;
      return;
    }

    // 16. Chat sidebar menu dropdown
    if (this.chatSidebarComp?.showMenuDropdown) {
      keyboardEvent.preventDefault();
      this.chatSidebarComp.showMenuDropdown = false;
      return;
    }

    // 17. Side panels: Group Info, Starred Messages, Notification Settings, Pinned Messages
    if (this.isGroupInfoOpen) {
      keyboardEvent.preventDefault();
      this.closeGroupInfoPanel();
      return;
    }

    if (this.isStarredMessagesPanelOpen) {
      keyboardEvent.preventDefault();
      this.isStarredMessagesPanelOpen = false;
      return;
    }

    if (this.isNotificationSettingsOpen) {
      keyboardEvent.preventDefault();
      this.isNotificationSettingsOpen = false;
      return;
    }

    if (this.showPinnedMessages) {
      keyboardEvent.preventDefault();
      this.showPinnedMessages = false;
      return;
    }

    // 18. Search views / global panels
    if (this.showAdvancedSearchResults) {
      keyboardEvent.preventDefault();
      this.closeAdvancedSearch();
      return;
    }

    if (this.showAdvancedSearchFilters) {
      keyboardEvent.preventDefault();
      this.showAdvancedSearchFilters = false;
      return;
    }

    if (this.showStarredMessages) {
      keyboardEvent.preventDefault();
      this.showStarredMessages = false;
      return;
    }

    // 19. Selected GIF preview before sending
    if (this.selectedGifUrl) {
      keyboardEvent.preventDefault();
      this.selectedGifUrl = '';
      return;
    }

    // 20. Message editing state (cancel edit mode first)
    if (this.editingMessage) {
      keyboardEvent.preventDefault();
      this.cancelEdit();
      return;
    }

    // 21. Replying state (cancel reply preview first)
    if (this.replyingToMessage) {
      keyboardEvent.preventDefault();
      this.cancelReply();
      return;
    }

    // 22. Check if user is typing in an input, textarea, or content-editable field
    const target = (keyboardEvent.target || document.activeElement) as HTMLElement | null;
    const isTypingField = !!target && (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable ||
      target.getAttribute?.('contenteditable') === 'true' ||
      target.classList?.contains('whatsapp-textarea')
    );

    if (isTypingField) {
      // In Telegram-like desktop behavior, pressing Esc while typing in input blurs focus
      // rather than closing the conversation
      target.blur();
      keyboardEvent.preventDefault();
      return;
    }

    // 23. Desktop layout: Navigate back to chat list when a conversation is open
    const isDesktop = !this.isMobileView && (typeof window !== 'undefined' ? window.innerWidth >= 768 : true);
    const isChatOpen = this.singleChatBox || this.groupChatBox || this.showChatWindow;

    if (isDesktop && isChatOpen) {
      keyboardEvent.preventDefault();
      this.saveCurrentChatDraft();
      this.closeChatWindow();
      this.cdRef.markForCheck();
      return;
    }

    // 24. If no chat is open, but sidebar search query is populated, clear search
    if (isDesktop && this.chatSidebarComp?.searchQuery) {
      keyboardEvent.preventDefault();
      this.chatSidebarComp.onClearSearch();
      this.cdRef.markForCheck();
      return;
    }
  }

  // FOOTER
  sideNavOpen = false;
  toggleSideNav() {
    this.sideNavOpen = !this.sideNavOpen;
  }

  // Message expand/collapse state tracking
  expandedMessages: Set<string> = new Set();
  private readonly MESSAGE_TRUNCATE_LENGTH = 200;

  /**
   * @param message 
   * @returns
   */
  isMessageLong(message: string): boolean {
    return message && message.length > this.MESSAGE_TRUNCATE_LENGTH;
  }

  private truncateSafely(str: string, maxLen: number): string {
    if (!str || str.length <= maxLen) return str;
    let cut = str.substring(0, maxLen);

    const pipePairs = (cut.match(/\|\|/g) || []).length;
    if (pipePairs % 2 !== 0) {
      const rest = str.substring(maxLen);
      const closeIdx = rest.indexOf('||');
      if (closeIdx !== -1 && closeIdx < 80) {
        cut += rest.substring(0, closeIdx + 2);
      } else {
        const lastOpen = cut.lastIndexOf('||');
        cut = lastOpen > 0 ? cut.substring(0, lastOpen).trim() : cut + '||';
      }
    }

    const openSpoilers = (cut.match(/\[spoiler\]/gi) || []).length;
    const closeSpoilers = (cut.match(/\[\/spoiler\]/gi) || []).length;
    if (openSpoilers > closeSpoilers) {
      const lastOpen = cut.toLowerCase().lastIndexOf('[spoiler]');
      cut = lastOpen > 0 ? cut.substring(0, lastOpen).trim() : cut + '[/spoiler]';
    }

    return cut.trim() + '...';
  }

  /**
   
   * @param message 
   * @param messageId 
   * @returns
   */
  getDisplayMessage(message: string, messageId: string): string {
    if (!message) return '';

    let text = message;
    if (!this.expandedMessages.has(messageId) && this.isMessageLong(message)) {
      text = this.truncateSafely(message, this.MESSAGE_TRUNCATE_LENGTH);
    }

    text = this.escapeHtml(text);
    text = text.replace(/\|\|([\s\S]+?)\|\|/g, '<span class="tg-spoiler" title="Click to reveal">$1</span>');
    text = text.replace(/\[spoiler\]([\s\S]+?)\[\/spoiler\]/gi, '<span class="tg-spoiler" title="Click to reveal">$1</span>');

    return text;
  }

  /**
   
   * @param messageId 
   * @param event
   */
  toggleMessageExpand(messageId: string, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (this.expandedMessages.has(messageId)) {
      this.expandedMessages.delete(messageId);
    } else {
      this.expandedMessages.add(messageId);
    }

    // Trigger change detection
    this.cdRef.detectChanges();
  }

  /**
   * @param messageId 
   * @returns
   */
  isMessageExpanded(messageId: string): boolean {
    return this.expandedMessages.has(messageId);
  }

  // Caption expand/collapse state tracking
  expandedCaptions: Set<string> = new Set();

  isCaptionLong(caption: string): boolean {
    return caption && caption.length > this.MESSAGE_TRUNCATE_LENGTH;
  }

  getDisplayCaption(caption: string, messageId: string): string {
    if (!caption) return '';
    if (this.expandedCaptions.has(messageId)) return caption;
    if (this.isCaptionLong(caption)) {
      return caption.substring(0, this.MESSAGE_TRUNCATE_LENGTH).trim() + '...';
    }
    return caption;
  }

  toggleCaptionExpand(messageId: string, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (this.expandedCaptions.has(messageId)) {
      this.expandedCaptions.delete(messageId);
    } else {
      this.expandedCaptions.add(messageId);
    }
    this.cdRef.detectChanges();
  }

  isCaptionExpanded(messageId: string): boolean {
    return this.expandedCaptions.has(messageId);
  }

  /**
   
   * @param message 
   * @param messageId 
   * @param senderId 
   * @returns 
   */
  getDisplayMessageWithMentions(message: string, messageId: string, senderId?: string): string {
    if (!message) return '';
    let displayText = message;
    if (!this.expandedMessages.has(messageId) && this.isMessageLong(message)) {
      displayText = message.substring(0, this.MESSAGE_TRUNCATE_LENGTH).trim() + '...';
    }
    const foundMessage = this.groupChatMessages.find(m => m.id === messageId || m.message === message);
    const meta = foundMessage?.meta;
    if (meta?.mentions?.length > 0) {
      return this.applyMentionsFromMetaWithMessage(displayText, meta.mentions);
    }
    return this.applyMentionsFromText(displayText);
  }

  private applyMentionsFromMetaWithMessage(message: string, mentions: any[]): string {
    let highlighted = this.escapeHtml(message);
    const validMentions = (mentions || []).filter((mention) => {
      return mention && mention.name &&
        typeof mention.position === 'number' &&
        mention.position < message.length &&
        !(mention.position > 0 && /[\w.+-]/.test(message[mention.position - 1]));
    });

    [...validMentions].reverse().forEach((mention) => {
      const mentionText = `@${mention.name}`;
      const mentionHtml = `<span class="mention-highlight">${mentionText}</span>`;

      if (mention.position >= 0 && mention.position <= highlighted.length) {
        const adjustedLength = Math.min(mention.length, message.length - mention.position);
        highlighted =
          highlighted.substring(0, mention.position) +
          mentionHtml +
          highlighted.substring(mention.position + adjustedLength);
      }
    });

    return highlighted;
  }

  username = localStorage.getItem("username");
  profileImg =
    localStorage.getItem("profileImg") != "undefined" &&
      localStorage.getItem("profileImg") != ""
      ? localStorage.getItem("profileImg")
      : "assets/user-icon.png";

  installPWA() {
    // Shows the installation guide first, then the browser's own Install dialog where there is one.
    void this.pwaService.installPWA();
  }

  navigateTo() {
    this.router.navigate(["/home"]);
  }
  guestToken: any = "";
  onClickLogout() {
    this.chatService.disconnectSocket();

    const browserUniqueId = localStorage.getItem("browserUniqueId");
    const guestID = localStorage.getItem("guestID");
    const fcmToken = localStorage.getItem("fcm_token");

    if (guestID && fcmToken) {
      this.userService.removeToken({ id: guestID, token: fcmToken }).subscribe({
        next: () => {
          console.log("FCM token removed");
        },
        error: (err) => {
          console.error("Token removal failed:", err);
        },
      });
    }

    if (browserUniqueId) {
      this.userService.logOut(browserUniqueId).subscribe({
        next: () => {
          console.log("Browser logout success");
        },
        error: (err) => {
          console.error("Logout API failed:", err);
        },
      });
    }

    this.soundService.syncToServiceWorker('active_user_id', null);
    // Clear localStorage but preserve unread counts and theme
    const unreadCounts = localStorage.getItem('unreadCounts');
    const savedThemes = ThemeService.preserveTheme();
    ChatCacheService.clearAllUserCaches();
    localStorage.clear();
    ThemeService.restoreTheme(savedThemes);
    if (browserUniqueId) {
      localStorage.setItem("browserUniqueId", browserUniqueId);
    }
    // Restore unread counts after clearing
    if (unreadCounts) {
      localStorage.setItem('unreadCounts', unreadCounts);
    }
    this.router.navigate(["/login"]);
  }

  groupName: string = '';
  groupDescription: string = '';
  // Track groups user has exited 
  exitedGroups: Set<string> = new Set();

  openGroupInfoModal() {
    this.showPinnedMessages = false;
    this.isStarredMessagesPanelOpen = false;
    this.isGroupInfoOpen = true;

    if (this.singleChatBox && this.singleUser) {
      const uid = this.singleUser.userId || this.chatUserId;
      if (uid) {
        const cachedOrListed =
          (this.allSingleUsers || []).find((u: any) => u.userId === uid) ||
          (this.onlineUserList || []).find((u: any) => u.userId === uid) ||
          (this.allGroupMembers || []).find((m: any) => m.userId === uid) ||
          this.chatMap?.[uid];

        const email =
          this.singleUser.emailId ||
          this.singleUser.email ||
          this.singleUser.mail ||
          cachedOrListed?.emailId ||
          cachedOrListed?.email ||
          cachedOrListed?.mail;

        if (email && (!this.singleUser.emailId || !this.singleUser.email)) {
          this.singleUser = {
            ...this.singleUser,
            emailId: email,
            email: email
          };
        }

        if (!this.singleUser?.emailId && !this.singleUser?.email) {
          this.userService.getUserDetails(uid).subscribe({
            next: (res: any) => {
              const userData = res?.data || res;
              const fetchedEmail = userData?.emailId || userData?.email || userData?.mail;
              if (fetchedEmail && this.singleUser && (this.singleUser.userId === uid || this.chatUserId === uid)) {
                this.singleUser = {
                  ...this.singleUser,
                  ...userData,
                  emailId: fetchedEmail,
                  email: fetchedEmail
                };
                this.cdRef.detectChanges();
              }
            },
            error: (err) => {
              console.warn('Could not fetch user details for chat info panel:', err);
            }
          });
        }
      }
    }
  }

  onGroupDeleted(id: string) {
    const channelId = String(id || '').trim();
    if (!channelId) return;

    console.log(`[onGroupDeleted] Removing group: ${channelId}`);

    this.allGroupUsers = this.allGroupUsers.filter(g => String(g.channelId || '').trim() !== channelId);
    this.filteredGroupUsers = this.filteredGroupUsers.filter(g => String(g.channelId || '').trim() !== channelId);
    this.sortedChats = this.sortedChats.filter(c => String(c.channelId || '').trim() !== channelId);

    // Sync chatMap as well
    delete this.chatMap[channelId];

    if (String(this.memberChannelId || '').trim() === channelId) {
      this.cancelReply();
      this.cancelEdit();
      this.groupChatBox = false;
      this.showChatWindow = false;
      this.chatService.isChatOpenMobile$.next(false);
      this.groupChannel = null;
      this.memberChannelId = "";
      this.isGroupInfoOpen = false;
    }

    this.sortAllChatsByRecentMessages();
    this.cdRef.detectChanges();
  }

  onGroupExited(channelId: string) {
    this.exitedGroups.add(channelId);
    // Find current chat in sortedChats and mark it
    const chatIndex = this.sortedChats.findIndex(c => c.channelId === channelId);
    if (chatIndex > -1) {
      this.sortedChats[chatIndex].isExited = true;
    }
    this.isGroupInfoOpen = false;
    this.cdRef.detectChanges();
  }

  onGroupChatCleared() {
    this.groupChatMessages = [];
    this.AllGroupChatMsg = { read: [], unread: [] };
    this.pinnedMessages = [];
    this.showPinnedMessages = false;

    // Persist the cleared channel so last message stays hidden after refresh
    const clearedChannelId = this.memberChannelId;
    const chatForTime = this.sortedChats.find(c => c.channelId === clearedChannelId);
    const lastMsgTime = chatForTime?.lastMessage?.createdAt || chatForTime?.lastMessage?.updatedAt || chatForTime?.updatedAt || null;
    console.log('[onGroupChatCleared] Marking as cleared, channelId:', clearedChannelId, 'lastMsgTime:', lastMsgTime);
    this.markChatAsCleared(clearedChannelId, lastMsgTime);

    // Update sidebar lists
    const chatIndex = this.sortedChats.findIndex(c => c.chatType === 'group' && c.channelId === this.memberChannelId);
    if (chatIndex !== -1) {
      this.sortedChats[chatIndex].lastMessage = null;
      this.sortedChats[chatIndex].lastMessageTimeAgo = "";
      this.sortedChats[chatIndex].isCleared = true;
    }

    const allIndex = this.allGroupUsers.findIndex(g => g.channelId === this.memberChannelId);
    if (allIndex !== -1) {
      this.allGroupUsers[allIndex].lastMessage = null;
      this.allGroupUsers[allIndex].lastMessageTimeAgo = "";
    }

    const filterIndex = this.filteredGroupUsers.findIndex(g => g.channelId === this.memberChannelId);
    if (filterIndex !== -1) {
      this.filteredGroupUsers[filterIndex].lastMessage = null;
      this.filteredGroupUsers[filterIndex].lastMessageTimeAgo = "";
    }

    // Force new array reference for CD
    this.sortedChats = [...this.sortedChats];
    this.cdRef.detectChanges();
  }

  onAllGroupMembersChange(members: any[]) {
    this.allGroupMembers = members;
    const activeMembers = members.filter(m => !m.isRemoved && !m.leftGroup);
    this.totalGroupMembers = activeMembers.length;
    this.groupMembersDisplayNames = activeMembers.map((m: any) =>
      m.userId === this.userId ? 'You' : (m.displayName || m.userName)
    );
    const currentGroup = this.allGroupUsers.find(g => g.channelId === this.memberChannelId);
    if (currentGroup) {
      currentGroup.members = members;
    }
    if (this.showSeenInfoModal && this.currentSeenInfoMessage) {
      this.refreshSeenInfoModal();
    }
  }

  closeGroupInfoPanel() {
    this.isGroupInfoOpen = false;
  }


  // Delete group chat
  private updateGroupAsExited(channelId: string) {
    const updateGroup = (group: any) => {
      if (group.channelId === channelId) {
        return { ...group, isExited: true };
      }
      return group;
    };

    this.allGroupUsers = this.allGroupUsers.map(updateGroup);
    this.filteredGroupUsers = this.filteredGroupUsers.map(updateGroup);
    this.sortedChats = this.sortedChats.map(updateGroup);
  }

  // Check if user has exited a group or been removed
  hasExitedGroup(channelId: string): boolean {
    if (!channelId) return false;
    const cid = String(channelId || '').trim();
    if (this.exitedGroups.has(cid)) {
      return true;
    }
    const group = this.allGroupUsers.find(g => String(g.channelId || '').trim() === cid) ||
      this.filteredGroupUsers.find(g => String(g.channelId || '').trim() === cid);

    if (group) {
      if (group.isExited) {
        return true;
      }

      if (group.members && Array.isArray(group.members)) {
        const currentUserMember = group.members.find((m: any) => String(m.userId).trim() === String(this.userId).trim());
        if (currentUserMember && (currentUserMember.isRemoved || currentUserMember.leftGroup)) {
          return true;
        }
      }
    }
    return false;
  }

  closeAllDialogs() {
    this.dialog.closeAll();
  }

  // Side Panel State
  isGroupInfoOpen: boolean = false;
  activeMessageMenuId: string | null = null;
  toggleMessageMenu(messageId: string, messageObj?: any) {
    if (!messageId) return;
    if (this.activeMessageMenuId === messageId) {
      this.activeMessageMenuId = null;
      this.reactingToMessageId = null;
      this.reactingToMessage = null;
    } else {
      this.activeMessageMenuId = messageId;
      this.reactingToMessageId = messageId;
      // Store message object reference for recently sent messages without ID
      if (messageObj) {
        this.reactingToMessage = messageObj;
      }
    }
  }

  handleMessageEdit(data: any) {
    console.log("Message Edit/Update Received:", data);
    const messageId = data?.message_id || data?.messageId || data?.id || data?._id;
    if (!messageId) return;
    this.zone.run(() => {
      const updateInList = (list: any[]) => {
        if (!list) return false;
        const msg = list.find((m: any) => (m._id || m.id || m.messageId) === messageId);
        if (msg) {
          if (data.data?.message) msg.message = data.data.message;
          else if (data.message && typeof data.message === 'string') msg.message = data.message;
          const newMeta = data.data?.meta || data.meta;
          if (newMeta) {
            msg.meta = { ...msg.meta, ...newMeta };
            // Sync reactions from meta
            if (newMeta.reactions) {
              msg.reactions = newMeta.reactions;
            }
          }
          this.cdRef.detectChanges();
          return true;
        }
        return false;
      };
      if (this.singleChatMessages) updateInList(this.singleChatMessages);
      if (this.groupChatMessages) updateInList(this.groupChatMessages);

      const channelId = data.channelId || data.channel_id || data.chatId || data.chat_id || data.groupId;
      if (channelId) {
        if (this.cachedSingleMessages[channelId]) {
          const updated = updateInList(this.cachedSingleMessages[channelId]);
          if (updated) {
            this.cachedSingleMessages[channelId] = [...this.cachedSingleMessages[channelId]];
          }
        }
        if (this.cachedGroupMessages[channelId]) {
          const updated = updateInList(this.cachedGroupMessages[channelId]);
          if (updated) {
            this.cachedGroupMessages[channelId] = [...this.cachedGroupMessages[channelId]];
          }
        }
      }

      this.updateCacheForActiveChannel();
    });
  }

  handleProfileImageError(event: any) {
    event.target.src = 'https://cdn-icons-png.flaticon.com/512/149/149071.png';
  }

  // DELETE MESSAGE FUNCTIONALITY
  deleteMessageAction(message: any, forEveryone: boolean) {
    if (forEveryone && this.isWriteBlockedInCurrentChat()) return;
    const channel = this.singleChatBox ? this.singleChannel : this.groupChannel;
    if (!channel) return;
    alertModalDelete(forEveryone ? "Delete for everyone?" : "Delete for me?").then(async (confirmed) => {
      if (confirmed) {
        const messageId = message._id || message.id || message.messageId;
        if (!messageId) {
          console.warn("Cannot delete message without _id. It may not have been synced to the backend yet.");
        }
        const isCarousel = (message.meta?.groupId || message.meta?.isGroupedUpload) &&
          message._allIds && message._allIds.length > 1;

        if (isCarousel) {
          const groupId = message.meta?.groupId;
          try {
            if (forEveryone) {
              await channel.massDeleteMessage({
                messageIds: message._allIds,
                delete_for_everyone: true
              });
              console.log(`Carousel deleted for everyone: ${message._allIds.length} messages`);
            } else {
              // Save message IDs AND groupId to localStorage for "delete for me"
              const groupId = message.meta?.groupId;
              const idsToSave = [...message._allIds];
              if (groupId) {
                idsToSave.push(groupId);
              }
              this.addDeletedForMeMessageIds(idsToSave);
              for (const id of message._allIds) {
                try {
                  await channel.deleteMessageForMe({ message_id: id });
                } catch (e) {
                  console.error('deleteMessageForMe failed for id', id, e);
                }
              }
              console.log(`Carousel deleted for me: ${message._allIds.length} messages`);
            }
            if (this.singleChatMessages && groupId) {
              this.singleChatMessages = this.singleChatMessages.filter(
                (m: any) => m.meta?.groupId !== groupId
              );
            }
            if (this.groupChatMessages && groupId) {
              this.groupChatMessages = this.groupChatMessages.filter(
                (m: any) => m.meta?.groupId !== groupId
              );
            }
            if (this.singleChatBox) this.processMessages();
            else if (this.groupChatBox) { this.processGroupMessages(); this.groupMessagesByDate(); }
            this.cdRef.detectChanges();
          } catch (err) {
            console.error('Carousel delete failed, falling back to single delete:', err);
            const params = { message_id: messageId || message.meta?.tempId || message.tempId };
            if (forEveryone) channel.deleteMessageForEveryone(params);
            else channel.deleteMessageForMe(params);
          }
        } else {
          const params = { message_id: messageId || message.meta?.tempId || message.tempId };
          if (forEveryone) {
            channel.deleteMessageForEveryone(params);
          } else {
            if (messageId) {
              this.addDeletedForMeMessageIds([messageId]);
            }
            channel.deleteMessageForMe(params);
          }
        }
      }
    });
  }
  // message-seen-all carries no message ids; only act when the reader and channel are known
  // so a bare echo can't mark the wrong chat read or tick messages as seen.
  handleMessageSeenAll(data: any, fallbackChannelId?: string) {
    const readerId = data?.receipt?.userId || data?.user_id || data?.userId;
    const channelId = data?.channelId || data?.channel_id || data?.chatId || fallbackChannelId;
    if (!readerId || !channelId) return;
    this.handleMessageSeen({ ...data, channelId });
  }

  handleMessageSeen(data: any) {
    console.log("handleMessageSeen", data);
    const channelId = data?.channelId || data?.chatId || this.activeChannel;
    if (!channelId) return;

    const readerId = data?.receipt?.userId || data?.user_id || data?.userId;
    const rawSeenTime = data?.receipt?.time || data?.time || data?.receipt?.updatedAt || data?.updatedAt || data?.receipt?.createdAt || data?.createdAt;
    let validSeenTime: string | undefined = undefined;
    if (rawSeenTime) {
      const parsed = new Date(rawSeenTime);
      if (!isNaN(parsed.getTime())) {
        validSeenTime = rawSeenTime;
      }
    }

    if (readerId === this.userId) {
      console.log("Message seen by me on another device. Syncing unread count for:", channelId);
      const isGroup = this.allGroupUsers.some(g => g.channelId === channelId);
      this.markChatAsRead(channelId, isGroup);
      return;
    }

    if (this.singleChatBox && this.activeChannel === channelId) {
      if (this.singleChatMessages && Array.isArray(this.singleChatMessages)) {
        this.singleChatMessages = this.singleChatMessages.map((msg: any) => {
          if (msg.sender_id === this.userId && !msg.seen) {
            let updated = { ...msg, seen: true };
            if (readerId && validSeenTime) {
              const seenReceipts = { ...(msg.seenReceipts || {}) };
              seenReceipts[String(readerId)] = validSeenTime;
              updated.seenReceipts = seenReceipts;
            }
            return updated;
          }
          return msg;
        });
      }
    }

    // ── GROUP CHAT: update message ticks in real-time ────────────────────────
    const isGroupChannel = this.allGroupUsers.some(g => g.channelId === channelId);

    // Active other members for this group
    const currentGroup = this.allGroupUsers.find(g => g.channelId === channelId);
    const members = (this.allGroupMembers && this.allGroupMembers.length > 0 && this.memberChannelId === channelId)
      ? this.allGroupMembers
      : (currentGroup?.members || []);
    const activeMembers = members.filter((m: any) => !m.isRemoved && !m.leftGroup);
    const activeOtherMembers = activeMembers.filter(
      (m: any) => String(m.userId || m._id).trim() !== String(this.userId).trim()
    );
    const activeOtherMemberIds: Set<string> = new Set<string>(
      activeOtherMembers.map((m: any) => String(m.userId || m._id).trim())
    );

    const strReaderId = readerId ? String(readerId).trim() : '';
    const isReaderActiveMember = !!(strReaderId && activeOtherMemberIds.has(strReaderId));

    if (this.groupChatBox && this.memberChannelId === channelId) {
      if (this.groupChatMessages && Array.isArray(this.groupChatMessages)) {
        // Cutoff timestamp if event provides messageId or time
        const targetMsgId = data?.message_id || data?.messageId || data?.receipt?.message_id || data?.receipt?.messageId;
        let cutoffTimestamp: number | null = null;
        if (targetMsgId) {
          const targetMsg = this.groupChatMessages.find((m: any) => {
            const mId = m._id || m.id || m.messageId;
            return mId && (mId === targetMsgId || String(mId) === String(targetMsgId));
          });
          if (targetMsg?.createdAt) {
            cutoffTimestamp = new Date(targetMsg.createdAt).getTime();
          }
        }
        if (cutoffTimestamp === null && validSeenTime) {
          cutoffTimestamp = new Date(validSeenTime).getTime();
        }

        this.groupChatMessages = this.groupChatMessages.map((msg: any) => {
          if (msg.sender_id === this.userId) {
            const msgTime = msg.createdAt ? new Date(msg.createdAt).getTime() : 0;
            const isEligible = cutoffTimestamp === null || (msgTime > 0 && msgTime <= cutoffTimestamp);

            const seenBy = Array.isArray(msg.seenBy) ? [...msg.seenBy.map(String)] : [];
            if (isEligible && isReaderActiveMember && !seenBy.includes(strReaderId)) {
              seenBy.push(strReaderId);
            }

            // Only active other members count towards seen
            const validSeenBy: string[] = seenBy.filter((id: string) => activeOtherMemberIds.has(id));
            const isSeen = activeOtherMemberIds.size > 0 && Array.from(activeOtherMemberIds).every((id: string) => validSeenBy.includes(id));

            let updated = { ...msg, seenBy: validSeenBy, seen: isSeen };
            if (isEligible && strReaderId && validSeenTime) {
              const seenReceipts = { ...(msg.seenReceipts || {}) };
              seenReceipts[strReaderId] = validSeenTime;
              updated.seenReceipts = seenReceipts;
            }
            return updated;
          }
          return msg;
        });
        if (this.showSeenInfoModal && this.currentSeenInfoMessage) {
          this.refreshSeenInfoModal();
        }
      }
    }

    // ── SIDEBAR & CACHE UPDATE ──────────────────────────────────────────────
    if (!isGroupChannel) {
      // 1-to-1 chats: keep existing behavior
      const markLastMsgSeenSingle = (chat: any): any => {
        if (chat?.channelId === channelId && chat.lastMessage && chat.lastMessage.sender_id === this.userId && !chat.lastMessage.seen) {
          return { ...chat, lastMessage: { ...chat.lastMessage, seen: true } };
        }
        return chat;
      };

      this.allSingleUsers = this.allSingleUsers.map(markLastMsgSeenSingle);
      this.filteredSingleUsers = this.filteredSingleUsers.map(markLastMsgSeenSingle);
      this.sortedChats = this.sortedChats.map(markLastMsgSeenSingle);

      try {
        const cache = JSON.parse(localStorage.getItem('chatMetadata') || '{}');
        if (cache[channelId]?.lastMessage) {
          cache[channelId].lastMessage.seen = true;
          localStorage.setItem('chatMetadata', JSON.stringify(cache));
        }
      } catch (e) {
        console.error('Failed to persist seen status to chatMetadata', e);
      }
    } else {
      // Group chats: apply everyone-has-seen rule
      let groupLastMsgSeen = false;
      let groupLastMsgSeenBy: string[] = [];

      if (this.groupChatBox && this.memberChannelId === channelId && this.groupChatMessages && this.groupChatMessages.length > 0) {
        const lastMsgInChat = this.groupChatMessages[this.groupChatMessages.length - 1];
        if (lastMsgInChat.sender_id === this.userId) {
          groupLastMsgSeen = lastMsgInChat.seen === true;
          groupLastMsgSeenBy = lastMsgInChat.seenBy || [];
        }
      } else {
        const lastMsg = currentGroup?.lastMessage;
        if (lastMsg && lastMsg.sender_id === this.userId) {
          const seenBy = Array.isArray(lastMsg.seenBy) ? [...lastMsg.seenBy.map(String)] : [];
          if (isReaderActiveMember && !seenBy.includes(strReaderId)) {
            seenBy.push(strReaderId);
          }
          groupLastMsgSeenBy = seenBy.filter((id: string) => activeOtherMemberIds.has(id));
          groupLastMsgSeen = activeOtherMemberIds.size > 0 && Array.from(activeOtherMemberIds).every((id: string) => groupLastMsgSeenBy.includes(id));
        }
      }

      const markLastMsgSeenGroup = (chat: any): any => {
        if (chat?.channelId === channelId && chat.lastMessage && chat.lastMessage.sender_id === this.userId) {
          return {
            ...chat,
            lastMessage: {
              ...chat.lastMessage,
              seenBy: groupLastMsgSeenBy,
              seen: groupLastMsgSeen
            }
          };
        }
        return chat;
      };

      this.allGroupUsers = this.allGroupUsers.map(markLastMsgSeenGroup);
      this.filteredGroupUsers = this.filteredGroupUsers.map(markLastMsgSeenGroup);
      this.sortedChats = this.sortedChats.map(markLastMsgSeenGroup);

      try {
        const cache = JSON.parse(localStorage.getItem('chatMetadata') || '{}');
        if (cache[channelId]?.lastMessage) {
          cache[channelId].lastMessage.seenBy = groupLastMsgSeenBy;
          cache[channelId].lastMessage.seen = groupLastMsgSeen;
          localStorage.setItem('chatMetadata', JSON.stringify(cache));
        }
      } catch (e) {
        console.error('Failed to persist seen status to chatMetadata', e);
      }
    }

    this.cdRef.detectChanges();
  }



  handleMessageDelete(data: any) {
    console.log("Message deleted event received:", data);
    const messageId = data?.message_id || data?.messageId || data?._id;
    if (!messageId) return;
    const senderId = data?.sender_id || data?.userId || data?.user_id;
    const deleteForEveryone = data?.delete_for_everyone || data?.deleteForEveryone;
    if (!deleteForEveryone && senderId && senderId !== this.userId) {
      console.log("Skipping 'delete for me' event from another user:", { senderId, myId: this.userId });
      return;
    }
    let channelId = data?.channelId || data?.channel_id || data?.chatId || data?.chat_id || data?.groupId;
    let isGroup = false;

    // First try determining group type using channelId mapping if available
    if (channelId) {
      if (this.allGroupUsers?.some(g => g.channelId === channelId)) {
        isGroup = true;
      }
    }

    const allLists = [this.sortedChats, this.allSingleUsers, this.allGroupUsers];
    let foundChatInSidebar: any = null;
    for (const list of allLists) {
      if (!list) continue;
      foundChatInSidebar = list.find(c => c.lastMessage && (c.lastMessage._id === messageId || c.lastMessage.id === messageId || (c.lastMessage.messageId === messageId)));
      if (foundChatInSidebar) break;
    }
    if (foundChatInSidebar) {
      channelId = foundChatInSidebar.channelId;
      isGroup = foundChatInSidebar.chatType === 'group' || !!foundChatInSidebar.groupName || !!foundChatInSidebar.isGroup;
    }
    if (this.singleChatMessages) {
      this.singleChatMessages = this.singleChatMessages.filter((m: any) => {
        if ((m._id || m.id) === messageId) return false;
        if (m._allIds && Array.isArray(m._allIds) && m._allIds.includes(messageId)) return false;
        return true;
      });
    }
    if (this.groupChatMessages) {
      this.groupChatMessages = this.groupChatMessages.filter((m: any) => {
        if ((m._id || m.id) === messageId) return false;
        if (m._allIds && Array.isArray(m._allIds) && m._allIds.includes(messageId)) return false;
        return true;
      });
    }

    if (channelId) {
      if (isGroup) {
        if (this.cachedGroupMessages[channelId]) {
          this.cachedGroupMessages[channelId] = this.cachedGroupMessages[channelId].filter((m: any) => {
            if ((m._id || m.id) === messageId) return false;
            if (m._allIds && Array.isArray(m._allIds) && m._allIds.includes(messageId)) return false;
            return true;
          });
        }
      } else {
        if (this.cachedSingleMessages[channelId]) {
          this.cachedSingleMessages[channelId] = this.cachedSingleMessages[channelId].filter((m: any) => {
            if ((m._id || m.id) === messageId) return false;
            if (m._allIds && Array.isArray(m._allIds) && m._allIds.includes(messageId)) return false;
            return true;
          });
        }
      }
    }

    if (this.singleChatBox) {
      this.processMessages();
    } else if (this.groupChatBox) {
      this.processGroupMessages();
      this.groupMessagesByDate();
    }

    if (channelId) {
      const isActive = (isGroup && this.groupChatBox && this.memberChannelId === channelId) ||
        (!isGroup && this.singleChatBox && this.activeChannel === channelId);

      // Extract actual sender if missing from payload but available in lastMessage
      let actualSenderId = senderId;
      if (!actualSenderId && foundChatInSidebar && foundChatInSidebar.lastMessage) {
        if (foundChatInSidebar.lastMessage._id === messageId || foundChatInSidebar.lastMessage.id === messageId || foundChatInSidebar.lastMessage.messageId === messageId) {
          actualSenderId = foundChatInSidebar.lastMessage.sender_id || foundChatInSidebar.lastMessage.sid;
        }
      }

      // Decrement unread count for the receiver if chat isn't active
      // If actualSenderId is still unknown, we assume it's safe to decrement since unread messages typically imply the deleted message was sent by the other party.
      const isFromMe = actualSenderId === this.userId;

      if (!isActive && !isFromMe) {
        let updatedCount = false;
        if (isGroup) {
          this.filteredGroupUsers = this.filteredGroupUsers.map(chat => {
            if (chat.channelId === channelId && chat.unreadCount && chat.unreadCount > 0) {
              updatedCount = true;
              return { ...chat, unreadCount: chat.unreadCount - 1 };
            }
            return chat;
          });
          this.allGroupUsers = this.allGroupUsers.map(chat => {
            if (chat.channelId === channelId && chat.unreadCount && chat.unreadCount > 0) {
              return { ...chat, unreadCount: chat.unreadCount - 1 };
            }
            return chat;
          });
        } else {
          this.filteredSingleUsers = this.filteredSingleUsers.map(chat => {
            if (chat.channelId === channelId && chat.unreadCount && chat.unreadCount > 0) {
              updatedCount = true;
              return { ...chat, unreadCount: chat.unreadCount - 1 };
            }
            return chat;
          });
          this.allSingleUsers = this.allSingleUsers.map(chat => {
            if (chat.channelId === channelId && chat.unreadCount && chat.unreadCount > 0) {
              return { ...chat, unreadCount: chat.unreadCount - 1 };
            }
            return chat;
          });
        }

        if (updatedCount) {
          const totalUnreadCount = this.calculateTotalUnreadCount();
          this.store.dispatch(setUnreadCount({ unreadCount: totalUnreadCount }));
          this.saveUnreadCountsToLocalStorage();
        }
      }

      if (isActive) {
        const currentMessages = isGroup ? this.groupChatMessages : this.singleChatMessages;
        if (currentMessages && currentMessages.length > 0) {
          const newLast = currentMessages[currentMessages.length - 1];
          this.updateLocalChatLastMessage(channelId, newLast, isGroup);
        } else {
          this.fetchAndUpdateLastMessage(channelId, isGroup);
        }
      } else if (foundChatInSidebar) {
        this.fetchAndUpdateLastMessage(channelId, isGroup);
      }
      this.updateCacheForActiveChannel();
    }
  }

  async fetchAndUpdateLastMessage(channelId: string, isGroup: boolean) {
    if (!this.converse) return;
    try {
      const channel = await this.converse.connectChannel({ channelId });
      const requestParams: any = { limit: 1 };
      const response = await channel.getMessages(requestParams);
      const messages = [...(response.msgs?.read || []), ...(response.msgs?.unread || [])];

      this.zone.run(() => {
        if (messages.length > 0) {
          messages.sort((a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
          const lastMsg = messages[messages.length - 1];
          this.updateLocalChatLastMessage(channelId, lastMsg, isGroup);
        } else {
          this.clearChannelSidebar(channelId, isGroup);
        }
      });
    } catch (err) {
      console.error("Failed to sync message after delete for channel", channelId, err);
    }
  }

  // Load deleted message IDs from localStorage and IndexedDB
  async loadDeletedForMeMessageIds() {
    try {
      const stored = localStorage.getItem('deletedForMeMessageIds');
      if (stored) {
        const ids = JSON.parse(stored);
        if (Array.isArray(ids)) {
          this.deletedForMeMessageIds = new Set(ids);
        }
      }
    } catch (e) {
      console.error('Failed to read deleted message IDs from localStorage:', e);
    }

    try {
      if (this.chatCacheService) {
        const idbIds = await this.chatCacheService.getMetadata<string[]>('deletedForMeMessageIds');
        if (idbIds && Array.isArray(idbIds)) {
          let hasNew = false;
          idbIds.forEach(id => {
            if (!this.deletedForMeMessageIds.has(id)) {
              this.deletedForMeMessageIds.add(id);
              hasNew = true;
            }
          });
          if (hasNew) {
            localStorage.setItem('deletedForMeMessageIds', JSON.stringify(Array.from(this.deletedForMeMessageIds)));
          }
        }
      }
    } catch (e) {
      console.error('Failed to load deleted message IDs from IndexedDB:', e);
    }
  }

  // Save deleted message IDs to localStorage and IndexedDB
  saveDeletedForMeMessageIds() {
    try {
      const ids = Array.from(this.deletedForMeMessageIds);
      localStorage.setItem('deletedForMeMessageIds', JSON.stringify(ids));
      if (this.chatCacheService) {
        this.chatCacheService.saveMetadata('deletedForMeMessageIds', ids);
      }
    } catch (e) {
      console.error('Failed to save deleted message IDs:', e);
    }
  }

  // Add message IDs to deleted list
  addDeletedForMeMessageIds(messageIds: string[]) {
    messageIds.forEach(id => {
      if (id) {
        this.deletedForMeMessageIds.add(id);
        if (this.chatCacheService) {
          this.chatCacheService.deleteMessage(id);
        }
      }
    });
    this.saveDeletedForMeMessageIds();
  }

  clearChannelSidebar(channelId: string, isGroup: boolean) {
    if (!channelId) return;
    const updateEntry = (list: any[]) => {
      if (!list) return;
      const idx = list.findIndex(u => u.channelId === channelId);
      if (idx !== -1) {
        list[idx] = {
          ...list[idx],
          lastMessage: null,
          lastMessageTimeAgo: '',
          isCleared: true
        };
      }
    };

    updateEntry(this.sortedChats);
    updateEntry(this.allSingleUsers);
    updateEntry(this.filteredSingleUsers);
    updateEntry(this.allGroupUsers);
    updateEntry(this.filteredGroupUsers);
    this.sortedChats = [...this.sortedChats];

    // Persist the state
    try {
      const chatMeta = JSON.parse(localStorage.getItem('chatMetadata') || '{}');
      if (chatMeta[channelId]) {
        delete chatMeta[channelId];
        localStorage.setItem('chatMetadata', JSON.stringify(chatMeta));
      }
      this.markChatAsCleared(channelId, null);
    } catch (e) {
      console.error('Error clearing chatMetadata on delete', e);
    }

    this.sortAllChatsByRecentMessages();
  }

  // PINNED MESSAGES FUNCTIONALITY
  pinnedMessages: any[] = [];
  showPinnedMessages: boolean = false;

  // Debounce timer for fetchPinnedMessages to prevent race conditions on rapid pin actions
  private _fetchPinnedDebounceTimer: any = null;

  fetchPinnedMessages(debounceMs: number = 0) {
    if (debounceMs > 0) {
      if (this._fetchPinnedDebounceTimer) {
        clearTimeout(this._fetchPinnedDebounceTimer);
      }
      this._fetchPinnedDebounceTimer = setTimeout(() => {
        this._fetchPinnedDebounceTimer = null;
        this._doFetchPinnedMessages();
      }, debounceMs);
      return;
    }
    this._doFetchPinnedMessages();
  }

  private _fetchPinnedSeq: number = 0;
  private _doFetchPinnedMessages() {
    const channel = this.singleChatBox ? this.singleChannel : this.groupChannel;
    if (!channel) return;
    const seq = ++this._fetchPinnedSeq; // monotonic stamp for stale-response guard
    if (typeof channel.getPinnedMessages === 'function') {
      channel.getPinnedMessages({ limit: 50 }).then((res: any) => {
        // Discard if a newer fetch was already started
        if (seq !== this._fetchPinnedSeq) {
          console.log(`fetchPinnedMessages: discarding stale response (seq ${seq}, current ${this._fetchPinnedSeq})`);
          return;
        }
        console.log("Pinned messages fetched:", res);
        const raw = res.data?.pinnedMsgs || [];
        const uniqueMap = new Map();
        for (const p of raw) {
          const mid = p.messageId || (p.message && (p.message._id || p.message.id)) || p._id || p.id;
          if (mid && !uniqueMap.has(mid)) {
            uniqueMap.set(mid, p);
          }
        }
        this.pinnedMessages = Array.from(uniqueMap.values());
        if (this.pinnedMessages.length === 0) {
          this.showPinnedMessages = false;
        }
        const activeChan = this.singleChatBox ? this.activeChannel : this.memberChannelId;
        if (activeChan) {
          this.cachedPinnedMessages[activeChan] = this.pinnedMessages;
        }
        this.cdRef.detectChanges();
      }).catch((err: any) => {
        console.error("Error fetching pinned messages:", err);
      });
    }
  }
  reformatPinSystemMessage(text: string): string {
    if (!text) return text;
    const match = text.match(/^(.+?\s+pinned\s+)['"](.+?)['"]\s*$/);
    if (!match) return text;
    const prefix = match[1];
    const content = match[2];
    const ext = (content.split('.').pop() || '').toLowerCase().split('?')[0];

    if (/^(jpg|jpeg|png|webp|svg|heic|heif|bmp|tiff?)$/.test(ext)) {
      return `${prefix}a photo`;
    } else if (ext === 'gif') {
      return `${prefix}a GIF`;
    } else if (/^(mp4|mov|avi|mkv|webm|flv|wmv|m4v|3gp)$/.test(ext)) {
      return `${prefix}a video`;
    } else if (/^(mp3|wav|ogg|aac|m4a|flac|wma|opus)$/.test(ext)) {
      return `${prefix}an audio`;
    } else if (/^(pdf|docx?|xlsx?|pptx?|txt|csv|js|git|html|angular|zip|rar|7z|tar|gz)$/i.test(ext)) {
      return `${prefix}a document`;
    }
    return text;
  }

  pinMessageAction(message: any) {
    if (this.isWriteBlockedInCurrentChat()) return;
    const channel = this.singleChatBox ? this.singleChannel : this.groupChannel;
    if (!channel) return;

    const messageId = message._id || message.id || message.messageId;
    if (!messageId) return;

    const chatId = this.singleChatBox ? this.singleUser?.channelId : this.memberChannelId;
    if (!chatId) return;

    if (typeof channel.addPinMessage === 'function') {
      channel.addPinMessage({ messageId: messageId, forAll: true }).then((res: any) => {
        console.log("Message pinned:", res);
        this.chatService.pinMessage(chatId, messageId).subscribe({
          next: (response: any) => {
            console.log("Pin message API response:", response);
          },
          error: (err: any) => {
            console.error("Error calling pin message API:", err);
          }
        });

        this.activeMessageMenuId = null; // Close menu
      }).catch((err: any) => {
        console.error("Error pinning message:", err);
      });
    }
  }
  unpinMessageAction(message: any) {
    if (this.isWriteBlockedInCurrentChat()) return;
    const channel = this.singleChatBox ? this.singleChannel : this.groupChannel;
    if (!channel) return;

    let messageId = '';
    let pinId = '';
    if (message.messageId) {
      messageId = message.messageId;
      pinId = message._id || message.id;
    } else if (message.message && typeof message.message === 'object' && message.message._id) {
      messageId = message.message._id;
      pinId = message._id || message.id;
    } else {
      messageId = message._id || message.id;
      const pinnedRecord = this.pinnedMessages.find(p =>
        p.messageId === messageId ||
        (p.message && (p.message._id === messageId || p.message.id === messageId))
      );
      if (pinnedRecord) {
        pinId = pinnedRecord._id || pinnedRecord.id;
      }
    }

    if (!messageId) {
      console.error("Unable to resolve messageId for unpinning", message);
      return;
    }

    console.log("Unpinning message:", { messageId, pinId });

    if (typeof channel.unPinMessage === 'function') {
      channel.unPinMessage({ messageId: messageId, pinId: pinId, forAll: true }).then((res: any) => {
        console.log("Message unpinned:", res);
        // this.fetchPinnedMessages();
        this.activeMessageMenuId = null;
      }).catch((err: any) => {
        console.error("Error unpinning message:", err);
      });
    }
  }

  handlePinMessage(data: any) {
    console.log("Socket: Pin Message received:", data);
    this.zone.run(() => {
      // Guard: only refresh pinned messages when the event belongs to the currently active channel
      const eventChannelId =
        data?.channelId ||
        data?.channel_id ||
        data?.data?.channelId ||
        data?.message?.channelId ||
        data?.meta?.channelId ||
        data?.meta?.channel_id;

      const activeChannelId = this.singleChatBox
        ? (this.singleUser?.channelId || this.activeChannel)
        : this.memberChannelId;

      if (eventChannelId && String(eventChannelId).trim() !== String(activeChannelId).trim()) {
        console.warn(`handlePinMessage: Ignoring pin event for channel ${eventChannelId} — currently on ${activeChannelId}`);
        delete this.cachedPinnedMessages[eventChannelId];
        return;
      }

      // Debounce so rapid successive pins result in one consolidated fetch
      this.fetchPinnedMessages(300);
    });
  }

  handleUnPinMessage(data: any) {
    console.log("Socket: UnPin Message received:", data);
    this.zone.run(() => {
      // Guard: only refresh pinned messages when the event belongs to the currently active channel
      const eventChannelId =
        data?.channelId ||
        data?.channel_id ||
        data?.data?.channelId ||
        data?.message?.channelId ||
        data?.meta?.channelId ||
        data?.meta?.channel_id;

      const activeChannelId = this.singleChatBox
        ? (this.singleUser?.channelId || this.activeChannel)
        : this.memberChannelId;

      if (eventChannelId && String(eventChannelId).trim() !== String(activeChannelId).trim()) {
        console.warn(`handleUnPinMessage: Ignoring unpin event for channel ${eventChannelId} — currently on ${activeChannelId}`);
        delete this.cachedPinnedMessages[eventChannelId];
        return;
      }

      // Debounce rapid consecutive unpin actions
      this.fetchPinnedMessages(300);
    });
  }

  getPinnedMessageId(msg: any): string {
    if (!msg) return '';
    if (msg.messageId) return msg.messageId;
    if (msg.message && typeof msg.message === 'object') {
      return msg.message._id || msg.message.id || '';
    }
    return msg._id || msg.id || '';
  }

  getPinnedMessageText(msg: any): string {
    if (!msg) return '';

    // Extract the actual message object
    const messageObj = msg.message && typeof msg.message === 'object' ? msg.message : msg;

    // Check for GIF - only special case
    if (messageObj.meta?.mediaType === 'gif') {
      return 'GIF';
    }

    // Check for Voice Message
    if (isVoiceMessage(msg)) {
      return '🎤 Voice message';
    }

    // For all other media, return the message text or filename
    if (msg.message && typeof msg.message === 'object') {
      return msg.message.message || 'Media/File';
    }
    if (msg.message && typeof msg.message === 'string') {
      return msg.message;
    }

    return 'Media/File';
  }

  getVoiceDuration(msg: any): string {
    const messageId = msg.messageId || (msg.message && (msg.message._id || msg.message.id || msg.message.messageId)) || msg._id || msg.id;
    let originalMessage = null;
    if (messageId) {
      originalMessage =
        this.singleChatMessages?.find(m => (m._id || m.id || m.messageId) === messageId) ||
        this.groupChatMessages?.find(m => (m._id || m.id || m.messageId) === messageId);
    }
    const messageObj = originalMessage || (msg.message && typeof msg.message === 'object' ? msg.message : msg);

    const duration = messageObj?.meta?.duration || messageObj?.duration || messageObj?.file?.duration;
    console.log("getVoiceDuration check:", { msg, messageId, originalMessage, messageObj, duration });
    if (!duration) return '';
    const minutes = Math.floor(duration / 60);
    const seconds = Math.floor(duration % 60);
    return `(${minutes}:${seconds < 10 ? '0' : ''}${seconds})`;
  }

  isMessagePinned = (messageId: string): boolean => {
    if (!messageId) return false;
    return this.pinnedMessages.some(m => (m._id || m.id || m.messageId) === messageId || (m.message && (m.message._id || m.message.id || m.message.messageId) === messageId));
  }

  togglePinnedMessages() {
    this.showPinnedMessages = !this.showPinnedMessages;
  }



  // REACTION FUNCTIONALITY
  reactingToMessageId: string | null = null;
  showReactionEmojiPicker: boolean = false;
  showQuickReactionMenuId: string | null = null;
  reactingToMessage: any = null; // Store actual message object reference
  commonReactions = ['👍', '❤️', '😂', '😮', '🙏'];

  // Helper method to get message ID consistently
  getMessageId(message: any): string | null {
    return message?._id || message?.id || message?.messageId || null;
  }
  toggleReactionPicker(message: any) {
    console.warn("toggleReactionPicker is deprecated.");
    this.toggleMessageMenu(message._id || message.id);
  }
  openFullReactionPicker(messageId: string) {
    if (this.isWriteBlockedInCurrentChat()) return;
    this.reactingToMessageId = messageId;
    this.showQuickReactionMenuId = null;
    this.showReactionEmojiPicker = true;
    this.activeMessageMenuId = null; // Close the message menu
  }
  closeReactionPicker() {
    this.showReactionEmojiPicker = false;
    this.showQuickReactionMenuId = null;
    this.reactingToMessageId = null;
    this.reactingToMessage = null;
  }
  onQuickReactionSelect(reaction: string, messageId: string) {
    this.reactingToMessageId = messageId;
    this.submitReaction(reaction);
    this.closeReactionPicker();
    this.activeMessageMenuId = null;
  }

  onReactionSelect(event: any) {
    this.submitReaction(event.emoji.native);
    this.closeReactionPicker();
  }

  submitReaction(emote: string) {
    if (this.isWriteBlockedInCurrentChat()) return;
    if (!this.reactingToMessageId) return;
    const channel = this.singleChatBox ? this.singleChannel : this.groupChannel;
    if (!channel) return;
    const list = this.singleChatBox ? this.singleChatMessages : this.groupChatMessages;
    if (!list) return;
    let msg = list.find((m: any) =>
      (m._id || m.id || m.messageId) === this.reactingToMessageId ||
      m.meta?.tempId === this.reactingToMessageId ||
      m.tempId === this.reactingToMessageId
    );
    if (!msg) {
      msg = list.find((m: any) => {
        if (m.meta && m.meta.fileUrl && typeof m.meta.fileUrl === 'string') {
          const isMedia = m.meta.fileType && (m.meta.fileType.startsWith('image/') || m.meta.fileType.startsWith('video/') || m.meta.fileType === 'image/gif');
          if (isMedia && m.sender_id === this.userId && m.meta.fileUrl && this.reactingToMessageId && m.meta.fileUrl.includes(this.reactingToMessageId)) {
            return true;
          }
        }
        return (!m._id && !m.id && !m.messageId && m.sender_id === this.userId && m.createdAt && list.indexOf(m) === list.length - 1);
      });
      if (msg) {
        console.log("Found recently sent media message without ID, using temporary reference");
      }
    }
    if (!msg) {
      console.error("Message not found for reaction update:", this.reactingToMessageId);
      return;
    }
    const correctMessageId = msg._id || msg.id || msg.messageId;
    if (!correctMessageId) {
      console.warn("Message ID is not yet synced. Reaction might fail locally if not processed by backend.");
    }
    const currentMeta = msg.meta || {};
    let reactions = currentMeta.reactions || [];
    if (!currentMeta.reactions && msg.reactions) {
      reactions = [...msg.reactions];
    }
    const existingIndex = reactions.findIndex((r: any) =>
      r.userId === this.userId || r.user_id === this.userId || r.sender_id === this.userId
    );
    if (existingIndex !== -1) {
      const existingReaction = reactions[existingIndex];
      let existingEmoji = existingReaction;
      if (typeof existingReaction === 'object' && existingReaction !== null) {
        existingEmoji = existingReaction.emoji || existingReaction.emote || '';
      }
      if (existingEmoji === emote) {
        reactions.splice(existingIndex, 1);
      } else {
        reactions.splice(existingIndex, 1);
        reactions.push({
          emoji: emote,
          userId: this.userId,
          userName: this.senderUsername || 'You',
          createdAt: new Date().toISOString()
        });
      }
    } else {
      // No existing reaction: ADD new
      reactions.push({
        emoji: emote,
        userId: this.userId,
        userName: this.senderUsername || 'You',
        createdAt: new Date().toISOString()
      });
    }

    // Optimistically update local message reactions for sender 
    msg.reactions = [...reactions];
    if (!msg.meta) msg.meta = {};
    msg.meta.reactions = [...reactions];
    this.cdRef.detectChanges();

    console.log("Submitting reaction via editMessage:", emote, "New reactions:", reactions);

    const editParams = {
      message_id: correctMessageId || this.reactingToMessageId,
      data: {
        meta: {
          ...currentMeta,
          reactions: reactions
        }
      }
    };
    channel.editMessage(editParams);
  }

  handleReactionUpdate(data: any) {
    console.log("Reaction Update Received:", data);
    const messageId = data.message_id || data.messageId;
    this.zone.run(() => {
      const updateInList = (list: any[]) => {
        if (!list) return;
        let msg = messageId ? list.find((m: any) => (m._id || m.id || m.messageId) === messageId) : null;
        if (!msg && data.meta && data.meta.fileUrl) {
          msg = list.find((m: any) => m.meta && m.meta.fileUrl === data.meta.fileUrl && m.sender_id === (data.userId || data.user_id || data.sender_id));
        }
        if (!msg && data.sender_id) {
          msg = list.find((m: any) => !m._id && !m.id && !m.messageId && m.sender_id === data.sender_id);
        }
        if (msg) {
          console.log("Updating message reactions locally (handleReactionUpdate):", msg._id || msg.id || msg.messageId);
          let currentReactions = [...(msg.reactions || [])];
          if (Array.isArray(data.reactions)) {
            currentReactions = [...data.reactions];
          }
          else if (data.emote && typeof data.emote === 'object') {
            const newReaction = data.emote;
            const userId = newReaction.userId || newReaction.user_id || newReaction.sender_id;
            if (userId) {
              currentReactions = currentReactions.filter((r: any) => {
                const rUserId = r.userId || r.user_id || r.sender_id;
                return rUserId !== userId;
              });
              currentReactions.push(newReaction);
            }
          }
          msg.reactions = currentReactions;
          if (!msg.meta) {
            msg.meta = {};
          }
          msg.meta.reactions = [...currentReactions];
          this.cdRef.detectChanges();
          return true; // Indicate that a message was updated
        }
        return false; // No message was updated
      };
      let singleChatUpdated = false;
      if (this.singleChatMessages) {
        singleChatUpdated = updateInList(this.singleChatMessages);
        if (singleChatUpdated) {
          // Create new array reference to trigger Angular change detection
          this.singleChatMessages = [...this.singleChatMessages];
        }
      }
      if (this.groupChatMessages) {
        const groupChatUpdated = updateInList(this.groupChatMessages);
        if (groupChatUpdated) {
          // Create new array reference to trigger Angular change detection
          this.groupChatMessages = [...this.groupChatMessages];
        }
      }

      const channelId = data.channelId || data.channel_id || data.chatId || data.chat_id || data.groupId;
      if (channelId) {
        if (this.cachedSingleMessages[channelId]) {
          const updated = updateInList(this.cachedSingleMessages[channelId]);
          if (updated) {
            this.cachedSingleMessages[channelId] = [...this.cachedSingleMessages[channelId]];
          }
        }
        if (this.cachedGroupMessages[channelId]) {
          const updated = updateInList(this.cachedGroupMessages[channelId]);
          if (updated) {
            this.cachedGroupMessages[channelId] = [...this.cachedGroupMessages[channelId]];
          }
        }
      }

      this.updateCacheForActiveChannel();
    });
  }

  // Aggregate reactions by emoji and count 
  getAggregatedReactions(reactions: any[]): any[] {
    if (!reactions || reactions.length === 0) return [];
    const reactionMap = new Map<string, { emoji: string; count: number; userIds: string[] }>()
    reactions.forEach((reaction: any) => {
      let emoji = reaction;
      if (typeof reaction === 'object' && reaction !== null) {
        emoji = reaction.emoji || reaction.emote || '';
      }
      if (!emoji) return;
      const userId = reaction.user_id || reaction.userId || reaction.sender_id;
      if (!reactionMap.has(emoji)) {
        reactionMap.set(emoji, { emoji, count: 0, userIds: [] });
      }
      const aggReaction = reactionMap.get(emoji)!;
      aggReaction.count++;
      if (userId && !aggReaction.userIds.includes(userId)) {
        aggReaction.userIds.push(userId);
      }
    });
    const sortedReactions = Array.from(reactionMap.values()).sort((a: any, b: any) => b.count - a.count);
    return sortedReactions;
  }

  getDisplayedReactions(reactions: any[]): any[] {
    const aggregated = this.getAggregatedReactions(reactions);
    return aggregated.slice(0, 3);
  }

  getRemainingReactionCount(reactions: any[]): number {
    const aggregated = this.getAggregatedReactions(reactions);
    if (aggregated.length <= 3) return 0;
    return aggregated.slice(3).reduce((sum: any, item: any) => sum + item.count, 0);
  }

  // Check if current user has reacted to a message
  hasUserReacted(reactions: any[], emoji: string): boolean {
    if (!reactions || reactions.length === 0) return false;
    return reactions.some((r: any) => {
      let rEmoji = r;
      if (typeof r === 'object' && r !== null) {
        rEmoji = r.emoji || r.emote || '';
      }
      const userId = r.user_id || r.userId || r.sender_id;
      return rEmoji === emoji && userId === this.userId;
    });
  }

  // Handle click on reaction chip - tap to remove if user has reacted
  onReactionChipClick(message: any, emoji: string, event: MouseEvent) {
    event.stopPropagation();
    if (this.hasUserReacted(message.reactions, emoji)) {
      this.reactingToMessageId = message._id || message.id || message.messageId;
      this.submitReaction(emoji);
      this.reactingToMessageId = null;
    } else {
      this.openReactionDetails(message);
    }
  }

  // Check if message has any reactions
  hasReactions(message: any): boolean {
    return message.reactions && message.reactions.length > 0;
  }

  isShortMessage(messageText: any): boolean {
    return true;
  }

  getReactionsByLimit(reactions: any[], limit: number): any[] {
    const agg = this.getAggregatedReactions(reactions);
    return agg.slice(0, limit);
  }

  getRemainderByLimit(reactions: any[], limit: number): number {
    const agg = this.getAggregatedReactions(reactions);
    if (agg.length <= limit) return 0;
    return agg.slice(limit).reduce((sum, item) => sum + item.count, 0);
  }

  // ==================== REPLY & FORWARD LOGIC ====================
  replyingToMessage: any = null;
  forwardingMessage: any = null;
  showForwardModal: boolean = false;
  forwardSearchTerm: string = "";
  editingMessage: any = null;

  // ==================== SEEN INFO MODAL ====================
  showSeenInfoModal: boolean = false;
  seenInfoMembers: SeenMember[] = [];
  seenInfoTotalMembers: number = 0;
  currentSeenInfoMessage: any = null;
  currentSeenReceiptsMap: Map<string, any> = new Map<string, any>();
  currentSeenUserIds: string[] = [];

  focusMessageInput() {
    if (this.singleChatBox && this.singleMessageInput) {
      this.singleMessageInput.focus();
    } else if (this.groupChatBox && this.groupMessageInput) {
      this.groupMessageInput.focus();
    }
  }

  replyToMessage(message: any) {
    if (this.isWriteBlockedInCurrentChat()) return;
    this.cancelEdit();
    this.replyingToMessage = message;
    this.activeMessageMenuId = null;
    setTimeout(() => {
      this.focusMessageInput();
    }, 100);
  }

  cancelReply() {
    this.replyingToMessage = null;
  }

  buildReplyToPayload(messageToReply: any): any {
    if (!messageToReply) return null;
    const senderId = messageToReply.sender_id || messageToReply.senderId || messageToReply.user_id || messageToReply.sid;
    const isSelf = senderId && String(senderId).trim() === String(this.userId).trim();
    const senderName = isSelf
      ? "You"
      : (messageToReply.senderName || messageToReply.name || (this.userNameMapping?.[senderId] || "User"));
    const sourceGroupName = messageToReply.sourceGroupName || messageToReply.meta?.sourceGroupName;
    const sourceGroupId = messageToReply.sourceGroupId || messageToReply.meta?.sourceGroupId;

    return {
      id: messageToReply._id || messageToReply.id || messageToReply.messageId,
      message: messageToReply.message || (messageToReply.files?.length || messageToReply.url ? "Media" : ""),
      sender_id: senderId,
      senderName,
      sourceGroupName,
      sourceGroupId,
      url: messageToReply.url,
      files: messageToReply.files,
      meta: messageToReply.meta
    };
  }

  // ==================== EDIT MESSAGE LOGIC ====================
  editMessage(message: any) {
    if (this.isWriteBlockedInCurrentChat()) return;
    // Only allow editing if the message has actual text content (not just a media placeholder)
    if (isMessageRedundantWithFile(message)) {
      return;
    }
    this.cancelReply();
    this.editingMessage = message;
    this.activeMessageMenuId = null;

    // Determine the text to edit: prefer caption for media, else use message text
    let messageText = "";
    if (message.meta?.caption) {
      messageText = message.meta.caption;
    } else if (message.message && !isMessageRedundantWithFile(message)) {
      messageText = message.message;
    }

    if (this.singleChatBox) {
      this.newMessage = messageText;
    } else {
      this.newGroupMessage = messageText;
    }
    setTimeout(() => {
      this.focusMessageInput();
    }, 100);
  }

  cancelEdit() {
    if (!this.editingMessage) {
      return;
    }
    this.editingMessage = null;
    if (this.singleChatBox && this.chatUserId) {
      this.newMessage = this.chatDraftService.getDraft('single', this.chatUserId, this.singleUser?.channelId);
      setTimeout(() => this.singleMessageInput?.adjustTextareaHeight(), 0);
    } else if (this.groupChatBox && this.memberChannelId) {
      this.newGroupMessage = this.chatDraftService.getDraft('group', this.memberChannelId);
      setTimeout(() => this.groupMessageInput?.adjustTextareaHeight(), 0);
    } else {
      this.newMessage = "";
      this.newGroupMessage = "";
    }
  }

  editLastSentMessage() {
    const messages = this.singleChatBox ? this.singleChatMessages : this.groupChatMessages;
    if (!messages || messages.length === 0) return;
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (!msg) continue;
      const senderId = msg.sender_id || msg.senderId || msg.message?.sender_id;
      if (senderId === this.userId && !msg.isDeleted && !msg.deletedForEveryone) {
        if (!isMessageRedundantWithFile(msg)) {
          this.editMessage(msg);
          break;
        }
      }
    }
  }

  // ==================== CHAT HISTORY EXPORT LOGIC ====================
  exportChatHistory(format: 'txt' | 'json') {
    const isGroup = this.groupChatBox;
    const messages = isGroup ? this.groupChatMessages : this.singleChatMessages;
    if (!messages || messages.length === 0) {
      alertModalWithoutConfirm('warning', 'No messages to export');
      return;
    }

    const chatTitle = (isGroup ? this.groupChatName : this.singleChatUsername) || 'chat';
    const cleanTitle = chatTitle.replace(/[^a-zA-Z0-9_-]/g, '_');
    const timestampStr = new Date().toISOString().slice(0, 10);

    if (format === 'json') {
      const exportData = {
        exportedAt: new Date().toISOString(),
        chatTitle: chatTitle,
        isGroup: isGroup,
        messageCount: messages.length,
        messages: messages.map((m: any) => {
          const senderName = m.senderName || m.name || (m.sender_id === this.userId ? 'You' : (this.userNameMapping[m.sender_id] || 'User'));
          const files = this.getFilesFromMessage(m);
          return {
            id: m._id || m.id || m.messageId,
            senderId: m.sender_id || m.senderId,
            senderName: senderName,
            timestamp: m.createdAt,
            message: m.message || '',
            caption: m.meta?.caption || undefined,
            replyTo: m.replyTo || m.meta?.replyTo || undefined,
            reactions: m.reactions || undefined,
            files: files && files.length > 0 ? files.map((f: any) => ({ name: f.name, url: f.fileUrl, type: f.type, size: f.size })) : undefined
          };
        })
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      this.triggerDownloadBlob(blob, `${cleanTitle}_history_${timestampStr}.json`);
    } else {
      let txtContent = `========================================================\n`;
      txtContent += `Chat History Export: ${chatTitle}\n`;
      txtContent += `Export Date: ${new Date().toLocaleString()}\n`;
      txtContent += `Total Messages: ${messages.length}\n`;
      txtContent += `========================================================\n\n`;

      messages.forEach((m: any) => {
        const time = m.createdAt ? new Date(m.createdAt).toLocaleString() : '';
        const senderName = m.senderName || m.name || (m.sender_id === this.userId ? 'You' : (this.userNameMapping[m.sender_id] || 'User'));
        const text = m.message || m.meta?.caption || '';
        const files = this.getFilesFromMessage(m);

        txtContent += `[${time}] ${senderName}:\n`;
        if (text) {
          txtContent += `  ${text}\n`;
        }
        if (files && files.length > 0) {
          files.forEach((f: any) => {
            txtContent += `  [Attachment: ${f.name || 'file'} - ${f.fileUrl || ''}]\n`;
          });
        }
        txtContent += `\n`;
      });

      const blob = new Blob([txtContent], { type: 'text/plain;charset=utf-8' });
      this.triggerDownloadBlob(blob, `${cleanTitle}_history_${timestampStr}.txt`);
    }
  }

  private triggerDownloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  }

  openForwardModal(message: any) {
    this.forwardingMessage = message;
    this.showForwardModal = true;
    this.activeMessageMenuId = null;
    this.forwardSearchTerm = "";
    // Ensure sortedChats is fresh
    if (this.sortedChats.length === 0) {
      this.sortAllChatsByRecentMessages();
    }
  }

  closeForwardModal() {
    this.showForwardModal = false;
    this.forwardingMessage = null;
  }

  // ==================== SEEN INFO LOGIC ====================
  private isGroupMemberActive(m: any): boolean {
    if (!m) return false;
    if (m.isRemoved === true || m.isRemoved === 'true') return false;
    if (m.leftGroup === true || m.leftGroup === 'true') return false;
    if (m.isExited === true || m.isExited === 'true') return false;
    if (m.status === 'removed' || m.status === 'left') return false;
    return true;
  }

  getActiveGroupMembersForChannel(channelId?: string): any[] {
    const targetChannelId = String(channelId || this.memberChannelId || this.activeChannel || '').trim();

    let rawMembers: any[] = [];
    if (this.memberChannelId && String(this.memberChannelId).trim() === targetChannelId && this.allGroupMembers && this.allGroupMembers.length > 0) {
      rawMembers = this.allGroupMembers;
    } else {
      const group = (this.allGroupUsers || []).find((g: any) => String(g.channelId || '').trim() === targetChannelId) ||
        (this.filteredGroupUsers || []).find((g: any) => String(g.channelId || '').trim() === targetChannelId) ||
        (this.sortedChats || []).find((g: any) => String(g.channelId || '').trim() === targetChannelId);
      if (group && Array.isArray(group.members) && group.members.length > 0) {
        rawMembers = group.members;
      }
    }

    if (rawMembers.length === 0 && this.allGroupMembers && this.allGroupMembers.length > 0) {
      rawMembers = this.allGroupMembers;
    }

    if (rawMembers.length === 0 && this.singleUser) {
      rawMembers = [this.singleUser];
    }

    const seenIds = new Set<string>();
    return rawMembers.filter((m: any) => {
      const mId = String(m?.userId || m?.user_id || m?._id || m?.id || '').trim();
      if (!mId || seenIds.has(mId)) return false;
      if (!this.isGroupMemberActive(m)) return false;
      seenIds.add(mId);
      return true;
    });
  }

  async openSeenInfoModal(message: any) {
    this.activeMessageMenuId = null;
    this.currentSeenInfoMessage = message;

    const channelId = String(
      message?.channel_id ||
      message?.channelId ||
      message?.meta?.channel_id ||
      this.memberChannelId ||
      this.activeChannel ||
      ''
    ).trim();

    // Get the current active members for the channel (excluding removed members)
    const currentMembers = this.getActiveGroupMembersForChannel(channelId);

    if (!currentMembers || currentMembers.length === 0) {
      console.warn('No active members found for seen info');
      return;
    }

    const targetMsgId = String(message?._id || message?.id || message?.messageId || message?.mid || '');
    const fallbackSeenBy = Array.isArray(message.seenBy) ? message.seenBy.map((id: any) => String(id).trim()) : [];
    let seenUserIds: string[] = [];
    const receiptsMap = new Map<string, any>();

    // Pre-populate receipts from message if already available
    if (Array.isArray(message.receipts)) {
      message.receipts.forEach((r: any) => {
        const uId = r.user_id || r.userId || r._id;
        const rTime = r.time || r.seenAt || r.timestamp || r.updatedAt || r.createdAt;
        if (uId && (r.status === 'seen' || !r.status)) {
          const strUId = String(uId).trim();
          receiptsMap.set(strUId, { ...r, time: rTime });
          seenUserIds.push(strUId);
        }
      });
    }
    if (message.seenReceipts && typeof message.seenReceipts === 'object') {
      Object.keys(message.seenReceipts).forEach((uId) => {
        const rTime = message.seenReceipts[uId];
        const strUId = String(uId).trim();
        if (!receiptsMap.has(strUId)) {
          receiptsMap.set(strUId, { user_id: strUId, time: rTime });
          seenUserIds.push(strUId);
        }
      });
    }

    const activeCh = this.groupChannel || (this.memberChannelId ? this.cachedGroupChannels?.[this.memberChannelId] : null) || this.singleChannel;

    try {
      if (targetMsgId && activeCh && typeof activeCh.getMessagesReceipts === 'function') {
        let receiptsResponse: any = null;
        try {
          receiptsResponse = await activeCh.getMessagesReceipts({
            time: new Date().toISOString(),
            fetchAll: true
          });
        } catch (e) {
          const timeToFetch = message.createdAt || new Date().toISOString();
          receiptsResponse = await activeCh.getMessagesReceipts({ time: timeToFetch });
        }

        const rawList = Array.isArray(receiptsResponse)
          ? receiptsResponse
          : (Array.isArray(receiptsResponse?.receipts) ? receiptsResponse.receipts : []);

        let msgReceipts = rawList.find((r: any) => {
          const rId = String(r?._id || r?.id || r?.message_id || r?.messageId || '');
          return rId && targetMsgId && (rId === targetMsgId || rId.endsWith(targetMsgId) || targetMsgId.endsWith(rId));
        });

        if (!msgReceipts && message.createdAt) {
          try {
            const fallbackRes: any = await activeCh.getMessagesReceipts({ time: message.createdAt });
            const fbList = Array.isArray(fallbackRes)
              ? fallbackRes
              : (Array.isArray(fallbackRes?.receipts) ? fallbackRes.receipts : []);
            msgReceipts = fbList.find((r: any) => {
              const rId = String(r?._id || r?.id || r?.message_id || r?.messageId || '');
              return rId && targetMsgId && (rId === targetMsgId || rId.endsWith(targetMsgId) || targetMsgId.endsWith(rId));
            });
          } catch (e) {
            console.warn('Fallback getMessagesReceipts error:', e);
          }
        }

        if (msgReceipts) {
          const innerReceipts = Array.isArray(msgReceipts.receipts)
            ? msgReceipts.receipts
            : (Array.isArray(msgReceipts.seen) ? msgReceipts.seen : (Array.isArray(msgReceipts) ? msgReceipts : []));

          innerReceipts.forEach((r: any) => {
            const uId = r.user_id || r.userId || r._id;
            const rTime = r.time || r.seenAt || r.timestamp || r.updatedAt || r.createdAt;
            if (uId && (r.status === 'seen' || !r.status)) {
              const strUId = String(uId).trim();
              seenUserIds.push(strUId);
              receiptsMap.set(strUId, { ...r, time: rTime });
            }
          });
        }
      }
    } catch (err) {
      console.error('Error fetching message receipts:', err);
    }

    // Merge fallback seenBy IDs
    fallbackSeenBy.forEach((id: string) => {
      const strId = String(id).trim();
      if (strId && !seenUserIds.includes(strId)) {
        seenUserIds.push(strId);
      }
    });

    const uniqueSeenIds = Array.from(new Set(seenUserIds));
    this.currentSeenReceiptsMap = receiptsMap;
    this.currentSeenUserIds = uniqueSeenIds;

    // Build seen members list with user details for active members only
    const seenMembers: SeenMember[] = [];
    for (const member of currentMembers) {
      const memberId = member.userId || member.user_id || member._id || member.id;
      if (!memberId) continue;
      const strId = String(memberId).trim();
      if (uniqueSeenIds.includes(strId)) {
        let seenAtTime: string | Date | undefined = undefined;
        const receiptData = receiptsMap.get(strId);
        if (receiptData) {
          const rawTime = receiptData.time || receiptData.seenAt || receiptData.timestamp || receiptData.updatedAt || receiptData.createdAt;
          if (rawTime) {
            const parsed = new Date(rawTime);
            if (!isNaN(parsed.getTime())) {
              seenAtTime = rawTime;
            }
          }
        }

        seenMembers.push({
          userId: strId,
          displayName: member.displayName || member.userName || member.name || 'Unknown',
          profileImage: member.profileImage || member.image || undefined,
          seenAt: seenAtTime
        });
      }
    }

    this.seenInfoMembers = seenMembers;
    this.seenInfoTotalMembers = currentMembers.length;
    this.showSeenInfoModal = true;
    this.cdRef.detectChanges();
  }

  refreshSeenInfoModal(): void {
    if (!this.showSeenInfoModal || !this.currentSeenInfoMessage) return;

    const targetMsgId = String(
      this.currentSeenInfoMessage?._id ||
      this.currentSeenInfoMessage?.id ||
      this.currentSeenInfoMessage?.messageId ||
      this.currentSeenInfoMessage?.mid ||
      ''
    ).trim();

    const latestMsg = (this.groupChatMessages || []).find((m: any) => {
      const mId = String(m?._id || m?.id || m?.messageId || m?.mid || '').trim();
      return mId && targetMsgId && (mId === targetMsgId || mId.endsWith(targetMsgId) || targetMsgId.endsWith(mId));
    }) || this.currentSeenInfoMessage;

    if (Array.isArray(latestMsg.seenBy)) {
      latestMsg.seenBy.forEach((id: any) => {
        const strId = String(id).trim();
        if (strId && !this.currentSeenUserIds.includes(strId)) {
          this.currentSeenUserIds.push(strId);
        }
      });
    }
    if (latestMsg.seenReceipts && typeof latestMsg.seenReceipts === 'object') {
      Object.keys(latestMsg.seenReceipts).forEach((uId) => {
        const strUId = String(uId).trim();
        const rTime = latestMsg.seenReceipts[uId];
        if (!this.currentSeenReceiptsMap.has(strUId)) {
          this.currentSeenReceiptsMap.set(strUId, { user_id: strUId, time: rTime });
        }
        if (strUId && !this.currentSeenUserIds.includes(strUId)) {
          this.currentSeenUserIds.push(strUId);
        }
      });
    }

    const channelId = String(
      latestMsg?.channel_id ||
      latestMsg?.channelId ||
      latestMsg?.meta?.channel_id ||
      this.memberChannelId ||
      this.activeChannel ||
      ''
    ).trim();

    const activeMembers = this.getActiveGroupMembersForChannel(channelId);

    const seenMembers: SeenMember[] = [];
    for (const member of activeMembers) {
      const memberId = member.userId || member.user_id || member._id || member.id;
      if (!memberId) continue;
      const strId = String(memberId).trim();
      if (this.currentSeenUserIds.includes(strId)) {
        let seenAtTime: string | Date | undefined = undefined;
        const receiptData = this.currentSeenReceiptsMap.get(strId);
        if (receiptData) {
          const rawTime = receiptData.time || receiptData.seenAt || receiptData.timestamp || receiptData.updatedAt || receiptData.createdAt;
          if (rawTime) {
            const parsed = new Date(rawTime);
            if (!isNaN(parsed.getTime())) {
              seenAtTime = rawTime;
            }
          }
        }

        seenMembers.push({
          userId: strId,
          displayName: member.displayName || member.userName || member.name || 'Unknown',
          profileImage: member.profileImage || member.image || undefined,
          seenAt: seenAtTime
        });
      }
    }

    this.seenInfoMembers = seenMembers;
    this.seenInfoTotalMembers = activeMembers.length;
    this.cdRef.detectChanges();
  }

  closeSeenInfoModal() {
    this.showSeenInfoModal = false;
    this.seenInfoMembers = [];
    this.seenInfoTotalMembers = 0;
    this.currentSeenInfoMessage = null;
    this.currentSeenReceiptsMap.clear();
    this.currentSeenUserIds = [];
  }

  get filteredForwardChats() {
    let chats = this.sortedChats.filter((c: any) => {
      if (c.chatType === 'group' || c.groupName) {
        return !this.hasExitedGroup(c.channelId);
      }
      return true;
    });

    if (this.forwardSearchTerm) {
      const term = this.forwardSearchTerm.toLowerCase();
      chats = chats.filter((c: any) => {
        const name = c.displayName || c.userName || c.groupName || "";
        return name.toLowerCase().includes(term);
      });
    }
    return chats;
  }


  private hasLocalBlobFiles(message: any): boolean {
    if (!message) return false;
    const isBlob = (u: any) => typeof u === 'string' && u.startsWith('blob:');
    const urls = [
      ...(message.files || []).map((f: any) => f?.fileUrl),
      ...(message.meta?.files || []).map((f: any) => f?.fileUrl),
      ...(message._processedFiles || []).map((f: any) => f?.fileUrl),
      ...(Array.isArray(message.url) ? message.url : [message.url]),
    ];
    return !!message.isUploading || urls.some(isBlob);
  }

  /**
   * A message still uploading (or a stale copy of it) only has local blob: URLs, which are useless
   * to anyone else and die on refresh. Wait for the uploaded copy of the same group instead.
   */
  private async resolveForwardSource(message: any): Promise<any | null> {
    if (!this.hasLocalBlobFiles(message)) return message;
    const groupId = message.meta?.groupId || message.meta?.tempId || message._id;
    const findUploaded = () => {
      const lists = [
        this.singleChatMessages,
        this.groupChatMessages,
        ...Object.values(this.cachedSingleMessages || {}),
        ...Object.values(this.cachedGroupMessages || {}),
      ];
      for (const list of lists) {
        const found = (list as any[] || []).find((m: any) =>
          (m.meta?.groupId === groupId || m._id === groupId) && !this.hasLocalBlobFiles(m));
        if (found) return found;
      }
      return null;
    };
    const deadline = Date.now() + 120000;
    while (Date.now() < deadline) {
      const uploaded = findUploaded();
      if (uploaded) return uploaded;
      await new Promise(r => setTimeout(r, 500));
    }
    return null;
  }

  async sendForward(chat: any) {
    if (!this.forwardingMessage) return;

    const source = await this.resolveForwardSource(this.forwardingMessage);
    if (!source) {
      alertModalWithoutConfirm("error", "Please wait for the upload to finish before forwarding");
      this.closeForwardModal();
      return;
    }
    this.forwardingMessage = source;

    let chatId = chat.channelId;

    let meta = this.forwardingMessage.meta;
    if (typeof meta === 'string') {
      try { meta = JSON.parse(meta); } catch (e) { }
    }

    // Use caption from meta if available, otherwise use message
    let content = meta?.caption || this.forwardingMessage.message;
    let fileUrl: string | string[] | null = null;

    // Check if this is a carousel/grouped upload (multiple files or has groupId)
    let isCarousel = (this.forwardingMessage.files && this.forwardingMessage.files.length > 1) ||
      (meta?.groupId || meta?.isGroupedUpload);

    let singleUrl = this.forwardingMessage.url ||
      this.forwardingMessage.file ||
      this.forwardingMessage._doc?.url ||
      this.forwardingMessage.data?.url ||
      meta?.url ||
      meta?.fileUrl ||
      meta?.gifUrl ||
      (this.forwardingMessage.files && this.forwardingMessage.files.length > 0 ? this.forwardingMessage.files[0].fileUrl : null);

    const extractedFiles = this.getFilesFromMessage(this.forwardingMessage);
    if (extractedFiles && extractedFiles.length > 1) {
      isCarousel = true;
    } else if (Array.isArray(singleUrl) && singleUrl.length > 1) {
      isCarousel = true;
    }
    console.log("DEBUG: sendForward - isCarousel:", isCarousel);
    if (isCarousel && extractedFiles && extractedFiles.length > 1) {
      fileUrl = extractedFiles.map((f: any) => f.fileUrl);
    } else if (isCarousel && this.forwardingMessage.files && this.forwardingMessage.files.length > 0) {
      fileUrl = this.forwardingMessage.files.map((f: any) => f.fileUrl);
    } else {
      if (Array.isArray(singleUrl)) {
        if (isCarousel && singleUrl.length > 1) {
          fileUrl = singleUrl.map((u: any) => Array.isArray(u) ? u[0] : u);
        } else {
          if (singleUrl.length > 0 && Array.isArray(singleUrl[0])) {
            singleUrl = singleUrl[0][0];
          } else if (singleUrl.length > 0) {
            singleUrl = singleUrl[0];
          }
          if (singleUrl) {
            fileUrl = [singleUrl];
          }
        }
      } else {
        if (singleUrl) {
          fileUrl = [singleUrl];
        }
      }
    }

    console.log("EXTRACTED URL FINAL:", fileUrl);
    if (!isCarousel && fileUrl && fileUrl.length > 0 && isURL(content) && !this.forwardingMessage.files?.length && meta?.mediaType !== 'gif') {
      fileUrl = null;
    }
    if (!content || String(content).trim() === "") {
      if (fileUrl && fileUrl.length > 0) {
        const url = Array.isArray(fileUrl) ? fileUrl[0] : fileUrl;
        const mediaType = meta?.mediaType || (isImage(url) ? 'image' : isVideo(url) ? 'video' : isAudio(url) ? 'audio' : 'file');

        if (mediaType === 'gif' || meta?.mediaType === 'gif' || (typeof url === 'string' && url.toLowerCase().includes('.gif'))) {
          content = "sent a gif";
        } else if (mediaType === 'image') {
          content = "sent a photo";
        } else if (mediaType === 'video') {
          content = "sent a video";
        } else if (isVoiceMessage(url) || (typeof url === 'string' && url.includes('voice_message_'))) {
          content = (isVoiceMessage(url)) ? "🎤 Voice message" : "sent an audio";
          if (!meta) meta = {};
          meta.mediaType = 'audio';
        } else if (isDocument(url)) {
          content = "sent a document";
        } else {
          const filename = url.split('/').pop() || url.split('\\').pop() || 'file';
          content = filename;
        }
      } else {
        content = "[Non-text message]";
      }
    } else if (fileUrl && fileUrl.length > 0 && meta?.mediaType === 'gif') {
      const url = Array.isArray(fileUrl) ? fileUrl[0] : fileUrl;
      const filename = url.split('/').pop() || url.split('\\').pop() || 'gif';
      content = filename;
    }

    let forwardedFrom = this.forwardingMessage.meta?.forwardedFrom;
    if (!forwardedFrom) {
      const senderId = this.forwardingMessage.sender_id || this.forwardingMessage.sid;

      if (senderId === this.userId) {
        let myDisplayName = this.userNameMapping[this.userId];

        if (!myDisplayName || myDisplayName === this.senderUsername) {
          for (const group of this.allGroupUsers) {
            if (group.members) {
              const me = group.members.find((m: any) => String(m.userId) === String(this.userId));
              if (me && me.displayName) {
                myDisplayName = me.displayName;
                break;
              }
            }
          }
        }

        forwardedFrom = myDisplayName || this.senderUsername || 'You';
      } else {
        const user = this.allSingleUsers.find(u => u.userId === senderId);
        if (user) {
          forwardedFrom = user.displayName || user.userName;
        } else {
          const groupMember = this.allGroupMembers?.find((m: any) => m.userId === senderId);
          forwardedFrom = groupMember?.displayName || groupMember?.userName || this.userNameMapping[senderId] || 'User';
        }
      }
    }
    // Carry over link-preview meta when forwarding a URL message
    const isGroupTarget = chat.chatType === 'group' || !!chat.groupName || !!chat.members;
    if (!isGroupTarget && !this.isValidChannelId(chatId)) {
      chatId = await this.ensureSingleChatForForward(chat);
      if (!chatId) {
        alertModalWithoutConfirm("error", "Failed to forward message");
        return;
      }
    }

    let linkPreviewMeta: any = { isForwarded: true, forwardedFrom };
    let reqSearch: string[] = [];
    if (isURL(String(content).trim())) {
      // 1. Extract from original message meta first (normalize to ensure title/description/image/icon exist)
      const existingMeta = this.normalizeLinkMeta(meta);
      linkPreviewMeta = {
        ...linkPreviewMeta,
        ...existingMeta,
        isForwarded: true,
        forwardedFrom,
        ...this.getLinkContentCategorization(content)
      };

      // 2. Fetch fresh metadata for the target chat
      try {
        const body: any = { url: content, channelId: chatId };
        const res: any = await this.OnlineUsersService.isUrlValidSave(body);
        if (res?.data) {
          const normRes = this.normalizeLinkMeta(res.data.meta);
          linkPreviewMeta = {
            ...linkPreviewMeta,
            title: (normRes.title || linkPreviewMeta.title || "").trim(),
            description: (normRes.description || linkPreviewMeta.description || "").trim(),
            image: (normRes.image || linkPreviewMeta.image || "").trim(),
            icon: (normRes.icon || linkPreviewMeta.icon || "").trim(),
            version: res.data.version || linkPreviewMeta.version || '',
            thread: res.data.thread || [],
            fileId: res.data.fileId || body.fileId
          };
          // Store meta for backend full-text search
          if (linkPreviewMeta.title) reqSearch.push(linkPreviewMeta.title);
          if (linkPreviewMeta.description) reqSearch.push(linkPreviewMeta.description);
        }
      } catch (err) {
        console.warn('Failed to update version for forwarded URL:', err);
      }
    }


    // Check if this is a carousel/grouped upload
    const originalGroupId = meta?.groupId;
    const isOriginalGroupedUpload = meta?.isGroupedUpload || !!originalGroupId;

    // Generate tempId to track message lifecycle and deduplicate
    let tempId: string;
    if (isCarousel || isOriginalGroupedUpload) {
      // For carousel/grouped upload, use groupId as tempId
      tempId = Date.now().toString() + Math.random().toString(36).substr(2, 5);
      linkPreviewMeta.groupId = tempId;
      linkPreviewMeta.isGroupedUpload = true;
    } else {
      tempId = Date.now().toString() + Math.random().toString(36).substr(2, 5);
    }
    linkPreviewMeta.tempId = tempId;
    linkPreviewMeta.channel_id = chatId;
    if (fileUrl && fileUrl.length > 0) {
      const actualUrl = Array.isArray(fileUrl[0]) ? fileUrl[0][0] : fileUrl[0];
      if (meta?.mediaType) {
        linkPreviewMeta.mediaType = meta.mediaType;
      } else {
        if (actualUrl.toLowerCase().endsWith('.gif')) {
          linkPreviewMeta.mediaType = 'gif';
        } else if (isImage(actualUrl)) {
          linkPreviewMeta.mediaType = 'image';
        } else if (isVideo(actualUrl)) {
          linkPreviewMeta.mediaType = 'video';
        }
      }
      if (meta?.fileType) {
        linkPreviewMeta.fileType = meta.fileType;
      } else {
        if (actualUrl.toLowerCase().endsWith('.gif')) {
          linkPreviewMeta.fileType = 'image/gif';
        } else if (isImage(actualUrl)) {
          linkPreviewMeta.fileType = 'image/jpeg';
        } else if (isVideo(actualUrl)) {
          linkPreviewMeta.fileType = 'video/mp4';
        } else if (isAudio(actualUrl)) {
          linkPreviewMeta.fileType = 'audio/mpeg';
        } else if (isDocument(actualUrl)) {
          linkPreviewMeta.fileType = 'application/pdf';
        }
      }

      // Preserve gifUrl if it exists
      if (meta?.gifUrl) {
        linkPreviewMeta.gifUrl = meta.gifUrl;
      } else if (actualUrl.toLowerCase().endsWith('.gif')) {
        // Set gifUrl for GIFs
        linkPreviewMeta.gifUrl = actualUrl;
      }
      // Preserve caption from original message
      if (meta?.caption) {
        linkPreviewMeta.caption = meta.caption;
        linkPreviewMeta.captionContentType = isURL(meta.caption) ? 'url' : 'text';
      }
      // Preserve duration for voice/audio messages
      if (meta?.duration) {
        linkPreviewMeta.duration = meta.duration;
      }

      const categorization = this.getContentCategorization(actualUrl, meta?.fileType || getFileType(actualUrl));
      linkPreviewMeta = { ...linkPreviewMeta, ...categorization };
    }

    // Build files array preserving original file names, types and sizes
    let filesForPush: any[] = [];
    if (extractedFiles && extractedFiles.length > 0) {
      filesForPush = extractedFiles.map((f: any, idx: number) => {
        const u = f.fileUrl || f.url || (Array.isArray(fileUrl) ? fileUrl[idx] : fileUrl);
        return {
          fileUrl: typeof u === 'string' ? u : (u?.fileUrl || u?.url || ''),
          type: f.type || getFileType(u),
          name: f.name || f.ogFileName || getCleanFileName('', u),
          size: f.size
        };
      });
    } else if (fileUrl) {
      const urls = Array.isArray(fileUrl) ? fileUrl : [fileUrl];
      filesForPush = urls.map((u: any) => ({
        fileUrl: typeof u === 'string' ? u : (u?.fileUrl || u?.url || ''),
        type: (typeof u === 'object' && u?.type) ? u.type : getFileType(typeof u === 'string' ? u : (u?.fileUrl || u?.url)),
        name: (typeof u === 'object' && (u?.name || u?.ogFileName)) ? (u.name || u.ogFileName) : getCleanFileName('', typeof u === 'string' ? u : (u?.fileUrl || u?.url)),
        size: typeof u === 'object' ? u?.size : undefined
      }));
    }

    if (filesForPush.length > 0) {
      linkPreviewMeta.files = filesForPush;
      if (filesForPush[0]?.name) {
        linkPreviewMeta.ogFileName = filesForPush[0].name;
      }
    }

    const now = new Date().toISOString();
    const sidebarMsg = {
      message: content,
      url: Array.isArray(fileUrl) ? (fileUrl[0] || '') : (fileUrl || ''),
      sender_id: this.userId,
      createdAt: now,
      files: filesForPush,
      meta: { ...linkPreviewMeta, channel_id: chatId }
    };

    const msgForPush = {
      ...sidebarMsg,
      _id: undefined,
      id: undefined,
      files: filesForPush
    };

    // Determine target chat & chat type
    const isGroup = chat.chatType === 'group' || !!chat.groupName || !!chat.members;
    let targetChat: any = null;
    if (isGroup) {
      const gid = chat.channelId || chat._id;
      targetChat = this.filteredGroupUsers.find(g => (g.channelId || g._id) === gid) ||
        this.allGroupUsers.find(g => (g.channelId || g._id) === gid);
    } else {
      const uid = chat.userId;
      const cid = chat.channelId;
      targetChat = (uid && this.filteredSingleUsers.find(u => u.userId === uid)) ||
        (cid && this.filteredSingleUsers.find(u => u.channelId === cid)) ||
        (uid && this.allSingleUsers.find(u => u.userId === uid)) ||
        (cid && this.allSingleUsers.find(u => u.channelId === cid));
    }

    const chatToOpen = targetChat || chat;
    const finalChatId = isGroup ? (chatToOpen.channelId || chatToOpen._id || chatId) : (chatToOpen.channelId || chatId);

    if (!this.isValidChannelId(finalChatId)) {
      alertModalWithoutConfirm("error", "Failed to forward message");
      this.closeForwardModal();
      return;
    }

    const previousSidebarState = {
      lastMessage: targetChat?.lastMessage,
      lastMessageTimeAgo: targetChat?.lastMessageTimeAgo,
      metadata: this.getChatMetadata(finalChatId)
    };

    // Update target chat last message in sidebar immediately
    this.updateSidebarForIncomingMessage(sidebarMsg, finalChatId, isGroup ? 'group' : 'single', false);

    // Append to cache if target chat is cached so it's ready when opened
    if (isGroup) {
      if (this.cachedGroupMessages[finalChatId]) {
        this.appendMessageToBackgroundCache(finalChatId, msgForPush, true);
      }
    } else {
      if (this.cachedSingleMessages[finalChatId]) {
        this.appendMessageToBackgroundCache(finalChatId, msgForPush, false);
      }
    }

    this.sortAllChatsByRecentMessages();

    // Redirect to the target chat to which the message was forwarded
    const navigateToTargetChat = async () => {
      try {
        if (isGroup) {
          const gid = chatToOpen.channelId || chatToOpen._id;
          if (gid && !this.filteredGroupUsers.some(g => (g.channelId || g._id) === gid)) {
            this.filteredGroupUsers.unshift(chatToOpen);
          }
          if (gid && this.allGroupUsers && !this.allGroupUsers.some(g => (g.channelId || g._id) === gid)) {
            this.allGroupUsers.unshift(chatToOpen);
          }
          await this.showGroupChat(chatToOpen);
          if (!this.groupChatMessages.some(m => m.meta?.tempId === tempId)) {
            this.groupChatMessages = [...this.groupChatMessages, msgForPush];
            this.processGroupMessages();
            setTimeout(() => this.scrollToBottom(), 100);
          }
        } else {
          const uid = chatToOpen.userId || chat.userId;
          if (uid) {
            if (!this.filteredSingleUsers.some(u => u.userId === uid)) {
              this.filteredSingleUsers.unshift(chatToOpen);
            }
            if (this.allSingleUsers && !this.allSingleUsers.some(u => u.userId === uid)) {
              this.allSingleUsers.unshift(chatToOpen);
            }
            await this.startSingleChat(uid);
            if (!this.singleChatMessages.some(m => m.meta?.tempId === tempId)) {
              this.singleChatMessages = [...this.singleChatMessages, msgForPush];
              this.processMessages();
              setTimeout(() => this.scrollToBottom(), 100);
            }
          } else {
            this.onChatSelected(chatToOpen);
          }
        }
      } catch (err) {
        console.error("Error redirecting to forwarded chat:", err);
      }
      this.cdRef.detectChanges();
    };

    navigateToTargetChat();

    this.chatService.forwardMessage(finalChatId, content, forwardedFrom, fileUrl, linkPreviewMeta, reqSearch.length > 0 ? reqSearch : undefined).subscribe({
      next: (res: any) => {
        console.log("Message forwarded as", res);
        alertModalWithoutConfirm("success", `Forwarded to ${chat.displayName || chat.groupName || 'Chat'}`);
        // If API returns original ID, update msgForPush._id here
        const messageId = res?._id || res?.id || (res?.data && (res.data._id || res.data.id));
        const resMeta = res?.meta || (res?.data && res.data.meta);

        let found: any = null;
        if (!isGroup) {
          found = this.singleChatMessages.find(m => m.meta?.tempId === tempId);
        } else {
          found = this.groupChatMessages.find(m => m.meta?.tempId === tempId);
        }

        if (found && messageId) {
          found._id = messageId;
          found.id = messageId;
          if (resMeta) {
            found.meta = this.normalizeLinkMeta({ ...found.meta, ...resMeta });
          }
          this.cdRef.detectChanges();
        }

        // Also update in background cache if present
        const cachedList = isGroup ? this.cachedGroupMessages[finalChatId] : this.cachedSingleMessages[finalChatId];
        if (cachedList && messageId) {
          const cachedMsg = cachedList.find((m: any) => m.meta?.tempId === tempId);
          if (cachedMsg) {
            cachedMsg._id = messageId;
            cachedMsg.id = messageId;
            if (resMeta) {
              cachedMsg.meta = this.normalizeLinkMeta({ ...cachedMsg.meta, ...resMeta });
            }
          }
        }
      },
      error: (err) => {
        console.error("Failed to forward:", err);
        alertModalWithoutConfirm("error", "Failed to forward message");
        this.rollbackFailedForward(finalChatId, isGroup, tempId, previousSidebarState);
      }
    });
    this.closeForwardModal();
  }

  private async ensureSingleChatForForward(chat: any): Promise<string | null> {
    if (!chat?.userId) return null;
    try {
      const res: any = await firstValueFrom(
        this.api.sendSingleChatUserData({
          members: [
            {
              userId: chat.userId,
              userName: chat.userName,
              profileImage: chat.profileImage,
              displayName: chat.displayName
            },
          ],
          type: "single",
        })
      );
      const channelId = res?.data?.chat?.channelId;
      if (!this.isValidChannelId(channelId)) return null;

      const attachChannel = (u: any) =>
        String(u.userId) === String(chat.userId)
          ? { ...u, channelId, type: 'single', chatType: 'single' }
          : u;
      this.allSingleUsers = this.allSingleUsers.map(attachChannel);
      this.filteredSingleUsers = this.filteredSingleUsers.map(attachChannel);
      if (this.singleUser && String(this.singleUser.userId) === String(chat.userId)) {
        this.singleUser = { ...this.singleUser, channelId };
      }
      chat.channelId = channelId;
      chat.chatType = 'single';
      return channelId;
    } catch (e) {
      console.error("Failed to create chat for forward:", e);
      return null;
    }
  }

  private rollbackFailedForward(chatId: string, isGroup: boolean, tempId: string, previousSidebarState: any) {
    const withoutTemp = (list: any[]) => (list || []).filter((m: any) => m.meta?.tempId !== tempId);
    if (isGroup) {
      this.groupChatMessages = withoutTemp(this.groupChatMessages);
      if (this.cachedGroupMessages[chatId]) this.cachedGroupMessages[chatId] = withoutTemp(this.cachedGroupMessages[chatId]);
      this.processGroupMessages();
    } else {
      this.singleChatMessages = withoutTemp(this.singleChatMessages);
      if (this.cachedSingleMessages[chatId]) this.cachedSingleMessages[chatId] = withoutTemp(this.cachedSingleMessages[chatId]);
      this.processMessages();
    }

    const restore = (c: any) =>
      String(c.channelId).trim() === String(chatId).trim()
        ? { ...c, lastMessage: previousSidebarState.lastMessage, lastMessageTimeAgo: previousSidebarState.lastMessageTimeAgo }
        : c;
    if (isGroup) {
      this.allGroupUsers = this.allGroupUsers.map(restore);
      this.filteredGroupUsers = [...this.allGroupUsers];
    } else {
      this.allSingleUsers = this.allSingleUsers.map(restore);
      this.filteredSingleUsers = [...this.allSingleUsers];
    }
    try {
      const cache = JSON.parse(localStorage.getItem('chatMetadata') || '{}');
      if (previousSidebarState.metadata) cache[chatId] = previousSidebarState.metadata;
      else delete cache[chatId];
      localStorage.setItem('chatMetadata', JSON.stringify(cache));
    } catch (e) { console.error('Error restoring chatMetadata', e); }
    this.sortAllChatsByRecentMessages();
    this.cdRef.detectChanges();
  }

  async scrollToMessage(event: any) {
    const messageId = typeof event === 'string' ? event : (event?.id || event?.messageId || event?._id);
    if (!messageId) return;

    const replyTo = typeof event === 'object' ? event?.replyTo : null;
    const forceGroup = typeof event === 'object' ? !!event?.forceGroup : false;
    const sourceGroupName = replyTo?.sourceGroupName || replyTo?.meta?.sourceGroupName;
    // A quote's channelId / meta.channel_id is the chat the quoted message lives in (usually this one).
    // It names a source group only on a "Reply privately" quote, and never when it is a one-on-one chat;
    // otherwise clicking an ordinary quote opened the single chat as a placeholder "Group".
    const isSingleChannel = (id: any) => !!id && (this.allSingleUsers || []).some((u: any) =>
      String(u.channelId || u.channel_id || '').trim() === String(id).trim());
    const candidateGroupId = replyTo?.sourceGroupId || replyTo?.meta?.sourceGroupId ||
      (sourceGroupName ? replyTo?.channelId || replyTo?.meta?.channel_id : '');
    const sourceGroupId = isSingleChannel(candidateGroupId) ? '' : candidateGroupId;

    // Check if message element is already present in current DOM
    const element = document.getElementById('msg-' + messageId);

    // If navigation to group is forced (clicked group name), or if we are currently in single chat and the reply came from a group:
    if (forceGroup || (!this.groupChatBox && (sourceGroupId || sourceGroupName))) {
      let targetGroup: any = null;
      const gId = sourceGroupId ? String(sourceGroupId).trim() : '';
      const gName = sourceGroupName ? String(sourceGroupName).trim().toLowerCase() : '';

      if (gId || gName) {
        targetGroup = (this.allGroupUsers || []).find((g: any) => {
          const cId = String(g.channelId || g._id || g.id || '').trim();
          const name = String(g.groupName || g.name || '').trim().toLowerCase();
          return (gId && cId === gId) || (gName && name === gName);
        });

        if (!targetGroup && this.filteredGroupUsers) {
          targetGroup = this.filteredGroupUsers.find((g: any) => {
            const cId = String(g.channelId || g._id || g.id || '').trim();
            const name = String(g.groupName || g.name || '').trim().toLowerCase();
            return (gId && cId === gId) || (gName && name === gName);
          });
        }

        if (!targetGroup && this.sortedChats) {
          targetGroup = this.sortedChats.find((g: any) => {
            const cId = String(g.channelId || g._id || g.id || '').trim();
            const name = String(g.groupName || g.name || '').trim().toLowerCase();
            return (g.groupName || g.isGroup) && ((gId && cId === gId) || (gName && name === gName));
          });
        }

        if (!targetGroup && sourceGroupId) {
          targetGroup = {
            channelId: sourceGroupId,
            _id: sourceGroupId,
            groupName: sourceGroupName || 'Group'
          };
        }
      }

      const isCurrentGroup = this.groupChatBox && (
        (gId && String(this.memberChannelId || '').trim() === gId) ||
        (targetGroup && String(this.memberChannelId || '').trim() === String(targetGroup.channelId || targetGroup._id || '').trim())
      );

      if (targetGroup && !isCurrentGroup) {
        this.targetSearchMessageId = messageId;
        await this.showGroupChat(targetGroup, messageId);
        return;
      }

      // If already in target group:
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.highlightMessage(element);
        return;
      } else {
        this.targetSearchMessageId = messageId;
        this.scrollToSearchedMessage(messageId, 'group');
        return;
      }
    }

    // Standard navigation within current chat:
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.highlightMessage(element);
      return;
    }

    // Element not found in current DOM: check if it belongs to a referenced group
    if (sourceGroupId || sourceGroupName) {
      const gId = sourceGroupId ? String(sourceGroupId).trim() : '';
      const gName = sourceGroupName ? String(sourceGroupName).trim().toLowerCase() : '';
      const targetGroup = (this.allGroupUsers || []).find((g: any) => {
        const cId = String(g.channelId || g._id || g.id || '').trim();
        const name = String(g.groupName || g.name || '').trim().toLowerCase();
        return (gId && cId === gId) || (gName && name === gName);
      }) || (sourceGroupId ? { channelId: sourceGroupId, _id: sourceGroupId, groupName: sourceGroupName || 'Group' } : null);

      if (targetGroup && (!this.groupChatBox || String(this.memberChannelId || '').trim() !== String(targetGroup.channelId || targetGroup._id || '').trim())) {
        this.targetSearchMessageId = messageId;
        await this.showGroupChat(targetGroup, messageId);
        return;
      }
    }

    // Older message in current chat:
    this.targetSearchMessageId = messageId;
    if (this.groupChatBox) {
      this.scrollToSearchedMessage(messageId, 'group');
    } else {
      this.scrollToSearchedMessage(messageId, 'single');
    }
  }

  toggleStarredMessagesPanel() {
    this.isStarredMessagesPanelOpen = !this.isStarredMessagesPanelOpen;
    if (this.isStarredMessagesPanelOpen) {
      this.isGroupInfoOpen = false;
      this.isQuickSearchOpen = false;
    }
  }

  get currentChatIdForSearch(): string {
    if (this.groupChatBox) {
      const group = (this.allGroupUsers || []).find((g: any) => g.channelId === this.memberChannelId) || this.chatMap[this.memberChannelId];
      return group?._id || group?.chatId || group?.channelId || this.memberChannelId || '';
    }
    const targetUserId = this.selectedChatUserId || this.chatUserId;
    const single = (targetUserId ? this.chatMap[targetUserId] || (this.allSingleUsers || []).find((u: any) => u.userId === targetUserId) : null) || this.singleUser || this.chatMap[this.activeChannel];
    return single?._id || single?.chatId || single?.channelId || this.activeChannel || '';
  }

  toggleQuickSearch(): void {
    this.isQuickSearchOpen = !this.isQuickSearchOpen;
    if (this.isQuickSearchOpen) {
      this.isStarredMessagesPanelOpen = false;
      this.isGroupInfoOpen = false;
      this.isNotificationSettingsOpen = false;
    }
  }

  closeQuickSearch(): void {
    this.isQuickSearchOpen = false;
  }

  onQuickSearchMessageSelected(message: any): void {
    if (!message) return;
    const messageId = message._id || message.id || message.messageId;
    if (!messageId) return;

    this.scrollToMessage(messageId);

    if (this.isMobileView) {
      this.isQuickSearchOpen = false;
    }
  }

  // Starred Message logic
  toggleStarredMessages() {
    this.showStarredMessages = !this.showStarredMessages;
    if (this.showStarredMessages) {
      if (this.isMobileView) {
        this.showChatWindow = false;
        this.chatService.isChatOpenMobile$.next(false);
      }
      this.showAdvancedSearchFilters = false;
      this.showAdvancedSearchResults = false;
      this.starredMessages = [];
      this.starredMessagesPage = 1;
      this.hasMoreStarredMessages = true;
      this.loadStarredMessages();
    } else {
      // Returning to chat view - scroll to bottom
      setTimeout(() => {
        this.scrollToBottom();
      }, 100);
    }
  }

  async loadStarredMessages(customLimit?: number) {
    if (this.isStarredLoading || !this.hasMoreStarredMessages) return;
    this.isStarredLoading = true;
    try {
      const limit = customLimit || 20;
      const res: any = await this.chatService.getStarredMessages({
        page: this.starredMessagesPage,
        limit: limit,
        allChannels: true,
        channelId: this.activeChannel || this.memberChannelId || (this.sortedChats?.length > 0 ? this.sortedChats[0].channelId : undefined)
      });
      if (res?.data) {
        const data = res.data as any;
        // Check for common response structures: data.starredMessages, data.data.starredMessages, or just data as an array
        const result = data.starredMessages || data.starredMsgs || (data.data && (data.data.starredMessages || data.data.starredMsgs)) || (Array.isArray(data) ? data : []);

        // Group merged carousels
        const combined = [...this.starredMessages, ...result];
        const merged: any[] = [];
        const groupMap = new Map<string, any>();

        combined.forEach(record => {
          const msg = record.message || record;
          const groupId = msg.meta?.groupId;
          if (groupId) {
            let existing = groupMap.get(groupId);
            if (existing) {
              const targetMsg = existing.message || existing;
              const currentFiles = this.getFilesFromMessage(record);
              currentFiles.forEach(f => {
                if (!targetMsg.files) targetMsg.files = [];
                if (!targetMsg.files.some((ef: any) => ef.fileUrl === f.fileUrl)) {
                  targetMsg.files.push(f);
                }
              });
              if (!existing._allIds) existing._allIds = [existing.messageId || existing._id];
              const mid = record.messageId || record._id;
              if (mid && !existing._allIds.includes(mid)) existing._allIds.push(mid);

              if (!existing._starRecordIds) existing._starRecordIds = [existing._id || existing.id];
              const sid = record._id || record.id;
              if (sid && !existing._starRecordIds.includes(sid)) existing._starRecordIds.push(sid);

              delete existing._processedFiles; // Invalidate cache after merging
            } else {
              const clone = JSON.parse(JSON.stringify(record));
              const cloneMsg = clone.message || clone;
              cloneMsg.files = this.getFilesFromMessage(record);
              clone._allIds = [clone.messageId || clone._id];
              clone._starRecordIds = [clone._id || clone.id];
              clone.currentSlideIndex = 0;
              groupMap.set(groupId, clone);
              merged.push(clone);
            }

          } else {
            merged.push(record);
          }
        });

        this.starredMessages = merged;
        this.hasMoreStarredMessages = result.length === limit;
        this.starredMessagesPage++;

        const activeChan = this.singleChatBox ? this.activeChannel : this.memberChannelId;
        if (activeChan) {
          this.cachedStarredMessages[activeChan] = this.starredMessages;
        }
      }
    } catch (err) {
      console.error("Failed to load starred messages:", err);
    } finally {
      this.isStarredLoading = false;
      this.cdRef.detectChanges();
    }
  }

  refreshStarredMessages(limit: number = 100) {
    this.starredMessages = [];
    this.starredMessagesPage = 1;
    this.hasMoreStarredMessages = true;
    this.loadStarredMessages(limit);
  }

  async addStarMessageAction(message: any) {
    const idsToStarSet = new Set<string>();
    const mainId = message._id || message.id || message.messageId;
    if (mainId) idsToStarSet.add(String(mainId));

    // Priority 1: Use _allIds if available (incoming carousels and newly uploaded ones)
    if (message._allIds && Array.isArray(message._allIds)) {
      message._allIds.forEach((id: string) => idsToStarSet.add(String(id)));
    }

    // Priority 2: Collect from files array (our own uploads)
    if (message.files && Array.isArray(message.files)) {
      message.files.forEach((f: any) => {
        const fId = String(f._id || f.id || "");
        if (fId) idsToStarSet.add(fId);
      });
    }

    // Priority 3: Collect from groupId siblings in memory (handles documents/carousels missing _allIds)
    const groupId = message.meta?.groupId || message.groupId;
    if (groupId) {
      const collectFromList = (list: any[]) => {
        if (!list) return;
        list.forEach(m => {
          if ((m.meta?.groupId === groupId || m.groupId === groupId)) {
            const mId = m._id || m.id || m.messageId;
            if (mId) idsToStarSet.add(String(mId));
            if (m._allIds) (m._allIds as any[]).forEach(id => idsToStarSet.add(String(id)));
          }
        });
      };
      collectFromList(this.singleChatMessages);
      collectFromList(this.groupChatMessages);
      collectFromList(this.advancedSearchResults);
    }

    const idsToStar = Array.from(idsToStarSet);
    const channelId = message.meta?.channel_id || message.channel_id || this.memberChannelId || this.activeChannel;
    if (!channelId || idsToStar.length === 0) return;


    try {
      // 1. Update UI immediately for all IDs
      idsToStar.forEach(id => this.updateMessageStarredStatus(id, null, true));

      // 2. Call API for each ID (similar to how send-message handles grouped uploads)
      const results = await Promise.all(
        idsToStar.map(id => this.chatService.addStarMessage(channelId, id))
      );

      // 3. Handle results
      results.forEach((res: any, index) => {
        if (res?.data || res) {
          const data = res.data || res;
          if (!data.messageId) data.messageId = idsToStar[index];
          this.handleStarMessageEvent(data);
        }
      });

      // 4. Force refresh of starred list
      this.refreshStarredMessages(100);

    } catch (err) {
      console.error("Failed to star message(s):", err);
      // Revert UI for all IDs on error
      idsToStar.forEach(id => this.updateMessageStarredStatus(id, null, false));
    }
  }


  async unStarMessageAction(message: any) {
    const idsToUnstar: { messageId: string, starredMessageId: string }[] = [];

    // Helper to resolve IDs for a single star record or message object
    const resolveIds = (m: any) => {
      let mId = "";
      let sId = "";
      if (m.messageId && (m._id || m.id)) {
        mId = String(m.messageId).trim();
        sId = String(m._id || m.id).trim();
      } else {
        mId = String(m._id || m.id || m.messageId || "").trim();
        const findLatest = (list: any[]) => list.find(item => String(item._id || item.id || item.messageId || "").trim() === mId);
        const target = findLatest(this.singleChatMessages) || findLatest(this.groupChatMessages) || m;
        const myStar = (target.starredMessages || m.starredMessages || []).find((s: any) => String(s.userId) === String(this.userId));
        sId = (myStar?.starId || target.starredMessageId || target.meta?.starredMessageId || "").toString().trim();

        if (!sId && mId) {
          const found = this.starredMessages.find(sm => {
            const smMessageId = String(sm.messageId || "").trim();
            const smNestedId = String(sm.message?._id || sm.message?.id || "").trim();
            return smMessageId === mId || smNestedId === mId;
          });
          if (found) sId = String(found._id || found.id || "").trim();
        }
      }
      return { mId, sId };
    };

    // If it's a grouped upload or has a groupId, find all associated stars
    const groupId = message.meta?.groupId || message.groupId;
    if (message.meta?.isGroupedUpload || (message._allIds && message._allIds.length > 1) || groupId) {
      const carouselIdsSet = new Set<string>();
      carouselIdsSet.add(String(message._id || message.id || message.messageId));
      if (message._allIds) message._allIds.forEach((id: any) => carouselIdsSet.add(String(id)));
      if (message.files) message.files.forEach((f: any) => carouselIdsSet.add(String(f._id || f.id || "")));

      // Also collect from other messages in memory sharing the same groupId
      if (groupId) {
        const collectFromList = (list: any[]) => {
          if (!list) return;
          list.forEach(m => {
            if ((m.meta?.groupId === groupId || m.groupId === groupId)) {
              const mId = m._id || m.id || m.messageId;
              if (mId) carouselIdsSet.add(String(mId));
              if (m._allIds) (m._allIds as any[]).forEach(id => carouselIdsSet.add(String(id)));
            }
          });
        };
        collectFromList(this.singleChatMessages);
        collectFromList(this.groupChatMessages);
        collectFromList(this.advancedSearchResults);
      }

      const carouselIds = Array.from(carouselIdsSet);

      // Search in global starred messages for matching group or individual IDs
      const visitedStarIds = new Set<string>();

      this.starredMessages.forEach(sm => {
        const smGroupId = sm.message?.meta?.groupId || sm.meta?.groupId;
        const smMessageId = String(sm.messageId || "").trim();
        const smNestedId = String(sm.message?._id || sm.message?.id || "").trim();

        // Match by groupId OR individual message ID
        const isMatch = (groupId && smGroupId === groupId) ||
          carouselIds.includes(smMessageId) ||
          carouselIds.includes(smNestedId);

        if (isMatch) {
          // Add all associated star record IDs if it's a grouped entry
          if (sm._starRecordIds && Array.isArray(sm._starRecordIds) && sm._allIds && Array.isArray(sm._allIds)) {
            sm._allIds.forEach((mId: string, idx: number) => {
              const sId = sm._starRecordIds[idx] || sm._starRecordIds[0]; // Fallback to first if mismatch (unlikely)
              const sIdStr = String(sId);
              if (sIdStr && !visitedStarIds.has(sIdStr)) {
                idsToUnstar.push({ messageId: String(mId), starredMessageId: sIdStr });
                visitedStarIds.add(sIdStr);
              }
            });
          } else {
            const sId = String(sm._id || sm.id);
            if (sId && !visitedStarIds.has(sId)) {
              idsToUnstar.push({ messageId: smMessageId || smNestedId, starredMessageId: sId });
              visitedStarIds.add(sId);
            }
          }
        }
      });

      // If we found none in global list but have a main one, fallback to it
      if (idsToUnstar.length === 0) {
        [...(this.singleChatMessages || []), ...(this.groupChatMessages || [])].forEach(m => {
          const mId = String(m._id || m.id || m.messageId || "").trim();
          if (!carouselIds.includes(mId)) return;
          const myStar = (m.starredMessages || []).find((s: any) => String(s.userId) === String(this.userId));
          const sId = String(myStar?.starId || "").trim();
          if (sId && !visitedStarIds.has(sId)) {
            idsToUnstar.push({ messageId: mId, starredMessageId: sId });
            visitedStarIds.add(sId);
          }
        });
      }

      if (idsToUnstar.length === 0) {
        const { mId, sId } = resolveIds(message);
        if (mId && sId) idsToUnstar.push({ messageId: mId, starredMessageId: sId });
      }
    } else {
      const { mId, sId } = resolveIds(message);
      if (mId && sId) idsToUnstar.push({ messageId: mId, starredMessageId: sId });
    }

    if (idsToUnstar.length === 0) {
      console.warn("Cannot unstar: No messageId or starredMessageId resolved", message);
      return;
    }

    const channelId = message.meta?.channel_id || message.channel_id || (message.message && (message.message.channel_id || message.message.channelId)) || this.memberChannelId || this.activeChannel;
    if (!channelId) return;

    try {
      // 1. Update UI immediately for all
      idsToUnstar.forEach(item => this.updateMessageStarredStatus(item.messageId, null, false));

      // 2. Call API for each
      await Promise.all(
        idsToUnstar.map(item => this.chatService.unStarMessage(channelId, item.messageId, item.starredMessageId))
      );

      // 3. Ensure global list is updated
      idsToUnstar.forEach(item => this.handleUnStarMessageEvent({ messageId: item.messageId }));
    } catch (err) {
      console.error("Failed to unstar message(s):", err);
    }
  }


  handleStarMessageEvent(data: any) {
    const starredMsg = data.data || data;
    const messageId = starredMsg.messageId;
    const starredId = starredMsg._id || starredMsg.id;

    // Update in UI lists
    this.updateMessageStarredStatus(messageId, starredId, true);

    // If global list is open, add it if not exists, with grouping support
    const msg = starredMsg.message || starredMsg;
    const groupId = msg.meta?.groupId;
    let handledInGroup = false;

    if (groupId) {
      const existing = this.starredMessages.find(m => (m.message?.meta?.groupId || m.meta?.groupId) === groupId);
      if (existing) {
        handledInGroup = true;
        const targetMsg = existing.message || existing;
        const currentFiles = this.getFilesFromMessage(starredMsg);
        currentFiles.forEach(f => {
          if (!targetMsg.files) targetMsg.files = [];
          if (!targetMsg.files.some((ef: any) => ef.fileUrl === f.fileUrl)) {
            targetMsg.files.push(f);
          }
        });
        if (!existing._allIds) existing._allIds = [existing.messageId || existing._id];
        if (messageId && !existing._allIds.includes(messageId)) existing._allIds.push(messageId);

        if (!existing._starRecordIds) existing._starRecordIds = [existing._id || existing.id];
        if (starredId && !existing._starRecordIds.includes(starredId)) existing._starRecordIds.push(starredId);

        delete existing._processedFiles; // Clear helper cache
      }
    }

    if (!handledInGroup) {
      const exists = this.starredMessages.find(m => (m._id || m.id) === starredId);
      if (!exists) {
        const clone = JSON.parse(JSON.stringify(starredMsg));
        const cloneMsg = clone.message || clone;
        cloneMsg.files = this.getFilesFromMessage(starredMsg);
        clone._allIds = [clone.messageId || clone._id];
        clone._starRecordIds = [clone._id || clone.id];
        clone.currentSlideIndex = 0;
        this.starredMessages = [clone, ...this.starredMessages];
      }
    }


    this.cdRef.detectChanges();
    const activeChan = this.singleChatBox ? this.activeChannel : this.memberChannelId;
    if (activeChan) {
      delete this.cachedStarredMessages[activeChan];
    }
    this.refreshStarredMessages(100);

  }

  handleUnStarMessageEvent(data: any) {
    const unstarredData = data.data || data;
    const messageId = String(unstarredData.messageId || "");

    this.updateMessageStarredStatus(messageId, null, false);

    // Update global list - handle merged carousels
    this.starredMessages = this.starredMessages.filter(m => {
      const smMessageId = String(m.messageId || "");
      const smNestedId = String(m.message?._id || m.message?.id || "");
      const allIds = m._allIds || [];

      // If it's a direct match and NOT a group, remove it
      if ((smMessageId === messageId || smNestedId === messageId) && (!allIds || allIds.length <= 1)) {
        return false;
      }

      // If it's a group, check if this ID is part of it
      if (allIds.includes(messageId)) {
        // Remove this specific ID from the group
        m._allIds = allIds.filter((id: string) => id !== messageId);

        // Remove the corresponding file
        const msg = m.message || m;
        if (msg.files) {
          // This is a bit tricky as files don't always have IDs mapping 1:1 in incoming data, 
          // but we'll try our best or just refresh the list.
          // For now, if no IDs left, remove the whole group.
          if (m._allIds.length === 0) return false;

          // Trigger a refresh call to be safe as files list might be out of sync
          this.refreshStarredMessages(100);
        }
      }

      return true;
    });

    this.cdRef.detectChanges();
    const activeChan = this.singleChatBox ? this.activeChannel : this.memberChannelId;
    if (activeChan) {
      delete this.cachedStarredMessages[activeChan];
    }
  }


  handleEditStarMessageEvent(data: any) {
    const editedMsg = data.data || data;
    const messageId = editedMsg.messageId;

    // Update global list if exists
    if (this.showStarredMessages) {
      const idx = this.starredMessages.findIndex(m => m.messageId === messageId);
      if (idx !== -1) {
        this.starredMessages[idx] = { ...this.starredMessages[idx], ...editedMsg };
      }
    }
    this.cdRef.detectChanges();
  }

  handleMediaError(event: any, message: any) {
    if (!event.target) return;
    const type = getReplyMediaType(message);
    if (type === 'Video') {
      event.target.src = 'assets/video-file.jpg';
    } else if (type === 'Audio') {
      event.target.src = 'assets/audio-thumbnail.png';
    } else {
      event.target.src = 'assets/fileAttachment.png';
    }
    // Safety check: if the replacement src also fails, we hide the element
    event.target.onerror = () => {
      (event.target as any).style.display = 'none';
    };
  }

  updateMessageStarredStatus(messageId: string, starredId: string | null, isStarred: boolean) {
    if (!messageId) return;
    const targetId = String(messageId);
    let changed = false;

    const updateInList = (list: any[]) => {
      if (!list) return [];
      let listChanged = false;
      const newList = list.map(m => {
        const mId = String(m._id || m.id || m.messageId || "");
        const metaId = String(m.meta?.id || m.meta?.messageId || "");
        const fileIds = (m.files || []).map((f: any) => String(f._id || f.id || ""));

        if (mId === targetId || metaId === targetId || fileIds.includes(targetId)) {
          listChanged = true;
          changed = true;
          const otherStars = (m.starredMessages || []).filter((s: any) => String(s.userId) !== String(this.userId));
          return {
            ...m,
            isStarred,
            starredMessageId: starredId,
            starredMessages: isStarred && starredId ? [...otherStars, { starId: starredId, userId: this.userId }] : otherStars,
            meta: {
              ...(m.meta || {}),
              isStarred,
              starredMessageId: starredId
            }
          };
        }
        return m;
      });
      return listChanged ? newList : list;
    };

    const newSingle = updateInList(this.singleChatMessages);
    if (newSingle !== this.singleChatMessages) {
      this.singleChatMessages = newSingle;
    }

    const newGroup = updateInList(this.groupChatMessages);
    if (newGroup !== this.groupChatMessages) {
      this.groupChatMessages = newGroup;
    }

    if (this.AllSingleChatMsg) {
      if (this.AllSingleChatMsg.read) this.AllSingleChatMsg.read = updateInList(this.AllSingleChatMsg.read);
      if (this.AllSingleChatMsg.unread) this.AllSingleChatMsg.unread = updateInList(this.AllSingleChatMsg.unread);
    }
    if (this.AllGroupChatMsg) {
      if (this.AllGroupChatMsg.read) this.AllGroupChatMsg.read = updateInList(this.AllGroupChatMsg.read);
      if (this.AllGroupChatMsg.unread) this.AllGroupChatMsg.unread = updateInList(this.AllGroupChatMsg.unread);
    }

    if (changed) {
      this.cdRef.detectChanges();
    }
  }

  onStarredScroll(event: any) {
    const element = event.target;
    if (element.scrollHeight - element.scrollTop <= element.clientHeight + 100) {
      this.loadStarredMessages();
    }
  }

  goToStarredMessage(message: any) {
    if (!message) return;

    // 1. Resolve channelId robustly
    const channelId = message.meta?.channel_id ||
      message.channel_id ||
      (message.message && (message.message.channel_id || message.message.channelId));

    if (!channelId) {
      console.warn("goToStarredMessage: No channelId found in message object", message);
      return;
    }

    const chat = this.chatMap[channelId];
    if (chat) {
      // Close all starred message views
      this.showStarredMessages = false;
      this.isStarredMessagesPanelOpen = false;

      // 2. Resolve the actual message ID for scrolling
      // Starred record usually has messageId at top level, or nested in .message._id
      const actualMsgId = message.messageId ||
        (message.message && (message.message._id || message.message.id || message.message.messageId)) ||
        message._id ||
        message.id;

      if (!actualMsgId) {
        console.warn("goToStarredMessage: Could not resolve messageId for scrolling", message);
        // We still navigate to the chat even if scroll target is missing
      }

      // Navigate to chat and trigger scroll
      // We wrap the message to ensure it looks like a proper message object for onChatSelected
      const scrollTask = {
        ...chat,
        fromSearch: true,
        fromStarred: true,
        lastMessage: {
          ...(message.message || message),
          _id: actualMsgId,
          id: actualMsgId,
          messageId: actualMsgId
        }
      };

      this.isNavigatedFromStarred = true;
      this.onChatSelected(scrollTask);
    } else {
      console.warn("goToStarredMessage: Chat not found in chatMap for channelId", channelId);
    }
  }


  getChatName(msg: any): string {
    if (!msg) return 'Chat';
    const channelId = String(msg.message?.channel_id || msg.channel_id || '').trim();
    if (!channelId) return 'Chat';

    let chat = this.chatMap ? this.chatMap[channelId] : null;
    if (!chat) {
      // Fallback search in full lists if map lookup fails
      chat = (this.allGroupUsers || []).find(g => String(g?.channelId || '').trim() === channelId) ||
        (this.allSingleUsers || []).find(u => String(u?.channelId || '').trim() === channelId);
    }

    if (!chat) {
      // If still not found, check if we can at least determine 'You' for single chats
      const senderId = msg.message?.sender_id || msg.sender_id;
      if (senderId && this.userId && senderId !== this.userId) {
        return 'You';
      }
      return 'Chat';
    }

    if (chat.groupName) {
      return chat.groupName;
    }

    // Single Chat
    const senderId = msg.message?.sender_id || msg.sender_id;
    if (senderId === this.userId) {
      return chat.displayName || chat.userName || 'User';
    } else {
      return 'You';
    }
  }

  private injectPendingUploads(channelId: string, messages: any[]): any[] {
    if (!channelId) return messages;
    const pending = this.pendingUploadsByChannel.get(channelId) || [];

    // Filter out any pending uploads that might already be in the messages (e.g. if they finished and were fetched)
    const existingIds = new Set(messages.map(m => String(m._id || m.id || m.messageId)));
    const newPending = pending.filter(m => !existingIds.has(String(m._id)) && !existingIds.has(String(m.meta?.groupId)));

    // Keep unsent outbox messages visible across refetches
    const existingTempIds = new Set(messages.map(m => m.meta?.tempId).filter(Boolean));
    const queued = (this.outboxByChannel.get(channelId) || [])
      .map(item => item.message)
      .filter(m => !existingTempIds.has(m.meta?.tempId));

    if (newPending.length === 0 && queued.length === 0) return messages;
    return [...messages, ...newPending, ...queued];
  }

  // ==================== OFFLINE OUTBOX ====================
  private static readonly OUTBOX_STORAGE_KEY = "chatOutbox";

  // Converse sendMessage is a fire-and-forget emit (never throws, no ack), and socket.connected stays true
  // for ~20s after the network drops — so also require the browser to be online, or the message is lost
  private canSendOnChannel(channel: any): boolean {
    if (!navigator.onLine || !channel) return false;
    return !channel.socket || channel.socket.connected;
  }

  private queueOutboxMessage(channelId: string, item: OutboxItem) {
    if (!channelId) return;
    const queue = this.outboxByChannel.get(channelId) || [];
    queue.push(item);
    this.outboxByChannel.set(channelId, queue);
    this.persistOutbox();
    this.scheduleOutboxRetry(1000);
  }

  // Survive reloads: queued messages are kept in localStorage until delivered
  private persistOutbox() {
    try {
      const entries = Array.from(this.outboxByChannel.entries()).filter(([, queue]) => queue.length > 0);
      if (entries.length === 0) {
        localStorage.removeItem(MainUnifiedChatComponent.OUTBOX_STORAGE_KEY);
      } else {
        localStorage.setItem(MainUnifiedChatComponent.OUTBOX_STORAGE_KEY, JSON.stringify(entries));
      }
    } catch (err) {
      console.warn("[Outbox] Failed to persist outbox:", err);
    }
  }

  private restoreOutbox() {
    try {
      const raw = localStorage.getItem(MainUnifiedChatComponent.OUTBOX_STORAGE_KEY);
      if (!raw || !this.userId) return;
      const entries: [string, OutboxItem[]][] = JSON.parse(raw);
      for (const [channelId, queue] of entries) {
        // Only this user's messages (another account may have queued them on this device)
        const own = (queue || []).filter(item => item?.message?.sender_id === this.userId);
        if (own.length > 0) this.outboxByChannel.set(channelId, own);
      }
      this.scheduleOutboxRetry(2000);
    } catch (err) {
      console.warn("[Outbox] Failed to restore outbox:", err);
    }
  }

  private scheduleOutboxRetry(delayMs = 5000) {
    if (this.outboxRetryTimer || this.outboxByChannel.size === 0) return;
    this.outboxRetryTimer = setTimeout(() => {
      this.outboxRetryTimer = null;
      this.zone.run(() => this.flushOutbox());
    }, delayMs);
  }

  private async getConnectedOutboxChannel(channelId: string, chatType: OutboxItem["chatType"]): Promise<any> {
    const isGroup = chatType === "group";
    const cache = isGroup ? this.cachedGroupChannels : this.cachedSingleChannels;
    const isActive = isGroup
      ? this.groupChatBox && channelId === this.memberChannelId
      : this.singleChatBox && channelId === this.activeChannel;
    const existing = cache[channelId] || (isActive ? (isGroup ? this.groupChannel : this.singleChannel) : null);
    if (!navigator.onLine) return null;
    if (this.canSendOnChannel(existing)) return existing;
    if (!this.converse?.checkConnection?.()) return null;

    try {
      if (existing?.socket) {
        try { existing.socket.disconnect(); } catch (_) { }
      }
      const channel = await this.converse.connectChannel({ channelId, batch: 100 });
      if (!channel) return null;
      cache[channelId] = channel;
      if (isActive) {
        if (isGroup) {
          this.groupChannel = channel;
          this.attachGroupChannelListeners(channel, channelId);
        } else {
          this.singleChannel = channel;
          this.attachSingleChannelListeners(channel, channelId);
        }
      }
      return channel;
    } catch (err) {
      console.warn(`[Outbox] Failed to connect channel ${channelId}:`, err);
      return null;
    }
  }

  async flushOutbox() {
    if (this.outboxFlushing || this.outboxByChannel.size === 0) return;
    this.outboxFlushing = true;
    try {
      for (const [channelId, queue] of Array.from(this.outboxByChannel.entries())) {
        if (queue.length === 0) continue;
        const channel = await this.getConnectedOutboxChannel(channelId, queue[0].chatType);
        if (!channel) continue;

        // Send in order; stop at the first failure so later messages don't overtake it
        while (queue.length > 0) {
          const item = queue[0];
          const { pending, ...payload } = item.message;
          try {
            await channel.sendMessage(payload);
          } catch (err) {
            console.warn(`[Outbox] Send failed for ${channelId}, will retry:`, err);
            break;
          }
          queue.shift();
          this.markOutboxMessageSent(channelId, item);
          this.onOutboxMessageDelivered(channelId, item);
          this.persistOutbox();
        }
        if (queue.length === 0) this.outboxByChannel.delete(channelId);
      }
    } finally {
      this.outboxFlushing = false;
      this.persistOutbox();
      this.cdRef.detectChanges();
      this.scheduleOutboxRetry();
    }
  }

  // After a reload the rendered message is a copy, so clear the clock by tempId as well as by reference
  private markOutboxMessageSent(channelId: string, item: OutboxItem) {
    item.message.pending = false;
    const tempId = item.message.meta?.tempId;
    const lists = item.chatType === "group"
      ? [this.groupChatMessages, this.cachedGroupMessages[channelId]]
      : [this.singleChatMessages, this.cachedSingleMessages[channelId]];
    for (const list of lists) {
      (list || []).forEach((m: any) => {
        if (m !== item.message && tempId && m?.meta?.tempId === tempId) m.pending = false;
      });
    }

    // Chat list preview: clock -> tick
    if (tempId) {
      this.forEachChatListEntry(channelId, (chat) => {
        if (chat.lastMessage?.meta?.tempId === tempId) chat.lastMessage.pending = false;
      });
    }
  }

  private forEachChatListEntry(channelId: string, fn: (chat: any) => void) {
    const lists = [this.allSingleUsers, this.filteredSingleUsers, this.allGroupUsers, this.filteredGroupUsers, this.sortedChats];
    for (const list of lists) {
      (list || []).forEach((chat: any) => {
        if ((chat?.channelId || chat?.channel_id) === channelId) fn(chat);
      });
    }
  }

  // Chat list comes from the server, which doesn't know about queued messages — overlay the newest one per chat
  private applyOutboxToChatPreviews() {
    this.outboxByChannel.forEach((queue, channelId) => {
      const last = queue[queue.length - 1];
      if (!last) return;
      const queuedAt = new Date(last.message.createdAt).getTime();
      this.forEachChatListEntry(channelId, (chat) => {
        const currentAt = chat.lastMessage?.createdAt ? new Date(chat.lastMessage.createdAt).getTime() : 0;
        if (currentAt > queuedAt) return;
        chat.lastMessage = {
          ...last.message,
          message: last.preview || last.message.message,
          pending: true,
        };
        chat.lastMessageTimeAgo = getTimeAgo(last.message.createdAt);
      });
    });
  }

  private onOutboxMessageDelivered(channelId: string, item: OutboxItem) {
    if (this.chatCacheService && channelId) {
      this.chatCacheService.saveMessage(channelId, item.message);
    }
    (item.notifications || []).forEach(n => this.chatService.emitPushNotification(n));
    if (item.chatType === "single") {
      this.OnlineUsersService.recentNotify(item.message);
    }
  }
}
